/**
 * Copyright 2024 Ceeblue B.V.
 * This file is part of https://github.com/CeeblueTV/web-utils which is released under GNU Affero General Public License.
 * See file LICENSE or go to https://spdx.org/licenses/AGPL-3.0-or-later.html for full license details.
 */
/**
 * BinaryReader allows to read binary data
 */
const _decoder$1 = new TextDecoder();
class BinaryReader {
    constructor(data) {
        this._data = ArrayBuffer.isView(data)
            ? new Uint8Array(data.buffer, data.byteOffset, data.byteLength)
            : new Uint8Array(data);
        this._size = this._data.byteLength;
        this._position = 0;
        this._view = new DataView(this._data.buffer, this._data.byteOffset, this._size);
    }
    data() {
        return this._data;
    }
    size() {
        return this._size;
    }
    available() {
        return this._size - this._position;
    }
    value(position = this._position) {
        return this._data[position];
    }
    position() {
        return this._position;
    }
    reset(position = 0) {
        this._position = Math.max(0, position > this._size ? this._size : position);
    }
    shrink(available) {
        const rest = this._size - this._position;
        if (available > rest) {
            return rest;
        }
        this._size = this._position + available;
        return available;
    }
    next(count = 1) {
        const rest = this._size - this._position;
        if (count > rest) {
            count = rest;
        }
        this._position = Math.max(0, this._position + count);
        return count;
    }
    read8() {
        return this.next(1) === 1 ? this._view.getUint8(this._position - 1) : 0;
    }
    read16() {
        return this.next(2) === 2 ? this._view.getUint16(this._position - 2) : 0;
    }
    read24() {
        return this.next(3) === 3
            ? (this._view.getUint16(this._position - 3) << 8) | (this._view.getUint8(this._position - 1) & 0xff)
            : 0;
    }
    read32() {
        return this.next(4) === 4 ? this._view.getUint32(this._position - 4) : 0;
    }
    read64() {
        if (this.next(8) !== 8) {
            return 0;
        }
        return this._view.getUint32(this._position - 8) * 4294967296 + this._view.getUint32(this._position - 4);
    }
    readFloat() {
        return this.next(4) === 4 ? this._view.getFloat32(this._position - 4) : 0;
    }
    readDouble() {
        return this.next(8) === 8 ? this._view.getFloat64(this._position - 8) : 0;
    }
    read7Bit() {
        let result = 0;
        let factor = 1;
        while (this.available()) {
            const byte = this.read8();
            result += (byte & 0x7f) * factor;
            if (!(byte & 0x80)) {
                break;
            }
            factor *= 128;
        }
        return result;
    }
    readString() {
        let i = this._position;
        while (i < this._size && this._data[i]) {
            ++i;
        }
        const result = this.read(i - this._position);
        this.next(); // skip the 0 termination
        return _decoder$1.decode(result);
    }
    readHex(size) {
        let hex = '';
        while (size-- > 0) {
            hex += ('0' + this.read8().toString(16)).slice(-2);
        }
        return hex;
    }
    /**
     * Read bytes, to convert bytes to string use String.fromCharCode(...reader.read(size)) or Util.stringify
     * @param {UInt32} size
     */
    read(size = this.available()) {
        if (this.available() < size) {
            return new Uint8Array(size); // default value = empty bytearray!
        }
        const pos = this._position;
        return this._data.subarray(pos, Math.max(pos, (this._position += size)));
    }
}/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol */


function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};/**
 * Copyright 2024 Ceeblue B.V.
 * This file is part of https://github.com/CeeblueTV/web-utils which is released under GNU Affero General Public License.
 * See file LICENSE or go to https://spdx.org/licenses/AGPL-3.0-or-later.html for full license details.
 */
const _decoder = new TextDecoder();
const _encoder = new TextEncoder();
/* eslint-disable @typescript-eslint/no-explicit-any */
const _perf = performance; // to increase x10 now performance!
/**
 * Some basic utility functions
 */
/**
 * An empty lambda function, pratical to disable default behavior of function or events which are not expected to be null
 * @example
 * console.log = Util.EMPTY_FUNCTION; // disable logs without breaking calls
 */
const EMPTY_FUNCTION = () => { };
/**
 * Returns an efficient timestamp in milliseconds elapsed since performance.timeOrigin,
 * representing the start of the current JavaScript execution context.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Performance/timeOrigin
 *
 * @remarks Each Web Worker runs in a separate JS context, so timestamps
 * are not directly comparable between different workers. Use {@link unixTime}
 * for comparable timestamps across different Web Workers.
 */
function time() {
    return Math.floor(_perf.now());
}
/**
 * Returns an efficient Unix timestamp in milliseconds.
 *
 * Unix timestamps are universally comparable across different JavaScript
 * contexts (e.g., Web Workers), as they reference the number of milliseconds
 * elapsed since January 1, 1970 (UNIX epoch).
 */
function unixTime() {
    return Math.floor(_perf.timeOrigin + _perf.now());
}
/**
 * Parse query and returns it in an easy-to-use Javascript object form
 * @param urlOrQueryOrSearch string, url, or searchParams containing query. If not set it uses `location.search` to determinate query.
 * @returns An javascript object containing each option
 */
function options(urlOrQueryOrSearch = typeof location === 'undefined'
    ? undefined
    : location) {
    if (!urlOrQueryOrSearch) {
        return {};
    }
    try {
        const url = urlOrQueryOrSearch;
        urlOrQueryOrSearch = new URL(url).searchParams;
    }
    catch (_a) {
        if (typeof urlOrQueryOrSearch == 'string') {
            if (urlOrQueryOrSearch.startsWith('?')) {
                urlOrQueryOrSearch = urlOrQueryOrSearch.substring(1);
            }
            urlOrQueryOrSearch = new URLSearchParams(urlOrQueryOrSearch);
        }
    }
    // works same if urlOrQueryOrSearch is null, integer, or a already object etc...
    return objectFrom(urlOrQueryOrSearch, { withType: true, noEmptyString: true });
}
/**
 * Returns an easy-to-use Javascript object something iterable, such as a Map, Set, or Array
 * @param value iterable input
 * @param params.withType `false`, if set it tries to cast string value to a JS number/boolean/undefined/null type.
 * @param params.noEmptyString `false`, if set it converts empty string value to a true boolean, usefull to allow a `if(result.key)` check for example
 * @returns An javascript object
 */
function objectFrom(value, params) {
    params = Object.assign({ withType: false, noEmptyString: false }, params);
    const obj = {};
    if (!value) {
        return obj;
    }
    for (const [key, val] of iterableEntries(value)) {
        value = val;
        if (params.withType && value != null && value.substring) {
            if (value) {
                const number = Number(value);
                if (isNaN(number)) {
                    switch (value.toLowerCase()) {
                        case 'true':
                            value = true;
                            break;
                        case 'false':
                            value = false;
                            break;
                        case 'null':
                            value = null;
                            break;
                        case 'undefined':
                            value = undefined;
                            break;
                    }
                }
                else {
                    value = number;
                }
            }
            else if (params.noEmptyString) {
                // if empty string => TRUE to allow a if(options.key) check for example
                value = true;
            }
        }
        if (obj[key]) {
            if (!Array.isArray(obj[key])) {
                obj[key] = new Array(obj[key]);
            }
            obj[key].push(value);
        }
        else {
            obj[key] = value;
        }
    }
    return obj;
}
/**
 * Returns a IterableIterator<[string, any]> from any iterable input like Map, Set, Array, or Object.
 *
 * For all other types of values (including `null` or `undefined`) it returns an empty iterator.
 *
 * @param value An iterable input
 * @returns a IterableIterator<[string, any]>
 */
function iterableEntries(value) {
    if (!value) {
        return (function* () { })();
    }
    if (typeof value.entries === 'function') {
        value = value.entries();
    }
    if (typeof value[Symbol.iterator] === 'function') {
        return value;
    }
    return (function* () {
        for (const key in value) {
            yield [key.toString(), value[key]];
        }
    })();
}
/**
 * Converts various data types, such as objects, strings, exceptions, errors,
 * or numbers, into a string representation. Since it offers a more comprehensive format,
 * this function is preferred to `JSON.stringify()`.
 *
 * @param obj Any objects, strings, exceptions, errors, or number
 * @param params.space `''`, allows to configure space in the string representation
 * @param params.decimal `2`, allows to choose the number of decimal to display in the string representation
 * @param params.recursion `2`, recursion depth to stringify recursively every object value until this depth,
 * beware if a value refers to a already parsed value an infinite loop will occur
 * @param params.noBin `false`, when set skip binary encoding and write inplace a bin-length information
 * @returns the final string representation
 */
// Online Javascript Editor for free
// Write, Edit and Run your Javascript code using JS Online Compiler
function stringify(obj, params = {}) {
    params = Object.assign({ space: ' ', decimal: 2, recursion: 2, noBin: false }, params);
    if (obj == null) {
        return String(obj);
    }
    const error = obj.error || obj.message;
    if (error) {
        // is a error!
        obj = error;
    }
    // number
    if (obj.toFixed) {
        return obj.toFixed(Number(params.decimal) || 0);
    }
    if (obj.byteLength != null && (obj === null || obj === void 0 ? void 0 : obj[Symbol.iterator])) {
        // Binary!
        if (!params.noBin) {
            return _decoder.decode(obj);
        }
        return '[' + obj.byteLength + '#bytes]';
    }
    // boolean or string type or stop recursivity
    if (typeof obj === 'boolean' || obj.substring || !params.recursion) {
        // is already a string OR has to be stringified
        return typeof obj === 'object' ? Object.prototype.toString.call(obj) : String(obj);
    }
    const space = params.space;
    if (obj instanceof Set) {
        let res = 'Set[';
        let first = true;
        for (const value of obj.values()) {
            if (!first) {
                res += ',' + space;
            }
            first = false;
            res += stringify(value, Object.assign(Object.assign({}, params), { recursion: params.recursion - 1 }));
        }
        return res + ']';
    }
    if (obj instanceof Map) {
        let res = 'Map{';
        let first = true;
        for (const [k, v] of obj.entries()) {
            if (!first) {
                res += ',' + space;
            }
            first = false;
            res += stringify(k, Object.assign(Object.assign({}, params), { recursion: params.recursion - 1 }));
            res += ':' + stringify(v, Object.assign(Object.assign({}, params), { recursion: params.recursion - 1 }));
        }
        return res + '}';
    }
    if (Array.isArray(obj)) {
        // Array!
        let res = '[';
        let first = true;
        for (const value of obj) {
            if (!first) {
                res += ',' + space;
            }
            first = false;
            res += stringify(value, Object.assign(Object.assign({}, params), { recursion: params.recursion - 1 }));
        }
        return res + ']';
    }
    let res = '{';
    let first = true;
    for (const name in obj) {
        if (!first) {
            res += ',' + space;
        }
        first = false;
        res += name + ':';
        res += stringify(obj[name], Object.assign(Object.assign({}, params), { recursion: params.recursion - 1 }));
    }
    return res + '}';
}
/**
 * Encode a string to a binary representation
 * @param value string value to convert
 * @returns binary conversion
 */
function toBin(value) {
    return _encoder.encode(value);
}
/**
 * Execute a promise in a safe way with a timeout if caller doesn't resolve it in the accurate time
 */
function safePromise(timeout, promise) {
    // Returns a race between our timeout and the passed in promise
    let timer;
    return Promise.race([
        promise instanceof Promise ? promise : new Promise(promise),
        new Promise((resolve, reject) => (timer = setTimeout(() => reject('timed out in ' + timeout + 'ms'), timeout)))
    ]).finally(() => clearTimeout(timer));
}
/**
 * Wait in milliseconds, requires a call with await keyword!
 */
function sleep(ms) {
    return __awaiter(this, void 0, void 0, function* () {
        return new Promise(resolve => {
            setTimeout(resolve, ms);
        });
    });
}
/**
 * Test equality between two value whatever their type, object and array included
 */
function equal(a, b, seen = new WeakMap()) {
    // 1. Check primitve identiy or NaN
    if (a === b) {
        return true;
    }
    if (Number.isNaN(a) && Number.isNaN(b)) {
        return true;
    }
    // 2. Check the both are complexe object
    if (Object(a) !== a || Object(b) !== b) {
        return false;
    }
    // 3. Catch circular reference
    if (seen.has(a)) {
        return seen.get(a) === b;
    }
    seen.set(a, b);
    // 4. Check « toStringTag » (Date, RegExp, Map, Set…)
    const tagA = Object.prototype.toString.call(a);
    if (tagA !== Object.prototype.toString.call(b)) {
        return false;
    }
    // 5. Special Case
    switch (tagA) {
        case '[object Date]':
            return a.getTime() === b.getTime();
        case '[object RegExp]':
            return a.source === b.source && a.flags === b.flags;
        case '[object Set]':
        case '[object Map]': {
            if (a.size !== b.size) {
                return false;
            }
            const aKeys = a.keys();
            const bKeys = a.keys();
            const aValues = a.values();
            const bValues = b.values();
            let aKey;
            while (!(aKey = aKeys.next()).done) {
                if (!equal(aKey.value, bKeys.next().value)) {
                    return false;
                }
                const aValue = aValues.next().value;
                const bValue = bValues.next().value;
                if (aValue !== aKey && !equal(aValue, bValue)) {
                    return false;
                }
            }
            return true;
        }
    }
    // 6. Arrays
    if (Array.isArray(a)) {
        if (!Array.isArray(b) || a.length !== b.length) {
            return false;
        }
        for (let i = 0; i < a.length; i++) {
            if (!equal(a[i], b[i], seen)) {
                return false;
            }
        }
        return true;
    }
    // 7. Generic object : keys + symbols
    const keysA = [...Object.keys(a), ...Object.getOwnPropertySymbols(a)];
    if (keysA.length !== Object.keys(b).length + Object.getOwnPropertySymbols(b).length) {
        return false;
    }
    for (const key of keysA) {
        if (!equal(a[key], b[key], seen)) {
            return false;
        }
    }
    return true;
}
/**
 * Fetch help method adding an explicit error property when Response is NOK, with the more accurate textual error inside
 */
function fetch(input, init) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = (yield self.fetch(input, init));
        if (!response.ok) {
            if (response.body) {
                response.error = yield response.text();
            }
            if (!response.error) {
                response.error = response.statusText || response.status.toString() || 'Unknown error';
            }
        }
        return response;
    });
}
/**
 * Fetch help method adding an explicit error property when Response is NOK, with the more accurate textual error inside
 * Also measure the rtt of fetching and returns it in the property Response.rtt (guaranteed to be ≥ 1),
 * supports subtracting server processing time using either the Response-Delay or CMSD-rd header when available
 *
 * WIP => replace the current implementation to use Resource Timing API
 */
function fetchWithRTT(input, init) {
    return __awaiter(this, void 0, void 0, function* () {
        // A first OPTIONS request to establish a connection (keep-alive)
        yield fetch(input, Object.assign(Object.assign({}, init), { method: 'OPTIONS' }));
        // Actual RTT measurement
        const startTime = time();
        const response = (yield fetch(input, init));
        response.rtt = time() - startTime;
        // remove the ResponseDelay if indicated by the server
        let responseDelay = Number(response.headers.get('Response-Delay')) || 0;
        if (!responseDelay) {
            // search if we have a CMSD info?
            // cmsd-dynamic "fly";rd=1
            const cmsd = response.headers.get('cmsd-dynamic');
            if (cmsd) {
                for (const param of cmsd.split(';')) {
                    const [name, value] = param.split('=');
                    if (name.trim().toLowerCase() === 'rd') {
                        responseDelay = Number(value) || responseDelay;
                    }
                }
            }
        }
        response.rtt = Math.max(1, response.rtt - responseDelay);
        return response;
    });
}
/**
 * Get Extension part from path
 * @param path path to parse
 * @returns the extension
 */
function getExtension(path) {
    const dot = path.lastIndexOf('.');
    const ext = dot >= 0 && dot > path.lastIndexOf('/') ? path.substring(dot) : '';
    return ext;
}
/**
 * Get File part from path
 * @param path path to parse
 * @returns the file name
 */
function getFile(path) {
    return path.substring(path.lastIndexOf('/') + 1);
}
/**
 * Get Base File part from path, without extension
 * @param path path to parse
 * @returns the base file name
 */
function getBaseFile(path) {
    const dot = path.lastIndexOf('.');
    const file = path.lastIndexOf('/') + 1;
    return dot >= 0 && dot >= file ? path.substring(file, dot) : path.substring(file);
}
function codesFromString(value) {
    const codes = [];
    for (let i = 0; i < value.length; ++i) {
        codes.push(value.charCodeAt(i));
    }
    return codes;
}
/**
 * String Trim function with customizable chars
 * @param value string to trim
 * @param chars chars to use to trim
 * @returns string trimmed
 */
function trim(value, chars = ' ') {
    const codes = codesFromString(chars);
    let start = 0;
    while (start < value.length && codes.includes(value.charCodeAt(start))) {
        ++start;
    }
    let end = value.length;
    while (end > 0 && codes.includes(value.charCodeAt(end - 1))) {
        --end;
    }
    return value.substring(start, end);
}
/**
 * String Trim Start function with customizable chars
 * @param value string to trim start
 * @param chars chars to use to trim start
 * @returns string trimmed
 */
function trimStart(value, chars = ' ') {
    const codes = codesFromString(chars);
    let i = 0;
    while (i < value.length && codes.includes(value.charCodeAt(i))) {
        ++i;
    }
    return value.substring(i);
}
/**
 * String Trim End function with customizable chars
 * @param value string to trim end
 * @param chars chars to use to trim end
 * @returns string trimmed
 */
function trimEnd(value, chars = ' ') {
    const codes = codesFromString(chars);
    let i = value.length;
    while (i > 0 && codes.includes(value.charCodeAt(i - 1))) {
        --i;
    }
    return value.substring(0, i);
}
/**
 * Wraps an object with a Proxy that makes property access case-insensitive.
 *
 * Property lookup (e.g. `obj.Foo` or `obj.foo`) will resolve to the same underlying key, regardless of casing.
 * Only affects string-based property access (not symbols).
 *
 * @param obj - The original object.
 * @returns A proxied object with case-insensitive property access.
 */
function caseInsensitive(obj) {
    return new Proxy(obj, {
        get(target, prop, receiver) {
            if (typeof prop === 'string') {
                const key = Object.keys(target).find(k => k.toLowerCase() === prop.toLowerCase());
                if (key !== undefined) {
                    return Reflect.get(target, key, receiver);
                }
            }
            return Reflect.get(target, prop, receiver);
        }
    });
}
/**
 * Fast, deterministic, non-cryptographic hash function for strings.
 *
 * Produces a 53-bit hash encoded as a 14-character hexadecimal string.
 * Useful for generating stable identifiers from strings with lower overhead
 * than a cryptographic hash.
 *
 * @note This function is designed for speed and consistency, not security.
 * It must not be used as a substitute for a cryptographic hash function.
 */
function hash(value) {
    let h1 = 0xdeadbeef;
    let h2 = 0x41c6ce57;
    for (let i = 0; i < value.length; i += 1) {
        const ch = value.charCodeAt(i);
        h1 = Math.imul(h1 ^ ch, 2654435761);
        h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    const hash = 4294967296 * (h2 & 0x1fffff) + (h1 >>> 0);
    return hash.toString(16).padStart(14, '0');
}var Util=/*#__PURE__*/Object.freeze({__proto__:null,EMPTY_FUNCTION:EMPTY_FUNCTION,caseInsensitive:caseInsensitive,equal:equal,fetch:fetch,fetchWithRTT:fetchWithRTT,getBaseFile:getBaseFile,getExtension:getExtension,getFile:getFile,hash:hash,iterableEntries:iterableEntries,objectFrom:objectFrom,options:options,safePromise:safePromise,sleep:sleep,stringify:stringify,time:time,toBin:toBin,trim:trim,trimEnd:trimEnd,trimStart:trimStart,unixTime:unixTime});/**
 * Copyright 2024 Ceeblue B.V.
 * This file is part of https://github.com/CeeblueTV/web-utils which is released under GNU Affero General Public License.
 * See file LICENSE or go to https://spdx.org/licenses/AGPL-3.0-or-later.html for full license details.
 */
/**
 * BinaryWriter allows to write data in its binary form
 */
class BinaryWriter {
    get view() {
        if (!this._view) {
            this._view = new DataView(this._data.buffer, this._data.byteOffset, this._data.byteLength);
        }
        return this._view;
    }
    get capacity() {
        return this._data.byteLength;
    }
    constructor(dataOrSize = 64, offset = 0, length) {
        if (typeof dataOrSize == 'number') {
            // allocate new buffer
            this._data = new Uint8Array(dataOrSize);
            this._size = 0;
        }
        else if (ArrayBuffer.isView(dataOrSize)) {
            // append to existing data!
            this._data = new Uint8Array(dataOrSize.buffer, dataOrSize.byteOffset, dataOrSize.byteLength);
            this._size = dataOrSize.byteLength;
        }
        else {
            // overrides data
            this._isConst = true; // better than boolean for memory usage
            if (length == null) {
                // /!\ Safari does not support undefined length, so we need to use byteLength instead
                length = dataOrSize.byteLength;
            }
            this._data = new Uint8Array(dataOrSize, offset, length);
            this._size = 0;
        }
    }
    data() {
        return new Uint8Array(this._data.buffer, this._data.byteOffset, this._size);
    }
    size() {
        return this._size || 0;
    }
    next(count = 1) {
        return this.reserve((this._size += count));
    }
    clear(size = 0) {
        return this.reserve((this._size = size));
    }
    /**
     * Write binary data
     * @param data
     */
    write(data) {
        let bin;
        if (typeof data === 'string') {
            bin = toBin(data);
        }
        else if (ArrayBuffer.isView(data)) {
            bin = new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
        }
        else if ('byteLength' in data) {
            bin = new Uint8Array(data);
        }
        else {
            bin = data;
        }
        this.reserve(this._size + bin.length);
        this._data.set(bin, this._size);
        this._size += bin.length;
        return this;
    }
    write8(value) {
        if (value > 0xff) {
            // cast to 8bits range
            value = 0xff;
        }
        this.reserve(this._size + 1);
        this._data[this._size++] = value;
        return this;
    }
    write16(value) {
        if (value > 0xffff) {
            // cast to 16bits range
            value = 0xffff;
        }
        this.reserve(this._size + 2);
        this.view.setUint16(this._size, value);
        this._size += 2;
        return this;
    }
    write24(value) {
        if (value > 0xffffff) {
            // cast to 24bits range
            value = 0xffffff;
        }
        this.reserve(this._size + 3);
        this.view.setUint16(this._size, value >> 8);
        this.view.setUint8((this._size += 2), value & 0xff);
        ++this._size;
        return this;
    }
    write32(value) {
        if (value > 0xffffffff) {
            // cast to 32bits range
            value = 0xffffffff;
        }
        this.reserve(this._size + 4);
        this.view.setUint32(this._size, value);
        this._size += 4;
        return this;
    }
    write64(value) {
        this.write32(value / 4294967296);
        return this.write32(value & 0xffffffff);
    }
    writeFloat(value) {
        this.reserve(this._size + 4);
        this.view.setFloat32(this._size, value);
        this._size += 4;
        return this;
    }
    writeDouble(value) {
        this.reserve(this._size + 8);
        this.view.setFloat64(this._size, value);
        this._size += 8;
        return this;
    }
    write7Bit(value) {
        let byte = value & 0x7f;
        while ((value = Math.floor(value / 0x80))) {
            // equivalent to >>=7 for JS!
            this.write8(0x80 | byte);
            byte = value & 0x7f;
        }
        return this.write8(byte);
    }
    writeString(value) {
        return this.write(toBin(value)).write8(0);
    }
    writeHex(value) {
        for (let i = 0; i < value.length; i += 2) {
            this.write8(parseInt(value.substring(i, i + 2), 16));
        }
        return this;
    }
    reserve(size) {
        if (!this._data) {
            throw Error('buffer not writable');
        }
        if (size <= this._data.byteLength) {
            return this;
        }
        if (this._isConst) {
            throw Error('writing exceeds maximum ' + this._data.byteLength + ' bytes limit');
        }
        --size;
        size |= size >> 1;
        size |= size >> 2;
        size |= size >> 4;
        size |= size >> 8;
        size |= size >> 16;
        ++size;
        const data = new Uint8Array(size);
        data.set(this._data); // copy old buffer!
        this._data = data;
        this._view = undefined; // release view
        return this;
    }
}/**
 * Copyright 2024 Ceeblue B.V.
 * This file is part of https://github.com/CeeblueTV/web-utils which is released under GNU Affero General Public License.
 * See file LICENSE or go to https://spdx.org/licenses/AGPL-3.0-or-later.html for full license details.
 */
/**
 * Log levels
 */
var LogLevel;
(function (LogLevel) {
    LogLevel["ERROR"] = "error";
    LogLevel["WARN"] = "warn";
    LogLevel["INFO"] = "info";
    LogLevel["DEBUG"] = "debug";
})(LogLevel || (LogLevel = {}));
// check coder issuer: everytime we don't forget to use the built Log
let _logging = 0;
setInterval(() => {
    console.assert(_logging === 0, _logging.toFixed(), 'calls to log was useless');
}, 10000);
// !cb-override-log-level
const _overrideLogLevel = options()['!cb-override-log-level'];
const _charLevels = new Array(128);
_charLevels[101] = _charLevels[69] = 1; // error
_charLevels[119] = _charLevels[87] = 2; // warn
_charLevels[105] = _charLevels[73] = 3; // info
_charLevels[100] = _charLevels[68] = 4; // debug
/**
 * Log instance
 */
class Log {
    get error() {
        return this._bind(LogLevel.ERROR);
    }
    get warn() {
        return this._bind(LogLevel.WARN);
    }
    get info() {
        return this._bind(LogLevel.INFO);
    }
    get debug() {
        return this._bind(LogLevel.DEBUG);
    }
    constructor(log, ...args) {
        if (!args.length) {
            // cannot have 0 args to be called correctly!
            args.push(undefined);
        }
        this._args = args;
        this._log = log;
        ++_logging;
    }
    _onLog(localLog, level) {
        var _a, _b;
        // we take like log-level by priority order:
        // 1- the overrideLogLevel
        // 2- the localLog.level
        // 3- the global log.level
        // 4- LogLevel.INFO
        const logLevel = (_b = (_a = _overrideLogLevel !== null && _overrideLogLevel !== void 0 ? _overrideLogLevel : localLog.level) !== null && _a !== void 0 ? _a : log.level) !== null && _b !== void 0 ? _b : LogLevel.INFO;
        if (logLevel === false) {
            // explicit null, no log at all!
            return false;
        }
        if (logLevel !== true && _charLevels[level.charCodeAt(0)] > _charLevels[logLevel.charCodeAt(0)]) {
            return false;
        }
        if (localLog.on) {
            localLog.on(level, this._args);
        }
        return this._args.length ? true : false;
    }
    _bind(level) {
        if (!this._done) {
            this._done = true;
            --_logging;
        }
        // call the global onLog in first (global filter)
        if (!this._onLog(log, level)) {
            return EMPTY_FUNCTION;
        }
        // call the local onLog (local filter)
        if (this._log !== log && !this._onLog(this._log, level)) {
            return EMPTY_FUNCTION;
        }
        // if not intercepted display the log
        return console[level].bind(console, ...this._args);
    }
}
/**
 * Inherits from this class to use logs
 */
class Loggable {
    constructor() {
        /**
         * Start a log
         * @param args
         * @returns a Log object with the levels of log to call
         */
        this.log = ((...args) => {
            return new Log(this.log, ...args);
        });
    }
}
/**
 * Global log
 */
const log = ((...args) => {
    return new Log(log, ...args);
});/**
 * Copyright 2024 Ceeblue B.V.
 * This file is part of https://github.com/CeeblueTV/web-utils which is released under GNU Affero General Public License.
 * See file LICENSE or go to https://spdx.org/licenses/AGPL-3.0-or-later.html for full license details.
 */
/**
 * BitReader allows to read binary data bit by bit
 */
class BitReader extends Loggable {
    constructor(data) {
        super();
        if (ArrayBuffer.isView(data)) {
            this._data = new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
        }
        else {
            this._data = new Uint8Array(data);
        }
        this._size = this._data.byteLength;
        this._position = 0;
        this._bit = 0;
    }
    data() {
        return this._data;
    }
    size() {
        return this._size;
    }
    available() {
        return (this._size - this._position) * 8 - this._bit;
    }
    next(count = 1) {
        let gotten = 0;
        while (this._position !== this._size && count--) {
            ++gotten;
            if (++this._bit === 8) {
                this._bit = 0;
                ++this._position;
            }
        }
        return gotten;
    }
    read(count = 1) {
        let result = 0;
        while (this._position !== this._size && count--) {
            result *= 2;
            if (this._data[this._position] & (0x80 >> this._bit++)) {
                ++result;
            }
            if (this._bit === 8) {
                this._bit = 0;
                ++this._position;
            }
        }
        return result;
    }
    read8() {
        return this.read(8);
    }
    read16() {
        return this.read(16);
    }
    read24() {
        return this.read(24);
    }
    read32() {
        return this.read(32);
    }
    readExpGolomb() {
        let i = 0;
        while (!this.read()) {
            if (!this.available()) {
                return 0;
            }
            ++i;
        }
        const result = this.read(i);
        if (i > 15) {
            this.log('Exponential-Golomb code exceeding unsigned 16 bits').warn();
            return 0;
        }
        return result + (1 << i) - 1;
    }
}/**
 * Copyright 2024 Ceeblue B.V.
 * This file is part of https://github.com/CeeblueTV/web-utils which is released under GNU Affero General Public License.
 * See file LICENSE or go to https://spdx.org/licenses/AGPL-3.0-or-later.html for full license details.
 */
/**
 * Class to compute a weighted average byte rate over a specified time interval.
 *
 * This class continuously tracks data transmission and computes the byte rate
 * based on a weighted average, considering both the duration and the number of
 * bytes in each sample. It allows for real-time monitoring of bandwidth usage
 * and provides mechanisms to dynamically adjust the measurement interval.
 *
 * Features:
 * - Computes the byte rate using a **weighted average** approach.
 * - Allows setting a custom interval for tracking.
 * - Supports dynamic clipping to manually shrink the observation window.
 */
class ByteRate {
    /**
     * Raised when new bytes are added
     */
    onBytes(bytes) { }
    /**
     * Returns the interval used for computing the byte rate
     */
    get interval() {
        return this._interval;
    }
    /**
     * Sets a new interval for computing the average byte rate
     */
    set interval(value) {
        this._interval = value;
        this.updateSamples();
    }
    /**
     * Constructor initializes the ByteRate object with a specified interval (default: 1000ms).
     * It sets up necessary variables to track byte rate over time.
     *
     * @param interval - Time interval in milliseconds to compute the byte rate.
     */
    constructor(interval = 1000) {
        this._interval = interval;
        this.clear();
    }
    /**
     * Returns the computed byte rate rounded to the nearest integer
     */
    value() {
        return Math.round(this.exact());
    }
    /**
     * Computes the exact byte rate in bytes per second
     */
    exact() {
        // compute rate/s
        this.updateSamples();
        const duration = time() - this._time;
        return duration ? (this._bytes / duration) * 1000 : 0;
    }
    /**
     * Adds a new byte sample to the tracking system.
     * Updates the list of samples and recomputes the byte rate
     *
     * @param bytes - Number of bytes added in this interval
     */
    addBytes(bytes) {
        var _a;
        const time$1 = time();
        const lastSample = this.updateSamples(time$1)[this._samples.length - 1];
        const lastTime = (_a = lastSample === null || lastSample === void 0 ? void 0 : lastSample.time) !== null && _a !== void 0 ? _a : this._time;
        if (time$1 > lastTime) {
            this._samples.push({ bytes, time: time$1, clip: false });
        }
        else {
            // no new duration => attach byte to last-one
            if (!lastSample) {
                // Ignore, was before our ByteRate scope !
                return this;
            }
            lastSample.bytes += bytes;
        }
        this._bytes += bytes;
        this.onBytes(bytes);
        return this;
    }
    /**
     * Clears all recorded byte rate data.
     */
    clear() {
        this._bytes = 0;
        this._time = time();
        this._samples = [];
        this._clip = false;
        return this;
    }
    /**
     * Clips the byte rate tracking by marking the last sample as clipped.
     * If a previous clip exists, removes the clipped sample and all preceding samples.
     * Allows to shrink the interval manually between two positions.
     */
    clip() {
        if (this._clip) {
            this._clip = false;
            let removes = 0;
            for (const sample of this._samples) {
                this._bytes -= sample.bytes;
                ++removes;
                this._time = sample.time;
                if (sample.clip) {
                    break;
                }
            }
            this._samples.splice(0, removes);
        }
        const lastSample = this._samples[this._samples.length - 1];
        if (lastSample) {
            lastSample.clip = true;
            this._clip = true;
        }
        return this;
    }
    updateSamples(now = time()) {
        // Remove obsolete sample
        const timeOK = now - this._interval;
        let removes = 0;
        let sample;
        while (this._time < timeOK && (sample = this._samples[removes])) {
            this._bytes -= sample.bytes;
            if (sample.clip) {
                this._clip = sample.clip = false;
            }
            if (sample.time > timeOK) {
                // only a part of the sample to delete !
                sample.bytes *= (sample.time - timeOK) / (sample.time - this._time);
                this._time = timeOK;
                this._bytes += sample.bytes;
                break;
            }
            ++removes;
            this._time = sample.time;
        }
        this._samples.splice(0, removes);
        return this._samples;
    }
}/**
 * Copyright 2024 Ceeblue B.V.
 * This file is part of https://github.com/CeeblueTV/web-utils which is released under GNU Affero General Public License.
 * See file LICENSE or go to https://spdx.org/licenses/AGPL-3.0-or-later.html for full license details.
 */
/**
 * Help class to manipulate and parse a net address. The Address can be only the domain field,
 * or a URL format with protocol and path part `(http://)domain(:port/path)`
 * @example
 * const address = new Address('nl-ams-42.live.ceeblue.tv:80');
 * console.log(address.domain) // 'nl-ams-42.live.ceeblue.tv'
 * console.log(address.port) // '80'
 * console.log(address) // 'nl-ams-42.live.ceeblue.tv:80'
 */
class NetAddress {
    /**
     * Static help function to build an end point from an address `(proto://)domain(:port/path)`
     *
     * Mainly it fix the protocol, in addition if:
     *  - the address passed is securized (TLS) and protocol is not => it tries to fix protocol to get its securize version
     *  - the address passed is non securized and protocol is (TLS) => it tries to fix protocol to get its unsecurized version
     * @param protocol protocol to set in the end point returned
     * @param address string address to fix with protocol as indicated
     * @returns the end point built
     * @example
     * console.log(NetAddress.fixProtocol('ws','http://domain/path')) // 'ws://domain/path'
     * console.log(NetAddress.fixProtocol('ws','https://domain/path')) // 'wss://domain/path'
     * console.log(NetAddress.fixProtocol('wss','http://domain/path')) // 'ws://domain/path'
     */
    static fixProtocol(protocol, address) {
        // Remove leading slashes for absolute pathes!
        address = address.replace(/^[\/]+/, '');
        const found = address.indexOf('://');
        // isolate protocol is present in address
        if (found >= 0) {
            // In this case replace by protocol in keeping SSL like given in address
            if (found > 2 && address.charAt(found - 1).toLowerCase() === 's') {
                // SSL!
                if (protocol.length <= 2 || !protocol.endsWith('s')) {
                    protocol += 's'; // Add SSL
                }
            }
            else {
                // Not SSL!
                if (protocol.length > 2 && protocol.endsWith('s')) {
                    protocol = protocol.slice(0, -1); // Remove SSL
                }
            }
            // Build address!
            address = address.substring(found + 3);
        }
        return protocol + '://' + address;
    }
    /**
     * The host part from address `(http://)domain:port/path)`
     */
    get host() {
        return this._host;
    }
    /**
     * The domain part from address `(http://)domain(:port/path)`
     */
    get domain() {
        return this._domain;
    }
    /**
     * The port part from address `(http://)domain(:port/path)`, or defaultPort if passed in NetAddress constructor
     */
    get port() {
        return this._port;
    }
    toString() {
        return this._address;
    }
    /**
     * @returns the string address as passed in the constructor
     * @override
     */
    valueOf() {
        return this._address;
    }
    /**
     * Build a NetAddress object and parse address
     * @param address string address to parse, accept an url format with protocol and path `(http://)domain(:port/path)`
     * @param defaultPort set a default port to use if there is no port in the string address parsed
     */
    constructor(address, defaultPort) {
        this._address = address;
        // Remove Protocol
        let pos = address.indexOf('/');
        if (pos >= 0) {
            // Remove ://
            if (address.charCodeAt(pos + 1) === 47) {
                // has //
                if (pos > 0) {
                    if (address.charCodeAt(pos - 1) === 58) {
                        // has ://
                        address = address.substring(pos + 2);
                    } //  something else #//
                }
                else {
                    // otherwise starts by //
                    address = address.substring(2);
                }
            }
            else if (!pos) {
                // starts by /, remove it
                address = address.substring(1);
            } // else something else #/
        }
        // Remove Path!
        pos = address.indexOf('/');
        if (pos >= 0) {
            address = address.substring(0, pos);
        }
        this._host = address;
        this._domain = address;
        this._port = defaultPort;
        const domainPortMatch = this._host.match(/^(?:\[([0-9a-fA-F:]+)\]|([^:/?#]+))(?::(\d+))?(?=[/#?]|$)/);
        if (domainPortMatch) {
            this._domain = domainPortMatch[1] || domainPortMatch[2];
            if (domainPortMatch[3]) {
                const port = parseInt(domainPortMatch[3]);
                if (port >= 0 && port <= 0xffff) {
                    this._port = port;
                }
            }
        }
    }
}/**
 * Copyright 2024 Ceeblue B.V.
 * This file is part of https://github.com/CeeblueTV/web-utils which is released under GNU Affero General Public License.
 * See file LICENSE or go to https://spdx.org/licenses/AGPL-3.0-or-later.html for full license details.
 */
/**
 * Type of connection
 */
var Type;
(function (Type) {
    Type["HESP"] = "HESP";
    Type["WRTS"] = "WebRTS";
    Type["WEBRTC"] = "WebRTC";
    Type["DIRECT_STREAMING"] = "DirectStreaming";
    Type["META"] = "Meta";
    Type["DATA"] = "Data";
})(Type || (Type = {}));
/**
 * Some connection utility functions
 */
/**
 * Defines the {@link Params.mediaExt} based on the type of parameters and its endpoint.
 * This method always assigns a value to params.mediaExt, defaulting to an empty string if indeterminable,
 * allowing detection of whether the function has been applied to the parameters.
 * @param type The type of parameters to define.
 * @param params The parameters for which the media extension is to be defined
 */
function defineMediaExt(type, params) {
    var _a;
    // Compute appropriate mediaExt out parameter
    if (!params.mediaExt) {
        try {
            const url = new URL(params.endPoint);
            // Set mediaExt with ?ext= param when set OR url extension
            params.mediaExt = (_a = url.searchParams.get('ext')) !== null && _a !== void 0 ? _a : getExtension(getFile(url.pathname));
        }
        catch (_b) {
            // not an URL, it's only a host
            params.mediaExt = '';
        }
    }
    // Normalize mediaExt in removing the possible '.' prefix and change it to lower case
    params.mediaExt = trimStart(params.mediaExt, '.').toLowerCase();
    switch (type) {
        case Type.DIRECT_STREAMING:
            if (!params.mediaExt) {
                params.mediaExt = 'mp4';
            }
            break;
        case Type.HESP:
            params.mediaExt = 'mp4';
            break;
        case Type.WEBRTC:
            params.mediaExt = 'rtp';
            break;
        case Type.WRTS: {
            // json means a manifest file endPoint, replace with default rts media extension
            if (!params.mediaExt || params.mediaExt === 'json') {
                params.mediaExt = 'rts';
            }
            break;
        }
        case Type.META:
            params.mediaExt = 'js';
            break;
        case Type.DATA:
            params.mediaExt = 'json';
            break;
        default:
            log('Unknown params type ' + type).warn();
            break;
    }
}
/**
 * Build an URL from {@link Type | type} and {@link Params | params}
 * Can assign {@link Params.mediaExt | params.mediaExt} or {@link Params.streamName | params.streamName}
 * @param type Type of the connection wanted
 * @param params Connection parameters
 * @param protocol Optional parameter to choose the prefered protocol to connect
 * @returns The URL of connection
 */
function buildURL(type, params, protocol = 'wss') {
    var _a;
    defineMediaExt(type, params);
    const url = new URL(NetAddress.fixProtocol(protocol, params.endPoint));
    if (url.pathname.length <= 1) {
        // build ceeblue path!
        switch (type) {
            case Type.HESP:
                url.pathname = '/hesp/' + params.streamName + '/index.json';
                break;
            case Type.WEBRTC:
                url.pathname = '/webrtc/' + params.streamName;
                break;
            case Type.WRTS:
                url.pathname = '/wrts/' + params.streamName + '.' + params.mediaExt;
                break;
            case Type.DIRECT_STREAMING:
                url.pathname = '/live/' + params.streamName + '.' + params.mediaExt;
                break;
            case Type.META:
                url.pathname = '/json_' + params.streamName + '.js';
                break;
            case Type.DATA:
                url.pathname = '/' + params.streamName + '.json';
                break;
            default:
                log('Unknown url type ' + type).warn();
                break;
        }
    }
    else {
        // Host has already a path! keep it unchanged, it's user intentionnal (used with some other WHIP/WHEP server?)
        if (!params.streamName) {
            // extract the second part of the URL's path (the first part being the protocol name), or the first path if no other part exists
            const parts = url.pathname.split('/');
            params.streamName = getBaseFile(parts[2] || parts[1] || parts[0]);
        }
    }
    if (params.accessToken) {
        url.searchParams.set('id', params.accessToken);
    }
    for (const [key, value] of (_a = params.query) !== null && _a !== void 0 ? _a : []) {
        url.searchParams.set(key, value);
    }
    return url;
}var Connect=/*#__PURE__*/Object.freeze({__proto__:null,get Type(){return Type},buildURL:buildURL,defineMediaExt:defineMediaExt});/**
 * Copyright 2024 Ceeblue B.V.
 * This file is part of https://github.com/CeeblueTV/web-utils which is released under GNU Affero General Public License.
 * See file LICENSE or go to https://spdx.org/licenses/AGPL-3.0-or-later.html for full license details.
 */
/**
 * A advanced EventEmitter which allows to declare event as natural function in the inheriting children class,
 * function must start by `on` prefix to be recognized as an event.
 * The function can define a behavior by default, and user can choose to redefine this behavior,
 * or add an additionnal subscription for this event.
 * In addition you can unsubscribe to multiple events with an `AbortController`
 * @example
 * class Logger extends EventEmitter {
 *    onLog(log:string) { console.log(log); } // behavior by default
 *
 *    test() {
 *       // raise event onLog
 *       this.onLog('test');
 *    }
 * }
 *
 * const logger = new Logger();
 * logger.test(); // displays a log 'test'
 *
 * // redefine default behavior to display nothing
 * logger.onLog = () => {}
 * logger.test(); // displays nothing
 *
 * // add an additionnal subscription
 * logger.on('log', console.log);
 * logger.test(); // displays a log 'test'
 *
 * // remove the additionnal subscription
 * logger.off('log', console.log);
 * logger.test(); // displays nothing
 *
 * // add two additionnal subscriptions with a AbortController
 * const controller = new AbortController();
 * logger.on('log', log => console.log(log), controller);
 * logger.on('log', log => console.error(log), controller);
 * logger.test(); // displays a log 'test' + an error 'test'
 *
 * // Unsubscribe all the subscription with the AbortController
 * controller.abort();
 * logger.test(); // displays nothing
 */
class EventEmitter extends Loggable {
    /**
     * Build our EventEmitter, usually call from children class
     */
    constructor() {
        super();
        this._events = new Map();
        // Fill events with events as defined!
        let proto = Object.getPrototypeOf(this);
        while (proto && proto !== Object.prototype) {
            for (const name of Object.getOwnPropertyNames(proto)) {
                if (name.length < 3 || !name.startsWith('on')) {
                    continue;
                }
                let defaultEvent = proto[name];
                if (defaultEvent instanceof Function) {
                    const events = new Set();
                    this._events.set(name.substring(2).toLowerCase(), events);
                    const raise = (...args) => {
                        // Call default event if not undefined, can happen if assigned to null/undefined
                        const result = defaultEvent ? defaultEvent.call(this, ...args) : undefined;
                        // Call subscribers
                        for (const event of events) {
                            event(...args);
                        }
                        return result;
                    };
                    Object.defineProperties(this, {
                        [name]: {
                            get: () => raise,
                            set: (value) => {
                                // Assign a default behavior!
                                defaultEvent = value;
                            }
                        }
                    });
                }
            }
            proto = Object.getPrototypeOf(proto);
        }
    }
    /**
     * Event subscription
     * @param name Name of event without the `on` prefix (ex: `log` to `onLog` event declared)
     * @param event Subscriber Function
     * @param options.signal Optional `AbortSignal` to stop this or multiple subscriptions in same time
     */
    on(name, event, options) {
        var _a;
        if (typeof event !== 'function') {
            throw Error('event callback must be a function');
        }
        const events = this._event(name);
        events.add(event);
        (_a = options === null || options === void 0 ? void 0 : options.signal) === null || _a === void 0 ? void 0 : _a.addEventListener('abort', () => events.delete(event), { once: true });
    }
    /**
     * Event subscription only one time, once time fired it's automatically unsubscribe
     * @param name Name of event without the `on` prefix (ex: `log` to `onLog` event declared)
     * @param event Subscriber Function
     * @param options.signal Optional `AbortSignal` to stop this or multiple subscriptions in same time
     */
    once(name, event, options) {
        var _a;
        if (typeof event !== 'function') {
            throw Error('event callback must be a function');
        }
        const events = this._event(name);
        const wrapper = (...args) => {
            events.delete(wrapper); // delete the wrapper from events
            event(...args); // execute event
        };
        events.add(wrapper);
        (_a = options === null || options === void 0 ? void 0 : options.signal) === null || _a === void 0 ? void 0 : _a.addEventListener('abort', () => events.delete(wrapper), { once: true });
    }
    /**
     * Event unsubscription
     * @param name Name of event without the 'on' prefix (ex: 'log' to 'onLog' event declared)
     * @param event Unsubscriber Function, must be the one passed to {@link on} or {@link once} subscription methods
     */
    off(name, event) {
        if (!event) {
            throw Error('event to unsubscribe cannot be null');
        }
        return this._event(name).delete(event);
    }
    _event(name) {
        const events = this._events.get(name.toLowerCase());
        if (!events) {
            throw Error('No event on' + name + ' on class ' + this.constructor.name);
        }
        return events;
    }
}/**
 * Copyright 2024 Ceeblue B.V.
 * This file is part of https://github.com/CeeblueTV/web-utils which is released under GNU Affero General Public License.
 * See file LICENSE or go to https://spdx.org/licenses/AGPL-3.0-or-later.html for full license details.
 */
/**
 * Some fix for JS MAP:
 * - find(key) search an item in the map and returns undefined if not found
 * - get(key) return the item if exists or otherwise create and returns it
 * - set(key, value) returns the value of the item (rather the MAP)
 */
class FixMap {
    [Symbol.iterator]() {
        return this._map[Symbol.iterator]();
    }
    get size() {
        return this._map.size;
    }
    constructor(_initValue) {
        this._initValue = _initValue;
        this._map = new Map();
    }
    get(key) {
        let value = this.find(key);
        if (value === undefined) {
            this._map.set(key, (value = this._initValue()));
        }
        return value;
    }
    find(key) {
        return this._map.get(key);
    }
    has(key) {
        return this._map.has(key);
    }
    clear() {
        this._map.clear();
    }
    delete(key) {
        return this._map.delete(key);
    }
    set(key, value) {
        this._map.set(key, value);
        return value;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    forEach(callbackfn, thisArg) {
        this._map.forEach(callbackfn, thisArg);
    }
}/**
 * Copyright 2024 Ceeblue B.V.
 * This file is part of https://github.com/CeeblueTV/web-utils which is released under GNU Affero General Public License.
 * See file LICENSE or go to https://spdx.org/licenses/AGPL-3.0-or-later.html for full license details.
 */
/**
 * Queue typed similar to a {@link https://en.cppreference.com/w/cpp/container/queue | std::queue<Type>} with possibility to limit the capacity like a FIFO
 * @example
 * const queue = new Queue<number>(2);
 * queue.push(1); // [1]
 * queue.push(2); // [1,2]
 * queue.push(3); // [2,3] 1 has been removed to respect 2 capacity
 */
class Queue {
    /**
     * Number of element in the queue
     */
    get size() {
        return this._queue.length;
    }
    /**
     * Maximum capacity for the queue, if not set queue has unlimited capacity
     */
    get capacity() {
        return this._capacity;
    }
    /**
     * Set a maximum capacity for the queue,
     * if you push new value exceding this capacity the firsts are removed (FIFO)
     * if set to undefined the queue is unlimited
     */
    set capacity(value) {
        this._capacity = value;
        if (value != null && this._queue.length > value) {
            this._queue.splice(0, this._queue.length - value);
        }
    }
    /**
     * The front element
     */
    get front() {
        return this._queue[0];
    }
    /**
     * The back element
     */
    get back() {
        return this._queue[this._queue.length - 1];
    }
    /**
     * Iterator though queue's elements
     */
    [Symbol.iterator]() {
        return this._queue[Symbol.iterator]();
    }
    /**
     * Instanciate a new queue object with the type passed as template
     * @param capacity if set it limits the size of the queue, any exceding element pops the first element pushed (FIFO)
     */
    constructor(capacity) {
        this._capacity = capacity;
        this._queue = new Array();
    }
    /**
     * Push a new element in the queue
     * @param value value of the element
     * @returns this
     */
    push(value) {
        if (this._capacity != null && this._queue.push(value) > this._capacity) {
            this.pop();
        }
        return this;
    }
    /**
     * Pop the first element from the queue
     * @returns The first element removed
     */
    pop() {
        return this._queue.shift();
    }
    /**
     * Clear all the elements, queue becomes empty
     * @returns this
     */
    clear() {
        this._queue.length = 0;
        return this;
    }
}/**
 * Copyright 2024 Ceeblue B.V.
 * This file is part of https://github.com/CeeblueTV/web-utils which is released under GNU Affero General Public License.
 * See file LICENSE or go to https://spdx.org/licenses/AGPL-3.0-or-later.html for full license details.
 */
/**
 * A collection of number Queue<number> with the following efficient mathematic computation:
 * - minimum value in the collection
 * - maximum value in the collection
 * - average value of the collection
 */
class Numbers extends Queue {
    /**
     * minimum value in the collection, or 0 if colleciton is empty
     */
    get minimum() {
        return this._min;
    }
    /**
     * maximum value in the collection, or 0 if colleciton is empty
     */
    get maximum() {
        return this._max;
    }
    /**
     * average value of the collection, or 0 if collection if empty
     */
    get average() {
        if (this._average == null) {
            this._average = this.size ? this._sum / this.size : 0;
        }
        return this._average;
    }
    /**
     * Instantiate the collection of the number
     * @param capacity if set it limits the number of values stored, any exceding number pops the first number pushed (FIFO)
     */
    constructor(capacity) {
        super(capacity);
        this._sum = 0;
        this._min = 0;
        this._max = 0;
    }
    /**
     * Push a value to the back to the collection
     * @param value number to add
     * @returns this
     */
    push(value) {
        if (value > this._max) {
            this._max = value;
        }
        else if (value < this._min) {
            this._min = value;
        }
        this._average = undefined;
        this._sum += value;
        super.push(value);
        return this;
    }
    /**
     * Pop the front number from the collection
     * @returns the front number removed
     */
    pop() {
        const front = super.pop();
        if (front === this._max) {
            this._max = Math.max(0, ...this);
        }
        else if (front === this._min) {
            this._min = Math.min(0, ...this);
        }
        this._average = undefined;
        this._sum -= front || 0;
        return front;
    }
    /**
     * Clear all the numbers, collection becomes empty
     * @returns this
     */
    clear() {
        this._min = this._max = this._sum = 0;
        super.clear();
        return this;
    }
}/**
 * Copyright 2024 Ceeblue B.V.
 * This file is part of https://github.com/CeeblueTV/web-utils which is released under GNU Affero General Public License.
 * See file LICENSE or go to https://spdx.org/licenses/AGPL-3.0-or-later.html for full license details.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Toolkit to deal with Session Description Protocol (SDP),
 * mainly to tranform a SDP string Offer/Answer to a manipulable JS object representation
 * @example
 * const peerConnection = new RTCPeerConnection();
 * peerConnection.createOffer()
 * .then( offer =>
 *    // Change offer.sdp string to a JS manipulable object
 *    const sdp = SDP.fromString(offer.sdp || '');
 *    // Change a property of SDP
 *    sdp.v = 2; // change SDP version
 *    // Reserialize to the legal SDP string format
 *    offer.sdp = SDP.toString(sdp);
 *    // Set SDP offer to peerConnection
 *    peerConnection.setLocalDescription(offer);
 * )
 */
const SDP = {
    /**
     * Unserialize SDP string to a manipulable JS object representation
     * It's an object with:
     *  - root SDP properties
     *  - media description iterable
     * @param lines SDP string reprensentation
     * @returns SDP object representation, iterable through media description
     * @example
     * [
     *    group: "DUNBLE 0 1",
     *    o: "- 1699450751193623 0 IN IP4 0.0.0.0",
     *    s: "-",
     *    t: "0 0",
     *    v: "0",
     *    ice-lite: "",
     *    length: 2,
     *    {
     *       m: "audio 9 UDP/TLS/RTP/SAVPF 111",
     *       c: "IN IP4 0.0.0.0",
     *       rtcp: "9",
     *       sendonly: "",
     *       setup: "passive",
     *       fingerprint: "sha-256 51:36:ED:78:A4:9F:25:8C:39:9A:0E:A0:B4:9B:6E:04:37:FF:AD:96:93:71:43:88:2C:0B:0F:AB:6F:9A:52:B8",
     *       ice-ufrag: "fa37",
     *       ice-pwd: "JncCHryDsbzayy4cBWDxS2",
     *       rtcp-mux: "",
     *       rtcp-rsize: "",
     *       rtpmap: "111 opus/48000/2",
     *       rtcp-fb: "111 nack",
     *       id: "0",
     *       fmtp: "111 minptime=10;useinbandfec=1",
     *       candidate: "1 1 udp 2130706431 89.105.221.108 56643 typ host",
     *       end-of-candidates: ""
     *    },
     *    {
     *       m: "video 9 UDP/TLS/RTP/SAVPF 106",
     *       c: "IN IP4 0.0.0.0",
     *       rtcp: "9",
     *       sendonly: "",
     *       setup: "passive",
     *       fingerprint: "sha-256 51:36:ED:78:A4:9F:25:8C:39:9A:0E:A0:B4:9B:6E:04:37:FF:AD:96:93:71:43:88:2C:0B:0F:AB:6F:9A:52:B8",
     *       ice-ufrag: "fa37",
     *       ice-pwd: "JncCHryDsbzayy4cBWDxS2",
     *       rtcp-mux: "",
     *       rtcp-rsize: "",
     *       rtpmap: "106 H264/90000",
     *       rtcp-fb: [
     *          "106 nack",
     *          "106 goog-remb"
     *       ],
     *       mid: "1",
     *       fmtp: "106 profile-level-id=42e01f;level-asymmetry-allowed=1;packetization-mode=1",
     *       candidate: "1 1 udp 2130706431 89.105.221.108 56643 typ host",
     *       end-of-candidates: ""
     *    }
     * ]
     */
    fromString(lines) {
        if (Array.isArray(lines)) {
            return lines; // already converted
        }
        const sdp = new Array();
        let media = sdp;
        let fingerprint;
        for (let line of lines.toString().split('\n')) {
            line = line.trim();
            if (!line) {
                continue;
            }
            let key = line[0];
            const value = line.substring(line.indexOf('=') + 1).trim();
            switch (key.toLowerCase()) {
                case 'a': {
                    if (!value) {
                        continue;
                    } // empty attribute!
                    key = this.addAttribute(media, value);
                    // save fingerprint to repeat it in medias
                    if (sdp === media && key.toLowerCase() === 'fingerprint') {
                        fingerprint = media.fingerprint;
                    }
                    break;
                }
                case 'm':
                    if (sdp.length && fingerprint && !sdp[sdp.length - 1].fingerprint) {
                        media.fingerprint = fingerprint;
                    }
                    sdp.push((media = { m: value }));
                    break;
                default:
                    media[key] = value;
            }
        }
        if (sdp.length && fingerprint && !sdp[sdp.length - 1].fingerprint) {
            media.fingerprint = fingerprint;
        }
        return sdp;
    },
    /**
     * Serialize SDP JS object to a SDP string legal representation
     * @param sdp SDP object reprensetation
     * @returns SDP string reprensentation
     */
    toString(sdp) {
        if (typeof sdp === 'string') {
            return sdp; // already converted
        }
        const medias = [];
        // https://www.cl.cam.ac.uk/~jac22/books/mm/book/node182.html
        let lines = 'v' in sdp ? 'v=' + sdp.v + '\n' : '';
        if ('o' in sdp) {
            lines += 'o=' + sdp.o + '\n';
        }
        if ('s' in sdp) {
            lines += 's=' + sdp.s + '\n';
        }
        const obj = sdp;
        for (const key of Object.keys(sdp)) {
            if (key === 'v' || key === 'o' || key === 's') {
                continue;
            }
            const value = obj[key];
            if (value == null) {
                continue;
            } // ignore this key/value
            const index = parseInt(key);
            if (!isNaN(index)) {
                // Is a number! Media object!
                medias[index] = value;
                continue;
            }
            const count = (Array.isArray(value) && value.length) || 1; // value can be numeric!
            for (let i = 0; i < count; ++i) {
                const line = Array.isArray(value) && value.length ? value[i] : value;
                if (key.length > 1) {
                    // when key is superior to 1 letter it's a attribute!
                    lines += 'a=' + key;
                    if (line) {
                        lines += ':';
                    }
                }
                else {
                    lines += key + '=';
                }
                lines += line + '\n';
            }
        }
        for (const media of medias) {
            lines += this.toString(media);
        }
        return lines;
    },
    /**
     * While set a property to a SDP object representation is possible directly,
     * we could prefer add a new property without overload a possible already existing value.
     * This function allows to add a property to our SDP representation:
     *  - if the key's attribute doesn't exists yet it adds it like a simple JS property sdp[key] = value
     *  - if the key's attribute exists already it morphs the value to a Array and push it inside
     * @param sdp the SDP object representation on which added the attribute
     * @param attribute the string attribut in a format "key:value" or just "key" to add an attribute without value
     * @returns the key part of the attribute added (or if value is empty it returns the same attribute as passed in argument)
     */
    addAttribute(sdp, attribute) {
        var _a;
        const a = SDP.parseAttribute(attribute);
        const value = (_a = a.value) !== null && _a !== void 0 ? _a : ''; // to allow to test if key exists even without value with if(sdp.key != null)
        const obj = sdp;
        const oldValue = obj[a.key];
        if (!oldValue) {
            obj[a.key] = value;
        }
        else if (Array.isArray(oldValue)) {
            oldValue.push(value);
        }
        else if (value !== oldValue) {
            obj[a.key] = [oldValue, value];
        }
        return a.key;
    },
    /**
     * While it's possible to delete a attribute manually on the SDP object representation with a delete sdp.key,
     * we could prefer remove only a value in a sdp.key array containing multiple values.
     * Like opposite to addAttribute this method allows to remove an unique attribute value.
     * @param sdp the SDP object representation on which removed the attribute
     * @param attribute the string attribut in a format "key:value" or just "key" to remove the whole attribute and all its values
     * @returns the key part of the attribute removed (or if value is empty it returns the same attribute as passed in argument)
     */
    removeAttribute(sdp, attribute) {
        const a = SDP.parseAttribute(attribute);
        const obj = sdp;
        if (a.value === undefined) {
            delete obj[attribute];
            return attribute;
        }
        const current = obj[attribute];
        if (Array.isArray(a.value)) {
            const i = current.findIndex((current) => current === a.value);
            if (i >= 0) {
                current.splice(i, 1);
            }
        }
        else if (current === a.value) {
            delete obj[attribute];
        }
        return a.key;
    },
    /**
     * Parse an attribute in a format "key:value"
     * @param attribute string attribute to parse
     * @returns the {key, value} result, with value undefined if attribute was a "key" without value
     */
    parseAttribute(attribute) {
        const found = attribute.indexOf(':');
        return {
            key: (found >= 0 ? attribute.substring(0, found) : attribute).trim(),
            value: found >= 0 ? attribute.substring(found + 1).trim() : undefined
        };
    }
};
Object.freeze(SDP);/**
 * Copyright 2024 Ceeblue B.V.
 * This file is part of https://github.com/CeeblueTV/web-utils which is released under GNU Affero General Public License.
 * See file LICENSE or go to https://spdx.org/licenses/AGPL-3.0-or-later.html for full license details.
 */
/**
 * The WebSocketReliable class extends WebSocket to bring up the following improvements:
 * - Fix all possible unintentional closing ways to get always a related error message, {@link onClose | onClose(error?) event}
 * - Make possible message sending while connecting. Indeed no need to wait {@link onOpen} before to send message,
 * you can open the socket and immediately send messages, it will be queue and flushs on connection etablishment
 * - Make possible a delayed connection, or a reconnection. Indeed you can create an unconnected Websocket instance
 * without passing any url argument and starts the conneciton more later with {@link WebSocketReliable.open(url) | open(url)} method
 * - Make possible to control sending/queueing message: send method take an optional queueing=true argument to
 * queue message rather send it, a futur call to flush will send it. Then queueing getter allows to handle the queue
 * if need to purge it or remove some queued message. Use it all together can help to prioritize messages or control overload.
 * @example
 * const ws = new WebSocketReliable(url);
 * ws.onClose = (error?:string) => {
 *    if(error) {
 *       console.error(error);
 *    }
 *    // reconnection attempt every seconds
 *    setTimeout(() => ws.open(url), 1000);
 * }
 * ws.onMessage = (message:string) => {
 *    console.log(message);
 * }
 * ws.send('hello'); // send immediatly a hello message is possible (no need to wait onOpen)
 */
class WebSocketReliable extends EventEmitter {
    /**
     * Fired when socket is connected
     * @event
     */
    onOpen() { }
    /**
     * Fired on message reception
     * @param message can be binary or string.
     * If you subscribe to the event with message as string type (and not union),
     * it means that you know that all your messages are distributed in a string format
     * @event
     */
    onMessage(message) { }
    /**
     * Fired on websocket close
     * @param error error description on an improper closure
     * @event
     */
    onClose(error) {
        if (error) {
            this.log('onClose', error).error();
        }
        else {
            this.log('onClose').info();
        }
    }
    /**
     * binaryType, fix binary type to arrayBuffer
     */
    get binaryType() {
        return 'arraybuffer';
    }
    get recvByteRate() {
        return this._recvByteRate.value();
    }
    get sendByteRate() {
        return this._sendByteRate.value();
    }
    /**
     * url of connection
     */
    get url() {
        var _a, _b;
        return (_b = (_a = this._ws) === null || _a === void 0 ? void 0 : _a.url) !== null && _b !== void 0 ? _b : '';
    }
    /**
     * extensions negociated by the server
     */
    get extensions() {
        var _a, _b;
        return (_b = (_a = this._ws) === null || _a === void 0 ? void 0 : _a.extensions) !== null && _b !== void 0 ? _b : '';
    }
    /**
     * protocol negociated by the server
     */
    get protocol() {
        var _a, _b;
        return (_b = (_a = this._ws) === null || _a === void 0 ? void 0 : _a.protocol) !== null && _b !== void 0 ? _b : '';
    }
    /**
     * opened equals true when connection is etablished, in other word when onOpen event is fired
     */
    get opened() {
        return this._opened;
    }
    /**
     * {@link https://developer.mozilla.org/docs/Web/API/WebSocket/readyState | Official websocket readyState}
     */
    get readyState() {
        return this._ws ? this._ws.readyState : 3;
    }
    /**
     * True when connection is closed, in other words when {@link onClose} event is fired
     * or when WebSocketReliable is build without url (disconnected creation)
     */
    get closed() {
        return this._closed;
    }
    /**
     * The number of bytes of data that were queued during calls to send() but not yet transmitted to the network
     */
    get bufferedAmount() {
        var _a;
        return this._queueingBytes + (((_a = this._ws) === null || _a === void 0 ? void 0 : _a.bufferedAmount) || 0);
    }
    /**
     * Queued messages from a call to send() waiting to be transmit one time websocket connection opened (or with an explicit call to flush() method)
     */
    get queueing() {
        return this._queueing;
    }
    /**
     * Create a WebSocketReliable object, and open it if an url is passed in argument
     * @param url URL of the WebSocket endpoint or null to start the connection later
     */
    constructor(url, protocols) {
        super();
        this._queueing = [];
        this._queueingBytes = 0;
        this._opened = false;
        this._closed = true;
        this._recvByteRate = new ByteRate();
        this._sendByteRate = new ByteRate();
        if (url) {
            this.open(url, protocols);
        }
    }
    /**
     * Open a WebSocket connection
     * @param url url of the websocket endpoint
     * @returns this
     */
    open(url, protocols) {
        this._closed = false;
        const ws = (this._ws = new WebSocket(url, protocols));
        ws.binaryType = this.binaryType;
        ws.onmessage = e => {
            var _a;
            this._recvByteRate.addBytes((_a = e.data.byteLength) !== null && _a !== void 0 ? _a : e.data.length);
            this.onMessage(e.data);
        };
        // Add details and fix close ways
        ws.onclose = (e) => {
            if (!this._opened) {
                // close during connection!
                this.close({
                    type: 'WebSocketReliableError',
                    name: 'Connection failed',
                    url: url.toString(),
                    reason: String(e.reason || e.code)
                });
            }
            else if (e.code === 1000 || e.code === 1005) {
                // normal disconnection from server
                this.close({
                    type: 'WebSocketReliableError',
                    name: 'Server shutdown',
                    url: url.toString()
                });
            }
            else {
                // abnormal disconnection from server
                this.close({
                    type: 'WebSocketReliableError',
                    name: 'Socket disconnection',
                    url: url.toString(),
                    reason: String(e.reason || e.code)
                });
            }
        };
        // Wrap send method to queue messages until connection is established.
        ws.onopen = _ => {
            this._opened = true;
            this.flush();
            this.onOpen();
        };
        return this;
    }
    /**
     * Send a message
     * @param message
     * @param queueing When set it reports the sending to a more later call to flush
     * @returns this
     */
    send(message, queueing = false) {
        if (this._closed) {
            throw Error('Open socket before to send data');
        }
        if (queueing || !this._opened) {
            this._queueing.push(message);
            this._queueingBytes += typeof message === 'string' ? message.length : message.byteLength;
        }
        else {
            this._send(message);
        }
        return this;
    }
    /**
     * Send queueing messages
     */
    flush() {
        if (this._ws) {
            for (const message of this._queueing) {
                this._send(message);
            }
        }
        this._queueing.length = 0;
        this._queueingBytes = 0;
    }
    /**
     * Close websocket
     * @param error the error reason if is not a proper close
     */
    close(error) {
        if (!this._ws || this._closed) {
            return;
        }
        this._closed = true;
        this._ws.onopen = this._ws.onclose = this._ws.onmessage = null; // otherwise can receive message AFTER close!
        this._ws.close(); // Don't set to undefined to keep this._ws properties valid!
        // release resources!
        this._queueing.length = 0;
        this._queueingBytes = 0;
        this.onClose(error);
        // Reset _opened in last to allow to differenciate in onClose an error while connecting OR while connected
        this._opened = false;
    }
    _send(message) {
        if (!this._ws) {
            return;
        }
        this._sendByteRate.addBytes(typeof message === 'string' ? message.length : message.byteLength);
        this._ws.send(message);
    }
}/**
 * Copyright 2024 Ceeblue B.V.
 * This file is part of https://github.com/CeeblueTV/web-utils which is released under GNU Affero General Public License.
 * See file LICENSE or go to https://spdx.org/licenses/AGPL-3.0-or-later.html for full license details.
 */
// Fixed blocks to 32 because timestamp is encoded on 32 bits
const _blocksPerRow = 32;
/**
 * Decode the timestamp from a video element and compute the latency between the timestamp and the current date.
 *
 * @param {HTMLVideoElement} sourceEl the video element to get the image from
 * @param {HTMLCanvasElement} canvas
 * @param {CanvasRenderingContext2D} context
 * @param {Date} now current date, new Date() by default
 * @param {Number} tolerance percentage of tolerance for the black and white threshold, 0.2 by default
 * @returns {Number} The latency in millisecond between 'now' and the decoded timestamp, 0 if the timestamp cannot be decoded
 */
function getLatency(sourceEl, canvas, context, now = new Date(), tolerance = 0.2) {
    canvas.width = sourceEl.videoWidth;
    canvas.height = Math.floor(canvas.width / _blocksPerRow);
    if (!canvas.width || !canvas.height) {
        // No pixel to parse!
        return 0;
    }
    context.drawImage(sourceEl, 0, 0, canvas.width, canvas.height, 0, 0, canvas.width, canvas.height);
    const timestamp = decodeTimestamp(context, canvas.width, tolerance);
    return timestamp == null ? 0 : now.getTime() - timestamp.getTime();
}
/**
 * Decode a previously encoded timestamp from a canvas
 *
 * @param {CanvasRenderingContext2D} context
 * @param {Number} lineWidth width of the line in pixels
 * @param {Number} tolerance percentage of tolerance for the black and white threshold, 0.2 by default
 * @returns {Date|null} The Date object representing the timestamp or null if the timestamp cannot be decoded
 */
function decodeTimestamp(context, lineWidth, tolerance = 0.2) {
    // Integer block width and ignore the right-hand remainder.
    const blockSize = Math.floor(lineWidth / _blocksPerRow);
    let binaryTime = '';
    if (blockSize < 1) {
        return null;
    }
    const effectiveWidth = blockSize * _blocksPerRow;
    const midBlock = Math.floor(blockSize / 2);
    const data = context.getImageData(0, midBlock, effectiveWidth, 1).data;
    const pixels = new Uint32Array(data.buffer);
    const blackThreshold = 0xff * tolerance;
    const whiteThreshold = 0xff * (1 - tolerance);
    // Sample the center pixel of each integer-sized block that the encoder painted.
    for (let i = 0; i < effectiveWidth; i += blockSize) {
        const pixel = pixels[i + midBlock] & 0xffffff;
        // Extract luminance from RGB
        const Y = 0.299 * ((pixel >> 16) & 0xff) + 0.587 * ((pixel >> 8) & 0xff) + 0.114 * (pixel & 0xff);
        if (Y < blackThreshold) {
            // Black
            binaryTime += '1';
        }
        else if (Y > whiteThreshold) {
            // White
            binaryTime += '0';
        }
        else {
            return null;
        }
    }
    const day = parseInt(binaryTime.slice(0, 5), 2);
    const hour = parseInt(binaryTime.slice(5, 10), 2);
    const minute = parseInt(binaryTime.slice(10, 16), 2);
    const second = parseInt(binaryTime.slice(16, 22), 2);
    const millisecond = parseInt(binaryTime.slice(22, 32), 2);
    // Basic sanity checks (match encoder ranges)
    if (day < 1 || day > 31 || hour > 23 || minute > 59 || second > 59 || millisecond > 999) {
        return null;
    }
    const now = new Date();
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), day, hour, minute, second, millisecond));
}
/**
 * Encode the given date (excluding year and month) into a line composed of blocks
 * of black and white pixels written on the top of the canvas.
 *
 * @param {CanvasRenderingContext2D} context
 * @param {Number} lineWidth width of the line in pixels
 * @param {Number} blocksPerRow number of blocks in the line, 32 by default
 * @param {Date} now current date, new Date() by default
 */
function encodeTimestamp(context, lineWidth, blocksPerRow = 32, now = new Date()) {
    const blockSize = Math.floor(lineWidth / blocksPerRow);
    const day = now.getUTCDate();
    const hour = now.getUTCHours();
    const minute = now.getUTCMinutes();
    const second = now.getUTCSeconds();
    const millisecond = now.getUTCMilliseconds();
    const binaryDay = day.toString(2).padStart(5, '0'); // 31 possible days/32
    const binaryHour = hour.toString(2).padStart(5, '0'); // 24 possible hours/32
    const binaryMinute = minute.toString(2).padStart(6, '0'); // 60 possible minutes/64
    const binarySecond = second.toString(2).padStart(6, '0'); // 60 possible seconds/64
    const binaryMillisecond = millisecond.toString(2).padStart(10, '0'); // 1000 possible milliseconds/1024
    const binaryTime = binaryDay + binaryHour + binaryMinute + binarySecond + binaryMillisecond;
    for (let i = 0; i < binaryTime.length; i++) {
        const x = (i % blocksPerRow) * blockSize;
        const y = Math.floor(i / blocksPerRow) * blockSize;
        context.fillStyle = binaryTime[i] === '1' ? 'black' : 'white';
        context.fillRect(x, y, blockSize, blockSize);
    }
}var EpochTime=/*#__PURE__*/Object.freeze({__proto__:null,decodeTimestamp:decodeTimestamp,encodeTimestamp:encodeTimestamp,getLatency:getLatency});/**
 * Logging levels for the CTA-608 parser.
 *
 * @group CTA-608
 *
 * @enum
 *
 * @beta
 */
const VerboseLevel = {
    ERROR: 0,
    TEXT: 1,
    WARNING: 2,
    INFO: 2,
    DEBUG: 3,
    DATA: 3,
};/**
 *
 * This code was ported from the dash.js project at:
 *   https://github.com/Dash-Industry-Forum/dash.js/blob/development/externals/cea608-parser.js
 *   https://github.com/Dash-Industry-Forum/dash.js/commit/8269b26a761e0853bb21d78780ed945144ecdd4d#diff-71bc295a2d6b6b7093a1d3290d53a4b2
 *
 * The original copyright appears below:
 *
 * The copyright in this software is being made available under the BSD License,
 * included below. This software may be subject to other third party and contributor
 * rights, including patent rights, and no such rights are granted under this license.
 *
 * Copyright (c) 2015-2016, DASH Industry Forum.
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without modification,
 * are permitted provided that the following conditions are met:
 *  1. Redistributions of source code must retain the above copyright notice, this
 *  list of conditions and the following disclaimer.
 *  * Redistributions in binary form must reproduce the above copyright notice,
 *  this list of conditions and the following disclaimer in the documentation and/or
 *  other materials provided with the distribution.
 *  2. Neither the name of Dash Industry Forum nor the names of its
 *  contributors may be used to endorse or promote products derived from this software
 *  without specific prior written permission.
 *
 *  THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS AS IS AND ANY
 *  EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 *  WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED.
 *  IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT,
 *  INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT
 *  NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR
 *  PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY,
 *  WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE)
 *  ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 *  POSSIBILITY OF SUCH DAMAGE.
 */
/**
 * CaptionsLogger. To be removed in the future.
 *
 * @group CTA-608
 * @beta
 */
class CaptionsLogger {
    constructor() {
        this.time = null;
        this.verboseLevel = VerboseLevel.ERROR;
    }
    log(severity, msg) {
        if (this.verboseLevel >= severity) {
            const m = typeof msg === 'function' ? msg() : msg;
            console.log(`${this.time} [${severity}] ${m}`);
        }
    }
}/**
 *
 * This code was ported from the dash.js project at:
 *   https://github.com/Dash-Industry-Forum/dash.js/blob/development/externals/cea608-parser.js
 *   https://github.com/Dash-Industry-Forum/dash.js/commit/8269b26a761e0853bb21d78780ed945144ecdd4d#diff-71bc295a2d6b6b7093a1d3290d53a4b2
 *
 * The original copyright appears below:
 *
 * The copyright in this software is being made available under the BSD License,
 * included below. This software may be subject to other third party and contributor
 * rights, including patent rights, and no such rights are granted under this license.
 *
 * Copyright (c) 2015-2016, DASH Industry Forum.
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without modification,
 * are permitted provided that the following conditions are met:
 *  1. Redistributions of source code must retain the above copyright notice, this
 *  list of conditions and the following disclaimer.
 *  * Redistributions in binary form must reproduce the above copyright notice,
 *  this list of conditions and the following disclaimer in the documentation and/or
 *  other materials provided with the distribution.
 *  2. Neither the name of Dash Industry Forum nor the names of its
 *  contributors may be used to endorse or promote products derived from this software
 *  without specific prior written permission.
 *
 *  THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS AS IS AND ANY
 *  EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 *  WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED.
 *  IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT,
 *  INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT
 *  NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR
 *  PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY,
 *  WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE)
 *  ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 *  POSSIBILITY OF SUCH DAMAGE.
 */
/**
 * Pen state.
 *
 * @group CTA-608
 * @beta
 */
class PenState {
    constructor() {
        this.foreground = 'white';
        this.underline = false;
        this.italics = false;
        this.background = 'black';
        this.flash = false;
    }
    reset() {
        this.foreground = 'white';
        this.underline = false;
        this.italics = false;
        this.background = 'black';
        this.flash = false;
    }
    setStyles(styles) {
        const attribs = [
            'foreground',
            'underline',
            'italics',
            'background',
            'flash',
        ];
        for (let i = 0; i < attribs.length; i++) {
            const style = attribs[i];
            if (Object.prototype.hasOwnProperty.call(styles, style)) {
                this[style] = styles[style];
            }
        }
    }
    isDefault() {
        return (this.foreground === 'white' &&
            !this.underline &&
            !this.italics &&
            this.background === 'black' &&
            !this.flash);
    }
    equals(other) {
        return (this.foreground === other.foreground &&
            this.underline === other.underline &&
            this.italics === other.italics &&
            this.background === other.background &&
            this.flash === other.flash);
    }
    copy(newPenState) {
        this.foreground = newPenState.foreground;
        this.underline = newPenState.underline;
        this.italics = newPenState.italics;
        this.background = newPenState.background;
        this.flash = newPenState.flash;
    }
}/**
 *
 * This code was ported from the dash.js project at:
 *   https://github.com/Dash-Industry-Forum/dash.js/blob/development/externals/cea608-parser.js
 *   https://github.com/Dash-Industry-Forum/dash.js/commit/8269b26a761e0853bb21d78780ed945144ecdd4d#diff-71bc295a2d6b6b7093a1d3290d53a4b2
 *
 * The original copyright appears below:
 *
 * The copyright in this software is being made available under the BSD License,
 * included below. This software may be subject to other third party and contributor
 * rights, including patent rights, and no such rights are granted under this license.
 *
 * Copyright (c) 2015-2016, DASH Industry Forum.
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without modification,
 * are permitted provided that the following conditions are met:
 *  1. Redistributions of source code must retain the above copyright notice, this
 *  list of conditions and the following disclaimer.
 *  * Redistributions in binary form must reproduce the above copyright notice,
 *  this list of conditions and the following disclaimer in the documentation and/or
 *  other materials provided with the distribution.
 *  2. Neither the name of Dash Industry Forum nor the names of its
 *  contributors may be used to endorse or promote products derived from this software
 *  without specific prior written permission.
 *
 *  THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS AS IS AND ANY
 *  EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 *  WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED.
 *  IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT,
 *  INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT
 *  NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR
 *  PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY,
 *  WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE)
 *  ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 *  POSSIBILITY OF SUCH DAMAGE.
 */
/**
 * Unicode character with styling and background.
 *
 * @group CTA-608
 * @beta
 */
class StyledUnicodeChar {
    constructor() {
        this.uchar = ' ';
        this.penState = new PenState();
    }
    reset() {
        this.uchar = ' ';
        this.penState.reset();
    }
    setChar(uchar, newPenState) {
        this.uchar = uchar;
        this.penState.copy(newPenState);
    }
    setPenState(newPenState) {
        this.penState.copy(newPenState);
    }
    equals(other) {
        return this.uchar === other.uchar && this.penState.equals(other.penState);
    }
    copy(newChar) {
        this.uchar = newChar.uchar;
        this.penState.copy(newChar.penState);
    }
    isEmpty() {
        return this.uchar === ' ' && this.penState.isDefault();
    }
}const NR_COLS = 100;/**
 *  Exceptions from regular ASCII. CodePoints are mapped to UTF-16 codes
 */
const specialCea608CharsCodes = {
    42: 225, // lowercase a, acute accent
    92: 233, // lowercase e, acute accent
    94: 237, // lowercase i, acute accent
    95: 243, // lowercase o, acute accent
    96: 250, // lowercase u, acute accent
    123: 231, // lowercase c with cedilla
    124: 247, // division symbol
    125: 209, // uppercase N tilde
    126: 241, // lowercase n tilde
    127: 9608, // Full block
    // THIS BLOCK INCLUDES THE 16 EXTENDED (TWO-BYTE) LINE 21 CHARACTERS
    // THAT COME FROM HI BYTE=0x11 AND LOW BETWEEN 0x30 AND 0x3F
    // THIS MEANS THAT \x50 MUST BE ADDED TO THE VALUES
    128: 174, // Registered symbol (R)
    129: 176, // degree sign
    130: 189, // 1/2 symbolOutputFilter
    131: 191, // Inverted (open) question mark
    132: 8482, // Trademark symbol (TM)
    133: 162, // Cents symbol
    134: 163, // Pounds sterling
    135: 9834, // Music 8'th note
    136: 224, // lowercase a, grave accent
    137: 32, // transparent space (regular)
    138: 232, // lowercase e, grave accent
    139: 226, // lowercase a, circumflex accent
    140: 234, // lowercase e, circumflex accent
    141: 238, // lowercase i, circumflex accent
    142: 244, // lowercase o, circumflex accent
    143: 251, // lowercase u, circumflex accent
    // THIS BLOCK INCLUDES THE 32 EXTENDED (TWO-BYTE) LINE 21 CHARACTERS
    // THAT COME FROM HI BYTE=0x12 AND LOW BETWEEN 0x20 AND 0x3F
    144: 193, // capital letter A with acute
    145: 201, // capital letter E with acute
    146: 211, // capital letter O with acute
    147: 218, // capital letter U with acute
    148: 220, // capital letter U with diaresis
    149: 252, // lowercase letter U with diaeresis
    150: 8216, // opening single quote
    151: 161, // inverted exclamation mark
    152: 42, // asterisk
    153: 8217, // closing single quote
    154: 9473, // box drawings heavy horizontal
    155: 169, // copyright sign
    156: 8480, // Service mark
    157: 8226, // (round) bullet
    158: 8220, // Left double quotation mark
    159: 8221, // Right double quotation mark
    160: 192, // uppercase A, grave accent
    161: 194, // uppercase A, circumflex
    162: 199, // uppercase C with cedilla
    163: 200, // uppercase E, grave accent
    164: 202, // uppercase E, circumflex
    165: 203, // capital letter E with diaresis
    166: 235, // lowercase letter e with diaresis
    167: 206, // uppercase I, circumflex
    168: 207, // uppercase I, with diaresis
    169: 239, // lowercase i, with diaresis
    170: 212, // uppercase O, circumflex
    171: 217, // uppercase U, grave accent
    172: 249, // lowercase u, grave accent
    173: 219, // uppercase U, circumflex
    174: 171, // left-pointing double angle quotation mark
    175: 187, // right-pointing double angle quotation mark
    // THIS BLOCK INCLUDES THE 32 EXTENDED (TWO-BYTE) LINE 21 CHARACTERS
    // THAT COME FROM HI BYTE=0x13 AND LOW BETWEEN 0x20 AND 0x3F
    176: 195, // Uppercase A, tilde
    177: 227, // Lowercase a, tilde
    178: 205, // Uppercase I, acute accent
    179: 204, // Uppercase I, grave accent
    180: 236, // Lowercase i, grave accent
    181: 210, // Uppercase O, grave accent
    182: 242, // Lowercase o, grave accent
    183: 213, // Uppercase O, tilde
    184: 245, // Lowercase o, tilde
    185: 123, // Open curly brace
    186: 125, // Closing curly brace
    187: 92, // Backslash
    188: 94, // Caret
    189: 95, // Underscore
    190: 124, // Pipe (vertical line)
    191: 8764, // Tilde operator
    192: 196, // Uppercase A, umlaut
    193: 228, // Lowercase A, umlaut
    194: 214, // Uppercase O, umlaut
    195: 246, // Lowercase o, umlaut
    196: 223, // Esszett (sharp S)
    197: 165, // Yen symbolOutputFilter
    198: 164, // Generic currency sign
    199: 9475, // Box drawings heavy vertical
    200: 197, // Uppercase A, ring
    201: 229, // Lowercase A, ring
    202: 216, // Uppercase O, stroke
    203: 248, // Lowercase o, strok
    204: 9487, // Box drawings heavy down and right
    205: 9491, // Box drawings heavy down and left
    206: 9495, // Box drawings heavy up and right
    207: 9499, // Box drawings heavy up and left
};function getCharForByte(byte) {
    return String.fromCharCode(specialCea608CharsCodes[byte] || byte);
}/**
 *
 * This code was ported from the dash.js project at:
 *   https://github.com/Dash-Industry-Forum/dash.js/blob/development/externals/cea608-parser.js
 *   https://github.com/Dash-Industry-Forum/dash.js/commit/8269b26a761e0853bb21d78780ed945144ecdd4d#diff-71bc295a2d6b6b7093a1d3290d53a4b2
 *
 * The original copyright appears below:
 *
 * The copyright in this software is being made available under the BSD License,
 * included below. This software may be subject to other third party and contributor
 * rights, including patent rights, and no such rights are granted under this license.
 *
 * Copyright (c) 2015-2016, DASH Industry Forum.
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without modification,
 * are permitted provided that the following conditions are met:
 *  1. Redistributions of source code must retain the above copyright notice, this
 *  list of conditions and the following disclaimer.
 *  * Redistributions in binary form must reproduce the above copyright notice,
 *  this list of conditions and the following disclaimer in the documentation and/or
 *  other materials provided with the distribution.
 *  2. Neither the name of Dash Industry Forum nor the names of its
 *  contributors may be used to endorse or promote products derived from this software
 *  without specific prior written permission.
 *
 *  THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS AS IS AND ANY
 *  EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 *  WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED.
 *  IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT,
 *  INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT
 *  NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR
 *  PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY,
 *  WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE)
 *  ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 *  POSSIBILITY OF SUCH DAMAGE.
 */
/**
 * CTA-608 row consisting of NR_COLS instances of StyledUnicodeChar.
 *
 * @group CTA-608
 * @beta
 */
class Row {
    constructor(logger = new CaptionsLogger()) {
        this.chars = [];
        this.cueStartTime = null;
        this.pos = 0;
        this.currPenState = new PenState();
        for (let i = 0; i < NR_COLS; i++) {
            this.chars.push(new StyledUnicodeChar());
        }
        this.logger = logger;
    }
    equals(other) {
        for (let i = 0; i < NR_COLS; i++) {
            if (!this.chars[i].equals(other.chars[i])) {
                return false;
            }
        }
        return true;
    }
    copy(other) {
        for (let i = 0; i < NR_COLS; i++) {
            this.chars[i].copy(other.chars[i]);
        }
    }
    isEmpty() {
        let empty = true;
        for (let i = 0; i < NR_COLS; i++) {
            if (!this.chars[i].isEmpty()) {
                empty = false;
                break;
            }
        }
        return empty;
    }
    /**
     *  Set the cursor to a valid column.
     */
    setCursor(absPos) {
        if (this.pos !== absPos) {
            this.pos = absPos;
        }
        if (this.pos < 0) {
            this.logger.log(VerboseLevel.DEBUG, 'Negative cursor position ' + this.pos);
            this.pos = 0;
        }
        else if (this.pos > NR_COLS) {
            this.logger.log(VerboseLevel.DEBUG, 'Too large cursor position ' + this.pos);
            this.pos = NR_COLS;
        }
    }
    /**
     * Move the cursor relative to current position.
     */
    moveCursor(relPos) {
        const newPos = this.pos + relPos;
        if (relPos > 1) {
            for (let i = this.pos + 1; i < newPos + 1; i++) {
                this.chars[i].setPenState(this.currPenState);
            }
        }
        this.setCursor(newPos);
    }
    /**
     * Backspace, move one step back and clear character.
     */
    backSpace() {
        this.moveCursor(-1);
        this.chars[this.pos].setChar(' ', this.currPenState);
    }
    insertChar(byte) {
        if (byte >= 0x90) {
            // Extended char
            this.backSpace();
        }
        const char = getCharForByte(byte);
        if (this.pos >= NR_COLS) {
            this.logger.log(VerboseLevel.ERROR, () => 'Cannot insert ' +
                byte.toString(16) +
                ' (' +
                char +
                ') at position ' +
                this.pos +
                '. Skipping it!');
            return;
        }
        this.chars[this.pos].setChar(char, this.currPenState);
        this.moveCursor(1);
    }
    clearFromPos(startPos) {
        let i;
        for (i = startPos; i < NR_COLS; i++) {
            this.chars[i].reset();
        }
    }
    clear() {
        this.clearFromPos(0);
        this.pos = 0;
        this.currPenState.reset();
    }
    clearToEndOfRow() {
        this.clearFromPos(this.pos);
    }
    getTextString() {
        const chars = [];
        let empty = true;
        for (let i = 0; i < NR_COLS; i++) {
            const char = this.chars[i].uchar;
            if (char !== ' ') {
                empty = false;
            }
            chars.push(char);
        }
        if (empty) {
            return '';
        }
        else {
            return chars.join('');
        }
    }
    setPenStyles(styles) {
        this.currPenState.setStyles(styles);
        const currChar = this.chars[this.pos];
        currChar.setPenState(this.currPenState);
    }
}const NR_ROWS = 15;/**
 *
 * This code was ported from the dash.js project at:
 *   https://github.com/Dash-Industry-Forum/dash.js/blob/development/externals/cea608-parser.js
 *   https://github.com/Dash-Industry-Forum/dash.js/commit/8269b26a761e0853bb21d78780ed945144ecdd4d#diff-71bc295a2d6b6b7093a1d3290d53a4b2
 *
 * The original copyright appears below:
 *
 * The copyright in this software is being made available under the BSD License,
 * included below. This software may be subject to other third party and contributor
 * rights, including patent rights, and no such rights are granted under this license.
 *
 * Copyright (c) 2015-2016, DASH Industry Forum.
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without modification,
 * are permitted provided that the following conditions are met:
 *  1. Redistributions of source code must retain the above copyright notice, this
 *  list of conditions and the following disclaimer.
 *  * Redistributions in binary form must reproduce the above copyright notice,
 *  this list of conditions and the following disclaimer in the documentation and/or
 *  other materials provided with the distribution.
 *  2. Neither the name of Dash Industry Forum nor the names of its
 *  contributors may be used to endorse or promote products derived from this software
 *  without specific prior written permission.
 *
 *  THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS AS IS AND ANY
 *  EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 *  WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED.
 *  IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT,
 *  INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT
 *  NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR
 *  PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY,
 *  WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE)
 *  ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 *  POSSIBILITY OF SUCH DAMAGE.
 */
/**
 * Keep a CTA-608 screen of 32x15 styled characters
 *
 * @group CTA-608
 * @beta
 */
class CaptionScreen {
    constructor(logger = new CaptionsLogger()) {
        this.rows = [];
        this.currRow = NR_ROWS - 1;
        this.nrRollUpRows = null;
        this.lastOutputScreen = null;
        for (let i = 0; i < NR_ROWS; i++) {
            this.rows.push(new Row(logger));
        }
        this.logger = logger;
    }
    reset() {
        for (let i = 0; i < NR_ROWS; i++) {
            this.rows[i].clear();
        }
        this.currRow = NR_ROWS - 1;
    }
    equals(other) {
        let equal = true;
        for (let i = 0; i < NR_ROWS; i++) {
            if (!this.rows[i].equals(other.rows[i])) {
                equal = false;
                break;
            }
        }
        return equal;
    }
    copy(other) {
        for (let i = 0; i < NR_ROWS; i++) {
            this.rows[i].copy(other.rows[i]);
        }
    }
    isEmpty() {
        let empty = true;
        for (let i = 0; i < NR_ROWS; i++) {
            if (!this.rows[i].isEmpty()) {
                empty = false;
                break;
            }
        }
        return empty;
    }
    backSpace() {
        const row = this.rows[this.currRow];
        row.backSpace();
    }
    clearToEndOfRow() {
        const row = this.rows[this.currRow];
        row.clearToEndOfRow();
    }
    /**
     * Insert a character (without styling) in the current row.
     */
    insertChar(char) {
        const row = this.rows[this.currRow];
        row.insertChar(char);
    }
    setPen(styles) {
        const row = this.rows[this.currRow];
        row.setPenStyles(styles);
    }
    moveCursor(relPos) {
        const row = this.rows[this.currRow];
        row.moveCursor(relPos);
    }
    setCursor(absPos) {
        this.logger.log(VerboseLevel.INFO, 'setCursor: ' + absPos);
        const row = this.rows[this.currRow];
        row.setCursor(absPos);
    }
    setPAC(pacData) {
        this.logger.log(VerboseLevel.INFO, () => 'pacData = ' + JSON.stringify(pacData));
        let newRow = pacData.row - 1;
        if (this.nrRollUpRows && newRow < this.nrRollUpRows - 1) {
            newRow = this.nrRollUpRows - 1;
        }
        // Make sure this only affects Roll-up Captions by checking this.nrRollUpRows
        if (this.nrRollUpRows && this.currRow !== newRow) {
            // clear all rows first
            for (let i = 0; i < NR_ROWS; i++) {
                this.rows[i].clear();
            }
            // Copy this.nrRollUpRows rows from lastOutputScreen and place it in the newRow location
            // topRowIndex - the start of rows to copy (inclusive index)
            const topRowIndex = this.currRow + 1 - this.nrRollUpRows;
            // We only copy if the last position was already shown.
            // We use the cueStartTime value to check this.
            const lastOutputScreen = this.lastOutputScreen;
            if (lastOutputScreen) {
                const prevLineTime = lastOutputScreen.rows[topRowIndex].cueStartTime;
                const time = this.logger.time;
                if (prevLineTime !== null && time !== null && prevLineTime < time) {
                    for (let i = 0; i < this.nrRollUpRows; i++) {
                        this.rows[newRow - this.nrRollUpRows + i + 1].copy(lastOutputScreen.rows[topRowIndex + i]);
                    }
                }
            }
        }
        this.currRow = newRow;
        const row = this.rows[this.currRow];
        if (pacData.indent !== null) {
            const indent = pacData.indent;
            const prevPos = Math.max(indent - 1, 0);
            row.setCursor(pacData.indent);
            pacData.color = row.chars[prevPos].penState.foreground;
        }
        const styles = {
            foreground: pacData.color,
            underline: pacData.underline,
            italics: pacData.italics,
            background: 'black',
            flash: false,
        };
        this.setPen(styles);
    }
    /**
     * Set background/extra foreground, but first do back_space, and then insert space (backwards compatibility).
     */
    setBkgData(bkgData) {
        this.logger.log(VerboseLevel.INFO, () => 'bkgData = ' + JSON.stringify(bkgData));
        this.backSpace();
        this.setPen(bkgData);
        this.insertChar(0x20); // Space
    }
    setRollUpRows(nrRows) {
        this.nrRollUpRows = nrRows;
    }
    rollUp() {
        if (this.nrRollUpRows === null) {
            this.logger.log(VerboseLevel.DEBUG, 'roll_up but nrRollUpRows not set yet');
            return; // Not properly setup
        }
        this.logger.log(VerboseLevel.TEXT, () => this.getDisplayText());
        const topRowIndex = this.currRow + 1 - this.nrRollUpRows;
        const topRow = this.rows.splice(topRowIndex, 1)[0];
        topRow.clear();
        this.rows.splice(this.currRow, 0, topRow);
        this.logger.log(VerboseLevel.INFO, 'Rolling up');
    }
    /**
     * Get all non-empty rows with as unicode text.
     */
    getDisplayText(asOneRow) {
        asOneRow = asOneRow || false;
        const displayText = [];
        let text = '';
        let rowNr = -1;
        for (let i = 0; i < NR_ROWS; i++) {
            const rowText = this.rows[i].getTextString();
            if (rowText) {
                rowNr = i + 1;
                if (asOneRow) {
                    displayText.push('Row ' + rowNr + ": '" + rowText + "'");
                }
                else {
                    displayText.push(rowText.trim());
                }
            }
        }
        if (displayText.length > 0) {
            if (asOneRow) {
                text = '[' + displayText.join(' | ') + ']';
            }
            else {
                text = displayText.join('\n');
            }
        }
        return text;
    }
    getTextAndFormat() {
        return this.rows;
    }
}/**
 *
 * This code was ported from the dash.js project at:
 *   https://github.com/Dash-Industry-Forum/dash.js/blob/development/externals/cea608-parser.js
 *   https://github.com/Dash-Industry-Forum/dash.js/commit/8269b26a761e0853bb21d78780ed945144ecdd4d#diff-71bc295a2d6b6b7093a1d3290d53a4b2
 *
 * The original copyright appears below:
 *
 * The copyright in this software is being made available under the BSD License,
 * included below. This software may be subject to other third party and contributor
 * rights, including patent rights, and no such rights are granted under this license.
 *
 * Copyright (c) 2015-2016, DASH Industry Forum.
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without modification,
 * are permitted provided that the following conditions are met:
 *  1. Redistributions of source code must retain the above copyright notice, this
 *  list of conditions and the following disclaimer.
 *  * Redistributions in binary form must reproduce the above copyright notice,
 *  this list of conditions and the following disclaimer in the documentation and/or
 *  other materials provided with the distribution.
 *  2. Neither the name of Dash Industry Forum nor the names of its
 *  contributors may be used to endorse or promote products derived from this software
 *  without specific prior written permission.
 *
 *  THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS AS IS AND ANY
 *  EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 *  WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED.
 *  IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT,
 *  INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT
 *  NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR
 *  PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY,
 *  WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE)
 *  ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 *  POSSIBILITY OF SUCH DAMAGE.
 */
/**
 * CTA-608 Channel
 *
 * @group CTA-608
 * @beta
 */
class Cta608Channel {
    constructor(channelNumber, outputFilter, logger = new CaptionsLogger()) {
        this.chNr = channelNumber;
        this.outputFilter = outputFilter;
        this.mode = null;
        this.displayedMemory = new CaptionScreen(logger);
        this.nonDisplayedMemory = new CaptionScreen(logger);
        this.lastOutputScreen = new CaptionScreen(logger);
        this.currRollUpRow = this.displayedMemory.rows[NR_ROWS - 1];
        this.writeScreen = this.displayedMemory;
        this.mode = null;
        this.cueStartTime = null; // Keeps track of where a cue started.
        this.logger = logger;
        this.logger.log(VerboseLevel.INFO, 'new Cea608Channel(' + this.chNr + ')');
    }
    reset() {
        var _a, _b;
        this.mode = null;
        this.displayedMemory.reset();
        this.nonDisplayedMemory.reset();
        this.lastOutputScreen.reset();
        (_b = (_a = this.outputFilter) === null || _a === void 0 ? void 0 : _a.reset) === null || _b === void 0 ? void 0 : _b.call(_a);
        this.currRollUpRow = this.displayedMemory.rows[NR_ROWS - 1];
        this.writeScreen = this.displayedMemory;
        this.mode = null;
        this.cueStartTime = null;
    }
    getHandler() {
        return this.outputFilter;
    }
    setHandler(outputFilter) {
        this.outputFilter = outputFilter;
    }
    setPAC(pacData) {
        this.writeScreen.setPAC(pacData);
    }
    setBkgData(bkgData) {
        this.writeScreen.setBkgData(bkgData);
    }
    setMode(newMode) {
        if (newMode === this.mode) {
            return;
        }
        this.mode = newMode;
        this.logger.log(VerboseLevel.INFO, () => 'MODE=' + newMode);
        if (this.mode === 'MODE_POP-ON') {
            this.writeScreen = this.nonDisplayedMemory;
        }
        else {
            this.writeScreen = this.displayedMemory;
            this.writeScreen.reset();
        }
        if (this.mode !== 'MODE_ROLL-UP') {
            this.displayedMemory.setRollUpRows(null);
            this.nonDisplayedMemory.setRollUpRows(null);
        }
        this.mode = newMode;
    }
    insertChars(chars) {
        for (let i = 0; i < chars.length; i++) {
            this.writeScreen.insertChar(chars[i]);
        }
        const screen = this.writeScreen === this.displayedMemory ? 'DISP' : 'NON_DISP';
        this.logger.log(VerboseLevel.INFO, () => screen + ': ' + this.writeScreen.getDisplayText(true));
        if (this.mode === 'MODE_PAINT-ON' || this.mode === 'MODE_ROLL-UP') {
            this.logger.log(VerboseLevel.TEXT, () => 'DISPLAYED: ' + this.displayedMemory.getDisplayText(true));
            this.outputDataUpdate();
        }
    }
    ccRCL() {
        // Resume Caption Loading (switch mode to Pop On)
        this.logger.log(VerboseLevel.INFO, 'RCL - Resume Caption Loading');
        this.setMode('MODE_POP-ON');
    }
    ccBS() {
        // BackSpace
        this.logger.log(VerboseLevel.INFO, 'BS - BackSpace');
        if (this.mode === 'MODE_TEXT') {
            return;
        }
        this.writeScreen.backSpace();
        if (this.writeScreen === this.displayedMemory) {
            this.outputDataUpdate();
        }
    }
    ccAOF() {
        // Reserved (formerly Alarm Off)
    }
    ccAON() {
        // Reserved (formerly Alarm On)
    }
    ccDER() {
        // Delete to End of Row
        this.logger.log(VerboseLevel.INFO, 'DER- Delete to End of Row');
        this.writeScreen.clearToEndOfRow();
        this.outputDataUpdate();
    }
    ccRU(nrRows) {
        // Roll-Up Captions-2,3,or 4 Rows
        this.logger.log(VerboseLevel.INFO, 'RU(' + nrRows + ') - Roll Up');
        this.writeScreen = this.displayedMemory;
        this.setMode('MODE_ROLL-UP');
        this.writeScreen.setRollUpRows(nrRows);
    }
    ccFON() {
        // Flash On
        this.logger.log(VerboseLevel.INFO, 'FON - Flash On');
        this.writeScreen.setPen({ flash: true });
    }
    ccRDC() {
        // Resume Direct Captioning (switch mode to PaintOn)
        this.logger.log(VerboseLevel.INFO, 'RDC - Resume Direct Captioning');
        this.setMode('MODE_PAINT-ON');
    }
    ccTR() {
        // Text Restart in text mode (not supported, however)
        this.logger.log(VerboseLevel.INFO, 'TR');
        this.setMode('MODE_TEXT');
    }
    ccRTD() {
        // Resume Text Display in Text mode (not supported, however)
        this.logger.log(VerboseLevel.INFO, 'RTD');
        this.setMode('MODE_TEXT');
    }
    ccEDM() {
        // Erase Displayed Memory
        this.logger.log(VerboseLevel.INFO, 'EDM - Erase Displayed Memory');
        this.displayedMemory.reset();
        this.outputDataUpdate(true);
    }
    ccCR() {
        // Carriage Return
        this.logger.log(VerboseLevel.INFO, 'CR - Carriage Return');
        this.writeScreen.rollUp();
        this.outputDataUpdate(true);
    }
    ccENM() {
        // Erase Non-Displayed Memory
        this.logger.log(VerboseLevel.INFO, 'ENM - Erase Non-displayed Memory');
        this.nonDisplayedMemory.reset();
    }
    ccEOC() {
        // End of Caption (Flip Memories)
        this.logger.log(VerboseLevel.INFO, 'EOC - End Of Caption');
        if (this.mode === 'MODE_POP-ON') {
            const tmp = this.displayedMemory;
            this.displayedMemory = this.nonDisplayedMemory;
            this.nonDisplayedMemory = tmp;
            this.writeScreen = this.nonDisplayedMemory;
            this.logger.log(VerboseLevel.TEXT, () => 'DISP: ' + this.displayedMemory.getDisplayText());
        }
        this.outputDataUpdate(true);
    }
    ccTO(nrCols) {
        // Tab Offset 1,2, or 3 columns
        this.logger.log(VerboseLevel.INFO, 'TO(' + nrCols + ') - Tab Offset');
        this.writeScreen.moveCursor(nrCols);
    }
    ccMIDROW(secondByte) {
        // Parse MIDROW command
        const styles = { flash: false };
        styles.underline = secondByte % 2 === 1;
        styles.italics = secondByte >= 0x2e;
        if (!styles.italics) {
            const colorIndex = Math.floor(secondByte / 2) - 0x10;
            const colors = [
                'white',
                'green',
                'blue',
                'cyan',
                'red',
                'yellow',
                'magenta',
            ];
            styles.foreground = colors[colorIndex];
        }
        else {
            styles.foreground = 'white';
        }
        this.logger.log(VerboseLevel.INFO, 'MIDROW: ' + JSON.stringify(styles));
        this.writeScreen.setPen(styles);
    }
    outputDataUpdate(dispatch = false) {
        const time = this.logger.time;
        if (time === null) {
            return;
        }
        if (this.outputFilter) {
            if (this.cueStartTime === null && !this.displayedMemory.isEmpty()) {
                // Start of a new cue
                this.cueStartTime = time;
            }
            else {
                if (!this.displayedMemory.equals(this.lastOutputScreen)) {
                    this.outputFilter.newCue(this.cueStartTime, time, this.lastOutputScreen);
                    if (dispatch && this.outputFilter.dispatchCue) {
                        this.outputFilter.dispatchCue();
                    }
                    this.cueStartTime = this.displayedMemory.isEmpty() ? null : time;
                }
            }
            this.lastOutputScreen.copy(this.displayedMemory);
        }
    }
    cueSplitAtTime(t) {
        if (this.outputFilter) {
            if (!this.displayedMemory.isEmpty()) {
                if (this.outputFilter.newCue) {
                    this.outputFilter.newCue(this.cueStartTime, t, this.displayedMemory);
                }
                this.cueStartTime = t;
            }
        }
    }
}const backgroundColors = [
    'white',
    'green',
    'blue',
    'cyan',
    'red',
    'yellow',
    'magenta',
    'black',
    'transparent',
];function createCmdHistory() {
    return {
        a: null,
        b: null,
    };
}function hasCmdRepeated(a, b, cmdHistory) {
    return cmdHistory.a === a && cmdHistory.b === b;
}function numArrayToHexArray(numArray) {
    const hexArray = [];
    for (let j = 0; j < numArray.length; j++) {
        hexArray.push(numArray[j].toString(16));
    }
    return hexArray;
}const rowsHighCh1 = {
    17: 2,
    18: 4,
    21: 6,
    22: 8,
    23: 10,
    19: 13,
    20: 15,
};const rowsHighCh2 = {
    25: 2,
    26: 4,
    29: 6,
    30: 8,
    31: 10,
    27: 13,
    28: 15,
};// Tables to look up row from PAC data
const rowsLowCh1 = {
    17: 1,
    18: 3,
    21: 5,
    22: 7,
    23: 9,
    16: 11,
    19: 12,
    20: 14,
};const rowsLowCh2 = {
    25: 1,
    26: 3,
    29: 5,
    30: 7,
    31: 9,
    24: 11,
    27: 12,
    28: 14,
};function setLastCmd(a, b, cmdHistory) {
    cmdHistory.a = a;
    cmdHistory.b = b;
}/**
 *
 * This code was ported from the dash.js project at:
 *   https://github.com/Dash-Industry-Forum/dash.js/blob/development/externals/cea608-parser.js
 *   https://github.com/Dash-Industry-Forum/dash.js/commit/8269b26a761e0853bb21d78780ed945144ecdd4d#diff-71bc295a2d6b6b7093a1d3290d53a4b2
 *
 * The original copyright appears below:
 *
 * The copyright in this software is being made available under the BSD License,
 * included below. This software may be subject to other third party and contributor
 * rights, including patent rights, and no such rights are granted under this license.
 *
 * Copyright (c) 2015-2016, DASH Industry Forum.
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without modification,
 * are permitted provided that the following conditions are met:
 *  1. Redistributions of source code must retain the above copyright notice, this
 *  list of conditions and the following disclaimer.
 *  * Redistributions in binary form must reproduce the above copyright notice,
 *  this list of conditions and the following disclaimer in the documentation and/or
 *  other materials provided with the distribution.
 *  2. Neither the name of Dash Industry Forum nor the names of its
 *  contributors may be used to endorse or promote products derived from this software
 *  without specific prior written permission.
 *
 *  THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS AS IS AND ANY
 *  EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 *  WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED.
 *  IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT,
 *  INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT
 *  NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR
 *  PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY,
 *  WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE)
 *  ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 *  POSSIBILITY OF SUCH DAMAGE.
 */
/**
 * CEA-608 caption parser.
 *
 * @group CTA-608
 * @beta
 */
class Cta608Parser {
    constructor(field, out1, out2) {
        this.currentChannel = 0;
        this.cmdHistory = createCmdHistory();
        this.lastTime = null;
        const logger = (this.logger = new CaptionsLogger());
        this.channels = [
            null,
            new Cta608Channel(field, out1, logger),
            new Cta608Channel(field + 1, out2, logger),
        ];
    }
    /**
     * Add data for time t in forms of list of bytes (unsigned ints). The bytes are treated as pairs.
     *
     * @param time - The time in milliseconds
     * @param byteList - The list of bytes
     */
    addData(time, byteList) {
        this.lastTime = time;
        this.logger.time = time;
        for (let i = 0; i < byteList.length; i += 2) {
            const a = byteList[i] & 0x7f;
            const b = byteList[i + 1] & 0x7f;
            let cmdFound = false;
            let charsFound = null;
            if (this.lastTime !== null) {
                time = this.lastTime + 0.5 * i * 1001 / 30000;
                this.logger.time = time;
            }
            if (a === 0 && b === 0) {
                continue;
            }
            else {
                this.logger.log(VerboseLevel.DATA, () => '[' +
                    numArrayToHexArray([byteList[i], byteList[i + 1]]) +
                    '] -> (' +
                    numArrayToHexArray([a, b]) +
                    ')');
            }
            const cmdHistory = this.cmdHistory;
            const isControlCode = a >= 0x10 && a <= 0x1f;
            if (isControlCode) {
                // Skip redundant control codes
                if (hasCmdRepeated(a, b, cmdHistory)) {
                    setLastCmd(null, null, cmdHistory);
                    this.logger.log(VerboseLevel.DEBUG, () => 'Repeated command (' +
                        numArrayToHexArray([a, b]) +
                        ') is dropped');
                    continue;
                }
                setLastCmd(a, b, this.cmdHistory);
                cmdFound = this.parseCmd(a, b);
                if (!cmdFound) {
                    cmdFound = this.parseMidrow(a, b);
                }
                if (!cmdFound) {
                    cmdFound = this.parsePAC(a, b);
                }
                if (!cmdFound) {
                    cmdFound = this.parseBackgroundAttributes(a, b);
                }
            }
            else {
                setLastCmd(null, null, cmdHistory);
            }
            if (!cmdFound) {
                charsFound = this.parseChars(a, b);
                if (charsFound.length) {
                    const currChNr = this.currentChannel;
                    if (currChNr && currChNr > 0) {
                        const channel = this.channels[currChNr];
                        channel.insertChars(charsFound);
                    }
                    else {
                        this.logger.log(VerboseLevel.WARNING, 'No channel found yet. TEXT-MODE?');
                    }
                }
            }
            if (!cmdFound && !charsFound) {
                this.logger.log(VerboseLevel.WARNING, () => "Couldn't parse cleaned data " +
                    numArrayToHexArray([a, b]) +
                    ' orig: ' +
                    numArrayToHexArray([byteList[i], byteList[i + 1]]));
            }
        }
    }
    /**
     * Parse Command.
     *
     * @param a - The first byte
     * @param b - The second byte
     * @returns True if a command was found
     */
    parseCmd(a, b) {
        const cond1 = (a === 0x14 || a === 0x1c || a === 0x15 || a === 0x1d) &&
            b >= 0x20 &&
            b <= 0x2f;
        const cond2 = (a === 0x17 || a === 0x1f) && b >= 0x21 && b <= 0x23;
        if (!(cond1 || cond2)) {
            return false;
        }
        const chNr = a === 0x14 || a === 0x15 || a === 0x17 ? 1 : 2;
        const channel = this.channels[chNr];
        if (a === 0x14 || a === 0x15 || a === 0x1c || a === 0x1d) {
            if (b === 0x20) {
                channel.ccRCL();
            }
            else if (b === 0x21) {
                channel.ccBS();
            }
            else if (b === 0x22) {
                channel.ccAOF();
            }
            else if (b === 0x23) {
                channel.ccAON();
            }
            else if (b === 0x24) {
                channel.ccDER();
            }
            else if (b === 0x25) {
                channel.ccRU(2);
            }
            else if (b === 0x26) {
                channel.ccRU(3);
            }
            else if (b === 0x27) {
                channel.ccRU(4);
            }
            else if (b === 0x28) {
                channel.ccFON();
            }
            else if (b === 0x29) {
                channel.ccRDC();
            }
            else if (b === 0x2a) {
                channel.ccTR();
            }
            else if (b === 0x2b) {
                channel.ccRTD();
            }
            else if (b === 0x2c) {
                channel.ccEDM();
            }
            else if (b === 0x2d) {
                channel.ccCR();
            }
            else if (b === 0x2e) {
                channel.ccENM();
            }
            else if (b === 0x2f) {
                channel.ccEOC();
            }
        }
        else {
            // a == 0x17 || a == 0x1F
            channel.ccTO(b - 0x20);
        }
        this.currentChannel = chNr;
        return true;
    }
    /**
     * Parse midrow styling command
     *
     * @param a - The first byte
     * @param b - The second byte
     * @returns `true` if midrow styling command was found
     */
    parseMidrow(a, b) {
        let chNr = 0;
        if ((a === 0x11 || a === 0x19) && b >= 0x20 && b <= 0x2f) {
            if (a === 0x11) {
                chNr = 1;
            }
            else {
                chNr = 2;
            }
            if (chNr !== this.currentChannel) {
                this.logger.log(VerboseLevel.ERROR, 'Mismatch channel in midrow parsing');
                return false;
            }
            const channel = this.channels[chNr];
            if (!channel) {
                return false;
            }
            channel.ccMIDROW(b);
            this.logger.log(VerboseLevel.DEBUG, () => 'MIDROW (' + numArrayToHexArray([a, b]) + ')');
            return true;
        }
        return false;
    }
    /**
     * Parse Preable Access Codes (Table 53).
     *
     * @param a - The first byte
     * @param b - The second byte
     * @returns A Boolean that tells if PAC found
     */
    parsePAC(a, b) {
        let row;
        const case1 = ((a >= 0x11 && a <= 0x17) || (a >= 0x19 && a <= 0x1f)) &&
            b >= 0x40 &&
            b <= 0x7f;
        const case2 = (a === 0x10 || a === 0x18) && b >= 0x40 && b <= 0x5f;
        if (!(case1 || case2)) {
            return false;
        }
        const chNr = a <= 0x17 ? 1 : 2;
        if (b >= 0x40 && b <= 0x5f) {
            row = chNr === 1 ? rowsLowCh1[a] : rowsLowCh2[a];
        }
        else {
            // 0x60 <= b <= 0x7F
            row = chNr === 1 ? rowsHighCh1[a] : rowsHighCh2[a];
        }
        const channel = this.channels[chNr];
        if (!channel) {
            return false;
        }
        channel.setPAC(this.interpretPAC(row, b));
        this.currentChannel = chNr;
        return true;
    }
    /**
     * Interpret the second byte of the pac, and return the information.
     *
     * @param row - The row number
     * @param byte - The second byte
     * @returns pacData with style parameters
     */
    interpretPAC(row, byte) {
        let pacIndex;
        const pacData = {
            color: null,
            italics: false,
            indent: null,
            underline: false,
            row: row,
        };
        if (byte > 0x5f) {
            pacIndex = byte - 0x60;
        }
        else {
            pacIndex = byte - 0x40;
        }
        pacData.underline = (pacIndex & 1) === 1;
        if (pacIndex <= 0xd) {
            pacData.color = [
                'white',
                'green',
                'blue',
                'cyan',
                'red',
                'yellow',
                'magenta',
                'white',
            ][Math.floor(pacIndex / 2)];
        }
        else if (pacIndex <= 0xf) {
            pacData.italics = true;
            pacData.color = 'white';
        }
        else {
            pacData.indent = Math.floor((pacIndex - 0x10) / 2) * 4;
        }
        return pacData; // Note that row has zero offset. The spec uses 1.
    }
    /**
     * Parse characters.
     *
     * @param a - The first byte
     * @param b - The second byte
     * @returns An array with 1 to 2 codes corresponding to chars, if found. null otherwise.
     */
    parseChars(a, b) {
        let channelNr;
        let charCodes = [];
        let charCode1 = null;
        if (a >= 0x19) {
            channelNr = 2;
            charCode1 = a - 8;
        }
        else {
            channelNr = 1;
            charCode1 = a;
        }
        if (charCode1 >= 0x11 && charCode1 <= 0x13) {
            // Special character
            let oneCode;
            if (charCode1 === 0x11) {
                oneCode = b + 0x50;
            }
            else if (charCode1 === 0x12) {
                oneCode = b + 0x70;
            }
            else {
                oneCode = b + 0x90;
            }
            this.logger.log(VerboseLevel.INFO, () => "Special char '" +
                getCharForByte(oneCode) +
                "' in channel " +
                channelNr);
            charCodes = [oneCode];
        }
        else if (a >= 0x20 && a <= 0x7f) {
            charCodes = b === 0 ? [a] : [a, b];
        }
        if (charCodes) {
            this.logger.log(VerboseLevel.DEBUG, () => 'Char codes =  ' + numArrayToHexArray(charCodes).join(','));
        }
        return charCodes;
    }
    /**
     * Parse extended background attributes as well as new foreground color black.
     *
     * @param a - The first byte
     * @param b - The second byte
     * @returns True if background attributes are found
     */
    parseBackgroundAttributes(a, b) {
        const case1 = (a === 0x10 || a === 0x18) && b >= 0x20 && b <= 0x2f;
        const case2 = (a === 0x17 || a === 0x1f) && b >= 0x2d && b <= 0x2f;
        if (!(case1 || case2)) {
            return false;
        }
        let index;
        const bkgData = {};
        if (a === 0x10 || a === 0x18) {
            index = Math.floor((b - 0x20) / 2);
            bkgData.background = backgroundColors[index];
            if (b % 2 === 1) {
                bkgData.background = bkgData.background + '_semi';
            }
        }
        else if (b === 0x2d) {
            bkgData.background = 'transparent';
        }
        else {
            bkgData.foreground = 'black';
            if (b === 0x2f) {
                bkgData.underline = true;
            }
        }
        const chNr = a <= 0x17 ? 1 : 2;
        const channel = this.channels[chNr];
        channel.setBkgData(bkgData);
        return true;
    }
    /**
     * Reset state of parser and its channels.
     */
    reset() {
        for (let i = 0; i < Object.keys(this.channels).length; i++) {
            const channel = this.channels[i];
            if (channel) {
                channel.reset();
            }
        }
        setLastCmd(null, null, this.cmdHistory);
    }
    /**
     * Trigger the generation of a cue, and the start of a new one if displayScreens are not empty.
     */
    cueSplitAtTime(t) {
        for (let i = 0; i < this.channels.length; i++) {
            const channel = this.channels[i];
            if (channel) {
                channel.cueSplitAtTime(t);
            }
        }
    }
}/**
 * Extract CTA-608 data from a DataView
 *
 * @param raw - The DataView to extract the data from
 * @param cta608Range - The range of the CTA-608 data
 * @returns The extracted CTA-608 data
 *
 * @group CTA-608
 * @beta
 */
function extractCta608Data(raw, cta608Range) {
    let pos = cta608Range[0];
    const fieldData = [[], []];
    pos += 8; // Skip the identifier up to userDataTypeCode
    const ccCount = raw.getUint8(pos) & 31;
    pos += 2; // Advance 1 and skip reserved byte
    for (let i = 0; i < ccCount; i++) {
        const byte = raw.getUint8(pos);
        const ccValid = byte & 4;
        const ccType = byte & 3;
        pos++;
        const ccData1 = raw.getUint8(pos); // Keep parity bit
        pos++;
        const ccData2 = raw.getUint8(pos); // Keep parity bit
        pos++;
        if (ccValid && ((ccData1 & 127) + (ccData2 & 127) !== 0)) { //Check validity and non-empty data
            if (ccType === 0) {
                fieldData[0].push(ccData1);
                fieldData[0].push(ccData2);
            }
            else if (ccType === 1) {
                fieldData[1].push(ccData1);
                fieldData[1].push(ccData2);
            }
        }
    }
    return fieldData;
}/**
 * Find CTA-608 NAL units in a video stream
 *
 * @param raw - The DataView to extract the data from
 * @param startPos - The start position of the data
 * @param size - The size of the data
 * @returns The extracted CTA-608 NAL units
 *
 * @group CTA-608
 * @beta
 */
function findCta608Nalus(raw, startPos, size) {
    let nalSize = 0, cursor = startPos, nalType = 0;
    const cta608NaluRanges = [];
    // Check SEI data according to ANSI-SCTE 128
    const isCTA608SEI = (payloadType, payloadSize, raw, pos) => {
        if (payloadType !== 4 || payloadSize < 8) {
            return null;
        }
        const countryCode = raw.getUint8(pos);
        const providerCode = raw.getUint16(pos + 1);
        const userIdentifier = raw.getUint32(pos + 3);
        const userDataTypeCode = raw.getUint8(pos + 7);
        return countryCode == 181 && providerCode == 49 && userIdentifier == 1195456820 && userDataTypeCode == 3;
    };
    while (cursor < startPos + size) {
        nalSize = raw.getUint32(cursor);
        nalType = raw.getUint8(cursor + 4) & 31;
        //console.log(time + "  NAL " + nalType);
        if (nalType === 6) {
            // SEI NAL Unit. The NAL header is the first byte
            //console.log("SEI NALU of size " + nalSize + " at time " + time);
            let pos = cursor + 5;
            let payloadType = -1;
            while (pos < cursor + 4 + nalSize - 1) { // The last byte should be rbsp_trailing_bits
                payloadType = 0;
                let b = 255;
                while (b === 255) {
                    b = raw.getUint8(pos);
                    payloadType += b;
                    pos++;
                }
                let payloadSize = 0;
                b = 255;
                while (b === 255) {
                    b = raw.getUint8(pos);
                    payloadSize += b;
                    pos++;
                }
                if (isCTA608SEI(payloadType, payloadSize, raw, pos)) {
                    //console.log("CTA608 SEI " + time + " " + payloadSize);
                    cta608NaluRanges.push([pos, payloadSize]);
                }
                pos += payloadSize;
            }
        }
        cursor += nalSize + 4;
    }
    return cta608NaluRanges;
}/**
 * The copyright in this software is being made available under the BSD License,
 * included below. This software may be subject to other third party and contributor
 * rights, including patent rights, and no such rights are granted under this license.
 *
 * Copyright (c) 2015-2016, DASH Industry Forum.
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without modification,
 * are permitted provided that the following conditions are met:
 *  1. Redistributions of source code must retain the above copyright notice, this
 *  list of conditions and the following disclaimer.
 *  * Redistributions in binary form must reproduce the above copyright notice,
 *  this list of conditions and the following disclaimer in the documentation and/or
 *  other materials provided with the distribution.
 *  2. Neither the name of Dash Industry Forum nor the names of its
 *  contributors may be used to endorse or promote products derived from this software
 *  without specific prior written permission.
 *
 *  THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS AS IS AND ANY
 *  EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 *  WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED.
 *  IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT,
 *  INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT
 *  NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR
 *  PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY,
 *  WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE)
 *  ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 *  POSSIBILITY OF SUCH DAMAGE.
 */
/**
 * SCC Parser
 *
 * @group CTA-608
 * @beta
 */
class SccParser {
    constructor(processor, field = 1) {
        this.hasHeader = false;
        this.nrLinesParsed = 0;
        this.processor = processor;
        this.field = field;
    }
    parse(text) {
        const lines = text.split(/\r?\n/);
        this.nrLinesParsed = 0;
        if (lines[0] === 'Scenarist_SCC V1.0') {
            this.hasHeader = true;
            this.nrLinesParsed++;
        }
        for (let l = 1; l < lines.length; l += 2) {
            if (lines[l] !== '') {
                break; // Every second line should be empty
            }
            this.nrLinesParsed++;
            const lineData = this.parseDataLine(lines[l + 1]);
            if (lineData === null) {
                break;
            }
            this.nrLinesParsed++;
            this.processor.addData(lineData[0], lineData[1]);
        }
    }
    parseDataLine(line) {
        if (!line) {
            return null;
        }
        const lineParts = line.split(/\s+/);
        const timeData = lineParts[0];
        const ceaData = [];
        for (let i = 1; i < lineParts.length; i++) {
            const fourHexChars = lineParts[i];
            const a = parseInt(fourHexChars.substring(0, 2), 16);
            const b = parseInt(fourHexChars.substring(2, 4), 16);
            ceaData.push(a, b);
        }
        const time = this.timeConverter(timeData);
        return [time, ceaData];
    }
    timeConverter(smpteTs) {
        const parts = smpteTs.split(':');
        if (parts.length === 3) {
            const lastParts = parts[2].split(';');
            parts[2] = lastParts[0];
            const frames = parseInt(lastParts[1], 10);
            return (30 * (60 * (60 * parseInt(parts[0], 10) + parseInt(parts[1], 10)) + parseInt(parts[2], 10)) + frames) * 1001 / 30000;
        }
        return 0; // in case if format is incorrect
    }
    getHeaderStatus() {
        return this.hasHeader;
    }
    getField() {
        return this.field;
    }
    getLinesParsed() {
        return this.nrLinesParsed;
    }
}let dashParser;
/**
 * @internal
 */
function setDashParser(parser) {
    dashParser = parser;
}
/**
 * @internal
 * Parse XML to Json
 *
 * @param raw - Raw string containing the xml from the Dash Manifest
 * @returns json with the Dash Manifest structure
 */
function parseDashManifest(raw) {
    return dashParser(raw);
}let xmlSerializer;
/**
 * @internal
 */
function setDashSerializer(serializer) {
    xmlSerializer = serializer;
}
function serializeDashManifest(json) {
    return xmlSerializer(json);
}let hlsParser;
/**
 * @internal
 */
function setHlsParser(parser) {
    hlsParser = parser;
}
function parseHlsManifest(text) {
    if (!text) {
        console.error("Can't parse empty HLS Manifest");
        return {};
    }
    return hlsParser(text);
}function iso8601DurationToNumber(isoDuration) {
    const hours = /(?:([.,\d]+)H)/.exec(isoDuration);
    const minutes = /(?:([.,\d]+)M)/.exec(isoDuration);
    const seconds = /(?:([.,\d]+)S)/.exec(isoDuration);
    let duration = 0;
    if (hours) {
        duration += +hours[1] * 60 * 60;
    }
    if (minutes) {
        duration += +minutes[1] * 60;
    }
    if (seconds) {
        duration += +seconds[1];
    }
    return duration;
}/**
 * @internal
 *
 * Get the type of the content. It can be obtained directly from AdaptationSet or Representation
 * or can be inferred with the existing properties.
 *
 * @param adaptationSet - AdaptationSet to get the type from
 * @param representation - Representation to get the type from
 * @returns type of the content
 */
function getContentType(adaptationSet, representation) {
    var _a, _b, _c, _d;
    if (adaptationSet.$.contentType) {
        return adaptationSet.$.contentType;
    }
    if ((_a = adaptationSet.ContentComponent) === null || _a === void 0 ? void 0 : _a.at(0)) {
        return adaptationSet.ContentComponent.at(0).$.contentType;
    }
    if (adaptationSet.$.mimeType || (representation === null || representation === void 0 ? void 0 : representation.$.mimeType)) {
        const type = ((_b = adaptationSet.$.mimeType) === null || _b === void 0 ? void 0 : _b.split('/')[0]) ||
            ((_c = representation === null || representation === void 0 ? void 0 : representation.$.mimeType) === null || _c === void 0 ? void 0 : _c.split('/')[0]);
        if (type === 'audio' || type === 'video' || type === 'text') {
            return type;
        }
        if (type === 'application') {
            return 'text';
        }
    }
    if (adaptationSet.$.maxHeight) {
        return 'video';
    }
    const adaptationRef = (_d = adaptationSet.$.id) !== null && _d !== void 0 ? _d : `group: ${adaptationSet.$.group}, lang: ${adaptationSet.$.lang}`;
    console.error(`Could not find contentType from adaptationSet ${adaptationRef}`);
    console.info('Using "text" as default contentType');
    return 'text';
}function getGroup(adaptationSet) {
    var _a;
    return (_a = adaptationSet.$.group) !== null && _a !== void 0 ? _a : getContentType(adaptationSet);
}/**
 * @internal
 *
 * Get the initialization url. It can be present on AdaptationSet or Representation.
 *
 * Url initialization is present on segments.
 *
 * @param adaptationSet - AdaptationSet to try to get the initialization url from
 * @param representation - Representation to try to get the initialization url from
 */
function getInitializationUrl(adaptationSet, representation) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    let initializationUrl;
    if (representation.SegmentBase) {
        initializationUrl = (_a = representation.BaseURL[0]) !== null && _a !== void 0 ? _a : '';
    }
    else if (adaptationSet.SegmentList || representation.SegmentList) {
        initializationUrl =
            ((_c = (_b = representation.SegmentList) === null || _b === void 0 ? void 0 : _b.at(0)) === null || _c === void 0 ? void 0 : _c.Initialization[0].$.sourceURL) ||
                ((_e = (_d = adaptationSet.SegmentList) === null || _d === void 0 ? void 0 : _d.at(0)) === null || _e === void 0 ? void 0 : _e.Initialization[0].$.sourceURL);
    }
    if (adaptationSet.SegmentTemplate || representation.SegmentTemplate) {
        initializationUrl =
            ((_g = (_f = adaptationSet.SegmentTemplate) === null || _f === void 0 ? void 0 : _f.at(0)) === null || _g === void 0 ? void 0 : _g.$.initialization) ||
                ((_j = (_h = representation.SegmentTemplate) === null || _h === void 0 ? void 0 : _h.at(0)) === null || _j === void 0 ? void 0 : _j.$.initialization);
        if (initializationUrl === null || initializationUrl === void 0 ? void 0 : initializationUrl.includes('$RepresentationID$')) {
            initializationUrl = initializationUrl.replace('$RepresentationID$', (_k = representation.$.id) !== null && _k !== void 0 ? _k : '');
        }
    }
    return initializationUrl;
}/**
 * @internal
 *
 * Generates a presentation id. It uses the period id as default or creates one
 * if none is present.
 *
 * @param period - Period to try to get the id from
 * @param duration - Duration of the content
 * @returns Presentation id
 */
function getPresentationId(period, duration) {
    var _a;
    return (_a = period.$.id) !== null && _a !== void 0 ? _a : `presentation-id-${duration}`;
}/**
 * @internal
 *
 * Maps SegmentBase from dash to Segment list from ham.
 *
 * @param representation - Representation to get the SegmentBase from
 * @param duration - Duration of the segment
 * @returns list of ham segments
 */
function mapSegmentBase(representation, duration) {
    return representation.SegmentBase.map((segment) => {
        var _a;
        return {
            duration,
            url: (_a = representation.BaseURL[0]) !== null && _a !== void 0 ? _a : '',
            byteRange: segment.$.indexRange,
        };
    });
}/**
 * @internal
 *
 * Calculates the duration of a segment.
 *
 * segmentDuration = duration / timescale
 *
 * @param duration - Duration of the segment
 * @param timescale - Timescale of the segment
 * @returns Segment duration
 */
function calculateDuration(duration, timescale) {
    if (!duration || !timescale) {
        return 1;
    }
    return +(duration !== null && duration !== void 0 ? duration : 1) / +(timescale !== null && timescale !== void 0 ? timescale : 1);
}/**
 * @internal
 *
 * Maps SegmentList from dash to Segment list from ham.
 *
 * @param segmentList - SegmentList list from dash
 * @returns list of ham segments
 */
function mapSegmentList(segmentList) {
    const segments = [];
    segmentList.map((segment) => {
        if (segment.SegmentURL) {
            return segment.SegmentURL.forEach((segmentURL) => {
                var _a;
                segments.push({
                    duration: calculateDuration(segment.$.duration, segment.$.timescale),
                    url: (_a = segmentURL.$.media) !== null && _a !== void 0 ? _a : '',
                });
            });
        }
    });
    return segments;
}/**
 * @internal
 *
 * Calculates the number of segments that a track has to use SegmentTemplate.
 *
 * Equation used:
 * segments = total duration / (segment duration / timescale)
 *
 * **This equation might be wrong, please double-check it**
 *
 * @param segmentTemplate - SegmentTemplate object
 * @param duration - Total duration of the content
 * @returns Number of segments
 */
function getNumberOfSegments(segmentTemplate, duration) {
    // FIXME: This equation may be wrong
    return Math.round(duration /
        calculateDuration(segmentTemplate.$.duration, segmentTemplate.$.timescale));
}/**
 * @internal
 *
 * Create the url from a segment template.
 *
 * Searches for substrings with the format `$value$` and replaces it with the correct value.
 * - RepresentationID: id of the representation
 * - Number: id of the segment. `%0Xd` defines the number `X` of digits it needs to have
 *
 * @param representation - Representation of the template
 * @param segmentTemplate - Segment template
 * @param segmentId - Segment id
 * @returns url from the segment template
 */
function getUrlFromTemplate(representation, segmentTemplate, segmentId) {
    const regexTemplate = /\$(.*?)\$/g;
    return segmentTemplate.$.media.replace(regexTemplate, (match) => {
        if (match.includes('RepresentationID')) {
            return representation.$.id;
        }
        /**
         * Number with 4 digits e.g: 0001
         */
        if (match.includes('Number%04d')) {
            return segmentId.toString().padStart(4, '0');
        }
        if (match.includes('Number')) {
            return segmentId;
        }
        console.error(`Unknown property ${match} from the SegmentTemplate on representation ${representation.$.id}`);
        return match;
    });
}/**
 * @internal
 *
 * Maps SegmentTemplate from dash to Segment list from ham.
 *
 * @param representation - Representation to generate the urls
 * @param duration - Duration of the segments
 * @param segmentTemplate - SegmentTemplate to get the properties from
 * @returns list of ham segments
 */
function mapSegmentTemplate(representation, duration, segmentTemplate) {
    var _a;
    const numberOfSegments = getNumberOfSegments(segmentTemplate, duration);
    const init = +((_a = segmentTemplate.$.startNumber) !== null && _a !== void 0 ? _a : 0);
    const segments = [];
    for (let id = init; id < numberOfSegments + init; id++) {
        segments.push({
            duration: calculateDuration(segmentTemplate.$.duration, segmentTemplate.$.timescale),
            url: getUrlFromTemplate(representation, segmentTemplate, id),
        });
    }
    return segments;
}/**
 * @internal
 *
 * Maps dash segments to ham segment.
 *
 * Checks the type of dash segments used to map them accordingly.
 * @see mapSegmentBase
 * @see mapSegmentList
 * @see mapSegmentTemplate
 *
 * @param adaptationSet - AdaptationSet to get the segments from
 * @param representation - Representation to get the segments from
 * @param duration - Duration of the segments
 * @returns list of ham segments
 */
function mapSegments(adaptationSet, representation, duration) {
    var _a, _b, _c, _d;
    const segmentTemplate = (_b = (_a = adaptationSet.SegmentTemplate) === null || _a === void 0 ? void 0 : _a.at(0)) !== null && _b !== void 0 ? _b : (_c = representation.SegmentTemplate) === null || _c === void 0 ? void 0 : _c.at(0);
    const segmentList = (_d = adaptationSet.SegmentList) !== null && _d !== void 0 ? _d : representation.SegmentList;
    if (representation.SegmentBase) {
        return mapSegmentBase(representation, duration);
    }
    else if (segmentList) {
        return mapSegmentList(segmentList);
    }
    else if (segmentTemplate) {
        return mapSegmentTemplate(representation, duration, segmentTemplate);
    }
    else {
        console.error(`Representation ${representation.$.id} has no segments`);
        return [];
    }
}/**
 * @internal
 *
 * Get the channels value (audio). It can be present on adaptationSet or representation.
 *
 * @param adaptationSet - AdaptationSet to try to get the channels from
 * @param representation - Representation to try to get the channels from
 * @returns Channels value
 */
function getChannels(adaptationSet, representation) {
    var _a, _b, _c, _d, _e, _f;
    const channels = +((_f = (_c = (_b = (_a = adaptationSet.AudioChannelConfiguration) === null || _a === void 0 ? void 0 : _a.at(0)) === null || _b === void 0 ? void 0 : _b.$.value) !== null && _c !== void 0 ? _c : (_e = (_d = representation.AudioChannelConfiguration) === null || _d === void 0 ? void 0 : _d.at(0)) === null || _e === void 0 ? void 0 : _e.$.value) !== null && _f !== void 0 ? _f : 0);
    if (!channels) {
        console.error(`Representation ${representation.$.id} has no channels`);
    }
    return channels;
}/**
 * @internal
 *
 * Get the codec value (video and audio). It can be present on adaptationSet or representation.
 *
 * @param adaptationSet - AdaptationSet to try to get the codec from
 * @param representation - Representation to try to get the codec from
 * @returns Content codec
 */
function getCodec$1(adaptationSet, representation) {
    var _a, _b;
    const codec = (_b = (_a = representation.$.codecs) !== null && _a !== void 0 ? _a : adaptationSet.$.codecs) !== null && _b !== void 0 ? _b : '';
    if (!codec) {
        console.error(`Representation ${representation.$.id} has no codecs`);
    }
    return codec;
}// Dash Timescale defaults
const TIMESCALE_48000 = 48000;
// Using 90000 as default for video since it is divisible by 24, 25 and 30
const VIDEO_SAMPLE_RATE = 90000;
// Using 1000 as default for text
const TEXT_SAMPLE_RATE = 1000;
// Frame rate numerator default
const FRAME_RATE_NUMERATOR_30 = 30;
// Manifest parser constants
const WHITE_SPACE = ' ';
const WHITE_SPACE_ENCODED = '%20'; //In some players , white space is not supported
const HYPHEN_MINUS_SEPARATOR = '-'; //In Dash it is used to separate the start and end of a byte range
const AT_SEPARATOR = '@'; //In Hls it is used to separate the start and end of a byte range
const FRAME_RATE_SEPARATOR = '/';
const NUMERATOR = 0;
const DENOMINATOR = 1;
const ZERO = 0;/**
 * @internal
 *
 * Get the frame rate from a dash manifest.
 *
 * This functions assumes the adaptationSet and representation set are type video
 *
 * @param adaptationSet - To try to get the frameRate from
 * @param representation - To try to get the frameRate from
 * @returns object containing numerator and denominator
 */
function getFrameRate$1(adaptationSet, representation) {
    var _a, _b, _c, _d;
    const frameRateDash = (_b = (_a = representation.$.frameRate) !== null && _a !== void 0 ? _a : adaptationSet.$.frameRate) !== null && _b !== void 0 ? _b : '';
    if (!frameRateDash) {
        console.error(`Representation ${representation.$.id} has no frame rate`);
    }
    const frameRate = frameRateDash.split(FRAME_RATE_SEPARATOR);
    const frameRateNumerator = parseInt((_c = frameRate.at(NUMERATOR)) !== null && _c !== void 0 ? _c : '');
    const frameRateDenominator = parseInt((_d = frameRate.at(DENOMINATOR)) !== null && _d !== void 0 ? _d : '');
    return {
        frameRateNumerator: isNaN(frameRateNumerator)
            ? FRAME_RATE_NUMERATOR_30
            : frameRateNumerator,
        frameRateDenominator: isNaN(frameRateDenominator)
            ? ZERO
            : frameRateDenominator,
    };
}/**
 * @internal
 *
 * Get the language from an adaptation set.
 *
 * @param adaptationSet - AdaptationSet to get the language from
 * @returns language of the content
 */
function getLanguage(adaptationSet) {
    let language = adaptationSet.$.lang;
    if (!language) {
        console.info(`AdaptationSet ${adaptationSet.$.id} has no lang, using "und" as default`);
        language = 'und';
    }
    return language;
}/**
 * @internal
 *
 * Get sample rate (audio).
 *
 * @param adaptationSet - AdaptationSet to try to get the sampleRate from
 * @param representation - Representation to try to get the sampleRate from
 * @returns Sample rate. In case it is not presents, it returns 0.
 */
function getSampleRate(adaptationSet, representation) {
    var _a, _b;
    const sampleRate = +((_b = (_a = representation.$.audioSamplingRate) !== null && _a !== void 0 ? _a : adaptationSet.$.audioSamplingRate) !== null && _b !== void 0 ? _b : 0);
    if (!sampleRate) {
        console.error(`Representation ${representation.$.id} has no audioSamplingRate`);
    }
    return sampleRate;
}/**
 * @internal
 *
 * Get the sar value. It can be present on adaptationSet or representation.
 *
 * @param adaptationSet - AdaptationSet to try to get the sar from
 * @param representation - AdaptationSet to try to get the sar from
 * @returns sar value. In case it is not present, returns empty string.
 */
function getSar(adaptationSet, representation) {
    var _a, _b;
    const sar = (_b = (_a = representation.$.sar) !== null && _a !== void 0 ? _a : adaptationSet.$.sar) !== null && _b !== void 0 ? _b : '';
    if (!sar) {
        console.error(`Representation ${representation.$.id} has no sar`);
    }
    return sar;
}/**
 * @internal
 *
 * Calculate the duration of a track.
 *
 * This is calculated using the sum of the duration of all the segments from the
 * track.
 *
 * An alternative to this could be number of segments * duration of a segment.
 *
 * @param segments - Segments to calculate the sum of the durations
 * @returns Duration of the track
 */
function getTrackDuration(segments) {
    return segments.reduce((acc, segment) => {
        return acc + segment.duration;
    }, 0);
}/**
 * @internal
 *
 * Map dash components to ham tracks.
 *
 * @param adaptationSet - AdaptationSet of the dash manifest
 * @param representation - Representation of the dash manifest
 * @param segments - Segments from the representation of the dash manifest
 * @param initializationUrl - Initialization url from the track
 * @returns AudioTrack, VideoTrack or TextTrack depending on the type
 */
function mapTracks(adaptationSet, representation, segments, initializationUrl) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    if (!adaptationSet) {
        throw new Error('Error: AdaptationSet is undefined');
    }
    const type = getContentType(adaptationSet, representation);
    if (type === 'video') {
        return {
            bandwidth: +((_a = representation.$.bandwidth) !== null && _a !== void 0 ? _a : 0),
            codec: getCodec$1(adaptationSet, representation),
            duration: getTrackDuration(segments),
            frameRate: getFrameRate$1(adaptationSet, representation),
            height: +((_b = representation.$.height) !== null && _b !== void 0 ? _b : 0),
            id: (_c = representation.$.id) !== null && _c !== void 0 ? _c : '',
            language: getLanguage(adaptationSet),
            par: (_d = adaptationSet.$.par) !== null && _d !== void 0 ? _d : '',
            sar: getSar(adaptationSet, representation),
            scanType: (_e = representation.$.scanType) !== null && _e !== void 0 ? _e : '',
            segments,
            type,
            width: +((_f = representation.$.width) !== null && _f !== void 0 ? _f : 0),
            urlInitialization: initializationUrl,
        };
    }
    else if (type === 'audio') {
        return {
            bandwidth: +((_g = representation.$.bandwidth) !== null && _g !== void 0 ? _g : 0),
            channels: getChannels(adaptationSet, representation),
            codec: getCodec$1(adaptationSet, representation),
            duration: getTrackDuration(segments),
            id: (_h = representation.$.id) !== null && _h !== void 0 ? _h : '',
            language: getLanguage(adaptationSet),
            sampleRate: getSampleRate(adaptationSet, representation),
            segments,
            type,
            urlInitialization: initializationUrl,
        };
    }
    else {
        // if (type === 'text')
        return {
            bandwidth: +((_j = representation.$.bandwidth) !== null && _j !== void 0 ? _j : 0),
            codec: getCodec$1(adaptationSet, representation),
            duration: getTrackDuration(segments),
            id: (_k = representation.$.id) !== null && _k !== void 0 ? _k : '',
            language: getLanguage(adaptationSet),
            segments,
            type,
            urlInitialization: initializationUrl,
        };
    }
}/**
 * @internal
 *
 * Main function to map dash to ham.
 *
 * @param dash - Dash manifest to map
 * @returns List of presentations in ham
 */
function mapDashToHam(dash) {
    return dash.MPD.Period.map((period) => {
        const duration = iso8601DurationToNumber(period.$.duration);
        const presentationId = getPresentationId(period, duration);
        const selectionSetGroups = {};
        period.AdaptationSet.map((adaptationSet) => {
            var _a, _b, _c, _d;
            const tracks = adaptationSet.Representation.map((representation) => {
                const segments = mapSegments(adaptationSet, representation, duration);
                return mapTracks(adaptationSet, representation, segments, getInitializationUrl(adaptationSet, representation));
            });
            const group = getGroup(adaptationSet);
            if (!selectionSetGroups[group]) {
                selectionSetGroups[group] = {
                    id: group,
                    switchingSets: [],
                };
            }
            selectionSetGroups[group].switchingSets.push({
                id: (_d = (_a = adaptationSet.$.id) !== null && _a !== void 0 ? _a : (_c = (_b = adaptationSet.ContentComponent) === null || _b === void 0 ? void 0 : _b.at(0)) === null || _c === void 0 ? void 0 : _c.$.id) !== null && _d !== void 0 ? _d : group,
                tracks,
            });
        });
        const selectionSets = Object.values(selectionSetGroups);
        return { id: presentationId, selectionSets };
    });
}function trackToSegmentBase(track) {
    var _a;
    const segments = [];
    if (track.segments.length > 0 &&
        track.byteRange &&
        track.segments[0].byteRange &&
        track.segments[0].byteRange.includes('@')) {
        let firstSegment = undefined;
        const initByteRange = track.byteRange.includes('-')
            ? track.byteRange.split('-')[1]
            : track.byteRange.includes('@')
                ? track.byteRange.split('@')[0]
                : '';
        const initRange = +initByteRange - 1;
        const byteFirstSegment = track.segments[0].byteRange.includes('-')
            ? track.segments[0].byteRange.split('-')[1]
            : track.segments[0].byteRange.includes('@')
                ? track.segments[0].byteRange.split('@')[1]
                : '';
        const numberFirstByteRange = +byteFirstSegment - 1;
        firstSegment = {
            $: {
                indexRange: `${initByteRange}-${numberFirstByteRange}`,
            },
            Initialization: [{ $: { range: `0-${initRange}` } }],
        };
        if (firstSegment && track.type === 'audio') {
            // All segments should have timescale, but for now, just the audio ones store this value
            const audioTrack = track;
            firstSegment.$.timescale = (_a = audioTrack.sampleRate.toString()) !== null && _a !== void 0 ? _a : '';
        }
        if (firstSegment) {
            segments.push(firstSegment);
        }
    }
    else {
        track.segments.forEach((segment) => {
            var _a;
            let newSegment;
            if (segment.byteRange) {
                const initRange = +segment.byteRange.split('-')[0] - 1;
                newSegment = {
                    $: {
                        indexRange: segment.byteRange,
                    },
                    Initialization: [{ $: { range: `0-${initRange}` } }],
                };
            }
            if (newSegment && track.type === 'audio') {
                // All segments should have timescale, but for now, just the audio ones store this value
                const audioTrack = track;
                newSegment.$.timescale = (_a = audioTrack.sampleRate.toString()) !== null && _a !== void 0 ? _a : '';
            }
            if (newSegment) {
                segments.push(newSegment);
            }
        });
    }
    return segments;
}/**
 * @internal
 *
 * This function tries to recreate the timescale value.
 *
 * This value is not stored on the ham object, so it is not possible (for now)
 * to get the original one.
 *
 * Just the audio tracks have this value stored on the `sampleRate` key.
 *
 * @param track - Track to get the timescale from
 * @returns Timescale in numbers
 */
function getTimescale(track) {
    if ((track === null || track === void 0 ? void 0 : track.type) === 'audio') {
        const audioTrack = track;
        return audioTrack.sampleRate !== 0
            ? audioTrack.sampleRate
            : TIMESCALE_48000;
    }
    if ((track === null || track === void 0 ? void 0 : track.type) === 'video') {
        return VIDEO_SAMPLE_RATE;
    }
    if ((track === null || track === void 0 ? void 0 : track.type) === 'text') {
        return TEXT_SAMPLE_RATE;
    }
    return VIDEO_SAMPLE_RATE;
}function trackToSegmentList(track) {
    var _a;
    const segmentList = [];
    const segmentURLs = [];
    track.segments.forEach((segment) => {
        segmentURLs.push({
            $: {
                media: segment.url,
            },
        });
    });
    if (!((_a = track.segments.at(0)) === null || _a === void 0 ? void 0 : _a.byteRange)) {
        const timescale = getTimescale(track);
        segmentList.push({
            $: {
                duration: ((track.duration * timescale) /
                    segmentURLs.length).toString(),
                timescale: timescale.toString(),
            },
            Initialization: [{ $: { sourceURL: track.urlInitialization } }],
            SegmentURL: segmentURLs,
        });
    }
    return segmentList;
}/**
 * @internal
 *
 * Get the framerate from a track.
 *
 * If frameRate numerator is not present, it uses 30 as default.
 *
 * @param track - to get the framerate from
 * @returns frame rate as a string formatted as `numerator/denominator`
 */
function getFrameRate(track) {
    var _a;
    let frameRate = undefined;
    if ((track === null || track === void 0 ? void 0 : track.type) === 'video') {
        const videoTrack = track;
        frameRate = `${(_a = videoTrack.frameRate.frameRateNumerator) !== null && _a !== void 0 ? _a : FRAME_RATE_NUMERATOR_30}`;
        frameRate =
            videoTrack.frameRate.frameRateDenominator !== ZERO
                ? `${frameRate}/${videoTrack.frameRate.frameRateDenominator}`
                : frameRate;
    }
    return frameRate;
}function tracksToRepresentation(tracks) {
    return tracks.map((track) => {
        var _a, _b;
        const representation = {
            $: {
                id: track.id,
                bandwidth: track.bandwidth.toString(),
            },
            SegmentBase: trackToSegmentBase(track),
            SegmentList: trackToSegmentList(track),
        };
        representation.$.mimeType = `${track.type}/mp4`; //Harcoded value
        if (track.type === 'video') {
            const videoTrack = track;
            representation.$ = {
                ...representation.$,
                frameRate: getFrameRate(track),
                width: videoTrack.width.toString(),
                height: videoTrack.height.toString(),
                codecs: videoTrack.codec,
            };
            if (videoTrack.scanType) {
                representation.$.scanType = videoTrack.scanType;
            }
        }
        if (track.type === 'audio') {
            const audioTrack = track;
            representation.$ = {
                ...representation.$,
                audioSamplingRate: audioTrack.sampleRate.toString(),
                codecs: audioTrack.codec,
            };
            representation.AudioChannelConfiguration = [
                {
                    $: {
                        schemeIdUri: 'urn:mpeg:dash:23003:3:audio_channel_configuration:2011', // hardcoded value
                        value: (_a = audioTrack.channels.toString()) !== null && _a !== void 0 ? _a : '',
                    },
                },
            ];
        }
        if ((_b = track.segments[0]) === null || _b === void 0 ? void 0 : _b.byteRange) {
            // Only BaseSegments have byteRange on segments, and BaseURL on the representation
            representation.BaseURL = [track.segments[0].url];
        }
        return representation;
    });
}function selectionSetsToAdaptationSet(selectionsSets) {
    return selectionsSets.flatMap((selectionSet) => {
        return selectionSet.switchingSets.map((switchingSet) => {
            const track = switchingSet.tracks[0];
            return {
                $: {
                    id: switchingSet.id,
                    group: selectionSet.id,
                    contentType: track === null || track === void 0 ? void 0 : track.type,
                    mimeType: `${track === null || track === void 0 ? void 0 : track.type}/mp4`,
                    frameRate: getFrameRate(track),
                    lang: track === null || track === void 0 ? void 0 : track.language,
                    codecs: track === null || track === void 0 ? void 0 : track.codec,
                },
                Representation: tracksToRepresentation(switchingSet.tracks),
            };
        });
    });
}function numberToIso8601Duration(duration) {
    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration % 3600) / 60);
    const seconds = duration % 60;
    if (hours > 0) {
        return `PT${hours}H${minutes}M${seconds}S`;
    }
    else if (minutes > 0) {
        return `PT${minutes}M${seconds}S`;
    }
    return `PT${seconds}S`;
}function presentationsToPeriods(presentations) {
    return presentations.map((presentation) => {
        return {
            $: {
                duration: numberToIso8601Duration(presentation.selectionSets[0].switchingSets[0].tracks[0]
                    .duration),
                id: presentation.id,
                start: 'PT0S',
            },
            AdaptationSet: selectionSetsToAdaptationSet(presentation.selectionSets),
        };
    });
}function mapHamToDash(hamManifests) {
    const periods = presentationsToPeriods(hamManifests);
    const duration = periods[0].$.duration;
    const manifest = {
        MPD: {
            $: {
                mediaPresentationDuration: duration,
                type: 'static',
            },
            Period: periods,
        },
    };
    return serializeDashManifest(manifest);
}//Add metadata to manifest.
//In the future, if any other fields are wanted to be added, they can be added here.
function addMetadataToDash(dashManifest, manifest) {
    if (!manifest.metadata) {
        manifest.metadata = new Map();
    }
    if (dashManifest.MPD.$ && dashManifest.MPD.$.profiles) {
        manifest.metadata.set('profiles', dashManifest.MPD.$.profiles);
    }
    if (dashManifest.MPD.$ && dashManifest.MPD.$.type) {
        manifest.metadata.set('type', dashManifest.MPD.$.type);
    }
    return manifest;
}function getMetadata(manifest) {
    const metadata = manifest === null || manifest === void 0 ? void 0 : manifest.metadata;
    return JSON.parse(JSON.stringify(metadata));
}class DashMapper {
    getManifestMetadata() {
        return getMetadata(this.manifest);
    }
    toHam(manifest) {
        const dashManifest = parseDashManifest(manifest.manifest);
        if (!dashManifest) {
            return [];
        }
        addMetadataToDash(dashManifest, manifest);
        return mapDashToHam(dashManifest);
    }
    toManifest(presentation) {
        const manifest = mapHamToDash(presentation);
        return { manifest, ancillaryManifests: [], type: 'dash' };
    }
}class MapperContext {
    constructor() { }
    static getInstance() {
        if (!MapperContext.instance) {
            MapperContext.instance = new MapperContext();
        }
        return MapperContext.instance;
    }
    setStrategy(strategy) {
        this.strategy = strategy;
    }
    getHamFormat(manifest) {
        return this.strategy.toHam(manifest);
    }
    getManifestFormat(presentation) {
        return this.strategy.toManifest(presentation);
    }
    getManifestMetadata() {
        return this.strategy.getManifestMetadata();
    }
}/**
 * Convert dash manifest into a ham object.
 *
 * @example
 * Example on how to import the cmaf module and convert the dash `manifest`
 * into the ham manifest.
 * ```ts
 * import cmaf from '@svta/common-media-library/cmaf-ham';
 *
 * const manifest = cmaf.dashToHam(dashManifest);
 * ```
 *
 * @param manifest - String of the XML Dash manifest
 * @returns List of presentations from ham
 *
 * @group CMAF
 * @alpha
 */
function dashToHam(manifest) {
    const mapperContext = MapperContext.getInstance();
    mapperContext.setStrategy(new DashMapper());
    return mapperContext.getHamFormat({ manifest, type: 'dash' });
}/**
 * Convert HAM object into Dash Manifest.
 *
 * @example
 * Example on how to import the cmaf module and convert the ham `presentations`
 * array into the dash manifest.
 * ```ts
 * import cmaf from '@svta/common-media-library/cmaf-ham';
 *
 * const manifest = cmaf.hamToDash(presentations);
 * ```
 *
 * @param presentation - List of presentations from ham
 * @returns Manifest object containing the Dash manifest as string
 *
 * @group CMAF
 * @alpha
 */
function hamToDash(presentation) {
    const mapperContext = MapperContext.getInstance();
    mapperContext.setStrategy(new DashMapper());
    return mapperContext.getManifestFormat(presentation);
}/**
 * @internal
 *
 * Get the byterange in hls format from ham track.
 *
 * @param track - Track to get the byterange from
 * @returns string containing the byterange in the hls format
 *
 * @group CMAF
 * @alpha
 */
function getByterange$1(track) {
    var _a, _b, _c, _d;
    if (track.byteRange) {
        return `BYTERANGE:${track.byteRange.replace(HYPHEN_MINUS_SEPARATOR, AT_SEPARATOR)}\n`;
    }
    else if ((_b = (_a = track.segments) === null || _a === void 0 ? void 0 : _a.at(0)) === null || _b === void 0 ? void 0 : _b.byteRange) {
        return `BYTERANGE:0@${Number((_d = (_c = track.segments.at(0)) === null || _c === void 0 ? void 0 : _c.byteRange) === null || _d === void 0 ? void 0 : _d.replace(HYPHEN_MINUS_SEPARATOR, AT_SEPARATOR).split(AT_SEPARATOR)[0]) - 1}\n`;
    }
    return '';
}/**
 * @internal
 *
 * Get url initialization from ham track.
 *
 * @param track - Track to get the url initialization from
 * @returns string containing the url initialization in the hls format
 *
 * @group CMAF
 * @alpha
 */
function getUrlInitialization(track) {
    var _a, _b;
    return ((_b = (_a = track.urlInitialization) === null || _a === void 0 ? void 0 : _a.replaceAll(WHITE_SPACE, WHITE_SPACE_ENCODED)) !== null && _b !== void 0 ? _b : '');
}/**
 * @internal
 *
 * Get the playlist data in hls format from ham track.
 *
 * @param track - Track to get the playlist data from
 * @returns string containing the playlist data in the hls format
 *
 * @group CMAF
 * @alpha
 */
function getPlaylistData(track) {
    return `#EXT-X-MAP:URI="${getUrlInitialization(track)}",${getByterange$1(track)}\n`;
}/**
 * @internal
 *
 * Format the ham segments to hls.
 *
 * @param segments - Segments to be formatted
 * @returns string containing the segments in the hls format
 *
 * @group CMAF
 * @alpha
 */
function getSegments(segments) {
    return segments
        .map((segment) => {
        const byteRange = segment.byteRange
            ? `#EXT-X-BYTERANGE:${segment.byteRange.replace(HYPHEN_MINUS_SEPARATOR, AT_SEPARATOR)}\n`
            : '';
        const url = segment.url.replaceAll(WHITE_SPACE, WHITE_SPACE_ENCODED);
        return `#EXTINF:${segment.duration},\n${byteRange}\n${url}`;
    })
        .join('\n');
}function generateManifestPlaylistPiece(track) {
    var _a;
    const mediaSequence = 0; //TODO : save mediaSequence in the model.
    const trackFileName = (_a = track.fileName) !== null && _a !== void 0 ? _a : `${track.id}.m3u8`;
    let mainRef = '';
    let playlist = `#EXTM3U\n#EXT-X-TARGETDURATION:${track.duration / track.segments.length}\n#EXT-X-PLAYLIST-TYPE:VOD\n#EXT-X-MEDIA-SEQUENCE:${mediaSequence}\n`;
    if (track.type.toLowerCase() === 'video') {
        const videoTrack = track;
        mainRef += `#EXT-X-STREAM-INF:BANDWIDTH=${videoTrack.bandwidth},CODECS="${videoTrack.codec}",RESOLUTION=${videoTrack.width}x${videoTrack.height}\n${trackFileName}\n`;
        playlist += getPlaylistData(videoTrack);
    }
    else if (track.type.toLowerCase() === 'audio') {
        const audioTrack = track;
        mainRef += `#EXT-X-MEDIA:TYPE=AUDIO,GROUP-ID="${audioTrack.id}",LANGUAGE="${audioTrack.language}",NAME="${audioTrack.id}",URI="${trackFileName}"\n`;
        playlist += getPlaylistData(audioTrack);
    }
    else if (track.type.toLowerCase() === 'text') {
        const textTrack = track;
        mainRef += `#EXT-X-MEDIA:TYPE=SUBTITLES,GROUP-ID="${textTrack.id}",NAME="${textTrack.id}",LANGUAGE="${textTrack.language}",URI="${trackFileName}"\n`;
    }
    playlist += `${getSegments(track.segments)}#EXT-X-ENDLIST`;
    return { mainRef, playlist };
}function mapHamToHls(presentations) {
    const version = 7; //TODO Add a way to change the version. For now version 7 is hardcoded as it is the first version of HLS with CMAF support
    let mainManifest = `#EXTM3U\n#EXT-X-VERSION:${version}\n\n`;
    const playlists = [];
    presentations.map((presentation) => {
        presentation.selectionSets.map((selectionSet) => {
            selectionSet.switchingSets.map((switchingSet) => {
                switchingSet.tracks.map((track) => {
                    var _a;
                    const { mainRef, playlist } = generateManifestPlaylistPiece(track);
                    mainManifest += mainRef;
                    const manifestFileName = (_a = track.fileName) !== null && _a !== void 0 ? _a : `${track.id}.m3u8`;
                    playlists.push({
                        manifest: playlist,
                        type: 'hls',
                        fileName: manifestFileName,
                    });
                });
            });
        });
    });
    return {
        manifest: mainManifest,
        ancillaryManifests: playlists,
        type: 'hls',
    };
}/**
 * @internal
 *
 * Get byterange from HLS Manifest.
 *
 * @param byteRange - Byterange object containning length and offset
 * @returns string containing the byterange. If byterange is undefined, it returns undefined
 *
 * @group CMAF
 * @alpha
 */
function getByterange(byteRange) {
    if (!byteRange) {
        return '';
    }
    return `${byteRange.length}@${byteRange.offset}`;
}/**
 * @internal
 *
 * Format the hls segments into the ham segments.
 *
 * @param segments - List of HLS segments
 * @returns ham formatted list of segments
 *
 * @group CMAF
 * @alpha
 */
function formatSegments(segments) {
    var _a;
    return ((_a = segments === null || segments === void 0 ? void 0 : segments.map((segment) => {
        const byteRange = getByterange(segment === null || segment === void 0 ? void 0 : segment.byterange);
        return {
            duration: segment.duration,
            url: segment.uri,
            ...(byteRange && { byteRange }),
        };
    })) !== null && _a !== void 0 ? _a : []);
}/**
 * @internal
 *
 * Get the codec for a type of content.
 *
 * @param type - Type of the content to get the codecs from
 * @param codecs - String containing multiple codecs separated with commas
 * @returns string containing codec
 *
 * @group CMAF
 * @alpha
 */
function getCodec(type, codecs) {
    var _a;
    if (type === 'audio') {
        // Using codec mp4a.40.2 for now, we should retrieve it by finding
        // the video playlist that is related to this audio group.
        return 'mp4a.40.2';
    }
    else if (type === 'video') {
        // CODECS could be a comma separated value
        // where it has video and audio codec. Using
        // position zero for now.
        // TODO: Get the correct video codec.
        return (_a = codecs === null || codecs === void 0 ? void 0 : codecs.split(',').at(0)) !== null && _a !== void 0 ? _a : '';
    }
    else {
        // if (type === 'text')
        return '';
    }
}/**
 * @internal
 *
 * Calculate the duration of a track.
 *
 * `target duration * number of segments`
 *
 * @param manifest - Manifest of the track
 * @param segments - Array of segments in a track
 * @returns duration of a track
 *
 * @group CMAF
 * @alpha
 */
function getDuration(manifest, segments) {
    if (!(manifest === null || manifest === void 0 ? void 0 : manifest.targetDuration)) {
        console.error('Could not calculate duration, manifest is undefined.');
        return null;
    }
    return (manifest === null || manifest === void 0 ? void 0 : manifest.targetDuration) * segments.length;
}/**
 * @internal
 *
 * This function is used to convert audio groups to switching sets.
 *
 *
 * @param mediaGroupsAudio - Any
 * @param manifestPlaylists - Array of Manifest
 * @returns Array of switchingSet
 *
 * @group CMAF
 * @alpha
 */
function audioGroupsToSwitchingSets(mediaGroupsAudio, manifestPlaylists) {
    var _a, _b;
    const audioSwitchingSets = [];
    const audioTracks = [];
    for (const audioEncodings in mediaGroupsAudio) {
        const encodings = mediaGroupsAudio[audioEncodings];
        for (const audio in encodings) {
            const attributes = encodings[audio];
            const { language, uri } = attributes;
            const audioParsed = parseHlsManifest((_a = manifestPlaylists.shift()) === null || _a === void 0 ? void 0 : _a.manifest);
            const map = (_b = audioParsed === null || audioParsed === void 0 ? void 0 : audioParsed.segments[0]) === null || _b === void 0 ? void 0 : _b.map;
            const segments = formatSegments(audioParsed === null || audioParsed === void 0 ? void 0 : audioParsed.segments);
            // TODO: channels, sampleRate, bandwith and codec need to be
            // updated with real values. Right now we are using simple hardcoded values.
            const byteRange = getByterange(map === null || map === void 0 ? void 0 : map.byterange);
            audioTracks.push({
                id: audio,
                type: 'audio',
                fileName: uri,
                codec: getCodec('audio'),
                duration: getDuration(audioParsed, segments),
                language: language,
                bandwidth: 0,
                segments: segments,
                sampleRate: 0,
                channels: 2,
                ...(byteRange && { byteRange }),
                ...((map === null || map === void 0 ? void 0 : map.uri) && { urlInitialization: map === null || map === void 0 ? void 0 : map.uri }),
            });
        }
    }
    audioSwitchingSets.push({
        id: 'audio',
        tracks: audioTracks,
    });
    return audioSwitchingSets;
}function subtitleGroupsToSwitchingSets(mediaGroupsSubtitles, manifestPlaylists) {
    var _a;
    const subtitleSwitchingSets = [];
    const textTracks = [];
    // Add selection set of type subtitles
    for (const subtitleEncodings in mediaGroupsSubtitles) {
        const encodings = mediaGroupsSubtitles[subtitleEncodings];
        for (const subtitle in encodings) {
            const attributes = encodings[subtitle];
            const { language, uri } = attributes;
            const subtitleParsed = parseHlsManifest((_a = manifestPlaylists.shift()) === null || _a === void 0 ? void 0 : _a.manifest);
            const segments = formatSegments(subtitleParsed === null || subtitleParsed === void 0 ? void 0 : subtitleParsed.segments);
            textTracks.push({
                id: subtitle,
                type: 'text',
                fileName: uri,
                codec: getCodec('text'),
                duration: getDuration(subtitleParsed, segments),
                language: language,
                bandwidth: 0,
                segments: segments,
            });
        }
    }
    subtitleSwitchingSets.push({
        id: 'text',
        tracks: textTracks,
    });
    return subtitleSwitchingSets;
}function videoPlaylistsToSwitchingSets(playlists, manifestPlaylists) {
    const switchingSetVideos = [];
    const videoTracks = [];
    let videoTrackId = 0;
    playlists.map((playlist) => {
        var _a, _b, _c, _d;
        const parsedHlsManifest = parseHlsManifest((_a = manifestPlaylists.shift()) === null || _a === void 0 ? void 0 : _a.manifest);
        const segments = formatSegments(parsedHlsManifest === null || parsedHlsManifest === void 0 ? void 0 : parsedHlsManifest.segments);
        const { LANGUAGE, CODECS, BANDWIDTH } = playlist.attributes;
        const map = (_c = (_b = parsedHlsManifest === null || parsedHlsManifest === void 0 ? void 0 : parsedHlsManifest.segments) === null || _b === void 0 ? void 0 : _b.at(0)) === null || _c === void 0 ? void 0 : _c.map;
        const byteRange = getByterange(map === null || map === void 0 ? void 0 : map.byterange);
        videoTracks.push({
            id: `video-${videoTrackId++}`,
            type: 'video',
            fileName: playlist.uri,
            codec: getCodec('video', CODECS),
            duration: getDuration(parsedHlsManifest, segments),
            language: LANGUAGE !== null && LANGUAGE !== void 0 ? LANGUAGE : 'und',
            bandwidth: BANDWIDTH,
            segments: segments,
            width: playlist.attributes.RESOLUTION.width,
            height: playlist.attributes.RESOLUTION.height,
            frameRate: {
                frameRateNumerator: (_d = playlist.attributes['FRAME-RATE']) !== null && _d !== void 0 ? _d : FRAME_RATE_NUMERATOR_30,
                frameRateDenominator: ZERO,
            },
            par: '',
            sar: '',
            scanType: '',
            ...(byteRange && { byteRange }),
            ...((map === null || map === void 0 ? void 0 : map.uri) && { urlInitialization: map === null || map === void 0 ? void 0 : map.uri }),
        });
    });
    switchingSetVideos.push({
        id: `video`,
        tracks: videoTracks,
    });
    return switchingSetVideos;
}//Add metadata to manifest.
//In the future, if any other fields are wanted to be added, they can be added here.
function addMetadataToHls(manifest, manifestParsed) {
    if (!manifest.metadata) {
        manifest.metadata = new Map();
    }
    if (!manifestParsed.version) {
        manifest.metadata.set('version', manifestParsed.version);
    }
    if (!manifestParsed.mediaSequence) {
        manifest.metadata.set('mediaSequence', manifestParsed.mediaSequence);
    }
    return manifest;
}function mapHlsToHam(manifest) {
    var _a, _b;
    const mainManifestParsed = parseHlsManifest(manifest.manifest);
    const manifestHls = addMetadataToHls(manifest, mainManifestParsed);
    const selectionSets = [];
    const manifestPlaylists = manifestHls.ancillaryManifests
        ? [...manifestHls.ancillaryManifests]
        : [];
    const audioSwitchingSets = audioGroupsToSwitchingSets((_a = mainManifestParsed.mediaGroups) === null || _a === void 0 ? void 0 : _a.AUDIO, manifestPlaylists);
    const subtitleSwitchingSets = subtitleGroupsToSwitchingSets((_b = mainManifestParsed.mediaGroups) === null || _b === void 0 ? void 0 : _b.SUBTITLES, manifestPlaylists);
    const videoSwitchingSets = videoPlaylistsToSwitchingSets(mainManifestParsed.playlists, manifestPlaylists);
    let selectionSetId = 0;
    if (audioSwitchingSets.length > 0) {
        selectionSets.push({
            id: (selectionSetId++).toString(),
            switchingSets: audioSwitchingSets,
        });
    }
    if (subtitleSwitchingSets.length > 0) {
        selectionSets.push({
            id: (selectionSetId++).toString(),
            switchingSets: subtitleSwitchingSets,
        });
    }
    if (videoSwitchingSets.length > 0) {
        selectionSets.push({
            id: (selectionSetId++).toString(),
            switchingSets: videoSwitchingSets,
        });
    }
    let presentationId = 0;
    return [
        { id: (presentationId++).toString(), selectionSets: selectionSets },
    ];
}class HlsMapper {
    getManifestMetadata() {
        return getMetadata(this.manifest);
    }
    toHam(manifest) {
        const presentations = mapHlsToHam(manifest);
        this.manifest = manifest;
        return presentations;
    }
    toManifest(presentation) {
        return mapHamToHls(presentation);
    }
}/**
 * Convert ham object into a hls manifest.
 *
 * @example
 * Example on how to import the cmaf module and convert the ham `presentations`
 * array into the hls manifest.
 * ```ts
 * import cmaf from '@svta/common-media-library/cmaf-ham';
 *
 * const manifest = cmaf.hamToHls(presentations);
 * ```
 *
 * @param presentation - List of presentations from ham
 * @returns Manifest object containing the Hls manifest as string and its playlists
 *
 * @group CMAF
 * @alpha
 */
function hamToHls(presentation) {
    const mapperContext = MapperContext.getInstance();
    mapperContext.setStrategy(new HlsMapper());
    return mapperContext.getManifestFormat(presentation);
}/**
 * Convert hls manifest into a ham object.
 *
 * @example
 * Example on how to import the cmaf module and convert the hls `manifest` and
 * `ancillaryManifests` array into the ham manifest.
 * ```ts
 * import cmaf from '@svta/common-media-library/cmaf-ham';
 *
 * const manifest = cmaf.hlsToHam(hlsManifest);
 * ```
 *
 * @param manifest - String of the Main manifest
 * @param ancillaryManifests - Ancillary Manifests . Must be in order, first audio, subtitle and video
 * @returns List of presentations from ham
 *
 * @group CMAF
 * @alpha
 */
function hlsToHam(manifest, ancillaryManifests) {
    const mapperContext = MapperContext.getInstance();
    mapperContext.setStrategy(new HlsMapper());
    return mapperContext.getHamFormat({
        manifest,
        ancillaryManifests: ancillaryManifests.map((ancillaryManifest) => ({
            manifest: ancillaryManifest,
            type: 'hls',
        })),
        type: 'hls',
    });
}/**
 * Get a list of Tracks contained on a SwitchingSet
 *
 * @param switchingSet - SwitchingSet object from HAM
 * @param predicate - Filtering function
 * @returns Track[]
 *
 * @group CMAF
 * @alpha
 */
function getTracksFromSwitchingSet(switchingSet, predicate) {
    const tracks = switchingSet.tracks;
    return predicate ? tracks.filter(predicate) : tracks;
}/**
 * Get a list of Tracks contained on a SelectionSet
 *
 * @param selectionSet - SelectionSet object from HAM
 * @param predicate - Filtering function
 * @returns Track[]
 *
 * @group CMAF
 * @alpha
 */
function getTracksFromSelectionSet(selectionSet, predicate) {
    const tracks = selectionSet.switchingSets.flatMap((switchingSet) => getTracksFromSwitchingSet(switchingSet));
    return predicate ? tracks.filter(predicate) : tracks;
}/**
 * Get a list of Tracks contained on a Presentation
 *
 * @param presentation - Presentation object from HAM
 * @param predicate - Filtering function
 * @returns Track[]
 *
 * @group CMAF
 * @alpha
 */
function getTracksFromPresentation(presentation, predicate) {
    const tracks = presentation.selectionSets.flatMap((selectionSet) => getTracksFromSelectionSet(selectionSet));
    return predicate ? tracks.filter(predicate) : tracks;
}/**
 * Validate a segment.
 *
 * Validations:
 * - segment has duration
 * - segment has url
 *
 * @param segment - Segment from cmaf ham model
 * @param trackId - Optional: parent track id
 * @param prevValidation - Optional: validation object from parent previous validate method call
 * @returns Validation
 *
 * @group CMAF
 * @alpha
 */
function validateSegment(segment, trackId, prevValidation) {
    const validation = prevValidation !== null && prevValidation !== void 0 ? prevValidation : {
        status: true,
        errorMessages: [],
    };
    const moreInformation = trackId
        ? ` in the track with id = ${trackId}`
        : '.';
    if (!segment.duration) {
        validation.status = false;
        validation.errorMessages.push(`Segment duration is undefined${moreInformation}`);
    }
    if (!segment.url) {
        validation.status = false;
        validation.errorMessages.push(`Segment url is undefined${moreInformation}`);
    }
    return validation;
}/**
 * Validate a list of segments.
 *
 * @example
 * ```ts
 * import cmaf, { Segment } from '@svta/common-media-library/cmaf-ham';
 * ...
 *
 * // const segments: Segment[] = ...;
 *
 * const validation = cmaf.validateSegments(segments);
 * ```
 *
 * Example output: `{ status: true|false, errorMessages: [...] }`
 *
 * @param segments - List of Segment from cmaf ham model
 * @param trackId - Optional: parent track id
 * @param prevValidation - Optional: validation object from parent previous validate method call
 * @returns Validation
 *
 * @group CMAF
 * @alpha
 *
 */
function validateSegments(segments, trackId, prevValidation) {
    const validation = prevValidation !== null && prevValidation !== void 0 ? prevValidation : {
        status: true,
        errorMessages: [],
    };
    segments.forEach((segment) => {
        validateSegment(segment, trackId, validation);
    });
    return validation;
}/**
 * Validate a track.
 * It validates in cascade, calling each child validation method.
 *
 * Validations:
 * - track has id
 * - Invokes specific audio, video and text validations
 *
 * @example
 * ```ts
 * import cmaf, { Track } from '@svta/common-media-library/cmaf-ham';
 * ...
 *
 * // const track: Track = ...;
 *
 * const validation = cmaf.validateTrack(track);
 * ```
 *
 * Example output: `{ status: true|false, errorMessages: [...] }`
 *
 * @param track - Track from cmaf ham model
 * @param switchingSetId - Optional: parent switching set id
 * @param prevValidation - Optional: validation object from parent previous validate method call
 * @returns Validation
 *
 * @group CMAF
 * @alpha
 *
 */
function validateTrack(track, switchingSetId, prevValidation) {
    const validation = prevValidation !== null && prevValidation !== void 0 ? prevValidation : {
        status: true,
        errorMessages: [],
    };
    const moreInformation = switchingSetId
        ? ` in the switching set with id = ${switchingSetId}`
        : '.';
    if (!track.id) {
        validation.status = false;
        validation.errorMessages.push(`Track id is undefined${moreInformation}`);
    }
    switch (track.type) {
        case 'video':
            _validateVideoTrack(track, switchingSetId, validation);
            break;
        case 'audio':
            _validateAudioTrack(track, switchingSetId, validation);
            break;
        case 'text':
            _validateTextTrack(track, switchingSetId, validation);
            break;
    }
    validateSegments(track.segments, track.id, validation);
    return validation;
}
/**
 * @internal
 *
 * Validate video Track
 *
 * Validations:
 * - track has codec
 *
 * @param videoTrack - Video track to validate
 * @param switchingSetId - Optional: id from the switching set containing the track (Only used for logs)
 * @param prevValidation - Optional: validation object from parent previous validate method call
 * @returns Validation object
 * @see Validation
 */
function _validateVideoTrack(videoTrack, switchingSetId, prevValidation) {
    const validation = prevValidation !== null && prevValidation !== void 0 ? prevValidation : {
        status: true,
        errorMessages: [],
    };
    const moreInformation = switchingSetId
        ? ` in the switching set with id = ${switchingSetId}`
        : '.';
    if (!videoTrack.codec) {
        validation.status = false;
        validation.errorMessages.push(`VideoTrack with id: ${videoTrack.id} does not have codec${moreInformation}`);
    }
    return validation;
}
/**
 * @internal
 *
 * Validate Audio Track
 *
 * Validations:
 * - track has codec
 *
 * @param audioTrack - Audio track to validate
 * @param switchingSetId - Optional: id from the switching set containing the track (Only used for logs)
 * @param prevValidation - Optional: validation object from parent previous validate method call
 * @returns Validation object
 * @see Validation
 */
function _validateAudioTrack(audioTrack, switchingSetId, prevValidation) {
    const validation = prevValidation !== null && prevValidation !== void 0 ? prevValidation : {
        status: true,
        errorMessages: [],
    };
    const moreInformation = switchingSetId
        ? ` in the switching set with id = ${switchingSetId}`
        : '.';
    if (!audioTrack.codec) {
        validation.status = false;
        validation.errorMessages.push(`AudioTrack with id: ${audioTrack.id} does not have codec${moreInformation}`);
    }
    return validation;
}
/**
 * @internal
 *
 * Validate Text Track
 *
 * Validations:
 * - track has language
 *
 * @param textTrack - Text track to validate
 * @param switchingSetId - Optional: id from the switching set containing the track (Only used for logs)
 * @param prevValidation - Optional: validation object from parent previous validate method call
 * @returns Validation object
 * @see Validation
 */
function _validateTextTrack(textTrack, switchingSetId, prevValidation) {
    const validation = prevValidation !== null && prevValidation !== void 0 ? prevValidation : {
        status: true,
        errorMessages: [],
    };
    const moreInformation = switchingSetId
        ? ` in the switching set with id = ${switchingSetId}`
        : '.';
    if (!textTrack.language) {
        validation.status = false;
        validation.errorMessages.push(`TextTrack with id: ${textTrack.id} does not have codec${moreInformation}`);
    }
    return validation;
}/**
 * Validate a list of tracks.
 * It validates in cascade, calling each child validation method.
 *
 * @example
 * ```ts
 * import cmaf, { Track } from '@svta/common-media-library/cmaf-ham';
 * ...
 *
 * // const tracks: Track[] = ...;
 *
 * const validation = cmaf.validateTracks(tracks);
 * ```
 *
 * Example output: `{ status: true|false, errorMessages: [...] }`
 *
 * @param tracks - List of Track from cmaf ham model
 * @param switchingSetId - Optional: parent switching set id
 * @param prevValidation - Optional: validation object from parent previous validate method call
 * @returns Validation
 *
 * @group CMAF
 * @alpha
 *
 */
function validateTracks(tracks, switchingSetId, prevValidation) {
    const validation = prevValidation !== null && prevValidation !== void 0 ? prevValidation : {
        status: true,
        errorMessages: [],
    };
    const moreInformation = switchingSetId
        ? ` in the switching set with id = ${switchingSetId}`
        : '.';
    let tracksDuration;
    tracks.forEach((track) => {
        if (!tracksDuration) {
            tracksDuration = track.duration;
        }
        if (tracksDuration !== track.duration) {
            validation.status = false;
            validation.errorMessages.push(`All the tracks must have the same duration${moreInformation}`);
        }
        validateTrack(track, switchingSetId, validation);
    });
    return validation;
}/**
 * Validate a switching set.
 * It validates in cascade, calling each child validation method.
 *
 * Validations:
 * - SwitchingSet has id
 *
 * @example
 * ```ts
 * import cmaf, { SwitchingSet } from '@svta/common-media-library/cmaf-ham';
 * ...
 *
 * // const switchingSet: SwitchingSet = ...;
 *
 * const validation = cmaf.validateSwitchingSet(switchingSet);
 * ```
 *
 * Example output: `{ status: true|false, errorMessages: [...] }`
 *
 * @param switchingSet - SwitchingSet from cmaf ham model
 * @param selectionSetId - Optional: parent selection set id
 * @param prevValidation - Optional: validation object from parent previous validate method call
 * @returns Validation
 *
 * @group CMAF
 * @alpha
 *
 */
function validateSwitchingSet(switchingSet, selectionSetId, prevValidation) {
    const validation = prevValidation !== null && prevValidation !== void 0 ? prevValidation : {
        status: true,
        errorMessages: [],
    };
    const moreInformation = selectionSetId
        ? ` in the selection set with id = ${selectionSetId}`
        : '.';
    if (!switchingSet.id) {
        validation.status = false;
        validation.errorMessages.push(`SwitchingSet id is undefined${moreInformation}`);
    }
    validateTracks(switchingSet.tracks, switchingSet.id, validation);
    return validation;
}/**
 * Validate a list of switching set.
 * It validates in cascade, calling each child validation method.
 *
 * @example
 * ```ts
 * import cmaf, { SwitchingSet } from '@svta/common-media-library/cmaf-ham';
 * ...
 *
 * // const switchingSets: SwitchingSet[] = ...;
 *
 * const validation = cmaf.validateSwitchingSets(switchingSets);
 * ```
 *
 * Example output: `{ status: true|false, errorMessages: [...] }`
 *
 * @param switchingSets - List of SwitchingSets from cmaf ham model
 * @param selectionSetId - Optional: parent selection set id
 * @param prevValidation - Optional: validation object from parent previous validate method call
 * @returns Validation
 *
 * @group CMAF
 * @alpha
 *
 */
function validateSwitchingSets(switchingSets, selectionSetId, prevValidation) {
    const validation = prevValidation !== null && prevValidation !== void 0 ? prevValidation : {
        status: true,
        errorMessages: [],
    };
    switchingSets.forEach((switchingSet) => {
        validateSwitchingSet(switchingSet, selectionSetId, validation);
    });
    return validation;
}/**
 * Validate a selection set.
 * It validates in cascade, calling each child validation method.
 *
 * Validations:
 * - SelectionSet has id
 *
 * @example
 * ```ts
 * import cmaf, { SelectionSet } from '@svta/common-media-library/cmaf-ham';
 * ...
 *
 * // const selectionSet: SelectionSet = ...;
 *
 * const validation = cmaf.validateSelectionSet(selectionSet);
 * ```
 *
 * Example output: `{ status: true|false, errorMessages: [...] }`
 *
 * @param selectionSet - SelectionSet from cmaf ham model
 * @param presentationId - Optional: parent presentation id
 * @param prevValidation - Optional: validation object from parent previous validate method call
 * @returns Validation
 *
 * @group CMAF
 * @alpha
 *
 */
function validateSelectionSet(selectionSet, presentationId, prevValidation) {
    const validation = prevValidation !== null && prevValidation !== void 0 ? prevValidation : {
        status: true,
        errorMessages: [],
    };
    const moreInformation = presentationId
        ? ` in the presentation with id = ${presentationId}`
        : '.';
    if (!selectionSet.id) {
        validation.status = false;
        validation.errorMessages.push(`SelectionSet id is undefined${moreInformation}`);
    }
    validateSwitchingSets(selectionSet.switchingSets, selectionSet.id, validation);
    return validation;
}/**
 * Validate a list of selection set.
 * It validates in cascade, calling each child validation method.
 *
 * @example
 * ```ts
 * import cmaf, { SelectionSet } from '@svta/common-media-library/cmaf-ham';
 * ...
 *
 * // const selectionSets: SelectionSet[] = ...;
 *
 * const validation = cmaf.validateSelectionSets(selectionSets);
 * ```
 *
 * Example output: `{ status: true|false, errorMessages: [...] }`
 *
 * @param selectionSets - List of SelectionSet from cmaf ham model
 * @param presentationId - Optional: parent presentation id
 * @param prevValidation - Optional: validation object from parent previous validate method call
 * @returns Validation
 *
 * @group CMAF
 * @alpha
 *
 */
function validateSelectionSets(selectionSets, presentationId, prevValidation) {
    const validation = prevValidation !== null && prevValidation !== void 0 ? prevValidation : {
        status: true,
        errorMessages: [],
    };
    selectionSets.forEach((selectionSet) => {
        validateSelectionSet(selectionSet, presentationId, validation);
    });
    return validation;
}/**
 * Validate a presentation.
 * It validates in cascade, calling each child validation method.
 *
 * Validations:
 * - Presentation has id
 *
 * @example
 * ```ts
 * import cmaf, { Presentation } from '@svta/common-media-library/cmaf-ham';
 * ...
 *
 * // const presentation: Presentation = ...;
 *
 * const validation = cmaf.validatePresentation(presentation);
 * ```
 *
 * Example output: `{ status: true|false, errorMessages: [...] }`
 *
 * @param presentation - Presentation from cmaf ham model
 * @returns Validation
 *
 * @group CMAF
 * @alpha
 *
 */
function validatePresentation(presentation) {
    const validation = { status: true, errorMessages: [] };
    if (!presentation.id) {
        validation.status = false;
        validation.errorMessages.push('Presentation id is undefined');
    }
    validateSelectionSets(presentation.selectionSets, presentation.id, validation);
    return validation;
}/**
 * Structured Field Item
 *
 * @group Structured Field
 *
 * @beta
 */
class SfItem {
    constructor(value, params) {
        if (Array.isArray(value)) {
            value = value.map((v) => (v instanceof SfItem) ? v : new SfItem(v));
        }
        this.value = value;
        this.params = params;
    }
}const DICT = 'Dict';function format(value) {
    if (Array.isArray(value)) {
        return JSON.stringify(value);
    }
    if (value instanceof Map) {
        return 'Map{}';
    }
    if (value instanceof Set) {
        return 'Set{}';
    }
    if (typeof value === 'object') {
        return JSON.stringify(value);
    }
    return String(value);
}
function throwError(action, src, type, cause) {
    return new Error(`failed to ${action} "${format(src)}" as ${type}`, { cause });
}function serializeError(src, type, cause) {
    return throwError('serialize', src, type, cause);
}/**
 * A class to represent structured field tokens when `Symbol` is not available.
 *
 * @group Structured Field
 *
 * @beta
 */
class SfToken {
    constructor(description) {
        this.description = description;
    }
}const BARE_ITEM = 'Bare Item';const BOOLEAN = 'Boolean';// 4.1.9.  Serializing a Boolean
//
// Given a Boolean as input_boolean, return an ASCII string suitable for
// use in a HTTP field value.
//
// 1.  If input_boolean is not a boolean, fail serialization.
//
// 2.  Let output be an empty string.
//
// 3.  Append "?" to output.
//
// 4.  If input_boolean is true, append "1" to output.
//
// 5.  If input_boolean is false, append "0" to output.
//
// 6.  Return output.
function serializeBoolean(value) {
    if (typeof value !== 'boolean') {
        throw serializeError(value, BOOLEAN);
    }
    return value ? '?1' : '?0';
}/**
 * Encodes binary data to base64
 *
 * @param binary - The binary data to encode
 * @returns The base64 encoded string
 *
 * @group Utils
 *
 * @beta
 */
function encodeBase64(binary) {
    return btoa(String.fromCharCode(...binary));
}const BYTES = 'Byte Sequence';// 4.1.8.  Serializing a Byte Sequence
//
// Given a Byte Sequence as input_bytes, return an ASCII string suitable
// for use in a HTTP field value.
//
// 1.  If input_bytes is not a sequence of bytes, fail serialization.
//
// 2.  Let output be an empty string.
//
// 3.  Append ":" to output.
//
// 4.  Append the result of base64-encoding input_bytes as per
//     [RFC4648], Section 4, taking account of the requirements below.
//
// 5.  Append ":" to output.
//
// 6.  Return output.
//
// The encoded data is required to be padded with "=", as per [RFC4648],
// Section 3.2.
//
// Likewise, encoded data SHOULD have pad bits set to zero, as per
// [RFC4648], Section 3.5, unless it is not possible to do so due to
// implementation constraints.
function serializeByteSequence(value) {
    if (ArrayBuffer.isView(value) === false) {
        throw serializeError(value, BYTES);
    }
    return `:${encodeBase64(value)}:`;
}const INTEGER = 'Integer';function isInvalidInt(value) {
    return value < -999999999999999 || 999999999999999 < value;
}// 4.1.4.  Serializing an Integer
//
// Given an Integer as input_integer, return an ASCII string suitable
// for use in a HTTP field value.
//
// 1.  If input_integer is not an integer in the range of
//     -999,999,999,999,999 to 999,999,999,999,999 inclusive, fail
//     serialization.
//
// 2.  Let output be an empty string.
//
// 3.  If input_integer is less than (but not equal to) 0, append "-" to
//     output.
//
// 4.  Append input_integer's numeric value represented in base 10 using
//     only decimal digits to output.
//
// 5.  Return output.
function serializeInteger(value) {
    if (isInvalidInt(value)) {
        throw serializeError(value, INTEGER);
    }
    return value.toString();
}// 4.1.10.  Serializing a Date
//
// Given a Date as input_integer, return an ASCII string suitable for
// use in an HTTP field value.
// 1.  Let output be "@".
// 2.  Append to output the result of running Serializing an Integer
//     with input_date (Section 4.1.4).
// 3.  Return output.
function serializeDate(value) {
    return `@${serializeInteger(value.getTime() / 1000)}`;
}/**
 * This implements the rounding procedure described in step 2 of the "Serializing a Decimal" specification.
 * This rounding style is known as "even rounding", "banker's rounding", or "commercial rounding".
 *
 * @param value - The value to round
 * @param precision - The number of decimal places to round to
 * @returns The rounded value
 *
 * @group Utils
 *
 * @beta
 */
function roundToEven(value, precision) {
    if (value < 0) {
        return -roundToEven(-value, precision);
    }
    const decimalShift = Math.pow(10, precision);
    const isEquidistant = Math.abs(((value * decimalShift) % 1) - 0.5) < Number.EPSILON;
    if (isEquidistant) {
        // If the tail of the decimal place is 'equidistant' we round to the nearest even value
        const flooredValue = Math.floor(value * decimalShift);
        return (flooredValue % 2 === 0 ? flooredValue : flooredValue + 1) / decimalShift;
    }
    else {
        // Otherwise, proceed as normal
        return Math.round(value * decimalShift) / decimalShift;
    }
}const DECIMAL = 'Decimal';// 4.1.5.  Serializing a Decimal
//
// Given a decimal number as input_decimal, return an ASCII string
// suitable for use in a HTTP field value.
//
// 1.   If input_decimal is not a decimal number, fail serialization.
//
// 2.   If input_decimal has more than three significant digits to the
//      right of the decimal point, round it to three decimal places,
//      rounding the final digit to the nearest value, or to the even
//      value if it is equidistant.
//
// 3.   If input_decimal has more than 12 significant digits to the left
//      of the decimal point after rounding, fail serialization.
//
// 4.   Let output be an empty string.
//
// 5.   If input_decimal is less than (but not equal to) 0, append "-"
//      to output.
//
// 6.   Append input_decimal's integer component represented in base 10
//      (using only decimal digits) to output; if it is zero, append
//      "0".
//
// 7.   Append "." to output.
//
// 8.   If input_decimal's fractional component is zero, append "0" to
//      output.
//
// 9.   Otherwise, append the significant digits of input_decimal's
//      fractional component represented in base 10 (using only decimal
//      digits) to output.
//
// 10.  Return output.
function serializeDecimal(value) {
    const roundedValue = roundToEven(value, 3); // round to 3 decimal places
    if (Math.floor(Math.abs(roundedValue)).toString().length > 12) {
        throw serializeError(value, DECIMAL);
    }
    const stringValue = roundedValue.toString();
    return stringValue.includes('.') ? stringValue : `${stringValue}.0`;
}const STRING$1 = 'String';const STRING_REGEX = /[\x00-\x1f\x7f]+/;// 4.1.6.  Serializing a String
//
// Given a String as input_string, return an ASCII string suitable for
// use in a HTTP field value.
//
// 1.  Convert input_string into a sequence of ASCII characters; if
//     conversion fails, fail serialization.
//
// 2.  If input_string contains characters in the range %x00-1f or %x7f
//     (i.e., not in VCHAR or SP), fail serialization.
//
// 3.  Let output be the string DQUOTE.
//
// 4.  For each character char in input_string:
//
//     1.  If char is "\" or DQUOTE:
//
//         1.  Append "\" to output.
//
//     2.  Append char to output.
//
// 5.  Append DQUOTE to output.
//
// 6.  Return output.
function serializeString(value) {
    if (STRING_REGEX.test(value)) {
        throw serializeError(value, STRING$1);
    }
    return `"${value.replace(/\\/g, `\\\\`).replace(/"/g, `\\"`)}"`;
}/**
 * Converts a symbol to a string.
 *
 * @param symbol - The symbol to convert.
 *
 * @returns The string representation of the symbol.
 *
 * @internal
 */
function symbolToStr(symbol) {
    return symbol.description || symbol.toString().slice(7, -1);
}const TOKEN = 'Token';function serializeToken(token) {
    const value = symbolToStr(token);
    if (/^([a-zA-Z*])([!#$%&'*+\-.^_`|~\w:/]*)$/.test(value) === false) {
        throw serializeError(value, TOKEN);
    }
    return value;
}// 4.1.3.1.  Serializing a Bare Item
//
// Given an Item as input_item, return an ASCII string suitable for use
// in a HTTP field value.
//
// 1.  If input_item is an Integer, return the result of running
//     Serializing an Integer (Section 4.1.4) with input_item.
//
// 2.  If input_item is a Decimal, return the result of running
//     Serializing a Decimal (Section 4.1.5) with input_item.
//
// 3.  If input_item is a String, return the result of running
//     Serializing a String (Section 4.1.6) with input_item.
//
// 4.  If input_item is a Token, return the result of running
//     Serializing a Token (Section 4.1.7) with input_item.
//
// 5.  If input_item is a Boolean, return the result of running
//     Serializing a Boolean (Section 4.1.9) with input_item.
//
// 6.  If input_item is a Byte Sequence, return the result of running
//     Serializing a Byte Sequence (Section 4.1.8) with input_item.
//
// 7.  If input_item is a Date, return the result of running Serializing
//     a Date (Section 4.1.10) with input_item.
//
// 8.  Otherwise, fail serialization.
function serializeBareItem(value) {
    switch (typeof value) {
        case 'number':
            if (!Number.isFinite(value)) {
                throw serializeError(value, BARE_ITEM);
            }
            if (Number.isInteger(value)) {
                return serializeInteger(value);
            }
            return serializeDecimal(value);
        case 'string':
            return serializeString(value);
        case 'symbol':
            return serializeToken(value);
        case 'boolean':
            return serializeBoolean(value);
        case 'object':
            if (value instanceof Date) {
                return serializeDate(value);
            }
            if (value instanceof Uint8Array) {
                return serializeByteSequence(value);
            }
            if (value instanceof SfToken) {
                return serializeToken(value);
            }
        default:
            // fail
            throw serializeError(value, BARE_ITEM);
    }
}const KEY = 'Key';// 4.1.1.3.  Serializing a Key
//
// Given a key as input_key, return an ASCII string suitable for use in
// a HTTP field value.
//
// 1.  Convert input_key into a sequence of ASCII characters; if
//     conversion fails, fail serialization.
//
// 2.  If input_key contains characters not in lcalpha, DIGIT, "_", "-",
//     ".", or "*" fail serialization.
//
// 3.  If the first character of input_key is not lcalpha or "*", fail
//     serialization.
//
// 4.  Let output be an empty string.
//
// 5.  Append input_key to output.
//
// 6.  Return output.
function serializeKey(value) {
    if (/^[a-z*][a-z0-9\-_.*]*$/.test(value) === false) {
        throw serializeError(value, KEY);
    }
    return value;
}// 4.1.1.2.  Serializing Parameters
//
// Given an ordered Dictionary as input_parameters (each member having a
// param_name and a param_value), return an ASCII string suitable for
// use in a HTTP field value.
//
// 1.  Let output be an empty string.
//
// 2.  For each param_name with a value of param_value in
//     input_parameters:
//
//     1.  Append ";" to output.
//
//     2.  Append the result of running Serializing a Key
//         (Section 4.1.1.3) with param_name to output.
//
//     3.  If param_value is not Boolean true:
//
//         1.  Append "=" to output.
//
//         2.  Append the result of running Serializing a bare Item
//             (Section 4.1.3.1) with param_value to output.
//
// 3.  Return output.
function serializeParams(params) {
    if (params == null) {
        return '';
    }
    return Object.entries(params)
        .map(([key, value]) => {
        if (value === true) {
            return `;${serializeKey(key)}`; // omit true
        }
        return `;${serializeKey(key)}=${serializeBareItem(value)}`;
    })
        .join('');
}// 4.1.3.  Serializing an Item
//
// Given an Item as bare_item and Parameters as item_parameters, return
// an ASCII string suitable for use in a HTTP field value.
//
// 1.  Let output be an empty string.
//
// 2.  Append the result of running Serializing a Bare Item
//     Section 4.1.3.1 with bare_item to output.
//
// 3.  Append the result of running Serializing Parameters
//     Section 4.1.1.2 with item_parameters to output.
//
// 4.  Return output.
function serializeItem(value) {
    if (value instanceof SfItem) {
        return `${serializeBareItem(value.value)}${serializeParams(value.params)}`;
    }
    else {
        return serializeBareItem(value);
    }
}// 4.1.1.1.  Serializing an Inner List
//
// Given an array of (member_value, parameters) tuples as inner_list,
// and parameters as list_parameters, return an ASCII string suitable
// for use in a HTTP field value.
//
// 1.  Let output be the string "(".
//
// 2.  For each (member_value, parameters) of inner_list:
//
//     1.  Append the result of running Serializing an Item
//         (Section 4.1.3) with (member_value, parameters) to output.
//
//     2.  If more values remain in inner_list, append a single SP to
//         output.
//
// 3.  Append ")" to output.
//
// 4.  Append the result of running Serializing Parameters
//     (Section 4.1.1.2) with list_parameters to output.
//
// 5.  Return output.
function serializeInnerList(value) {
    return `(${value.value.map(serializeItem).join(' ')})${serializeParams(value.params)}`;
}// 4.1.2.  Serializing a Dictionary
//
// Given an ordered Dictionary as input_dictionary (each member having a
// member_name and a tuple value of (member_value, parameters)), return
// an ASCII string suitable for use in a HTTP field value.
//
// 1.  Let output be an empty string.
//
// 2.  For each member_name with a value of (member_value, parameters)
//     in input_dictionary:
//
//     1.  Append the result of running Serializing a Key
//         (Section 4.1.1.3) with member's member_name to output.
//
//     2.  If member_value is Boolean true:
//
//         1.  Append the result of running Serializing Parameters
//             (Section 4.1.1.2) with parameters to output.
//
//     3.  Otherwise:
//
//         1.  Append "=" to output.
//
//         2.  If member_value is an array, append the result of running
//             Serializing an Inner List (Section 4.1.1.1) with
//             (member_value, parameters) to output.
//
//         3.  Otherwise, append the result of running Serializing an
//             Item (Section 4.1.3) with (member_value, parameters) to
//             output.
//
//     4.  If more members remain in input_dictionary:
//
//         1.  Append "," to output.
//
//         2.  Append a single SP to output.
//
// 3.  Return output.
function serializeDict(dict, options = { whitespace: true }) {
    if (typeof dict !== 'object' || dict == null) {
        throw serializeError(dict, DICT);
    }
    const entries = dict instanceof Map ? dict.entries() : Object.entries(dict);
    const optionalWhiteSpace = (options === null || options === void 0 ? void 0 : options.whitespace) ? ' ' : '';
    return Array.from(entries)
        .map(([key, item]) => {
        if (item instanceof SfItem === false) {
            item = new SfItem(item);
        }
        let output = serializeKey(key);
        if (item.value === true) {
            output += serializeParams(item.params);
        }
        else {
            output += '=';
            if (Array.isArray(item.value)) {
                output += serializeInnerList(item);
            }
            else {
                output += serializeItem(item);
            }
        }
        return output;
    })
        .join(`,${optionalWhiteSpace}`);
}/**
 * Encode an object into a structured field dictionary
 *
 * @param value - The structured field dictionary to encode
 * @param options - Encoding options
 *
 * @returns The structured field string
 *
 * @group Structured Field
 *
 * @beta
 */
function encodeSfDict(value, options) {
    return serializeDict(value, options);
}/**
 * CMCD object header name.
 *
 * @group CMCD
 *
 * @beta
 */
const CMCD_OBJECT = 'CMCD-Object';/**
 * CMCD request header name.
 *
 * @group CMCD
 *
 * @beta
 */
const CMCD_REQUEST = 'CMCD-Request';/**
 * CMCD session header name.
 *
 * @group CMCD
 *
 * @beta
 */
const CMCD_SESSION = 'CMCD-Session';/**
 * CMCD status header name.
 *
 * @group CMCD
 *
 * @beta
 */
const CMCD_STATUS = 'CMCD-Status';/**
 * The map of CMCD keys to their appropriate header shard.
 *
 * @group CMCD
 *
 * @internal
 */
const CMCD_HEADER_MAP = {
    // Object
    br: CMCD_OBJECT,
    ab: CMCD_OBJECT,
    d: CMCD_OBJECT,
    ot: CMCD_OBJECT,
    tb: CMCD_OBJECT,
    tpb: CMCD_OBJECT,
    lb: CMCD_OBJECT,
    tab: CMCD_OBJECT,
    lab: CMCD_OBJECT,
    url: CMCD_OBJECT,
    // Request
    pb: CMCD_REQUEST,
    bl: CMCD_REQUEST,
    tbl: CMCD_REQUEST,
    dl: CMCD_REQUEST,
    ltc: CMCD_REQUEST,
    mtp: CMCD_REQUEST,
    nor: CMCD_REQUEST,
    nrr: CMCD_REQUEST,
    rc: CMCD_REQUEST,
    sn: CMCD_REQUEST,
    sta: CMCD_REQUEST,
    su: CMCD_REQUEST,
    ttfb: CMCD_REQUEST,
    ttfbb: CMCD_REQUEST,
    ttlb: CMCD_REQUEST,
    cmsdd: CMCD_REQUEST,
    cmsds: CMCD_REQUEST,
    smrt: CMCD_REQUEST,
    df: CMCD_REQUEST,
    cs: CMCD_REQUEST,
    // TODO: Which header to put the `ts` field is not defined yet.
    ts: CMCD_REQUEST,
    // Session
    cid: CMCD_SESSION,
    pr: CMCD_SESSION,
    sf: CMCD_SESSION,
    sid: CMCD_SESSION,
    st: CMCD_SESSION,
    v: CMCD_SESSION,
    msd: CMCD_SESSION,
    // Status
    bs: CMCD_STATUS,
    bsd: CMCD_STATUS,
    cdn: CMCD_STATUS,
    rtp: CMCD_STATUS,
    bg: CMCD_STATUS,
    pt: CMCD_STATUS,
    ec: CMCD_STATUS,
    e: CMCD_STATUS,
};/**
 * CMCD header fields.
 *
 * @group CMCD
 *
 * @enum
 *
 * @beta
 */
const CmcdHeaderField = {
    /**
     * keys whose values vary with the object being requested.
     */
    OBJECT: CMCD_OBJECT,
    /**
     * keys whose values vary with each request.
     */
    REQUEST: CMCD_REQUEST,
    /**
     * keys whose values are expected to be invariant over the life of the session.
     */
    SESSION: CMCD_SESSION,
    /**
     * keys whose values do not vary with every request or object.
     */
    STATUS: CMCD_STATUS,
};function createHeaderMap(headerMap) {
    return Object.keys(headerMap)
        .reduce((acc, field) => {
        var _a;
        (_a = headerMap[field]) === null || _a === void 0 ? void 0 : _a.forEach(key => acc[key] = field);
        return acc;
    }, {});
}
/**
 * Group a CMCD data object into header shards
 *
 * @param cmcd - The CMCD data object to convert.
 * @param customHeaderMap - A map of CMCD header fields to custom CMCD keys.
 *
 * @returns The CMCD header shards.
 *
 * @group CMCD
 *
 * @beta
 */
function groupCmcdHeaders(cmcd, customHeaderMap) {
    const result = {};
    if (!cmcd) {
        return result;
    }
    const keys = Object.keys(cmcd);
    const custom = customHeaderMap ? createHeaderMap(customHeaderMap) : {};
    return keys.reduce((acc, key) => {
        var _a;
        const field = CMCD_HEADER_MAP[key] || custom[key] || CmcdHeaderField.REQUEST;
        const data = (_a = acc[field]) !== null && _a !== void 0 ? _a : (acc[field] = {});
        data[key] = cmcd[key];
        return acc;
    }, result);
}/**
 * Checks if the given key is a token field.
 *
 * @param key - The key to check.
 *
 * @returns `true` if the key is a token field.
 *
 * @internal
 */
function isTokenField(key) {
    return [
        'ot',
        'sf',
        'st',
        'e',
        'sta',
    ].includes(key);
}/**
 * Checks if the given value is valid
 *
 * @param value - The value to check.
 *
 * @returns `true` if the key is a value is valid.
 *
 * @internal
 */
function isValid(value) {
    if (typeof value === 'number') {
        return Number.isFinite(value);
    }
    return value != null && value !== '' && value !== false;
}/**
 * CMCD event mode variable name.
 *
 * @group CMCD
 *
 * @beta
 */
const CMCD_EVENT_MODE = 'event';/**
 * Constructs a relative path from a URL.
 *
 * @param url - The destination URL
 * @param base - The base URL
 * @returns The relative path
 *
 * @group Utils
 *
 * @beta
 */
function urlToRelativePath(url, base) {
    const to = new URL(url);
    const from = new URL(base);
    if (to.origin !== from.origin) {
        return url;
    }
    const toPath = to.pathname.split('/').slice(1);
    const fromPath = from.pathname.split('/').slice(1, -1);
    // remove common parents
    const length = Math.min(toPath.length, fromPath.length);
    for (let i = 0; i < length; i++) {
        if (toPath[i] !== fromPath[i]) {
            break;
        }
        toPath.shift();
        fromPath.shift();
    }
    // add back paths
    while (fromPath.length) {
        fromPath.shift();
        toPath.unshift('..');
    }
    const relativePath = toPath.join('/');
    // preserve query parameters and hash of the destination url
    return relativePath + to.search + to.hash;
}const toRounded = (value) => Math.round(value);
const toUrlSafe = (value, options) => {
    if (Array.isArray(value)) {
        return value.map(item => toUrlSafe(item, options));
    }
    if (value instanceof SfItem && typeof value.value === 'string') {
        return new SfItem(toUrlSafe(value.value, options), value.params);
    }
    else {
        if (options.baseUrl) {
            value = urlToRelativePath(value, options.baseUrl);
        }
        return options.version === 1 ? encodeURIComponent(value) : value;
    }
};
const toHundred = (value) => toRounded(value / 100) * 100;
const nor = (value, options) => {
    let norValue = value;
    if (options.version >= 2) {
        if (value instanceof SfItem && typeof value.value === 'string') {
            norValue = new SfItem([value]);
        }
        else if (typeof value === 'string') {
            norValue = [value];
        }
    }
    return toUrlSafe(norValue, options);
};
/**
 * The default formatters for CMCD values.
 *
 * @group CMCD
 *
 * @beta
 */
const CMCD_FORMATTER_MAP = {
    /**
     * Bitrate (kbps) rounded integer
     */
    br: toRounded,
    /**
     * Duration (milliseconds) rounded integer
     */
    d: toRounded,
    /**
     * Buffer Length (milliseconds) rounded nearest 100ms
     */
    bl: toHundred,
    /**
     * Deadline (milliseconds) rounded nearest 100ms
     */
    dl: toHundred,
    /**
     * Measured Throughput (kbps) rounded nearest 100kbps
     */
    mtp: toHundred,
    /**
     * Next Object Request URL encoded
     */
    nor,
    /**
     * Requested maximum throughput (kbps) rounded nearest 100kbps
     */
    rtp: toHundred,
    /**
     * Top Bitrate (kbps) rounded integer
     */
    tb: toRounded,
};/**
 * CMCD request mode variable name.
 *
 * @group CMCD
 *
 * @beta
 */
const CMCD_REQUEST_MODE = 'request';/**
 * CMCD response mode variable name.
 *
 * @group CMCD
 *
 * @beta
 */
const CMCD_RESPONSE_MODE = 'response';/**
 * Defines the common keys for CMCD (Common Media Client Data) version 2.
 *
 * @group CMCD
 *
 * @beta
 */
const CMCD_COMMON_KEYS = [
    'ab',
    'bg',
    'bl',
    'br',
    'bs',
    'bsd',
    'cdn',
    'cid',
    'cs',
    'df',
    'ec',
    'lab',
    'lb',
    'ltc',
    'msd',
    'mtp',
    'pb',
    'pr',
    'pt',
    'sf',
    'sid',
    'sn',
    'st',
    'sta',
    'tab',
    'tb',
    'tbl',
    'tpb',
    'ts',
    'v',
];/**
 * Defines the event-specific keys for CMCD (Common Media Client Data) version 2.
 *
 * @group CMCD
 *
 * @beta
 */
const CMCD_EVENT_KEYS = [
    'e',
];const CUSTOM_KEY_REGEX = /^[a-zA-Z0-9-.]+-[a-zA-Z0-9-.]+$/;
/**
 * Check if a key is a custom key.
 *
 * @param key - The key to check.
 *
 * @returns `true` if the key is a custom key, `false` otherwise.
 *
 * @group CMCD
 *
 * @beta
 */
function isCmcdCustomKey(key) {
    return CUSTOM_KEY_REGEX.test(key);
}/**
 * Check if a key is a valid CMCD event key.
 *
 * @param key - The key to check.
 *
 * @returns `true` if the key is a valid CMCD event key, `false` otherwise.
 *
 * @group CMCD
 *
 * @beta
 *
 * @example
 * {@includeCode ../../test/cmcd/isCmcdEventKey.test.ts#example}
 */
function isCmcdEventKey(key) {
    return CMCD_COMMON_KEYS.includes(key) ||
        CMCD_EVENT_KEYS.includes(key) ||
        isCmcdCustomKey(key);
}/**
 * Defines the request-specific keys for CMCD (Common Media Client Data) version 2.
 *
 * @group CMCD
 *
 * @beta
 */
const CMCD_REQUEST_KEYS = [
    'd',
    'dl',
    'nor',
    'ot',
    'rtp',
    'su',
];/**
 * Check if a key is a valid CMCD request key.
 *
 * @param key - The key to check.
 *
 * @returns `true` if the key is a valid CMCD request key, `false` otherwise.
 *
 * @group CMCD
 *
 * @beta
 *
 * @example
 * {@includeCode ../../test/cmcd/isCmcdRequestKey.test.ts#example}
 */
function isCmcdRequestKey(key) {
    return CMCD_COMMON_KEYS.includes(key) ||
        CMCD_REQUEST_KEYS.includes(key) ||
        isCmcdCustomKey(key);
}/**
 * CMCD v2 - Response-only and timing keys.
 *
 * @group CMCD
 *
 * @beta
 */
const CMCD_RESPONSE_KEYS = [
    'cmsdd',
    'cmsds',
    'rc',
    'smrt',
    'ttfb',
    'ttfbb',
    'ttlb',
    'url',
];/**
 * Check if a key is a valid CMCD response key.
 *
 * @param key - The key to check.
 *
 * @returns `true` if the key is a valid CMCD request key, `false` otherwise.
 *
 * @group CMCD
 *
 * @beta
 *
 * @example
 * {@includeCode ../../test/cmcd/isCmcdResponseKey.test.ts#example}
 */
function isCmcdResponseKey(key) {
    return CMCD_COMMON_KEYS.includes(key) ||
        CMCD_REQUEST_KEYS.includes(key) ||
        CMCD_RESPONSE_KEYS.includes(key) ||
        isCmcdCustomKey(key);
}/**
 * Defines the keys for CMCD (Common Media Client Data) version 1.
 *
 * @group CMCD
 *
 * @beta
 */
const CMCD_V1_KEYS = [
    'bl',
    'br',
    'bs',
    'cid',
    'd',
    'dl',
    'mtp',
    'nor',
    'nrr',
    'ot',
    'pr',
    'rtp',
    'sf',
    'sid',
    'st',
    'su',
    'tb',
    'v',
];/**
 * Filter function for CMCD v1 keys.
 *
 * @param key - The CMCD key to filter.
 *
 * @returns `true` if the key should be included, `false` otherwise.
 *
 * @group CMCD
 *
 * @beta
 *
 * @example
 * {@includeCode ../../test/cmcd/isCmcdV1Key.test.ts#example}
 */
function isCmcdV1Key(key) {
    return CMCD_V1_KEYS.includes(key) || isCmcdCustomKey(key);
}const filterMap = {
    [CMCD_RESPONSE_MODE]: isCmcdResponseKey,
    [CMCD_EVENT_MODE]: isCmcdEventKey,
    [CMCD_REQUEST_MODE]: isCmcdRequestKey,
};
/**
 * Convert a generic object to CMCD data.
 *
 * @param obj - The CMCD object to process.
 * @param options - Options for encoding.
 *
 * @group CMCD
 *
 * @beta
 */
function prepareCmcdData(obj, options = {}) {
    const results = {};
    if (obj == null || typeof obj !== 'object') {
        return results;
    }
    const version = options.version || obj['v'] || 1;
    const reportingMode = options.reportingMode || CMCD_REQUEST_MODE;
    const keyFilter = version === 1 ? isCmcdV1Key : filterMap[reportingMode];
    // Filter keys based on the version, reporting mode and options
    let keys = Object.keys(obj).filter(keyFilter);
    const filter = options.filter;
    if (typeof filter === 'function') {
        keys = keys.filter(filter);
    }
    // Ensure all required keys are present before sorting
    const needsTimestamp = reportingMode === CMCD_RESPONSE_MODE || reportingMode === CMCD_EVENT_MODE;
    if (needsTimestamp && !keys.includes('ts')) {
        keys.push('ts');
    }
    if (version > 1 && !keys.includes('v')) {
        keys.push('v');
    }
    const formatters = Object.assign({}, CMCD_FORMATTER_MAP, options.formatters);
    const formatterOptions = {
        version,
        reportingMode,
        baseUrl: options.baseUrl,
    };
    keys.sort().forEach(key => {
        let value = obj[key];
        const formatter = formatters[key];
        if (typeof formatter === 'function') {
            value = formatter(value, formatterOptions);
        }
        // Version should only be reported if not equal to 1.
        if (key === 'v') {
            if (version === 1) {
                return;
            }
            else {
                value = version;
            }
        }
        // Playback rate should only be sent if not equal to 1.
        if (key == 'pr' && value === 1) {
            return;
        }
        // Ensure a timestamp is set for response and event modes
        if (needsTimestamp && key === 'ts' && !Number.isFinite(value)) {
            value = Date.now();
        }
        // ignore invalid values
        if (!isValid(value)) {
            return;
        }
        if (isTokenField(key) && typeof value === 'string') {
            value = new SfToken(value);
        }
        results[key] = value;
    });
    return results;
}/**
 * Convert a CMCD data object to request headers
 *
 * @param cmcd - The CMCD data object to convert.
 * @param options - Options for encoding the CMCD object.
 *
 * @returns The CMCD header shards.
 *
 * @group CMCD
 *
 * @beta
 *
 * @example
 * {@includeCode ../../test/cmcd/toCmcdHeaders.test.ts#example}
 */
function toCmcdHeaders(cmcd, options = {}) {
    const result = {};
    if (!cmcd) {
        return result;
    }
    const data = prepareCmcdData(cmcd, options);
    const shards = groupCmcdHeaders(data, options === null || options === void 0 ? void 0 : options.customHeaderMap);
    return Object.entries(shards)
        .reduce((acc, [field, value]) => {
        const shard = encodeSfDict(value, { whitespace: false });
        if (shard) {
            acc[field] = shard;
        }
        return acc;
    }, result);
}/**
 * Append CMCD query args to a header object.
 *
 * @param headers - The headers to append to.
 * @param cmcd - The CMCD object to append.
 * @param options - Encode options.
 *
 * @returns The headers with the CMCD header shards appended.
 *
 * @group CMCD
 *
 * @beta
 *
 * @example
 * {@includeCode ../../test/cmcd/appendCmcdHeaders.test.ts#example}
 */
function appendCmcdHeaders(headers, cmcd, options) {
    return Object.assign(headers, toCmcdHeaders(cmcd, options));
}/**
 * CMCD parameter name.
 *
 * @group CMCD
 *
 * @beta
 */
const CMCD_PARAM = 'CMCD';/**
 * Encode a CMCD object to a string.
 *
 * @param cmcd - The CMCD object to encode.
 * @param options - Options for encoding.
 *
 * @returns The encoded CMCD string.
 *
 * @group CMCD
 *
 * @beta
 *
 * @example
 * {@includeCode ../../test/cmcd/encodeCmcd.test.ts#example}
 */
function encodeCmcd(cmcd, options = {}) {
    if (!cmcd) {
        return '';
    }
    return encodeSfDict(prepareCmcdData(cmcd, options), { whitespace: false });
}/**
 * Convert a CMCD data object to a URL encoded string.
 *
 * @param cmcd - The CMCD object to convert.
 * @param options - Options for encoding the CMCD object.
 *
 * @returns The URL encoded CMCD data.
 *
 * @group CMCD
 *
 * @beta
 */
function toCmcdUrl(cmcd, options = {}) {
    if (!cmcd) {
        return '';
    }
    const params = encodeCmcd(cmcd, options);
    return encodeURIComponent(params);
}/**
 * Convert a CMCD data object to a query arg.
 *
 * @param cmcd - The CMCD object to convert.
 * @param options - Options for encoding the CMCD object.
 *
 * @returns The CMCD query arg.
 *
 * @group CMCD
 *
 * @beta
 *
 * @example
 * {@includeCode ../../test/cmcd/toCmcdQuery.test.ts#example}
 */
function toCmcdQuery(cmcd, options = {}) {
    if (!cmcd) {
        return '';
    }
    const value = toCmcdUrl(cmcd, options);
    return `${CMCD_PARAM}=${value}`;
}const REGEX = /CMCD=[^&#]+/;
/**
 * Append CMCD query args to a URL.
 *
 * @param url - The URL to append to.
 * @param cmcd - The CMCD object to append.
 * @param options - Options for encoding the CMCD object.
 *
 * @returns The URL with the CMCD query args appended.
 *
 * @group CMCD
 *
 * @beta
 *
 * @example
 * {@includeCode ../../test/cmcd/appendCmcdQuery.test.ts#example}
 */
function appendCmcdQuery(url, cmcd, options) {
    // TODO: Replace with URLSearchParams once we drop Safari < 10.1 & Chrome < 49 support.
    // https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams
    const query = toCmcdQuery(cmcd, options);
    if (!query) {
        return url;
    }
    if (REGEX.test(url)) {
        return url.replace(REGEX, query);
    }
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${query}`;
}/**
 * The default time interval in secondswhen using using event mode
 *
 * @group CMCD
 *
 * @beta
 */
const CMCD_DEFAULT_TIME_INTERVAL = 30;/**
 * CMCD `headers` transmission mode.
 *
 * @group CMCD
 *
 * @beta
 */
const CMCD_HEADERS = 'headers';/**
 * CMCD `json` transmission mode.
 *
 * @group CMCD
 *
 * @beta
 *
 * @deprecated JSON transmission mode is deprecated and will be removed in future versions.
 */
const CMCD_JSON = 'json';const keySet = new Set([...CMCD_V1_KEYS, ...CMCD_COMMON_KEYS, ...CMCD_REQUEST_KEYS, ...CMCD_RESPONSE_KEYS, ...CMCD_EVENT_KEYS]);
/**
 * A list of all CMCD keys.
 *
 * @group CMCD
 *
 * @beta
 */
const CMCD_KEYS = Array.from(keySet);/**
 * CMCD `query` transmission mode.
 *
 * @group CMCD
 *
 * @beta
 */
const CMCD_QUERY = 'query';/**
 * CMCD Version 1
 *
 * @group CMCD
 *
 * @beta
 */
const CMCD_V1 = 1;/**
 * CMCD Version 2
 *
 * @group CMCD
 *
 * @beta
 */
const CMCD_V2 = 2;/**
 * CMCD transmission modes.
 *
 * @group CMCD
 *
 * @enum
 *
 * @beta
 */
const CmcdTransmissionMode = {
    /**
     * JSON
     *
     * @deprecated JSON transmission mode is deprecated and will be removed in future versions.
     */
    JSON: CMCD_JSON,
    /**
     * Query string
     */
    QUERY: CMCD_QUERY,
    /**
     * Request headers
     */
    HEADERS: CMCD_HEADERS,
};/**
 * CMCD encoding types.
 *
 * @group CMCD
 *
 * @enum
 *
 * @beta
 *
 * @deprecated Use {@link CmcdTransmissionMode} instead.
 *
 * @see {@link CmcdTransmissionMode}
 */
const CmcdEncoding = CmcdTransmissionMode;/**
 * CMCD event types for the 'e' key (event mode).
 *
 * @group CMCD
 *
 * @enum
 *
 * @beta
 */
const CmcdEventType = {
    /**
     * A change in the player state.
     */
    PLAY_STATE: 'ps',
    /**
     * An error event.
     */
    ERROR: 'e',
    /**
     * A periodic report sent on a time interval.
     */
    TIME_INTERVAL: 't',
    /**
     * A change of the content ID.
     */
    CONTENT_ID: 'c',
    /**
     * A change in the application's backgrounded state.
     */
    BACKGROUNDED_MODE: 'b',
    /**
     * The player was muted.
     */
    MUTE: 'm',
    /**
     * Player unmuted.
     */
    UNMUTE: 'um',
    /**
     * The player view was expanded.
     */
    PLAYER_EXPAND: 'pe',
    /**
     * The player view was collapsed.
     */
    PLAYER_COLLAPSE: 'pc',
};/**
 * The default formatters for CMCD values.
 *
 * @group CMCD
 *
 * @beta
 *
 * @deprecated Use `CMCD_FORMATTER_MAP` instead.
 */
const CmcdFormatters = CMCD_FORMATTER_MAP;/**
 * Common Media Object Type
 *
 * @internal
 */
const CmObjectType = {
    /**
     * text file, such as a manifest or playlist
     */
    MANIFEST: 'm',
    /**
     * audio only
     */
    AUDIO: 'a',
    /**
     * video only
     */
    VIDEO: 'v',
    /**
     * muxed audio and video
     */
    MUXED: 'av',
    /**
     * init segment
     */
    INIT: 'i',
    /**
     * caption or subtitle
     */
    CAPTION: 'c',
    /**
     * ISOBMFF timed text track
     */
    TIMED_TEXT: 'tt',
    /**
     * cryptographic key, license or certificate.
     */
    KEY: 'k',
    /**
     * other
     */
    OTHER: 'o',
};/**
 * Common Media Client Data Object Type
 *
 * @group CMCD
 *
 * @beta
 *
 * @enum
 */
const CmcdObjectType = CmObjectType;/**
 * CMCD v2 player states for the 'sta' key.
 *
 * @group CMCD
 *
 * @enum
 *
 * @beta
 */
const CmcdPlayerState = {
    /**
     * Starting: Initial startup of the player.
     */
    STARTING: 's',
    /**
     * Playing: The player is actively rendering content.
     */
    PLAYING: 'p',
    /**
     * Seeking: The player is seeking to a new position.
     */
    SEEKING: 'k',
    /**
     * Rebuffering: The player is buffering data during playback.
     */
    REBUFFERING: 'r',
    /**
     * Paused: The player is paused.
     */
    PAUSED: 'a',
    /**
     * Waiting: The player is waiting for a user action or another event.
     */
    WAITING: 'w',
    /**
     * Ended: The media has finished playing.
     */
    ENDED: 'e',
    /**
     * Fatal Error: The player has encountered a fatal error.
     */
    FATAL_ERROR: 'f',
    /**
     * Quit: User initiated end of playback before media asset completion.
     */
    QUIT: 'q',
    /**
     * Preloading: The player is loading assets ahead of starting in order to provide a fast startup. The expectation is that playback will commence at a future time.
     */
    PRELOADING: 'd',
};/**
 * CMCD reporting mode types.
 *
 * @group CMCD
 *
 * @enum
 *
 * @beta
 */
const CmcdReportingMode = {
    /**
     * Request mode
     */
    REQUEST: CMCD_REQUEST_MODE,
    /**
     * Response mode
     */
    RESPONSE: CMCD_RESPONSE_MODE,
    /**
     * Event mode
     */
    EVENT: CMCD_EVENT_MODE,
};/**
 * Common Media Streaming Format
 *
 * @internal
 */
const CmStreamingFormat = {
    /**
     * MPEG DASH
     */
    DASH: 'd',
    /**
     * HTTP Live Streaming (HLS)
     */
    HLS: 'h',
    /**
     * Smooth Streaming
     */
    SMOOTH: 's',
    /**
     * Other
     */
    OTHER: 'o',
};/**
 * Common Media Client Data Streaming Format
 *
 * @group CMCD
 *
 * @enum
 *
 * @beta
 */
const CmcdStreamingFormat = CmStreamingFormat;/**
 * Common Media Stream Type
 *
 * @internal
 */
const CmStreamType = {
    /**
     *  All segments are available – e.g., VOD
     */
    VOD: 'v',
    /**
     * Segments become available over time – e.g., LIVE
     */
    LIVE: 'l',
};/**
 * Common Media Client Data Stream Type
 *
 * @group CMCD
 *
 * @enum
 *
 * @beta
 */
const CmcdStreamType = CmStreamType;function parsedValue(value, src) {
    return { value, src };
}function parseError(src, type, cause) {
    return throwError('parse', src, type, cause);
}const INNER = 'Inner List';// 4.2.8.  Parsing a Boolean
//
// Given an ASCII string as input_string, return a Boolean. input_string
// is modified to remove the parsed value.
//
// 1.  If the first character of input_string is not "?", fail parsing.
//
// 2.  Discard the first character of input_string.
//
// 3.  If the first character of input_string matches "1", discard the
//     first character, and return true.
//
// 4.  If the first character of input_string matches "0", discard the
//     first character, and return false.
//
// 5.  No value has matched; fail parsing.
function parseBoolean(src) {
    let i = 0;
    if (src[i] !== '?') {
        throw parseError(src, BOOLEAN);
    }
    i++;
    if (src[i] === '1') {
        return parsedValue(true, src.substring(++i));
    }
    if (src[i] === '0') {
        return parsedValue(false, src.substring(++i));
    }
    throw parseError(src, BOOLEAN);
}/**
 * Decodes a base64 encoded string into binary data
 *
 * @param str - The base64 encoded string to decode
 * @returns The decoded binary data
 *
 * @group Utils
 *
 * @beta
 */
function decodeBase64(str) {
    return new Uint8Array([...atob(str)].map((a) => a.charCodeAt(0)));
}// 4.2.7.  Parsing a Byte Sequence
//
// Given an ASCII string as input_string, return a Byte Sequence.
// input_string is modified to remove the parsed value.
//
// 1.  If the first character of input_string is not ":", fail parsing.
//
// 2.  Discard the first character of input_string.
//
// 3.  If there is not a ":" character before the end of input_string,
//     fail parsing.
//
// 4.  Let b64_content be the result of consuming content of
//     input_string up to but not including the first instance of the
//     character ":".
//
// 5.  Consume the ":" character at the beginning of input_string.
//
// 6.  If b64_content contains a character not included in ALPHA, DIGIT,
//     "+", "/" and "=", fail parsing.
//
// 7.  Let binary_content be the result of Base 64 Decoding [RFC4648]
//     b64_content, synthesizing padding if necessary (note the
//     requirements about recipient behavior below).
//
// 8.  Return binary_content.
//
// Because some implementations of base64 do not allow rejection of
// encoded data that is not properly "=" padded (see [RFC4648],
// Section 3.2), parsers SHOULD NOT fail when "=" padding is not
// present, unless they cannot be configured to do so.
//
// Because some implementations of base64 do not allow rejection of
// encoded data that has non-zero pad bits (see [RFC4648], Section 3.5),
// parsers SHOULD NOT fail when non-zero pad bits are present, unless
// they cannot be configured to do so.
//
// This specification does not relax the requirements in [RFC4648],
// Section 3.1 and 3.3; therefore, parsers MUST fail on characters
// outside the base64 alphabet, and on line feeds in encoded data.
function parseByteSequence(src) {
    if (src[0] !== ':') {
        throw parseError(src, BYTES);
    }
    src = src.substring(1);
    if (src.includes(':') === false) {
        throw parseError(src, BYTES);
    }
    const re = /(^.*?)(:)/g;
    const b64_content = re.exec(src)[1];
    src = src.substring(re.lastIndex);
    // pass b64_content char check step 6
    return parsedValue(decodeBase64(b64_content), src);
}const DATE = 'Date';const INTEGER_DECIMAL = `${INTEGER} or ${DECIMAL}`;// 4.2.4.  Parsing an Integer or Decimal
//
// Given an ASCII string as input_string, return an Integer or Decimal.
// input_string is modified to remove the parsed value.
//
// NOTE: This algorithm parses both Integers (Section 3.3.1) and
// Decimals (Section 3.3.2), and returns the corresponding structure.
//
// 1.   Let type be "integer".
//
// 2.   Let sign be 1.
//
// 3.   Let input_number be an empty string.
//
// 4.   If the first character of input_string is "-", consume it and
//      set sign to -1.
//
// 5.   If input_string is empty, there is an empty integer; fail
//      parsing.
//
// 6.   If the first character of input_string is not a DIGIT, fail
//      parsing.
//
// 7.   While input_string is not empty:
//
//      1.  Let char be the result of consuming the first character of
//          input_string.
//
//      2.  If char is a DIGIT, append it to input_number.
//
//      3.  Else, if type is "integer" and char is ".":
//
//          1.  If input_number contains more than 12 characters, fail
//              parsing.
//
//          2.  Otherwise, append char to input_number and set type to
//              "decimal".
//
//      4.  Otherwise, prepend char to input_string, and exit the loop.
//
//      5.  If type is "integer" and input_number contains more than 15
//          characters, fail parsing.
//
//      6.  If type is "decimal" and input_number contains more than 16
//          characters, fail parsing.
//
// 8.   If type is "integer":
//
//      1.  Parse input_number as an integer and let output_number be
//          the product of the result and sign.
//
//      2.  If output_number is outside the range -999,999,999,999,999
//          to 999,999,999,999,999 inclusive, fail parsing.
//
// 9.   Otherwise:
//
//      1.  If the final character of input_number is ".", fail parsing.
//
//      2.  If the number of characters after "." in input_number is
//          greater than three, fail parsing.
//
//      3.  Parse input_number as a decimal number and let output_number
//          be the product of the result and sign.
//
// 10.  Return output_number.
function parseIntegerOrDecimal(src) {
    const orig = src;
    let sign = 1;
    let num = '';
    let value;
    const i = 0;
    const error = parseError(orig, INTEGER_DECIMAL);
    if (src[i] === '-') {
        sign = -1;
        src = src.substring(1);
    }
    if (src.length <= 0) {
        throw error;
    }
    const re_integer = /^(\d+)?/g;
    const result_integer = re_integer.exec(src);
    if (result_integer[0].length === 0) {
        throw error;
    }
    num += result_integer[1];
    src = src.substring(re_integer.lastIndex);
    if (src[0] === '.') {
        // decimal
        if (num.length > 12) {
            throw error;
        }
        const re_decimal = /^(\.\d+)?/g;
        const result_decimal = re_decimal.exec(src);
        src = src.substring(re_decimal.lastIndex);
        // 9.2.  If the number of characters after "." in input_number is greater than three, fail parsing.
        if (result_decimal[0].length === 0 || result_decimal[1].length > 4) {
            throw error;
        }
        num += result_decimal[1];
        // 7.6.  If type is "decimal" and input_number contains more than 16 characters, fail parsing.
        if (num.length > 16) {
            throw error;
        }
        value = parseFloat(num) * sign;
    }
    else {
        // integer
        // 7.5.  If type is "integer" and input_number contains more than 15 characters, fail parsing.
        if (num.length > 15) {
            throw error;
        }
        value = parseInt(num) * sign;
        if (isInvalidInt(value)) {
            throw parseError(num, INTEGER_DECIMAL);
        }
    }
    return parsedValue(value, src);
}// 4.2.9.  Parsing a Date
//
// Given an ASCII string as input_string, return a Date. input_string is
// modified to remove the parsed value.
//
// 1.  If the first character of input_string is not "@", fail parsing.
//
// 2.  Discard the first character of input_string.
//
// 3.  Let output_date be the result of running Parsing an Integer or
//     Decimal (Section 4.2.4) with input_string.
//
// 4.  If output_date is a Decimal, fail parsing.
//
// 5.  Return output_date.
function parseDate(src) {
    let i = 0;
    if (src[i] !== '@') {
        throw parseError(src, DATE);
    }
    i++;
    const date = parseIntegerOrDecimal(src.substring(i));
    if (Number.isInteger(date.value) === false) {
        throw parseError(src, DATE);
    }
    return parsedValue(new Date(date.value * 1000), date.src);
}// 4.2.5.  Parsing a String
//
// Given an ASCII string as input_string, return an unquoted String.
// input_string is modified to remove the parsed value.
//
// 1.  Let output_string be an empty string.
//
// 2.  If the first character of input_string is not DQUOTE, fail
//     parsing.
//
// 3.  Discard the first character of input_string.
//
// 4.  While input_string is not empty:
//
//     1.  Let char be the result of consuming the first character of
//         input_string.
//
//     2.  If char is a backslash ("\"):
//
//         1.  If input_string is now empty, fail parsing.
//
//         2.  Let next_char be the result of consuming the first
//             character of input_string.
//
//         3.  If next_char is not DQUOTE or "\", fail parsing.
//
//         4.  Append next_char to output_string.
//
//     3.  Else, if char is DQUOTE, return output_string.
//
//     4.  Else, if char is in the range %x00-1f or %x7f (i.e., is not
//         in VCHAR or SP), fail parsing.
//
//     5.  Else, append char to output_string.
//
// 5.  Reached the end of input_string without finding a closing DQUOTE;
//     fail parsing.
function parseString(src) {
    let output = '';
    let i = 0;
    if (src[i] !== `"`) {
        throw parseError(src, STRING$1);
    }
    i++;
    while (src.length > i) {
        if (src[i] === `\\`) {
            if (src.length <= i + 1) {
                throw parseError(src, STRING$1);
            }
            i++;
            if (src[i] !== `"` && src[i] !== `\\`) {
                throw parseError(src, STRING$1);
            }
            output += src[i];
        }
        else if (src[i] === `"`) {
            return parsedValue(output, src.substring(++i));
        }
        else if (STRING_REGEX.test(src[i])) {
            throw parseError(src, STRING$1);
        }
        else {
            output += src[i];
        }
        i++;
    }
    throw parseError(src, STRING$1);
}// 4.2.6.  Parsing a Token
//
// Given an ASCII string as input_string, return a Token. input_string
// is modified to remove the parsed value.
//
// 1.  If the first character of input_string is not ALPHA or "*", fail
//     parsing.
//
// 2.  Let output_string be an empty string.
//
// 3.  While input_string is not empty:
//
//     1.  If the first character of input_string is not in tchar, ":"
//         or "/", return output_string.
//
//     2.  Let char be the result of consuming the first character of
//         input_string.
//
//     3.  Append char to output_string.
//
// 4.  Return output_string.
function parseToken(src, options) {
    if (/^[a-zA-Z*]$/.test(src[0]) === false) {
        throw parseError(src, TOKEN);
    }
    const re = /^([!#$%&'*+\-.^_`|~\w:/]+)/g;
    const value = re.exec(src)[1];
    src = src.substring(re.lastIndex);
    return parsedValue((options === null || options === void 0 ? void 0 : options.useSymbol) === false ? new SfToken(value) : Symbol.for(value), src);
}// 4.2.3.1.  Parsing a Bare Item
//
// Given an ASCII string as input_string, return a bare Item.
// input_string is modified to remove the parsed value.
//
// 1.  If the first character of input_string is a "-" or a DIGIT,
//     return the result of running Parsing an Integer or Decimal
//     (Section 4.2.4) with input_string.
//
// 2.  If the first character of input_string is a DQUOTE, return the
//     result of running Parsing a String (Section 4.2.5) with
//     input_string.
//
// 3.  If the first character of input_string is ":", return the result
//     of running Parsing a Byte Sequence (Section 4.2.7) with
//     input_string.
//
// 4.  If the first character of input_string is "?", return the result
//     of running Parsing a Boolean (Section 4.2.8) with input_string.
//
// 5.  If the first character of input_string is an ALPHA or "*", return
//     the result of running Parsing a Token (Section 4.2.6) with
//     input_string.
//
// 6.  If the first character of input_string is "@", return the result
//     of running Parsing a Date (Section 4.2.9) with input_string.
//
// 7.  Otherwise, the item type is unrecognized; fail parsing.
function parseBareItem(src, options) {
    const first = src[0];
    if (first === `"`) {
        return parseString(src);
    }
    if (/^[-0-9]/.test(first)) {
        return parseIntegerOrDecimal(src);
    }
    if (first === `?`) {
        return parseBoolean(src);
    }
    if (first === `:`) {
        return parseByteSequence(src);
    }
    if (/^[a-zA-Z*]/.test(first)) {
        return parseToken(src, options);
    }
    if (first === `@`) {
        return parseDate(src);
    }
    throw parseError(src, BARE_ITEM);
}// 4.2.3.3.  Parsing a Key
//
// Given an ASCII string as input_string, return a key. input_string is
// modified to remove the parsed value.
//
// 1.  If the first character of input_string is not lcalpha or "*",
//     fail parsing.
//
// 2.  Let output_string be an empty string.
//
// 3.  While input_string is not empty:
//
//     1.  If the first character of input_string is not one of lcalpha,
//         DIGIT, "_", "-", ".", or "*", return output_string.
//
//     2.  Let char be the result of consuming the first character of
//         input_string.
//
//     3.  Append char to output_string.
//
// 4.  Return output_string.
function parseKey(src) {
    let i = 0;
    if (/^[a-z*]$/.test(src[i]) === false) {
        throw parseError(src, KEY);
    }
    let value = '';
    while (src.length > i) {
        if (/^[a-z0-9_\-.*]$/.test(src[i]) === false) {
            return parsedValue(value, src.substring(i));
        }
        value += src[i];
        i++;
    }
    return parsedValue(value, src.substring(i));
}// 4.2.3.2.  Parsing Parameters
//
// Given an ASCII string as input_string, return an ordered map whose
// values are bare Items. input_string is modified to remove the parsed
// value.
//
// 1.  Let parameters be an empty, ordered map.
//
// 2.  While input_string is not empty:
//
//     1.  If the first character of input_string is not ";", exit the
//         loop.
//
//     2.  Consume a ";" character from the beginning of input_string.
//
//     3.  Discard any leading SP characters from input_string.
//
//     4.  let param_name be the result of running Parsing a Key
//         (Section 4.2.3.3) with input_string.
//
//     5.  Let param_value be Boolean true.
//
//     6.  If the first character of input_string is "=":
//
//         1.  Consume the "=" character at the beginning of
//             input_string.
//
//         2.  Let param_value be the result of running Parsing a Bare
//             Item (Section 4.2.3.1) with input_string.
//
//     7.  Append key param_name with value param_value to parameters.
//         If parameters already contains a name param_name (comparing
//         character-for-character), overwrite its value.
//
// 3.  Return parameters.
//
// Note that when duplicate Parameter keys are encountered, this has the
// effect of ignoring all but the last instance.
function parseParameters(src, options) {
    let parameters = undefined;
    while (src.length > 0) {
        if (src[0] !== ';') {
            break;
        }
        src = src.substring(1).trim();
        const parsedKey = parseKey(src);
        const key = parsedKey.value;
        let value = true;
        src = parsedKey.src;
        if (src[0] === '=') {
            src = src.substring(1);
            const parsedBareItem = parseBareItem(src, options);
            value = parsedBareItem.value;
            src = parsedBareItem.src;
        }
        // initialize as object when params exists
        if (parameters == null) {
            parameters = {};
        }
        // override if param_name exists
        parameters[key] = value;
    }
    return parsedValue(parameters, src);
}// 4.2.3.  Parsing an Item
//
// Given an ASCII string as input_string, return a (bare_item,
// parameters) tuple. input_string is modified to remove the parsed
// value.
//
// 1.  Let bare_item be the result of running Parsing a Bare Item
//     (Section 4.2.3.1) with input_string.
//
// 2.  Let parameters be the result of running Parsing Parameters
//     (Section 4.2.3.2) with input_string.
//
// 3.  Return the tuple (bare_item, parameters).
function parseItem(src, options) {
    const parsedBareItem = parseBareItem(src, options);
    src = parsedBareItem.src;
    const parsedParameters = parseParameters(src, options);
    src = parsedParameters.src;
    return parsedValue(new SfItem(parsedBareItem.value, parsedParameters.value), src);
}// 4.2.1.2.  Parsing an Inner List
//
// Given an ASCII string as input_string, return the tuple (inner_list,
// parameters), where inner_list is an array of (bare_item, parameters)
// tuples. input_string is modified to remove the parsed value.
//
// 1.  Consume the first character of input_string; if it is not "(",
//     fail parsing.
//
// 2.  Let inner_list be an empty array.
//
// 3.  While input_string is not empty:
//
//     1.  Discard any leading SP characters from input_string.
//
//     2.  If the first character of input_string is ")":
//
//         1.  Consume the first character of input_string.
//
//         2.  Let parameters be the result of running Parsing
//             Parameters (Section 4.2.3.2) with input_string.
//
//         3.  Return the tuple (inner_list, parameters).
//
//     3.  Let item be the result of running Parsing an Item
//         (Section 4.2.3) with input_string.
//
//     4.  Append item to inner_list.
//
//     5.  If the first character of input_string is not SP or ")", fail
//         parsing.
//
// 4.  The end of the inner list was not found; fail parsing.
function parseInnerList(src, options) {
    if (src[0] !== '(') {
        throw parseError(src, INNER);
    }
    src = src.substring(1);
    const innerList = [];
    while (src.length > 0) {
        src = src.trim();
        if (src[0] === ')') {
            src = src.substring(1);
            const parsedParameters = parseParameters(src, options);
            return parsedValue(new SfItem(innerList, parsedParameters.value), parsedParameters.src);
        }
        const parsedItem = parseItem(src, options);
        innerList.push(parsedItem.value);
        src = parsedItem.src;
        if (src[0] !== ' ' && src[0] !== ')') {
            throw parseError(src, INNER);
        }
    }
    throw parseError(src, INNER);
}// 4.2.1.1.  Parsing an Item or Inner List
//
// Given an ASCII string as input_string, return the tuple
// (item_or_inner_list, parameters), where item_or_inner_list can be
// either a single bare item, or an array of (bare_item, parameters)
// tuples. input_string is modified to remove the parsed value.
//
// 1.  If the first character of input_string is "(", return the result
//     of running Parsing an Inner List (Section 4.2.1.2) with
//     input_string.
//
// 2.  Return the result of running Parsing an Item (Section 4.2.3) with
//     input_string.
function parseItemOrInnerList(src, options) {
    if (src[0] === '(') {
        return parseInnerList(src, options);
    }
    return parseItem(src, options);
}// 4.2.2.  Parsing a Dictionary
//
// Given an ASCII string as input_string, return an ordered map whose
// values are (item_or_inner_list, parameters) tuples. input_string is
// modified to remove the parsed value.
//
// 1.  Let dictionary be an empty, ordered map.
//
// 2.  While input_string is not empty:
//
//     1.  Let this_key be the result of running Parsing a Key
//         (Section 4.2.3.3) with input_string.
//
//     2.  If the first character of input_string is "=":
//
//         1.  Consume the first character of input_string.
//
//         2.  Let member be the result of running Parsing an Item or
//             Inner List (Section 4.2.1.1) with input_string.
//
//     3.  Otherwise:
//
//         1.  Let value be Boolean true.
//
//         2.  Let parameters be the result of running Parsing
//             Parameters Section 4.2.3.2 with input_string.
//
//         3.  Let member be the tuple (value, parameters).
//
//     4.  Add name this_key with value member to dictionary.  If
//         dictionary already contains a name this_key (comparing
//         character-for-character), overwrite its value.
//
//     5.  Discard any leading OWS characters from input_string.
//
//     6.  If input_string is empty, return dictionary.
//
//     7.  Consume the first character of input_string; if it is not
//         ",", fail parsing.
//
//     8.  Discard any leading OWS characters from input_string.
//
//     9.  If input_string is empty, there is a trailing comma; fail
//         parsing.
//
// 3.  No structured data has been found; return dictionary (which is
//     empty).
//
// Note that when duplicate Dictionary keys are encountered, this has
// the effect of ignoring all but the last instance.
function parseDict(src, options) {
    const value = {};
    while (src.length > 0) {
        let member;
        const parsedKey = parseKey(src);
        const key = parsedKey.value;
        src = parsedKey.src;
        if (src[0] === '=') {
            const parsedItemOrInnerList = parseItemOrInnerList(src.substring(1), options);
            member = parsedItemOrInnerList.value;
            src = parsedItemOrInnerList.src;
        }
        else {
            const parsedParameters = parseParameters(src, options);
            member = new SfItem(true, parsedParameters.value);
            src = parsedParameters.src;
        }
        value[key] = member;
        src = src.trim();
        if (src.length === 0) {
            return parsedValue(value, src);
        }
        if (src[0] !== ',') {
            throw parseError(src, DICT);
        }
        src = src.substring(1).trim();
        if (src.length === 0 || src[0] === ',') {
            throw parseError(src, DICT);
        }
    }
    return parsedValue(value, src);
}/**
 * Decode a structured field string into a structured field dictionary
 *
 * @param input - The structured field string to decode
 * @returns The structured field dictionary
 *
 * @group Structured Field
 *
 * @beta
 */
function decodeSfDict(input, options) {
    try {
        const { src, value } = parseDict(input.trim(), options);
        if (src !== '') {
            throw parseError(src, DICT);
        }
        return value;
    }
    catch (cause) {
        throw parseError(input, DICT, cause);
    }
}function reduceValue(value) {
    if (Array.isArray(value)) {
        return value.map(reduceValue);
    }
    if (typeof value === 'symbol') {
        return symbolToStr(value);
    }
    if (value instanceof SfItem && !value.params) {
        return reduceValue(value.value);
    }
    if (typeof value === 'string') {
        return decodeURIComponent(value);
    }
    return value;
}
/**
 * Decode a CMCD string to an object.
 *
 * @param cmcd - The CMCD string to decode.
 *
 * @returns The decoded CMCD object.
 *
 * @group CMCD
 *
 * @beta
 *
 * @example
 * {@includeCode ../../test/cmcd/decodeCmcd.test.ts#example}
 */
function decodeCmcd(cmcd) {
    if (!cmcd) {
        return {};
    }
    const sfDict = decodeSfDict(cmcd);
    return Object
        .entries(sfDict)
        .reduce((acc, [key, item]) => {
        acc[key] = reduceValue(item.value);
        return acc;
    }, {});
}const keys = [CMCD_OBJECT, CMCD_REQUEST, CMCD_SESSION, CMCD_STATUS];
/**
 * Decode CMCD data from request headers.
 *
 * @param headers - The request headers to decode.
 *
 * @returns The decoded CMCD data.
 *
 * @group CMCD
 *
 * @beta
 *
 * @example
 * {@includeCode ../../test/cmcd/fromCmcdHeaders.test.ts#example}
 */
function fromCmcdHeaders(headers) {
    if (!(headers instanceof Headers)) {
        headers = new Headers(headers);
    }
    return keys.reduce((acc, key) => {
        const value = headers.get(key);
        return Object.assign(acc, decodeCmcd(value));
    }, {});
}/**
 * Decode CMCD data from a query string.
 *
 * @param query - The query string to decode.
 *
 * @returns The decoded CMCD data.
 *
 * @group CMCD
 *
 * @beta
 *
 * @example
 * {@includeCode ../../test/cmcd/fromCmcdQuery.test.ts#example}
 */
function fromCmcdQuery(query) {
    if (typeof query === 'string') {
        query = new URLSearchParams(query);
    }
    const value = query.get(CMCD_PARAM);
    return decodeCmcd(value);
}/**
 * Decode CMCD data from a url encoded string.
 *
 * @param url - The url encoded string to decode.
 *
 * @returns The decoded CMCD data.
 *
 * @group CMCD
 *
 * @beta
 *
 * @example
 * {@includeCode ../../test/cmcd/fromCmcdUrl.test.ts#example}
 */
function fromCmcdUrl(url) {
    return decodeCmcd(decodeURIComponent(url.replace(/^CMCD=/, '')));
}/**
 * Convert a CMCD data object to JSON.
 *
 * @param cmcd - The CMCD object to convert.
 * @param options - Options for encoding the CMCD object.
 *
 * @returns The CMCD JSON.
 *
 * @group CMCD
 *
 * @beta
 *
 * @deprecated Sending CMCD as JSON objects is deprecated. Use `toCmcdUrl` to create an array of url strings instead.
 */
function toCmcdJson(cmcd, options) {
    const data = prepareCmcdData(cmcd, options);
    return JSON.stringify(data, (_, value) => typeof value === 'symbol' || value instanceof SfToken ? symbolToStr(value) : value);
}/**
 * Converts CMCD data into a report format.
 *
 * @param data - The CMCD data to be transformed into a report
 * @param target - The target configuration for the CMCD report
 *
 * @return A CMCD report object
 *
 * @group CMCD
 *
 * @beta
 *
 * @example
 * {@includeCode ../../test/cmcd/toCmcdReport.test.ts#example}
 */
function toCmcdReport(data, target) {
    if (!target || !target.url) {
        return null;
    }
    const url = new URL(target.url);
    const method = target.method || 'GET';
    const headers = {};
    const transimissionMode = target.transmissionMode || CMCD_QUERY;
    const options = {
        version: target.version,
        reportingMode: target.reportingMode,
    };
    if (target.enabledKeys) {
        options.filter = (key) => target.enabledKeys.includes(key);
    }
    switch (transimissionMode) {
        case CMCD_QUERY:
            const param = encodeCmcd(data, options);
            if (param) {
                url.searchParams.set(CMCD_PARAM, param);
            }
            break;
        case CMCD_HEADERS:
            Object.assign(headers, toCmcdHeaders(data, options));
            break;
    }
    return {
        url: url.toString(),
        method,
        headers,
    };
}/**
 * CMSD dynamic header name.
 *
 * @group CMSD
 *
 * @beta
 */
const CMSD_DYNAMIC = 'CMSD-Dynamic';/**
 * CMSD static header name.
 *
 * @group CMSD
 *
 * @beta
 */
const CMSD_STATIC = 'CMSD-Static';/**
 * CMSD Version 1
 *
 * @group CMSD
 *
 * @beta
 */
const CMSD_V1 = 1;/**
 * CMSD header fields.
 *
 * @group CMSD
 *
 * @enum
 *
 * @beta
 */
const CmsdHeaderField = {
    /**
     * Keys whose values persist over multiple requests for the object.
     */
    STATIC: CMSD_STATIC,
    /**
     * Keys whose values apply only to the next transmission hop. Typically a
     * new CMSD-Dynamic header instance will be added by each intermediary
     * participating in the delivery.
     */
    DYNAMIC: CMSD_DYNAMIC,
};/**
 * Common Media Server Data Object Type
 *
 * @group CMSD
 *
 * @enum
 *
 * @beta
 */
const CmsdObjectType = CmObjectType;/**
 * Common Media Server Data Streaming Format
 *
 * @group CMSD
 *
 * @enum
 *
 * @beta
 */
const CmsdStreamingFormat = CmStreamingFormat;/**
 * Common Media Server Data Stream Type
 *
 * @group CMSD
 *
 * @enum
 *
 * @beta
 */
const CmsdStreamType = CmStreamType;const LIST = 'List';// 4.2.1.  Parsing a List
//
// Given an ASCII string as input_string, return an array of
// (item_or_inner_list, parameters) tuples. input_string is modified to
// remove the parsed value.
//
// 1.  Let members be an empty array.
//
// 2.  While input_string is not empty:
//
//     1.  Append the result of running Parsing an Item or Inner List
//         (Section 4.2.1.1) with input_string to members.
//
//     2.  Discard any leading OWS characters from input_string.
//
//     3.  If input_string is empty, return members.
//
//     4.  Consume the first character of input_string; if it is not
//         ",", fail parsing.
//
//     5.  Discard any leading OWS characters from input_string.
//
//     6.  If input_string is empty, there is a trailing comma; fail
//         parsing.
//
// 3.  No structured data has been found; return members (which is
//     empty).
function parseList(src, options) {
    const value = [];
    while (src.length > 0) {
        const parsedItemOrInnerList = parseItemOrInnerList(src, options);
        value.push(parsedItemOrInnerList.value);
        src = parsedItemOrInnerList.src.trim();
        if (src.length === 0) {
            return parsedValue(value, src);
        }
        if (src[0] !== ',') {
            throw parseError(src, LIST);
        }
        src = src.substring(1).trim();
        if (src.length === 0 || src[0] === ',') {
            throw parseError(src, LIST);
        }
    }
    return parsedValue(value, src);
}/**
 * Decode a structured field string into a structured field list
 *
 * @param input - The structured field string to decode
 * @returns The structured field list
 *
 * @group Structured Field
 *
 * @beta
 */
function decodeSfList(input, options) {
    try {
        const { src, value } = parseList(input.trim(), options);
        if (src !== '') {
            throw parseError(src, LIST);
        }
        return value;
    }
    catch (cause) {
        throw parseError(input, LIST, cause);
    }
}/**
 * Decode a CMSD dynamic string to an object.
 *
 * @param cmsd - The CMSD string to decode.
 *
 * @returns The decoded CMSD object.
 *
 * @group CMSD
 *
 * @beta
 */
function decodeCmsdDynamic(cmsd) {
    if (!cmsd) {
        return [];
    }
    const sfDict = decodeSfList(cmsd);
    return sfDict;
}/**
 * Decode a CMSD Static dict string to an object.
 *
 * @param cmsd - The CMSD string to decode.
 *
 * @returns The decoded CMSD object.
 *
 * @group CMSD
 *
 * @beta
 */
function decodeCmsdStatic(cmsd) {
    if (!cmsd) {
        return {};
    }
    return Object
        .entries(decodeSfDict(cmsd))
        .reduce((acc, [key, item]) => {
        const { value } = item;
        acc[key] = (typeof value === 'symbol' ? symbolToStr(value) : value);
        return acc;
    }, {});
}// 4.1.1.  Serializing a List
//
// Given an array of (member_value, parameters) tuples as input_list,
// return an ASCII string suitable for use in a HTTP field value.
//
// 1.  Let output be an empty string.
//
// 2.  For each (member_value, parameters) of input_list:
//
//     1.  If member_value is an array, append the result of running
//         Serializing an Inner List (Section 4.1.1.1) with
//         (member_value, parameters) to output.
//
//     2.  Otherwise, append the result of running Serializing an Item
//         (Section 4.1.3) with (member_value, parameters) to output.
//
//     3.  If more member_values remain in input_list:
//
//         1.  Append "," to output.
//
//         2.  Append a single SP to output.
//
// 3.  Return output.
function serializeList(list, options = { whitespace: true }) {
    if (Array.isArray(list) === false) {
        throw serializeError(list, LIST);
    }
    const optionalWhiteSpace = (options === null || options === void 0 ? void 0 : options.whitespace) ? ' ' : '';
    return list
        .map(item => {
        if (item instanceof SfItem === false) {
            item = new SfItem(item);
        }
        // TODO: Fix this type assertion
        const i = item;
        if (Array.isArray(i.value)) {
            return serializeInnerList(i);
        }
        return serializeItem(i);
    })
        .join(`,${optionalWhiteSpace}`);
}/**
 * Encode a list into a structured field dictionary
 *
 * @param value - The structured field list to encode
 * @param options - Encoding options
 *
 * @returns The structured field string
 *
 * @group Structured Field
 *
 * @beta
 */
function encodeSfList(value, options) {
    return serializeList(value, options);
}function encodeCmsdDynamic(value, cmsd) {
    if (!value) {
        return '';
    }
    if (typeof value === 'string') {
        if (!cmsd) {
            return '';
        }
        value = [new SfItem(value, cmsd)];
    }
    return encodeSfList(value, { whitespace: false });
}function processCmsd(obj, options) {
    const results = {};
    if (obj == null || typeof obj !== 'object') {
        return results;
    }
    const keys = Object.keys(obj);
    const useSymbol = (options === null || options === void 0 ? void 0 : options.useSymbol) !== false;
    keys.forEach(key => {
        let value = obj[key];
        // Version should only be reported if not equal to 1.
        if (key === 'v' && value === 1) {
            return;
        }
        // ignore invalid values
        if (!isValid(value)) {
            return;
        }
        if (isTokenField(key) && typeof value === 'string') {
            value = useSymbol ? Symbol.for(value) : new SfToken(value);
        }
        results[key] = value;
    });
    return results;
}/**
 * Encode a CMSD Static object.
 *
 * @param cmsd - The CMSD object to encode.
 * @param options - Encoding options
 *
 * @returns The encoded CMSD string.
 *
 * @group CMSD
 *
 * @beta
 */
function encodeCmsdStatic(cmsd, options) {
    if (!cmsd) {
        return '';
    }
    return encodeSfDict(processCmsd(cmsd, options), { whitespace: false });
}/**
 * The default penalty for a pathway in milliseconds.
 *
 * @group Content Steering
 *
 * @beta
 */
const DEFAULT_PATHWAY_PENALTY = 300000;/**
 * The default time to live for content steering in seconds.
 *
 * @group Content Steering
 *
 * @beta
 */
const DEFAULT_TTL = 300;/**
 * Validates a pathway clone.
 *
 * @param clone - The pathway clone to validate.
 * @returns `true` if the pathway clone is valid, `false` otherwise.
 *
 * @group Content Steering
 *
 * @beta
 */
function isValidPathwayClone(clone) {
    if (typeof clone !== 'object' || !clone) {
        return false;
    }
    const { ID, 'BASE-ID': BASE_ID, 'URI-REPLACEMENT': URI_REPLACEMENT } = clone;
    if (typeof ID !== 'string' || typeof BASE_ID !== 'string' || URI_REPLACEMENT == null || typeof URI_REPLACEMENT !== 'object') {
        return false;
    }
    return true;
}/**
 * Validates a steering manifest.
 *
 * @param manifest - The steering manifest to validate.
 * @returns `true` if the steering manifest is valid, `false` otherwise.
 *
 * @group Content Steering
 *
 * @beta
 */
function isValidSteeringManifest(manifest) {
    if (!manifest) {
        return false;
    }
    const { VERSION, TTL, 'PATHWAY-PRIORITY': PATHWAY_PRIORITY, 'PATHWAY-CLONES': PATHWAY_CLONES } = manifest;
    // version is required and must be a valid number
    if (typeof VERSION !== 'number' || VERSION !== 1) {
        return false;
    }
    // TTL must be a valid number and greater than 0
    if (typeof TTL !== 'number' || TTL <= 0) {
        return false;
    }
    // PATHWAY-PRIORITY must be an array of strings and must not contain duplicates
    if (!Array.isArray(PATHWAY_PRIORITY) || PATHWAY_PRIORITY.length === 0 || new Set(PATHWAY_PRIORITY).size !== PATHWAY_PRIORITY.length) {
        return false;
    }
    // PATHWAY-CLONES must be an array of valid pathway clones
    if (PATHWAY_CLONES && (!Array.isArray(PATHWAY_CLONES) || PATHWAY_CLONES.length === 0 || PATHWAY_CLONES.some(clone => !isValidPathwayClone(clone)))) {
        return false;
    }
    return true;
}/**
 * Parses a DASH framerate string into a number.
 *
 * @param frameRate - The frame rate string to parse.
 * @returns The frame rate as a number.
 *
 * @group DASH
 * @beta
 *
 * @example
 * {@includeCode ../../test/dash/parseFrameRate.test.ts#example}
 */
function parseFrameRate(frameRate) {
    const [numerator, denominator] = frameRate.split('/').map(value => parseInt(value, 10));
    if (denominator === undefined) {
        return numerator;
    }
    if (isNaN(numerator) || isNaN(denominator) || denominator === 0) {
        return NaN;
    }
    return numerator / denominator;
}const TOKENS = /\$(RepresentationID|Number|SubNumber|Bandwidth|Time)?(?:%0([0-9]+)([diouxX]))?\$/g;
/**
 * Process a URI template used in `SegmentTemplate` nodes.
 *
 * @param uriTemplate - URI template to process.
 * @param representationId - Representation ID.
 * @param number - Number.
 * @param subNumber - Sub-number.
 * @param bandwidth - Bandwidth.
 * @param time - Time. Should be passed as a number unless the value is larger than `MAX_SAFE_INTEGER`, then it should be provided as a string. If the value is a string all format tags will be ignored.
 *
 * @returns Processed URI template.
 *
 * @group DASH
 * @beta
 *
 * @example
 * {@includeCode ../../test/dash/processUriTemplate.test.ts#example}
 */
function processUriTemplate(uriTemplate, representationId, number, subNumber, bandwidth, time) {
    const uri = uriTemplate.replace(TOKENS, (match, name, widthStr, format) => {
        let value;
        switch (name) {
            case undefined: // $$ case
                return '$';
            case 'RepresentationID':
                value = representationId;
                break;
            case 'Number':
                value = number;
                break;
            case 'SubNumber':
                value = subNumber;
                break;
            case 'Bandwidth':
                value = bandwidth;
                break;
            case 'Time':
                if (typeof time === 'string') {
                    return time;
                }
                value = time ? Math.round(time) : time;
                break;
            default:
                value = null;
        }
        if (value == null) {
            return match;
        }
        let valueString;
        switch (format) {
            case undefined: // Happens if there is no format specifier.
            case 'd':
            case 'i':
            case 'u':
                valueString = value.toString();
                break;
            case 'o':
                valueString = value.toString(8);
                break;
            case 'x':
                valueString = value.toString(16);
                break;
            case 'X':
                valueString = value.toString(16).toUpperCase();
                break;
            default:
                valueString = value.toString();
                break;
        }
        const width = parseInt(widthStr, 10) || 1;
        return valueString.padStart(width, '0');
    });
    return uri;
}/**
 * The scheme ID URI for thumbnail tiles in DASH.
 *
 * @group DASH
 *
 * @beta
 */
const THUMBNAIL_TILE_SCHEME_ID_URI = 'http://dashif.org/guidelines/thumbnail_tile';
/**
 * Cipher Block Chaining Sample encryption scheme.
 *
 * @group DRM
 *
 * @beta
 */
const CBCS = 'cbcs';/**
 * Common Encryption scheme.
 *
 * @group DRM
 *
 * @beta
 */
const CENC = 'cenc';/**
 * The DASH MP4 Protection Scheme schemeIdUri.
 *
 * @group DRM
 * @beta
 */
const MP4_PROTECTION_SCHEME = 'urn:mpeg:dash:mp4protection:2011';/**
 * Finds and returns the ContentProtection element for MP4 Common Encryption.
 *
 * @param cpArray - Array of ContentProtection elements.
 * @returns The Common Encryption content protection element, or null if not found.
 *
 * @group DRM
 * @beta
 *
 * @example
 * {@includeCode ../../../test/drm/cenc/findCencContentProtection.test.ts#example}
 */
function findCencContentProtection(cpArray) {
    var _a;
    if (!cpArray) {
        return null;
    }
    for (const cp of cpArray) {
        if (((_a = cp.schemeIdUri) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === MP4_PROTECTION_SCHEME &&
            cp.value &&
            (cp.value.toLowerCase() === CENC ||
                cp.value.toLowerCase() === CBCS)) {
            return cp;
        }
    }
    return null;
}/**
 * Extracts a license server URL from an array of ContentProtection elements.
 *
 * @param contentProtectionElements - Array of ContentProtection nodes from the manifest.
 * @param schemeIdUri - schemeIdUri we are searching for.
 * @returns The license server URL, and if not found then null.
 *
 * @group DRM
 * @beta
 *
 * @example
 * {@includeCode ../../../test/drm/cenc/getLicenseServerUrlFromContentProtection.test.ts#example}
 */
function getLicenseServerUrlFromContentProtection(contentProtectionElements, schemeIdUri) {
    var _a;
    return ((_a = contentProtectionElements.find(cp => cp.schemeIdUri === schemeIdUri)) === null || _a === void 0 ? void 0 : _a.laUrl) || null;
}/**
 * Returns the data portion of a single PSSH box.
 *
 * @param pssh - The PSSH ArrayBuffer.
 * @returns The data portion of the PSSH.
 *
 * @group DRM
 * @beta
 *
 * @example
 * {@includeCode ../../../test/drm/cenc/getPsshData.test.ts#example}
 */
function getPsshData(pssh) {
    const offset = 8; // Box size and type fields
    const view = new DataView(pssh);
    const version = view.getUint8(offset); // Read version
    let dataOffset = offset + 20;
    if (version > 0) {
        const kidCount = view.getUint32(dataOffset);
        dataOffset += 4 + (16 * kidCount);
    }
    dataOffset += 4; // Data size
    return pssh.slice(dataOffset);
}/**
 * Parses a list of PSSH boxes into keysystem-specific PSSH data.
 *
 * @param data - The concatenated list of PSSH boxes as provided by
 * the CDM as initialization data when CommonEncryption content is detected
 * @returns An object that has a property named according to each of
 * the detected key system UUIDs (e.g. 00000000-0000-0000-0000-0000000000)
 * and a ArrayBuffer (the entire PSSH box) as the property value
 *
 * @group DRM
 * @beta
 *
 * @example
 * {@includeCode ../../../test/drm/cenc/parsePsshList.test.ts#example}
 */
function parsePsshList(data) {
    if (!data) {
        return {};
    }
    // data.buffer first for Uint8Array support
    const dv = new DataView(data instanceof ArrayBuffer ? data : data.buffer);
    const pssh = {};
    let byteCursor = 0;
    while (true) {
        if (byteCursor >= dv.byteLength) {
            break;
        }
        const boxStart = byteCursor;
        // Box size
        const size = dv.getUint32(byteCursor);
        const nextBox = byteCursor + size;
        byteCursor += 4;
        // Verify PSSH
        if (dv.getUint32(byteCursor) !== 0x70737368) {
            byteCursor = nextBox;
            continue;
        }
        byteCursor += 4;
        // Version must be 0 or 1
        const version = dv.getUint8(byteCursor);
        if (version !== 0 && version !== 1) {
            byteCursor = nextBox;
            continue;
        }
        byteCursor += 1; // Move past version
        byteCursor += 3; // Skip flags
        // 16-byte UUID/SystemID
        let systemID = '';
        let val;
        for (let i = 0; i < 4; i++) {
            val = dv.getUint8(byteCursor + i).toString(16);
            systemID += val.length === 1 ? '0' + val : val;
        }
        byteCursor += 4;
        systemID += '-';
        for (let i = 0; i < 2; i++) {
            val = dv.getUint8(byteCursor + i).toString(16);
            systemID += val.length === 1 ? '0' + val : val;
        }
        byteCursor += 2;
        systemID += '-';
        for (let i = 0; i < 2; i++) {
            val = dv.getUint8(byteCursor + i).toString(16);
            systemID += val.length === 1 ? '0' + val : val;
        }
        byteCursor += 2;
        systemID += '-';
        for (let i = 0; i < 2; i++) {
            val = dv.getUint8(byteCursor + i).toString(16);
            systemID += val.length === 1 ? '0' + val : val;
        }
        byteCursor += 2;
        systemID += '-';
        for (let i = 0; i < 6; i++) {
            val = dv.getUint8(byteCursor + i).toString(16);
            systemID += val.length === 1 ? '0' + val : val;
        }
        byteCursor += 6;
        systemID = systemID.toLowerCase();
        // PSSH Data Size
        byteCursor += 4;
        // PSSH boxdata
        pssh[systemID] = data.slice(boxStart, nextBox);
        byteCursor = nextBox;
    }
    return pssh;
}/**
 * Returns the PSSH box associated with the given key system from the concatenated
 * list of PSSH boxes in the provided initData.
 *
 * @param uuid - The desired key system UUID
 * @param initData - 'cenc' initialization data. Concatenated list of PSSH boxes.
 * @returns The PSSH box ArrayBuffer corresponding to the given key system, or null if not found.
 *
 * @group DRM
 * @beta
 *
 * @example
 * {@includeCode ../../../test/drm/cenc/getPsshForKeySystem.test.ts#example}
 */
function getPsshForKeySystem(uuid, initData) {
    if (!initData || !uuid) {
        return null;
    }
    const psshList = parsePsshList(initData);
    return psshList[uuid.toLowerCase()] || null;
}/**
 * Parse a standard common encryption PSSH which contains a simple
 * base64-encoding of the init data
 *
 * @param cpData - The ContentProtection element that may contain PSSH data.
 * @param BASE64 - The BASE64 reference.
 * @returns init data as an ArrayBuffer, or null if not found.
 *
 * @group DRM
 * @beta
 *
 * @example
 * {@includeCode ../../../test/drm/cenc/parseInitDataFromContentProtection.test.ts#example}
 */
function parseInitDataFromContentProtection(cpData, BASE64) {
    if ((cpData === null || cpData === void 0 ? void 0 : cpData.pssh) && cpData.pssh) {
        const cleanedText = cpData.pssh.replace(/\r?\n|\r/g, '').replace(/\s+/g, '');
        return BASE64.decodeArray(cleanedText).buffer;
    }
    return null;
}/**
 * Creates a valid KeySystemConfiguration from supported audio and video capabilities.
 *
 * @param supportedAudio - supported audio capabilities.
 * @param supportedVideo - supported video capabilities.
 * @returns KeySystemConfiguration object.
 *
 * @group DRM
 * @beta
 *
 * @example
 * {@includeCode ../../../test/drm/keysystem/createMediaKeySystemConfiguration.test.ts#example}
 */
function createMediaKeySystemConfiguration(supportedAudio, supportedVideo) {
    const config = {};
    if (supportedAudio && supportedAudio.length > 0) {
        config.audioCapabilities = supportedAudio;
    }
    if (supportedVideo && supportedVideo.length > 0) {
        config.videoCapabilities = supportedVideo;
    }
    // default values
    config.distinctiveIdentifier = 'optional';
    config.persistentState = 'optional';
    config.sessionTypes = ['temporary'];
    return config;
}/**
 * Attempts to get key system access using requestMediaKeySystemAccess from EME.
 *
 * @param requests - An array of key system access requests.
 * @returns MediaKeySystemAccess object if successful, or null if no system is supported.
 *
 * @group DRM
 * @beta
 */
async function getKeySystemAccess(requests) {
    for (const { keySystem, configurations } of requests) {
        try {
            return await navigator.requestMediaKeySystemAccess(keySystem, configurations);
        }
        catch {
            // legacy approach could be used here
        }
    }
    return null;
}const isTypeSupported = (typeof MediaKeys !== 'undefined' && typeof MediaKeys.isTypeSupported === 'function') ? MediaKeys.isTypeSupported : undefined;
/**
 * Filters and returns the supported key system configuration for a given system string.
 *
 * @param keySystem - Key system string such as 'com.widevine.alpha'
 * @param configs - An array of key system configurations.
 * @returns The first supported configuration or null if none are supported.
 *
 * @group DRM
 * @beta
 */
function getSupportedKeySystemConfiguration(keySystem, configs) {
    if (!configs || typeof isTypeSupported === 'undefined') {
        return { supportedAudio: [], supportedVideo: [] };
    }
    for (const config of configs) {
        const audios = config.audioCapabilities || [];
        const videos = config.videoCapabilities || [];
        const supportedAudio = audios.filter(audio => { var _a; return isTypeSupported(keySystem, (_a = audio.contentType) !== null && _a !== void 0 ? _a : ''); });
        const supportedVideo = videos.filter(video => { var _a; return isTypeSupported(keySystem, (_a = video.contentType) !== null && _a !== void 0 ? _a : ''); });
        const hasAudio = supportedAudio.length > 0;
        const hasVideo = supportedVideo.length > 0;
        if (hasAudio || hasVideo) {
            return { supportedAudio, supportedVideo };
        }
    }
    return { supportedAudio: [], supportedVideo: [] };
}/**
 * Fallback method to get key system access using legacy MediaKeys.isTypeSupported().
 *
 * @param requests - An array of key system access requests.
 * @returns A KeySystemAccess object if successful, or null if no system is supported.
 *
 * @group DRM
 * @beta
 */
function getLegacyKeySystemAccess(requests) {
    for (const { keySystem, configurations } of requests) {
        const supportedConfig = getSupportedKeySystemConfiguration(keySystem, configurations);
        if (supportedConfig) {
            const configuration = createMediaKeySystemConfiguration(supportedConfig.supportedAudio, supportedConfig.supportedVideo);
            return {
                keySystem,
                configurations: [configuration],
            };
        }
    }
    return null;
}/**
 * Converts a string to a Uint16Array.
 *
 * @param str - The string to convert
 * @returns A Uint16Array representation of the string
 *
 * @group Utils
 *
 * @beta
 *
 * @example
 * {@includeCode ../../test/utils/stringToUint16.test.ts#example}
 */
function stringToUint16(str) {
    const buffer = new ArrayBuffer(str.length * 2);
    const view = new DataView(buffer);
    for (let i = 0; i < str.length; i++) {
        view.setUint16(i * 2, str.charCodeAt(i), true); // true for little-endian
    }
    return new Uint16Array(buffer);
}/**
 * Concatenates InitData, ID, and Certificate for FairPlay DRM.
 *
 * @param initData - The initialization data (PSSH box)
 * @param id - The content ID (string or Uint16Array)
 * @param cert - The application certificate (Uint8Array)
 * @returns A Uint8Array containing the concatenated data
 *
 * @group DRM
 * @beta
 *
 * @example
 * {@includeCode ../../../test/drm/fairplay/concatInitDataIdAndCertificate.test.ts#example}
 */
function concatInitDataIdAndCertificate(initData, id, cert) {
    if (typeof id === 'string') {
        id = stringToUint16(id);
    }
    const buffer = new ArrayBuffer(initData.byteLength + 4 + id.byteLength + 4 + cert.byteLength);
    const dataView = new DataView(buffer);
    let offset = 0;
    new Uint8Array(buffer, offset, initData.byteLength).set(initData);
    offset += initData.byteLength;
    dataView.setUint32(offset, id.byteLength, true);
    offset += 4;
    new Uint16Array(buffer, offset, id.length).set(id);
    offset += id.byteLength;
    dataView.setUint32(offset, cert.byteLength, true);
    offset += 4;
    new Uint8Array(buffer, offset, cert.byteLength).set(cert);
    return new Uint8Array(buffer);
}/**
 * Decodes a FairPlay DRM license response.
 *
 * @param response - The license response, either a base64 string or ArrayBuffer
 * @returns A Uint8Array containing the decoded license
 *
 * @group DRM
 * @beta
 *
 * @example
 * {@includeCode ../../../test/drm/fairplay/decodeFairPlayLicense.test.ts#example}
 */
function decodeFairPlayLicense(response) {
    return typeof response === 'string'
        ? decodeBase64(response)
        : new Uint8Array(response);
}/**
 * UTF-16 Encoding.
 *
 * @group Utils
 *
 * @beta
 */
const UTF_16 = 'utf-16';/**
 * UTF-16 Big Endian Encoding.
 *
 * @group Utils
 *
 * @beta
 */
const UTF_16_BE = 'utf-16be';/**
 * UTF-16 Little Endian Encoding.
 *
 * @group Utils
 *
 * @beta
 */
const UTF_16_LE = 'utf-16le';/**
 * UTF-8 Encoding.
 *
 * @group Utils
 *
 * @beta
 */
const UTF_8 = 'utf-8';/**
 * Converts an ArrayBuffer or ArrayBufferView to a string. Similar to `TextDecoder.decode`
 * but with a fallback for environments that don't support `TextDecoder`.
 *
 * @param data - The data to decode.
 * @param options - The options for the decoding.
 * @returns The string representation of the ArrayBuffer.
 *
 * @group Utils
 *
 * @beta
 *
 * @example
 * {@includeCode ../../test/utils/decodeText.test.ts#example}
 */
function decodeText(data, options = {}) {
    let view;
    if (data instanceof ArrayBuffer) {
        view = new DataView(data);
    }
    else {
        view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    }
    let byteOffset = 0;
    let { encoding } = options;
    // If no encoding is provided, try to detect it from the BOM
    if (!encoding) {
        const first = view.getUint8(0);
        const second = view.getUint8(1);
        // UTF-8 BOM
        if (first == 0xef && second == 0xbb && view.getUint8(2) == 0xbf) {
            encoding = UTF_8;
            byteOffset = 3;
        }
        // UTF-16 BE BOM
        else if (first == 0xfe && second == 0xff) {
            encoding = UTF_16_BE;
            byteOffset = 2;
        }
        // UTF-16 LE BOM
        else if (first == 0xff && second == 0xfe) {
            encoding = UTF_16_LE;
            byteOffset = 2;
        }
        else {
            encoding = UTF_8;
        }
    }
    if (typeof TextDecoder !== 'undefined') {
        return new TextDecoder(encoding).decode(view);
    }
    const { byteLength } = view;
    const endian = encoding !== UTF_16_BE;
    let str = '';
    let char;
    while (byteOffset < byteLength) {
        switch (encoding) {
            case UTF_8:
                char = view.getUint8(byteOffset);
                // Single byte (ASCII)
                if (char < 128) {
                    byteOffset++;
                }
                // 2-byte sequence
                else if (char >= 194 && char <= 223) {
                    if (byteOffset + 1 < byteLength) {
                        const byte2 = view.getUint8(byteOffset + 1);
                        if (byte2 >= 128 && byte2 <= 191) {
                            char = ((char & 0x1F) << 6) | (byte2 & 0x3F);
                            byteOffset += 2;
                        }
                        else {
                            // Invalid sequence, skip
                            byteOffset++;
                        }
                    }
                    else {
                        // Incomplete sequence, skip
                        byteOffset++;
                    }
                }
                // 3-byte sequence
                else if (char >= 224 && char <= 239) {
                    if (byteOffset + 2 <= byteLength - 1) {
                        const byte2 = view.getUint8(byteOffset + 1);
                        const byte3 = view.getUint8(byteOffset + 2);
                        if (byte2 >= 128 && byte2 <= 191 && byte3 >= 128 && byte3 <= 191) {
                            char = ((char & 0x0F) << 12) | ((byte2 & 0x3F) << 6) | (byte3 & 0x3F);
                            byteOffset += 3;
                        }
                        else {
                            // Invalid sequence, skip
                            byteOffset++;
                        }
                    }
                    else {
                        // Incomplete sequence, skip
                        byteOffset++;
                    }
                }
                // 4-byte sequence
                else if (char >= 240 && char <= 244) {
                    if (byteOffset + 3 <= byteLength - 1) {
                        const byte2 = view.getUint8(byteOffset + 1);
                        const byte3 = view.getUint8(byteOffset + 2);
                        const byte4 = view.getUint8(byteOffset + 3);
                        if (byte2 >= 128 && byte2 <= 191 && byte3 >= 128 && byte3 <= 191 && byte4 >= 128 && byte4 <= 191) {
                            char = ((char & 0x07) << 18) | ((byte2 & 0x3F) << 12) | ((byte3 & 0x3F) << 6) | (byte4 & 0x3F);
                            byteOffset += 4;
                        }
                        else {
                            // Invalid sequence, skip
                            byteOffset++;
                        }
                    }
                    else {
                        // Incomplete sequence, skip
                        byteOffset++;
                    }
                }
                // Invalid byte, skip
                else {
                    byteOffset++;
                }
                break;
            case UTF_16_BE:
            case UTF_16:
            case UTF_16_LE:
                char = view.getUint16(byteOffset, endian);
                byteOffset += 2;
                break;
        }
        str += String.fromCodePoint(char);
    }
    return str;
}/**
 * Extracts the content ID from InitData using skd:// URI or query parameters.
 *
 * @param initData - The initialization data (PSSH box)
 * @returns The extracted content ID
 *
 * @group DRM
 * @beta
 *
 * @example
 * {@includeCode ../../../test/drm/fairplay/extractContentId.test.ts#example}
 */
function extractContentId(initData, encoding = UTF_16) {
    const initDataString = decodeText(initData, { encoding });
    // Try extracting skd:// content ID
    return initDataString.split('skd://')[1] || '';
}/**
 * Extracts the content ID from the license server URL or InitData.
 *
 * @param licenseServerUrl - The license server URL
 * @param initData - The initialization data (PSSH box)
 * @param queryParam - The query parameter key to extract (default: 'ContentId')
 * @returns The extracted content ID as a string or null if not found
 *
 * @group DRM
 * @beta
 *
 * @example
 * {@includeCode ../../../test/drm/fairplay/getId.test.ts#example}
 */
function getId(licenseServerUrl, initData, queryParam = 'ContentId') {
    try {
        const url = new URL(licenseServerUrl);
        const params = new URLSearchParams(url.search);
        return params.get(queryParam) || extractContentId(initData);
    }
    catch {
        // in case if URL parsing fails,
        // fallback to extracting from initData
        return extractContentId(initData);
    }
}/**
 * Retrieves the FairPlay license server URL from InitData.
 *
 * @param initData - The initialization data (PSSH box)
 * @returns The license server URL as a string
 *
 * @group DRM
 * @beta
 *
 * @example
 * {@includeCode ../../../test/drm/fairplay/getLicenseServerUrl.test.ts#example}
 */
function getLicenseServerUrl(initData) {
    let initDataString = '';
    for (let i = 0; i < initData.length; i++) {
        if (initData[i] !== 0) { // Ignore null characters
            initDataString += String.fromCharCode(initData[i]);
        }
    }
    const match = initDataString.match(/skd:\/\/([^"\s]+)/);
    return match ? `https://${match[1]}` : '';
}/**
 * Recursively finds all elements by name within an XML structure.
 *
 * @param node - The current XML node to search within.
 * @param name - The name of the target nodes to find.
 * @param found - An array to collect matching nodes.
 * @returns An array of all matching XmlNodes.
 *
 * @group XML
 *
 * @beta
 *
 */
function getElementsByName(node, name, found = []) {
    if (!node) {
        return found;
    }
    if (node.nodeName === name) {
        found.push(node);
    }
    if (node.childNodes) {
        for (const child of node.childNodes) {
            getElementsByName(child, name, found);
        }
    }
    return found;
}const escapedHtml = /&(?:amp|lt|gt|quot|apos|nbsp|lrm|rlm|#[xX]?[0-9a-fA-F]+);/g;
/**
 * Unescapes HTML entities
 *
 * @param text - The text to unescape
 * @returns The unescaped text
 *
 * @group Utils
 *
 * @beta
 *
 * @example
 * {@includeCode ../../test/utils/unescapeHtml.test.ts#example}
 */
function unescapeHtml(text) {
    if (text.indexOf('&') === -1) {
        return text;
    }
    return text.replace(escapedHtml, (match) => {
        switch (match) {
            case '&amp;': return '&';
            case '&lt;': return '<';
            case '&gt;': return '>';
            case '&quot;': return '"';
            case '&apos;': return '\'';
            case '&nbsp;': return '\u{a0}';
            case '&lrm;': return '\u{200e}';
            case '&rlm;': return '\u{200f}';
            default: {
                if (match[1] === '#') {
                    const code = match[2] === 'x' || match[2] === 'X' ? parseInt(match.slice(3), 16) : parseInt(match.slice(2), 10);
                    return String.fromCodePoint(code);
                }
                return match;
            }
        }
    });
}/**
 * Parse XML into a JS object with no validation and some failure tolerance
 *
 * @param input - The input XML string
 * @param options - Optional parsing options
 * @returns The parsed XML
 *
 * @group XML
 *
 * @beta
 *
 * @example
 * {@includeCode ../../test/xml/parseXml.test.ts#example}
 */
function parseXml(input, options = {}) {
    let pos = options.pos || 0;
    const length = input.length;
    const keepComments = !!options.keepComments;
    const keepWhitespace = !!options.keepWhitespace;
    const openBracket = '<';
    const openBracketCC = '<'.charCodeAt(0);
    const closeBracket = '>';
    const closeBracketCC = '>'.charCodeAt(0);
    const minusCC = '-'.charCodeAt(0);
    const slashCC = '/'.charCodeAt(0);
    const questionCC = '?'.charCodeAt(0);
    const exclamationCC = '!'.charCodeAt(0);
    const singleQuoteCC = "'".charCodeAt(0);
    const doubleQuoteCC = '"'.charCodeAt(0);
    const openCornerBracketCC = '['.charCodeAt(0);
    const closeCornerBracketCC = ']'.charCodeAt(0);
    const nameSpacer = '\r\n\t>/= ';
    function createTextNode(value, nodeName = '#text') {
        return {
            nodeName,
            nodeValue: value,
            attributes: {},
            childNodes: [],
        };
    }
    /**
     * parsing a list of entries
     */
    function parseChildren(tagName = '') {
        const children = [];
        while (input[pos]) {
            if (input.charCodeAt(pos) == openBracketCC) {
                if (input.charCodeAt(pos + 1) === slashCC) {
                    const closeStart = pos + 2;
                    pos = input.indexOf(closeBracket, pos);
                    if (!input.startsWith(tagName, closeStart)) {
                        const parsedText = input.substring(0, pos).split('\n');
                        throw new Error('Unexpected close tag\nLine: ' + (parsedText.length - 1) +
                            '\nColumn: ' + (parsedText[parsedText.length - 1].length + 1) +
                            '\nChar: ' + input[pos]);
                    }
                    if (pos + 1) {
                        pos += 1;
                    }
                    return children;
                }
                else if (input.charCodeAt(pos + 1) === questionCC) {
                    // xml declaration
                    pos = input.indexOf(closeBracket, pos);
                    pos++;
                    continue;
                }
                else if (input.charCodeAt(pos + 1) === exclamationCC) {
                    if (input.charCodeAt(pos + 2) == minusCC) {
                        // comment support
                        const startCommentPos = pos;
                        while (pos !== -1 && !(input.charCodeAt(pos) === closeBracketCC && input.charCodeAt(pos - 1) == minusCC && input.charCodeAt(pos - 2) == minusCC && pos != -1)) {
                            pos = input.indexOf(closeBracket, pos + 1);
                        }
                        if (pos === -1) {
                            pos = length;
                        }
                        if (keepComments) {
                            children.push(createTextNode(input.substring(startCommentPos, pos + 1), '#comment'));
                        }
                    }
                    else if (input.charCodeAt(pos + 2) === openCornerBracketCC &&
                        input.charCodeAt(pos + 8) === openCornerBracketCC &&
                        input.startsWith('CDATA', pos + 3)) {
                        // cdata
                        const cdataEndIndex = input.indexOf(']]>', pos);
                        if (cdataEndIndex == -1) {
                            children.push(createTextNode(input.substr(pos + 9), '#cdata'));
                            pos = length;
                        }
                        else {
                            children.push(createTextNode(input.substring(pos + 9, cdataEndIndex), '#cdata'));
                            pos = cdataEndIndex + 3;
                        }
                        continue;
                    }
                    else {
                        // doctypesupport
                        const startDoctype = pos + 1;
                        pos += 2;
                        let encapsuled = false;
                        while ((input.charCodeAt(pos) !== closeBracketCC || encapsuled === true) && input[pos]) {
                            if (input.charCodeAt(pos) === openCornerBracketCC) {
                                encapsuled = true;
                            }
                            else if (encapsuled === true && input.charCodeAt(pos) === closeCornerBracketCC) {
                                encapsuled = false;
                            }
                            pos++;
                        }
                        children.push(createTextNode(input.substring(startDoctype, pos), '#doctype'));
                    }
                    pos++;
                    continue;
                }
                const node = parseNode();
                children.push(node);
            }
            else {
                const text = parseText();
                if (keepWhitespace) {
                    if (text.length > 0) {
                        children.push(createTextNode(text));
                    }
                }
                else {
                    const trimmed = text.trim();
                    if (trimmed.length > 0) {
                        children.push(createTextNode(trimmed));
                    }
                }
                pos++;
            }
        }
        return children;
    }
    /**
     * returns the text outside of texts until the first '&lt;'
     */
    function parseText() {
        const start = pos;
        pos = input.indexOf(openBracket, pos) - 1;
        if (pos === -2) {
            pos = length;
        }
        return unescapeHtml(input.slice(start, pos + 1));
    }
    /**
     * returns text until the first nonAlphabetic letter
     */
    function parseName() {
        const start = pos;
        while (nameSpacer.indexOf(input[pos]) === -1 && input[pos]) {
            pos++;
        }
        return input.slice(start, pos);
    }
    /**
     * parses the attributes of a node
     */
    function parseAttributes() {
        const attributes = {};
        // parsing attributes
        while (input.charCodeAt(pos) !== closeBracketCC && input[pos]) {
            const c = input.charCodeAt(pos);
            if ((c > 64 && c < 91) || (c > 96 && c < 123)) {
                const name = parseName();
                let value = '';
                // search beginning of the string
                let code = input.charCodeAt(pos);
                while (code !== singleQuoteCC && code !== doubleQuoteCC) {
                    pos++;
                    code = input.charCodeAt(pos);
                }
                if (code === singleQuoteCC || code === doubleQuoteCC) {
                    value = parseString();
                    if (pos === -1) {
                        throw new Error('Missing closing quote');
                    }
                }
                else {
                    pos--;
                }
                attributes[name] = unescapeHtml(value);
            }
            pos++;
        }
        return attributes;
    }
    /**
     * parses a node
     */
    function parseNode() {
        pos++;
        const nodeName = parseName();
        let localName = nodeName;
        let prefix = null;
        const nsIndex = nodeName.indexOf(':');
        if (nsIndex !== -1) {
            prefix = nodeName.slice(0, nsIndex);
            localName = nodeName.slice(nsIndex + 1);
        }
        const attributes = parseAttributes();
        let childNodes = [];
        // optional parsing of children
        const prev = input.charCodeAt(pos - 1);
        pos++;
        if (prev !== slashCC) {
            childNodes = parseChildren(nodeName);
        }
        return {
            nodeName,
            nodeValue: null,
            attributes,
            childNodes,
            prefix,
            localName,
        };
    }
    /**
     * is parsing a string, that starts with a char and with the same usually ' or "
     */
    function parseString() {
        const startChar = input[pos];
        const startpos = pos + 1;
        pos = input.indexOf(startChar, startpos);
        return input.slice(startpos, pos);
    }
    return {
        nodeName: '#document',
        nodeValue: null,
        childNodes: parseChildren(''),
        attributes: {},
    };
}/**
 * License Challenge.
 *
 * @group DRM
 *
 * @beta
 */
const CHALLENGE = 'Challenge';/**
 * Playready Key Message.
 *
 * @group DRM
 *
 * @beta
 */
const PLAYREADY_KEY_MESSAGE = 'PlayReadyKeyMessage';/**
 * Gets the PlayReady license request from the MediaKeyMessageEvent.
 *
 * @param message - An ArrayBuffer from the content decryption module.
 * @param encoding - The message encoding type. Default is UTF-16.
 * @returns The license request as an ArrayBuffer.
 *
 * @group DRM
 * @beta
 *
 * @example
 * {@includeCode ../../../test/drm/playready/getLicenseRequestFromMessage.test.ts#example}
 */
function getLicenseRequestFromMessage(message, encoding = UTF_16) {
    var _a;
    // If encoding is configured for UTF-16 and the number of bytes is odd,
    // assume an 'unwrapped' raw CDM message.
    if (encoding === UTF_16 && (message === null || message === void 0 ? void 0 : message.byteLength) % 2 === 1) {
        return message;
    }
    const msg = decodeText(message, { encoding });
    const xml = parseXml(msg);
    const playReadyKeyMessage = getElementsByName(xml, PLAYREADY_KEY_MESSAGE)[0];
    if (!playReadyKeyMessage) {
        // The message from the CDM is not wrapped and contains the direct challenge.
        return message;
    }
    const challengeNode = getElementsByName(playReadyKeyMessage, CHALLENGE)[0];
    const challengeValue = (_a = challengeNode === null || challengeNode === void 0 ? void 0 : challengeNode.childNodes[0]) === null || _a === void 0 ? void 0 : _a.nodeValue;
    if (challengeValue) {
        return decodeBase64(challengeValue).buffer;
    }
    return message;
}/**
 * Content-Type header.
 *
 * @group DRM
 *
 * @beta
 */
const CONTENT_TYPE = 'Content-Type';/**
 * Playready HTTP Headers.
 *
 * @group DRM
 *
 * @beta
 */
const HTTP_HEADERS = 'HttpHeaders';/**
 * XML formatted, UTF-8 encoded, text header.
 *
 * @group DRM
 *
 * @beta
 */
const TEXT_XML_UTF8 = 'text/xml; charset=utf-8';/**
 * Gets the PlayReady license request headers from the MediaKeyMessageEvent.
 *
 * @param message - An ArrayBuffer from the content decryption module.
 * @param encoding - The message encoding type. Default is UTF-16.
 * @returns Request headers.
 *
 * @group DRM
 * @beta
 *
 * @example
 * {@includeCode ../../../test/drm/playready/getRequestHeadersFromMessage.test.ts#example}
 */
function getRequestHeadersFromMessage(message, encoding = UTF_16) {
    var _a, _b, _c, _d, _e;
    const headers = {};
    // If message format configured/defaulted to utf-16 AND number of bytes is odd,
    // assume 'unwrapped' raw CDM message.
    if (encoding === UTF_16 && message && message.byteLength % 2 === 1) {
        headers[CONTENT_TYPE] = TEXT_XML_UTF8;
        return headers;
    }
    const msg = decodeText(message, { encoding });
    const xml = parseXml(msg);
    const httpHeaders = (_a = getElementsByName(xml, HTTP_HEADERS)[0]) === null || _a === void 0 ? void 0 : _a.childNodes;
    if (!httpHeaders) {
        return headers;
    }
    for (const header of httpHeaders) {
        const name = (_c = (_b = getElementsByName(header, 'name')[0]) === null || _b === void 0 ? void 0 : _b.childNodes[0]) === null || _c === void 0 ? void 0 : _c.nodeValue;
        const value = (_e = (_d = getElementsByName(header, 'value')[0]) === null || _d === void 0 ? void 0 : _d.childNodes[0]) === null || _e === void 0 ? void 0 : _e.nodeValue;
        if (name && value) {
            headers[name] = value;
        }
    }
    // Some versions of the PlayReady CDM return 'Content' instead of 'Content-Type'.
    // This does not adhere to the W3C spec and license servers may reject the request.
    if (headers.hasOwnProperty('Content')) {
        headers[CONTENT_TYPE] = headers['Content'];
        delete headers['Content'];
    }
    // Set 'Content-Type' header by default if not provided in the the CDM message
    // or if the message is unwrapped.
    if (!headers.hasOwnProperty(CONTENT_TYPE)) {
        headers[CONTENT_TYPE] = TEXT_XML_UTF8;
    }
    return headers;
}/**
 * Converts a 16-byte key ID from little-endian to big-endian format.
 *
 * @param keyId - The little-endian key ID to convert.
 * @returns The big-endian representation of the key ID.
 *
 * @group DRM
 * @beta
 *
 * @example
 * {@includeCode ../../../test/drm/playready/toBigEndianKeyId.test.ts#example}
 */
function toBigEndianKeyId(keyId) {
    if (keyId.byteLength !== 16) {
        return keyId;
    }
    const buffer = keyId.slice(0);
    const dataView = new DataView(buffer);
    dataView.setUint32(0, dataView.getUint32(0, true), false);
    dataView.setUint16(4, dataView.getUint16(4, true), false);
    dataView.setUint16(6, dataView.getUint16(6, true), false);
    return buffer;
}/**
 * Clear Key System.
 *
 * @group DRM
 *
 * @beta
 */
const CLEAR_KEY_SYSTEM = 'org.w3.clearkey';/**
 * Clear Key UUID.
 *
 * @group DRM
 *
 * @beta
 */
const CLEAR_KEY_UUID = 'e2719d58-a985-b3c9-781a-b030af78d30e';/**
 * Encryption schemes.
 *
 * @group DRM
 *
 * @beta
 */
const EncryptionScheme = {
    CENC: CENC,
    CBCS: CBCS,
};/**
 * Expired Media Key Status.
 *
 * @group DRM
 *
 * @beta
 */
const EXPIRED = 'expired';/**
 * FairPlay Key System.
 *
 * @group DRM
 *
 * @beta
 */
const FAIRPLAY_KEY_SYSTEM = 'com.apple.fps.1_0';/**
 * FairPlay UUID.
 *
 * @group DRM
 *
 * @beta
 */
const FAIRPLAY_UUID = '29701fe4-3cc7-4a34-8c5b-ae90c7439a47';/**
 * Hardware Secure All Robustness.
 *
 * @group DRM
 *
 * @beta
 */
const HW_SECURE_ALL = 'HW_SECURE_ALL';/**
 * Hardware Secure Crypto Robustness.
 *
 * @group DRM
 *
 * @beta
 */
const HW_SECURE_CRYPTO = 'HW_SECURE_CRYPTO';/**
 * Hardware Secure Decode Robustness.
 *
 * @group DRM
 *
 * @beta
 */
const HW_SECURE_DECODE = 'HW_SECURE_DECODE';/**
 * Individualization Request.
 *
 * @group DRM
 *
 * @beta
 */
const INDIVIDUALIZATION_REQUEST = 'individualization-request';/**
 * Key IDs.
 *
 * @group DRM
 *
 * @beta
 */
const KEYIDS = 'keyids';/**
 * WEBM.
 *
 * @group DRM
 *
 * @beta
 */
const WEBM = 'webm';/**
 * Initialization Data Type.
 *
 * @group DRM
 *
 * @beta
 */
const InitializationDataType = {
    CENC: CENC,
    CBCS: CBCS,
    KEYIDS: KEYIDS,
    WEBM: WEBM,
};/**
 * Internal Error Media Key Status.
 *
 * @group DRM
 *
 * @beta
 */
const INTERNAL_ERROR = 'internal-error';/**
 * License Acquisition.
 *
 * @group DRM
 *
 * @beta
 */
const LICENSE_ACQUISITION = 'LicenseAcquisition';/**
 * License Release.
 *
 * @group DRM
 *
 * @beta
 */
const LICENSE_RELEASE = 'license-release';/**
 * License Renewal.
 *
 * @group DRM
 *
 * @beta
 */
const LICENSE_RENEWAL = 'license-renewal';/**
 * License Request.
 *
 * @group DRM
 *
 * @beta
 */
const LICENSE_REQUEST = 'license-request';/**
 * Media Key Message Types.
 *
 * @group DRM
 *
 * @beta
 */
const MediaKeyMessageType = {
    LICENSE_REQUEST: LICENSE_REQUEST,
    LICENSE_RENEWAL: LICENSE_RENEWAL,
    LICENSE_RELEASE: LICENSE_RELEASE,
    INDIVIDUALIZATION_REQUEST: INDIVIDUALIZATION_REQUEST,
};/**
 * Output Downscaled Media Key Status.
 *
 * @group DRM
 *
 * @beta
 */
const OUTPUT_DOWNSCALED = 'output-downscaled';/**
 * Output Restricted Media Key Status.
 *
 * @group DRM
 *
 * @beta
 */
const OUTPUT_RESTRICTED = 'output-restricted';/**
 * Released Media Key Status.
 *
 * @group DRM
 *
 * @beta
 */
const RELEASED = 'released';/**
 * Status Pending Media Key Status.
 *
 * @group DRM
 *
 * @beta
 */
const STATUS_PENDING = 'status-pending';/**
 * Usable Media Key Status.
 *
 * @group DRM
 *
 * @beta
 */
const USABLE = 'usable';/**
 * Media Key Statuses.
 *
 * @group DRM
 *
 * @beta
 */
const MediaKeyStatus = {
    USABLE: USABLE,
    EXPIRED: EXPIRED,
    RELEASED: RELEASED,
    OUTPUT_RESTRICTED: OUTPUT_RESTRICTED,
    OUTPUT_DOWNSCALED: OUTPUT_DOWNSCALED,
    STATUS_PENDING: STATUS_PENDING,
    INTERNAL_ERROR: INTERNAL_ERROR,
};/**
 * Playready Key System.
 *
 * @group DRM
 *
 * @beta
 */
const PLAYREADY_KEY_SYSTEM = 'com.microsoft.playready';/**
 * Playready Recommendation Key System.
 *
 * @group DRM
 *
 * @beta
 */
const PLAYREADY_RECOMMENDATION_KEY_SYSTEM = 'com.microsoft.playready.recommendation';/**
 * Playready UUID.
 *
 * @group DRM
 *
 * @beta
 */
const PLAYREADY_UUID = '9a04f079-9840-4286-ab92-e65be0885f95';/**
 * Software Secure Crypto Robustness.
 *
 * @group DRM
 *
 * @beta
 */
const SW_SECURE_CRYPTO = 'SW_SECURE_CRYPTO';/**
 * Software Secure Decode Robustness.
 *
 * @group DRM
 *
 * @beta
 */
const SW_SECURE_DECODE = 'SW_SECURE_DECODE';/**
 * W3C Clear Key UUID.
 *
 * @group DRM
 *
 * @beta
 */
const W3C_CLEAR_KEY_UUID = '1077efec-c0b2-4d02-ace3-3c1e52e2fb4b';/**
 * Widevine Key System.
 *
 * @group DRM
 *
 * @beta
 */
const WIDEVINE_KEY_SYSTEM = 'com.widevine.alpha';/**
 * Widevine UUID.
 *
 * @group DRM
 *
 * @beta
 */
const WIDEVINE_UUID = 'edef8ba9-79d6-4ace-a3c8-27dcd51d21ed';/**
 * Widevine Robustness.
 *
 * @group DRM
 *
 * @beta
 */
const WidevineRobustness = {
    SW_SECURE_CRYPTO: SW_SECURE_CRYPTO,
    SW_SECURE_DECODE: SW_SECURE_DECODE,
    HW_SECURE_CRYPTO: HW_SECURE_CRYPTO,
    HW_SECURE_DECODE: HW_SECURE_DECODE,
    HW_SECURE_ALL: HW_SECURE_ALL,
};/**
 * Returns true if an ID3 header can be found at offset in data
 *
 * @param data - The data to search in
 * @param offset - The offset at which to start searching
 *
 * @returns `true` if an ID3 header is found
 *
 * @internal
 *
 * @group ID3
 */
function isId3Header(data, offset) {
    /*
     * http://id3.org/id3v2.3.0
     * [0]     = 'I'
     * [1]     = 'D'
     * [2]     = '3'
     * [3,4]   = {Version}
     * [5]     = {Flags}
     * [6-9]   = {ID3 Size}
     *
     * An ID3v2 tag can be detected with the following pattern:
     *  $49 44 33 yy yy xx zz zz zz zz
     * Where yy is less than $FF, xx is the 'flags' byte and zz is less than $80
     */
    if (offset + 10 <= data.length) {
        // look for 'ID3' identifier
        if (data[offset] === 0x49 &&
            data[offset + 1] === 0x44 &&
            data[offset + 2] === 0x33) {
            // check version is within range
            if (data[offset + 3] < 0xff && data[offset + 4] < 0xff) {
                // check size is within range
                if (data[offset + 6] < 0x80 &&
                    data[offset + 7] < 0x80 &&
                    data[offset + 8] < 0x80 &&
                    data[offset + 9] < 0x80) {
                    return true;
                }
            }
        }
    }
    return false;
}/**
 * Read ID3 size
 *
 * @param data - The data to read from
 * @param offset - The offset at which to start reading
 *
 * @returns The size
 *
 * @internal
 *
 * @group ID3
 */
function readId3Size(data, offset) {
    let size = 0;
    size = (data[offset] & 0x7f) << 21;
    size |= (data[offset + 1] & 0x7f) << 14;
    size |= (data[offset + 2] & 0x7f) << 7;
    size |= data[offset + 3] & 0x7f;
    return size;
}/**
 * Checks if the given data contains an ID3 tag.
 *
 * @param data - The data to check
 * @param offset - The offset at which to start checking
 *
 * @returns `true` if an ID3 tag is found
 *
 * @group ID3
 *
 * @beta
 */
function canParseId3(data, offset) {
    return (isId3Header(data, offset) &&
        readId3Size(data, offset + 6) + 10 <= data.length - offset);
}/**
 * Returns true if an ID3 footer can be found at offset in data
 *
 * @param data - The data to search in
 * @param offset - The offset at which to start searching
 *
 * @returns `true` if an ID3 footer is found
 *
 * @internal
 *
 * @group ID3
 */
function isId3Footer(data, offset) {
    /*
     * The footer is a copy of the header, but with a different identifier
     */
    if (offset + 10 <= data.length) {
        // look for '3DI' identifier
        if (data[offset] === 0x33 &&
            data[offset + 1] === 0x44 &&
            data[offset + 2] === 0x49) {
            // check version is within range
            if (data[offset + 3] < 0xff && data[offset + 4] < 0xff) {
                // check size is within range
                if (data[offset + 6] < 0x80 &&
                    data[offset + 7] < 0x80 &&
                    data[offset + 8] < 0x80 &&
                    data[offset + 9] < 0x80) {
                    return true;
                }
            }
        }
    }
    return false;
}/**
 * Returns any adjacent ID3 tags found in data starting at offset, as one block of data
 *
 * @param data - The data to search in
 * @param offset - The offset at which to start searching
 *
 * @returns The block of data containing any ID3 tags found
 * or `undefined` if no header is found at the starting offset
 *
 * @internal
 *
 * @group ID3
 */
function getId3Data(data, offset) {
    const front = offset;
    let length = 0;
    while (isId3Header(data, offset)) {
        // ID3 header is 10 bytes
        length += 10;
        const size = readId3Size(data, offset + 6);
        length += size;
        if (isId3Footer(data, offset + 10)) {
            // ID3 footer is 10 bytes
            length += 10;
        }
        offset += length;
    }
    if (length > 0) {
        return data.subarray(front, front + length);
    }
    return undefined;
}/**
 * Converts a UTF-8 array to a string.
 *
 * @param array - The UTF-8 array to convert
 * @param exitOnNull - Whether to exit on the first null byte
 *
 * @returns The string
 *
 * @group Utils
 *
 * @beta
 *
 * @deprecated This function is a special case of {@link decodeText} that is used by the ID3 library to decode UTF-8 encoded strings. Use {@link decodeText} instead.
 */
function utf8ArrayToStr(array, exitOnNull = false) {
    const byteLength = exitOnNull ? array.indexOf(0) : array.length;
    const view = new DataView(array.buffer, array.byteOffset, byteLength);
    const result = decodeText(view, { encoding: UTF_8 });
    return exitOnNull ? result : result.replace(/\0/g, '');
}function toArrayBuffer(view) {
    if (view instanceof ArrayBuffer) {
        return view;
    }
    else {
        if (view.byteOffset == 0 && view.byteLength == view.buffer.byteLength) {
            // This is a TypedArray over the whole buffer.
            return view.buffer;
        }
        // This is a 'view' on the buffer.  Create a new buffer that only contains
        // the data.  Note that since this isn't an ArrayBuffer, the 'new' call
        // will allocate a new buffer to hold the copy.
        return new Uint8Array(view).buffer;
    }
}function toUint8(data, offset = 0, length = Infinity) {
    return view(data, offset, length, Uint8Array);
}
function view(data, offset, length, Type) {
    const buffer = unsafeGetArrayBuffer(data);
    let bytesPerElement = 1;
    if ('BYTES_PER_ELEMENT' in Type) {
        bytesPerElement = Type.BYTES_PER_ELEMENT;
    }
    // Absolute end of the |data| view within |buffer|.
    const dataOffset = isArrayBufferView(data) ? data.byteOffset : 0;
    const dataEnd = ((dataOffset) + data.byteLength) / bytesPerElement;
    // Absolute start of the result within |buffer|.
    const rawStart = ((dataOffset) + offset) / bytesPerElement;
    const start = Math.floor(Math.max(0, Math.min(rawStart, dataEnd)));
    // Absolute end of the result within |buffer|.
    const end = Math.floor(Math.min(start + Math.max(length, 0), dataEnd));
    return new Type(buffer, start, end - start);
}
function unsafeGetArrayBuffer(view) {
    if (view instanceof ArrayBuffer) {
        return view;
    }
    else {
        return view.buffer;
    }
}
function isArrayBufferView(obj) {
    return obj && obj.buffer instanceof ArrayBuffer && obj.byteLength !== undefined && obj.byteOffset !== undefined;
}function decodeId3ImageFrame(frame) {
    const metadataFrame = {
        key: frame.type,
        description: '',
        data: '',
        mimeType: null,
        pictureType: null,
    };
    const utf8Encoding = 0x03;
    if (frame.size < 2) {
        return undefined;
    }
    if (frame.data[0] !== utf8Encoding) {
        console.log('Ignore frame with unrecognized character ' + 'encoding');
        return undefined;
    }
    const mimeTypeEndIndex = frame.data.subarray(1).indexOf(0);
    if (mimeTypeEndIndex === -1) {
        return undefined;
    }
    const mimeType = utf8ArrayToStr(toUint8(frame.data, 1, mimeTypeEndIndex));
    const pictureType = frame.data[2 + mimeTypeEndIndex];
    const descriptionEndIndex = frame.data
        .subarray(3 + mimeTypeEndIndex)
        .indexOf(0);
    if (descriptionEndIndex === -1) {
        return undefined;
    }
    const description = utf8ArrayToStr(toUint8(frame.data, 3 + mimeTypeEndIndex, descriptionEndIndex));
    let data;
    if (mimeType === '-->') {
        data = utf8ArrayToStr(toUint8(frame.data, 4 + mimeTypeEndIndex + descriptionEndIndex));
    }
    else {
        data = toArrayBuffer(frame.data.subarray(4 + mimeTypeEndIndex + descriptionEndIndex));
    }
    metadataFrame.mimeType = mimeType;
    metadataFrame.pictureType = pictureType;
    metadataFrame.description = description;
    metadataFrame.data = data;
    return metadataFrame;
}/**
 * Decode an ID3 PRIV frame.
 *
 * @param frame - the ID3 PRIV frame
 *
 * @returns The decoded ID3 PRIV frame
 *
 * @internal
 *
 * @group ID3
 */
function decodeId3PrivFrame(frame) {
    /*
    Format: <text string>\0<binary data>
    */
    if (frame.size < 2) {
        return undefined;
    }
    const owner = utf8ArrayToStr(frame.data, true);
    const privateData = new Uint8Array(frame.data.subarray(owner.length + 1));
    return { key: frame.type, info: owner, data: privateData.buffer };
}/**
 * Decodes an ID3 text frame
 *
 * @param frame - the ID3 text frame
 *
 * @returns The decoded ID3 text frame
 *
 * @internal
 *
 * @group ID3
 */
function decodeId3TextFrame(frame) {
    if (frame.size < 2) {
        return undefined;
    }
    if (frame.type === 'TXXX') {
        /*
        Format:
        [0]   = {Text Encoding}
        [1-?] = {Description}\0{Value}
        */
        let index = 1;
        const { data } = frame;
        const description = utf8ArrayToStr(data.subarray(index), true);
        index += description.length + 1;
        const value = utf8ArrayToStr(data.subarray(index));
        return { key: frame.type, info: description, data: value };
    }
    /*
    Format:
    [0]   = {Text Encoding}
    [1-?] = {Value}
    */
    const text = utf8ArrayToStr(frame.data.subarray(1));
    return { key: frame.type, info: '', data: text };
}/**
 * Decode a URL frame
 *
 * @param frame - the ID3 URL frame
 *
 * @returns The decoded ID3 URL frame
 *
 * @internal
 *
 * @group ID3
 */
function decodeId3UrlFrame(frame) {
    if (frame.type === 'WXXX') {
        /*
        Format:
        [0]   = {Text Encoding}
        [1-?] = {Description}\0{URL}
        */
        if (frame.size < 2) {
            return undefined;
        }
        let index = 1;
        const description = utf8ArrayToStr(frame.data.subarray(index), true);
        index += description.length + 1;
        const value = utf8ArrayToStr(frame.data.subarray(index));
        return { key: frame.type, info: description, data: value };
    }
    /*
    Format:
    [0-?] = {URL}
    */
    const url = utf8ArrayToStr(frame.data);
    return { key: frame.type, info: '', data: url };
}/**
 * Decode an ID3 frame.
 *
 * @param frame - the ID3 frame
 *
 * @returns The decoded ID3 frame
 *
 * @internal
 *
 * @group ID3
 */
function decodeId3Frame(frame) {
    if (frame.type === 'PRIV') {
        return decodeId3PrivFrame(frame);
    }
    else if (frame.type[0] === 'W') {
        return decodeId3UrlFrame(frame);
    }
    else if (frame.type === 'APIC') {
        return decodeId3ImageFrame(frame);
    }
    return decodeId3TextFrame(frame);
}/**
 * Returns the data of an ID3 frame.
 *
 * @param data - The data to read from
 *
 * @returns The data of the ID3 frame
 *
 * @internal
 *
 * @group ID3
 */
function getId3FrameData(data) {
    /*
    Frame ID       $xx xx xx xx (four characters)
    Size           $xx xx xx xx
    Flags          $xx xx
    */
    const type = String.fromCharCode(data[0], data[1], data[2], data[3]);
    const size = readId3Size(data, 4);
    // skip frame id, size, and flags
    const offset = 10;
    return { type, size, data: data.subarray(offset, offset + size) };
}const HEADER_FOOTER_SIZE = 10;
const FRAME_SIZE = 10;
/**
 * Returns an array of ID3 frames found in all the ID3 tags in the id3Data
 *
 * @param id3Data - The ID3 data containing one or more ID3 tags
 *
 * @returns Array of ID3 frame objects
 *
 * @group ID3
 *
 * @beta
 */
function getId3Frames(id3Data) {
    let offset = 0;
    const frames = [];
    while (isId3Header(id3Data, offset)) {
        const size = readId3Size(id3Data, offset + 6);
        if ((id3Data[offset + 5] >> 6) & 1) {
            // skip extended header
            offset += HEADER_FOOTER_SIZE;
        }
        // skip past ID3 header
        offset += HEADER_FOOTER_SIZE;
        const end = offset + size;
        // loop through frames in the ID3 tag
        while (offset + FRAME_SIZE < end) {
            const frameData = getId3FrameData(id3Data.subarray(offset));
            const frame = decodeId3Frame(frameData);
            if (frame) {
                frames.push(frame);
            }
            // skip frame header and frame data
            offset += frameData.size + HEADER_FOOTER_SIZE;
        }
        if (isId3Footer(id3Data, offset)) {
            offset += HEADER_FOOTER_SIZE;
        }
    }
    return frames;
}/**
 * Returns true if the ID3 frame is an Elementary Stream timestamp frame
 *
 * @param frame - the ID3 frame
 *
 * @returns `true` if the ID3 frame is an Elementary Stream timestamp frame
 *
 * @internal
 *
 * @group ID3
 */
function isId3TimestampFrame(frame) {
    return (frame &&
        frame.key === 'PRIV' &&
        frame.info === 'com.apple.streaming.transportStreamTimestamp');
}/**
 * Read a 33 bit timestamp from an ID3 frame.
 *
 * @param timeStampFrame - the ID3 frame
 *
 * @returns The timestamp
 *
 * @internal
 *
 * @group ID3
 */
function readId3Timestamp(timeStampFrame) {
    if (timeStampFrame.data.byteLength === 8) {
        const data = new Uint8Array(timeStampFrame.data);
        // timestamp is 33 bit expressed as a big-endian eight-octet number,
        // with the upper 31 bits set to zero.
        const pts33Bit = data[3] & 0x1;
        let timestamp = (data[4] << 23) + (data[5] << 15) + (data[6] << 7) + data[7];
        timestamp /= 45;
        if (pts33Bit) {
            timestamp += 47721858.84;
        } // 2^32 / 90
        return Math.round(timestamp);
    }
    return undefined;
}/**
 * Searches for the Elementary Stream timestamp found in the ID3 data chunk
 *
 * @param data - Block of data containing one or more ID3 tags
 *
 * @returns The timestamp
 *
 * @group ID3
 *
 * @beta
 */
function getId3Timestamp(data) {
    const frames = getId3Frames(data);
    for (let i = 0; i < frames.length; i++) {
        const frame = frames[i];
        if (isId3TimestampFrame(frame)) {
            return readId3Timestamp(frame);
        }
    }
    return undefined;
}/**
 * The scheme ID URI for ID3 tags.
 *
 * @group ID3
 *
 * @beta
 *
 * @see {@link https://aomediacodec.github.io/id3-emsg/ | Carriage of ID3 Timed Metadata in the Common Media Application Format (CMAF)}
 * @see {@link https://developer.apple.com/documentation/http-live-streaming/about-the-common-media-application-format-with-http-live-streaming-hls#CMAF-Tracks-Segments-Headers-and-Fragments | CMAF Tracks, Segments, Headers, and Fragments}
 */
const ID3_SCHEME_ID_URI = 'https://aomedia.org/emsg/ID3';
const DURATION = /^([-])?P(?:([\d.]*)Y)?(?:([\d.]*)M)?(?:([\d.]*)D)?T?(?:([\d.]*)H)?(?:([\d.]*)M)?(?:([\d.]*)S)?$/;
const SECONDS_IN_YEAR = 365 * 24 * 60 * 60;
const SECONDS_IN_MONTH = 30 * 24 * 60 * 60;
const SECONDS_IN_DAY = 24 * 60 * 60;
const SECONDS_IN_HOUR = 60 * 60;
const SECONDS_IN_MIN = 60;
/**
 * Decode an ISO 8601 duration string into seconds.
 *
 * @param isoDuration - ISO 8601 duration string.
 * @returns Duration in seconds.
 *
 * @group ISO 8601
 * @beta
 *
 * @example
 * {@includeCode ../../../test/iso/8601/decodeIso8601Duration.test.ts#example}
 */
function decodeIso8601Duration(isoDuration) {
    const match = DURATION.exec(isoDuration);
    if (!match) {
        return NaN;
    }
    const duration = Number(match[2] || 0) * SECONDS_IN_YEAR +
        Number(match[3] || 0) * SECONDS_IN_MONTH +
        Number(match[4] || 0) * SECONDS_IN_DAY +
        Number(match[5] || 0) * SECONDS_IN_HOUR +
        Number(match[6] || 0) * SECONDS_IN_MIN +
        Number(match[7] || 0);
    if (!isFinite(duration)) {
        return NaN;
    }
    return match[1] === undefined ? duration : -duration;
}/**
 * Encode a duration in seconds to an ISO 8601 duration string.
 *
 * @param duration - Duration in seconds.
 * @returns ISO 8601 duration string.
 *
 * @group ISO 8601
 * @beta
 *
 * @example
 * {@includeCode ../../../test/iso/8601/encodeIso8601Duration.test.ts#example}
 */
function encodeIso8601Duration(duration) {
    if (!isFinite(duration)) {
        return 'PT';
    }
    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration % 3600) / 60);
    const seconds = duration % 60;
    if (hours > 0) {
        return `PT${hours}H${minutes}M${seconds}S`;
    }
    else if (minutes > 0) {
        return `PT${minutes}M${seconds}S`;
    }
    return `PT${seconds}S`;
}const CONTAINERS = [
    'dinf',
    'edts',
    'enca',
    'encv',
    'grpl',
    'mdia',
    'meco',
    'meta',
    'mfra',
    'minf',
    'moof',
    'moov',
    'mvex',
    'prsl',
    'schi',
    'sinf',
    'stbl',
    'strk',
    'traf',
    'trak',
    'tref',
    'udta',
    'vttc',
];/**
 * The data field type
 *
 * @group ISOBMFF
 *
 * @beta
 */
const DATA = 'data';/**
 * The integer field type
 *
 * @group ISOBMFF
 *
 * @beta
 */
const INT = 'int';/**
 * The string field type
 *
 * @group ISOBMFF
 *
 * @beta
 */
const STRING = 'string';/**
 * The template field type
 *
 * @group ISOBMFF
 *
 * @beta
 */
const TEMPLATE = 'template';/**
 * The unsigned integer field type
 *
 * @group ISOBMFF
 *
 * @beta
 */
const UINT = 'uint';/**
 * The UTF8 field type
 *
 * @group ISOBMFF
 *
 * @beta
 */
const UTF8 = 'utf8';function readData(dataView, offset, size) {
    const length = (size > 0) ? size : (dataView.byteLength - (offset - dataView.byteOffset));
    return new Uint8Array(dataView.buffer, offset, Math.max(length, 0));
}function readInt(dataView, offset, size) {
    let result = NaN;
    const cursor = offset - dataView.byteOffset;
    switch (size) {
        case 1:
            result = dataView.getInt8(cursor);
            break;
        case 2:
            result = dataView.getInt16(cursor);
            break;
        case 4:
            result = dataView.getInt32(cursor);
            break;
        case 8:
            // Warning: JavaScript cannot handle 64-bit integers natively.
            // This will give unexpected results for integers >= 2^53
            const s1 = dataView.getInt32(cursor);
            const s2 = dataView.getInt32(cursor + 4);
            result = (s1 * Math.pow(2, 32)) + s2;
            break;
    }
    return result;
}function readUint(dataView, offset, size) {
    const cursor = offset - dataView.byteOffset;
    let value = NaN;
    let s1;
    let s2;
    switch (size) {
        case 1:
            value = dataView.getUint8(cursor);
            break;
        case 2:
            value = dataView.getUint16(cursor);
            break;
        case 3:
            s1 = dataView.getUint16(cursor);
            s2 = dataView.getUint8(cursor + 2);
            value = (s1 << 8) + s2;
            break;
        case 4:
            value = dataView.getUint32(cursor);
            break;
        case 8:
            // Warning: JavaScript cannot handle 64-bit integers natively.
            // This will give unexpected results for integers >= 2^53
            s1 = dataView.getUint32(cursor);
            s2 = dataView.getUint32(cursor + 4);
            value = (s1 * Math.pow(2, 32)) + s2;
            break;
    }
    return value;
}function readString(dataView, offset, length) {
    let str = '';
    for (let c = 0; c < length; c++) {
        const cursor = offset + c;
        const char = readUint(dataView, cursor, 1);
        str += String.fromCharCode(char);
    }
    return str;
}function readTemplate(dataView, offset, size) {
    const half = size / 2;
    const pre = readUint(dataView, offset, half);
    const post = readUint(dataView, offset + half, half);
    return pre + (post / Math.pow(2, half));
}function readTerminatedString(dataView, offset) {
    let str = '';
    let cursor = offset;
    while (cursor - dataView.byteOffset < dataView.byteLength) {
        const char = readUint(dataView, cursor, 1);
        if (char === 0) {
            break;
        }
        str += String.fromCharCode(char);
        cursor++;
    }
    return str;
}function readUtf8String(dataView, offset) {
    const length = dataView.byteLength - (offset - dataView.byteOffset);
    return (length > 0) ? decodeText(new DataView(dataView.buffer, offset, length), { encoding: UTF_8 }) : '';
}function readUtf8TerminatedString(dataView, offset) {
    const length = dataView.byteLength - (offset - dataView.byteOffset);
    let data = '';
    if (length > 0) {
        const view = new DataView(dataView.buffer, offset, length);
        let l = 0;
        for (; l < length; l++) {
            if (view.getUint8(l) === 0) {
                break;
            }
        }
        // remap the Dataview with the actual length
        data = decodeText(new DataView(dataView.buffer, offset, l), { encoding: UTF_8 });
    }
    return data;
}/**
 * ISO BMFF data view. Similar to DataView, but with additional methods for reading ISO BMFF data.
 * It implements the iterator protocol, so it can be used in a for...of loop.
 *
 * @group ISOBMFF
 *
 * @beta
 */
class IsoView {
    /**
     * Creates a new IsoView instance. Similar to DataView, but with additional
     * methods for reading ISO BMFF data. It implements the iterator protocol,
     * so it can be used in a for...of loop.
     *
     * @param raw - The raw data to view.
     * @param config - The configuration for the IsoView.
     */
    constructor(raw, config) {
        this.truncated = false;
        /**
         * Creates a new IsoView instance with a slice of the current data view.
         *
         * @param size - The size of the slice.
         * @returns A new IsoView instance.
         */
        this.slice = (size) => {
            const dataView = new DataView(this.dataView.buffer, this.offset, size);
            this.offset += size;
            return new IsoView(dataView, this.config);
        };
        this.read = (type, size = 0) => {
            // TODO: Change all sizes from bits to bytes
            const { dataView, offset } = this;
            let result;
            let cursor = size;
            switch (type) {
                case UINT:
                    result = readUint(dataView, offset, size);
                    break;
                case INT:
                    result = readInt(dataView, offset, size);
                    break;
                case TEMPLATE:
                    result = readTemplate(dataView, offset, size);
                    break;
                case STRING:
                    if (size === -1) {
                        result = readTerminatedString(dataView, offset);
                        cursor = result.length + 1;
                    }
                    else {
                        result = readString(dataView, offset, size);
                    }
                    break;
                case DATA:
                    result = readData(dataView, offset, size);
                    cursor = result.length;
                    break;
                case UTF8:
                    if (size === -1) {
                        result = readUtf8TerminatedString(dataView, offset);
                        cursor = result.length + 1;
                    }
                    else {
                        result = readUtf8String(dataView, offset);
                    }
                    break;
                default:
                    result = -1;
            }
            this.offset += cursor;
            return result;
        };
        /**
         * Reads a unsigned integer from the data view.
         *
         * @param size - The size of the integer in bytes.
         * @returns The unsigned integer.
         */
        this.readUint = (size) => {
            return this.read(UINT, size);
        };
        /**
         * Reads a signed integer from the data view.
         *
         * @param size - The size of the integer in bytes.
         * @returns The signed integer.
         */
        this.readInt = (size) => {
            return this.read(INT, size);
        };
        /**
         * Reads a string from the data view.
         *
         * @param size - The size of the string in bytes.
         * @returns The string.
         */
        this.readString = (size) => {
            return this.read(STRING, size);
        };
        /**
         * Reads a template from the data view.
         *
         * @param size - The size of the template in bytes.
         * @returns The template.
         */
        this.readTemplate = (size) => {
            return this.read(TEMPLATE, size);
        };
        /**
         * Reads a byte array from the data view.
         *
         * @param size - The size of the data in bytes.
         * @returns The data.
         */
        this.readData = (size) => {
            return this.read(DATA, size);
        };
        /**
         * Reads a UTF-8 string from the data view.
         *
         * @param size - The size of the string in bytes.
         * @returns The UTF-8 string.
         */
        this.readUtf8 = (size) => {
            return this.read(UTF8, size);
        };
        /**
         * Reads a full box from the data view.
         *
         * @returns The full box.
         */
        this.readFullBox = () => {
            return {
                version: this.readUint(1),
                flags: this.readUint(3),
            };
        };
        /**
         * Reads an array of values from the data view.
         *
         * @param type - The type of the values.
         * @param size - The size of the values in bytes.
         * @param length - The number of values to read.
         * @returns The array of values.
         */
        this.readArray = (type, size, length) => {
            const value = [];
            for (let i = 0; i < length; i++) {
                value.push(this.read(type, size));
            }
            return value;
        };
        /**
         * Reads a raw box from the data view.
         *
         * @returns The box.
         */
        this.readBox = () => {
            const { dataView, offset } = this;
            // read box size and type without advancing the cursor in case the box is truncated
            let cursor = 0;
            const box = {
                size: readUint(dataView, offset, 4),
                type: readString(dataView, offset + 4, 4),
            };
            cursor += 8;
            if (box.size === 1) {
                box.largesize = readUint(dataView, offset + cursor, 8);
                cursor += 8;
            }
            const actualSize = box.largesize || box.size;
            if (this.cursor + actualSize > dataView.byteLength) {
                this.truncated = true;
                throw new Error('Truncated box');
            }
            this.offset += cursor;
            if (box.type === 'uuid') {
                box.usertype = this.readArray('uint', 1, 16);
            }
            const viewSize = box.size === 0 ? this.bytesRemaining : actualSize - cursor;
            box.data = this.slice(viewSize);
            return box;
        };
        /**
         * Reads a number of boxes from the data view.
         *
         * @param length - The number of boxes to read.
         * @returns The boxes.
         */
        this.readBoxes = (length) => {
            const result = [];
            for (const box of this) {
                result.push(box);
                if (length > 0 && result.length >= length) {
                    break;
                }
            }
            return result;
        };
        /**
         * Reads a number of entries from the data view.
         *
         * @param length - The number of entries to read.
         * @param map - The function to map the entries.
         * @returns The entries.
         */
        this.readEntries = (length, map) => {
            const result = [];
            for (let i = 0; i < length; i++) {
                result.push(map());
            }
            return result;
        };
        this.dataView = (raw instanceof ArrayBuffer) ? new DataView(raw) : (raw instanceof Uint8Array) ? new DataView(raw.buffer, raw.byteOffset, raw.byteLength) : raw;
        this.offset = this.dataView.byteOffset;
        this.config = config || { recursive: false, parsers: {} };
    }
    /**
     * The current byteoffset in the data view.
     */
    get cursor() {
        return this.offset - this.dataView.byteOffset;
    }
    /**
     * Whether the end of the data view has been reached.
     */
    get done() {
        return this.cursor >= this.dataView.byteLength || this.truncated;
    }
    /**
     * The number of bytes remaining in the data view.
     */
    get bytesRemaining() {
        return this.dataView.byteLength - this.cursor;
    }
    /**
     * Iterates over the boxes in the data view.
     *
     * @returns A generator of boxes.
     */
    *[Symbol.iterator]() {
        const { parsers = {}, recursive = false } = this.config;
        while (!this.done) {
            try {
                const { type, data, ...rest } = this.readBox();
                const box = { type, ...rest };
                const parser = parsers[type] || parsers[type.trim()]; // url and urn boxes have a trailing space in their type field
                if (parser) {
                    Object.assign(box, parser(data, this.config));
                }
                box.view = data;
                if (CONTAINERS.includes(type)) {
                    const boxes = [];
                    for (const child of data) {
                        if (recursive) {
                            yield child;
                        }
                        boxes.push(child);
                    }
                    box.boxes = boxes;
                }
                yield box;
            }
            catch (error) {
                break;
            }
        }
    }
}/**
 * Create an IsoView from a raw ISO data.
 *
 * @param raw - The raw ISO data
 * @param config - The configuration for the IsoView
 *
 * @returns The created IsoView
 *
 * @group ISOBMFF
 *
 * @beta
 */
function createIsoView(raw, config) {
    return new IsoView(raw, config);
}function filter(iterator, fn, recursive, result) {
    for (const box of iterator) {
        if (fn(box)) {
            result.push(box);
        }
        const { boxes } = box;
        if (recursive && Array.isArray(boxes)) {
            filter(boxes, fn, recursive, result);
        }
    }
    return result;
}
/**
 * Filters boxes based on the given filter function.
 *
 * @param raw - The raw boxes to filter.
 * @param config - The box parser configuration.
 * @param fn - The filter function.
 * @returns The filtered boxes.
 *
 * @group ISOBMFF
 * @beta
 */
function filterBoxes(raw, fn, config) {
    var _a;
    if (raw instanceof DataView || raw instanceof Uint8Array || raw instanceof ArrayBuffer) {
        raw = createIsoView(raw, { ...config, recursive: false });
    }
    const recursive = (_a = config === null || config === void 0 ? void 0 : config.recursive) !== null && _a !== void 0 ? _a : true;
    return filter(raw, fn, recursive, []);
}/**
 * Filter boxes by type from an IsoView
 *
 * @param type - The type(s) of boxes to filter
 * @param raw - The raw ISO data
 * @param config - The configuration for the IsoView
 *
 * @returns The filtered boxes
 *
 * @group ISOBMFF
 *
 * @beta
 */
function filterBoxesByType(raw, type, config = {}) {
    if (!Array.isArray(type)) {
        type = [type];
    }
    return filterBoxes(raw, (box) => type.includes(box.type), config);
}function find(iterator, recursive, fn) {
    for (const box of iterator) {
        if (fn(box)) {
            return box;
        }
        const { boxes } = box;
        if (recursive && Array.isArray(boxes)) {
            const result = find(boxes, recursive, fn);
            if (result) {
                return result;
            }
        }
    }
    return null;
}
/**
 * Find a box from an IsoView that matches a filter function
 *
 * @param raw - The raw ISO data
 * @param config - The configuration for the IsoView
 * @param fn - The filter function
 *
 * @returns The first box that matches the filter function
 *
 * @group ISOBMFF
 *
 * @beta
 */
function findBox(raw, fn, config) {
    var _a;
    const recursive = (_a = config === null || config === void 0 ? void 0 : config.recursive) !== null && _a !== void 0 ? _a : true;
    if (raw instanceof DataView || raw instanceof Uint8Array || raw instanceof ArrayBuffer) {
        raw = createIsoView(raw, { ...config, recursive: false });
    }
    return find(raw, recursive, fn);
}/**
 * Find a box from an IsoView that matches a given type
 *
 * @param type - The type of box to find
 * @param raw - The raw ISO data
 * @param config - The configuration for the IsoView
 *
 * @returns The first box that matches the type
 *
 * @group ISOBMFF
 *
 * @beta
 */
function findBoxByType(raw, type, config = {}) {
    return findBox(raw, (box) => box.type === type, config);
}/**
 * Parse boxes from an IsoView
 *
 * @param raw - The raw ISO data
 * @param config - The configuration for the IsoView
 *
 * @returns The parsed boxes
 *
 * @group ISOBMFF
 *
 * @beta
 */
function parseBoxes(raw, config) {
    const boxes = [];
    for (const box of createIsoView(raw, config)) {
        boxes.push(box);
    }
    return boxes;
}/**
 * Parse a AudioRenderingIndicationBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed AudioRenderingIndicationBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function ardi(view) {
    return {
        ...view.readFullBox(),
        audioRenderingIndication: view.readUint(1),
    };
}/**
 * Parse a VisualSampleEntryBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed VisualSampleEntryBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function avc1(view) {
    const { readArray, readUint, readInt, readTemplate, readData } = view;
    return {
        reserved1: readArray(UINT, 1, 6),
        dataReferenceIndex: readUint(2),
        preDefined1: readUint(2),
        reserved2: readUint(2),
        preDefined2: readArray(UINT, 4, 3),
        width: readUint(2),
        height: readUint(2),
        horizresolution: readTemplate(4),
        vertresolution: readTemplate(4),
        reserved3: readUint(4),
        frameCount: readUint(2),
        compressorName: readArray(UINT, 1, 32),
        depth: readUint(2),
        preDefined3: readInt(2),
        config: readData(-1),
    };
}/**
 * Parse a VisualSampleEntryBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed VisualSampleEntryBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function avc2(view) {
    return avc1(view);
}/**
 * Parse a VisualSampleEntryBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed VisualSampleEntryBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function avc3(view) {
    return avc1(view);
}/**
 * Parse a VisualSampleEntryBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed VisualSampleEntryBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function avc4(view) {
    return avc1(view);
}/**
 * Parse a CompositionTimeToSampleBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed CompositionTimeToSampleBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function ctts(view) {
    const { version, flags } = view.readFullBox();
    const read = version === 1 ? view.readInt : view.readUint;
    const entryCount = view.readUint(4);
    const entries = view.readEntries(entryCount, () => ({
        sampleCount: view.readUint(4),
        sampleOffset: read(4),
    }));
    return {
        version,
        flags,
        entryCount,
        entries,
    };
}/**
 * Parse a DataReferenceBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed DataReferenceBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function dref(view) {
    const { version, flags } = view.readFullBox();
    const entryCount = view.readUint(4);
    const entries = view.readBoxes(entryCount);
    return {
        version,
        flags,
        entryCount,
        entries,
    };
}/**
 * Parse a ExtendedLanguageBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed ExtendedLanguageBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function elng(view) {
    return {
        ...view.readFullBox(),
        extendedLanguage: view.readUtf8(-1),
    };
}/**
 * Parse a Box from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed Box
 *
 * @group ISOBMFF
 *
 * @beta
 */
function elst(view) {
    const { version, flags } = view.readFullBox();
    const v1 = version === 1;
    const size = v1 ? 8 : 4;
    const entryCount = view.readUint(4);
    const entries = view.readEntries(entryCount, () => ({
        segmentDuration: view.readUint(size),
        mediaTime: view.readInt(size),
        mediaRateInteger: view.readInt(2),
        mediaRateFraction: view.readInt(2),
    }));
    return {
        version,
        flags,
        entryCount,
        entries,
    };
}/**
 * Parse an EventMessageBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed EventMessageBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function emsg(view) {
    const { readUint, readString, readData } = view;
    const result = { ...view.readFullBox() };
    if (result.version == 1) {
        result.timescale = readUint(4);
        result.presentationTime = readUint(8);
        result.eventDuration = readUint(4);
        result.id = readUint(4);
        result.schemeIdUri = readString(-1);
        result.value = readString(-1);
    }
    else {
        result.schemeIdUri = readString(-1);
        result.value = readString(-1);
        result.timescale = readUint(4);
        result.presentationTimeDelta = readUint(4);
        result.eventDuration = readUint(4);
        result.id = readUint(4);
    }
    result.messageData = readData(-1);
    return result;
}/**
 * Parse an AudioSampleEntry from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed AudioSampleEntry
 *
 * @group ISOBMFF
 *
 * @beta
 */
function mp4a(view) {
    const { readArray, readUint, readTemplate, readData } = view;
    return {
        reserved1: readArray(UINT, 1, 6),
        dataReferenceIndex: readUint(2),
        reserved2: readArray(UINT, 4, 2),
        channelcount: readUint(2),
        samplesize: readUint(2),
        preDefined: readUint(2),
        reserved3: readUint(2),
        samplerate: readTemplate(4),
        esds: readData(-1),
    };
}/**
 * Parse an AudioSampleEntry from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed AudioSampleEntry
 *
 * @group ISOBMFF
 *
 * @beta
 */
function enca(view) {
    return mp4a(view);
}/**
 * Parse a VisualSampleEntryBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed VisualSampleEntryBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function encv(view) {
    return avc1(view);
}/**
 * Parse a Box from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed Box
 *
 * @group ISOBMFF
 *
 * @beta
 */
function free(view) {
    return {
        data: view.readData(-1),
    };
}/**
 * Parse an OriginalFormatBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed OriginalFormatBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function frma(view) {
    return {
        dataFormat: view.readUint(4),
    };
}/**
 * Parse a FileTypeBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed FileTypeBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function ftyp(view) {
    const size = 4;
    const majorBrand = view.readString(4);
    const minorVersion = view.readUint(4);
    const length = view.bytesRemaining / size;
    const compatibleBrands = view.readArray(STRING, size, length);
    return {
        majorBrand,
        minorVersion,
        compatibleBrands,
    };
}/**
 * Parse a HandlerReferenceBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed HandlerReferenceBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function hdlr(view) {
    return {
        ...view.readFullBox(),
        preDefined: view.readUint(4),
        handlerType: view.readString(4),
        reserved: view.readArray(UINT, 4, 3),
        name: view.readString(-1),
    };
}/**
 * Parse a VisualSampleEntryBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed VisualSampleEntryBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function hev1(view) {
    return avc1(view);
}/**
 * Parse a VisualSampleEntryBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed VisualSampleEntryBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function hvc1(view) {
    return avc1(view);
}/**
 * Parse a WebVTTCueIdBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed WebVTTCueIdBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function iden(view) {
    return {
        cueId: view.readUtf8(-1),
    };
}/**
 * Parse a IdentifiedMediaDataBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed IdentifiedMediaDataBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function imda(view) {
    return {
        imdaIdentifier: view.readUint(4),
        data: view.readData(-1),
    };
}/**
 * Parse a TrackKinBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed TrackKindBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function kind(view) {
    return {
        ...view.readFullBox(),
        schemeUri: view.readUtf8(-1),
        value: view.readUtf8(-1),
    };
}/**
 * Parse a LabelBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed LabelBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function labl(view) {
    const { version, flags } = view.readFullBox();
    return {
        version,
        flags,
        isGroupLabel: (flags & 0x1) !== 0,
        labelId: view.readUint(2),
        language: view.readUtf8(-1),
        label: view.readUtf8(-1),
    };
}/**
 * Parse a MediaDataBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed MediaDataBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function mdat(view) {
    return {
        data: view.readData(-1),
    };
}/**
 * Parse a MediaHeaderBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed MediaHeaderBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function mdhd(view) {
    const { version, flags } = view.readFullBox();
    const creationTime = view.readUint(version == 1 ? 8 : 4);
    const modificationTime = view.readUint(version == 1 ? 8 : 4);
    const timescale = view.readUint(4);
    const duration = view.readUint(version == 1 ? 8 : 4);
    const lang = view.readUint(2);
    const language = String.fromCharCode(((lang >> 10) & 0x1F) + 0x60, ((lang >> 5) & 0x1F) + 0x60, (lang & 0x1F) + 0x60);
    const preDefined = view.readUint(2);
    return {
        version,
        flags,
        creationTime,
        modificationTime,
        timescale,
        duration,
        language,
        preDefined,
    };
}/**
 * Parse a MovieExtendsHeaderBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed MovieExtendsHeaderBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function mehd(view) {
    const { version, flags } = view.readFullBox();
    return {
        version,
        flags,
        fragmentDuration: view.readUint((version === 1) ? 8 : 4),
    };
}/**
 * Parse a MetaBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed MetaBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function meta(view) {
    return view.readFullBox();
}/**
 * Parse a MovieFragmentHeaderBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed MovieFragmentHeaderBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function mfhd(view) {
    return {
        ...view.readFullBox(),
        sequenceNumber: view.readUint(4),
    };
}/**
 * Parse a MovieFragmentRandomAccessBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed MovieFragmentRandomAccessBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function mfro(view) {
    return {
        ...view.readFullBox(),
        mfraSize: view.readUint(4),
    };
}/**
 * Parse a Box from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed Box
 *
 * @group ISOBMFF
 *
 * @beta
 */
function mvhd(view) {
    const { readUint, readTemplate, readArray } = view;
    const { version, flags } = view.readFullBox();
    const size = (version == 1) ? 8 : 4;
    return {
        version,
        flags,
        creationTime: readUint(size),
        modificationTime: readUint(size),
        timescale: readUint(4),
        duration: readUint(size),
        rate: readTemplate(4),
        volume: readTemplate(2),
        reserved1: readUint(2),
        reserved2: readArray(UINT, 4, 2),
        matrix: readArray(UINT, 4, 9),
        preDefined: readArray(UINT, 4, 6),
        nextTrackId: readUint(4),
    };
}/**
 * Parse a WebVTTCuePayloadBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed WebVTTCuePayloadBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function payl(view) {
    return {
        cueText: view.readUtf8(-1),
    };
}/**
 * Parse a ProducerReferenceTimeBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed ProducerReferenceTimeBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function prft(view) {
    const { version, flags } = view.readFullBox();
    return {
        version,
        flags,
        referenceTrackId: view.readUint(4),
        ntpTimestampSec: view.readUint(4),
        ntpTimestampFrac: view.readUint(4),
        mediaTime: view.readUint(version === 1 ? 8 : 4),
    };
}/**
 * Parse a PreselectionGroupBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed PreselectionGroupBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function prsl(view) {
    const { version, flags } = view.readFullBox();
    const groupId = view.readUint(4);
    const numEntitiesInGroup = view.readUint(4);
    const entities = view.readEntries(numEntitiesInGroup, () => ({
        entityId: view.readUint(4),
    }));
    const preselectionTag = flags & 0x1000 ? view.readUtf8(-1) : undefined;
    const selectionPriority = flags & 0x2000 ? view.readUint(1) : undefined;
    const interleavingTag = flags & 0x4000 ? view.readUtf8(-1) : undefined;
    return {
        version,
        flags,
        groupId,
        numEntitiesInGroup,
        entities,
        preselectionTag,
        selectionPriority,
        interleavingTag,
    };
}/**
 * Parse a ProtectionSystemSpecificHeaderBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed ProtectionSystemSpecificHeaderBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function pssh(view) {
    const { readUint, readArray } = view;
    const { version, flags } = view.readFullBox();
    const systemId = readArray(UINT, 1, 16);
    let kidCount = 0;
    let kid = [];
    if (version > 0) {
        kidCount = readUint(4);
        kid = readArray(UINT, 1, kidCount);
    }
    const dataSize = readUint(4);
    const data = readArray(UINT, 1, dataSize);
    return {
        version,
        flags,
        systemId,
        kidCount,
        kid,
        dataSize,
        data,
    };
}/**
 * Parse a SchemeTypeBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed SchemeTypeBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function schm(view) {
    const { version, flags } = view.readFullBox();
    return {
        version,
        flags,
        schemeType: view.readUint(4),
        schemeVersion: view.readUint(4),
        schemeUri: flags & 0x000001 ? view.readString(-1) : undefined,
    };
}/**
 * Parse a SampleDependencyTypeBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed SampleDependencyTypeBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function sdtp(view) {
    return {
        ...view.readFullBox(),
        sampleDependencyTable: view.readArray(UINT, 1, view.bytesRemaining),
    };
}/**
 * Parse a SegmentIndexBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed SegmentIndexBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function sidx(view) {
    const { readUint } = view;
    const { version, flags } = view.readFullBox();
    const v1 = version === 1;
    const size = v1 ? 8 : 4;
    const referenceId = readUint(4);
    const timescale = readUint(4);
    const earliestPresentationTime = readUint(size);
    const firstOffset = readUint(size);
    const reserved = readUint(2);
    const referenceCount = readUint(2);
    const references = view.readEntries(referenceCount, () => {
        const entry = {};
        entry.reference = readUint(4);
        entry.subsegmentDuration = readUint(4);
        entry.sap = readUint(4);
        entry.referenceType = (entry.reference >> 31) & 0x00000001;
        entry.referencedSize = entry.reference & 0x7FFFFFFF;
        entry.startsWithSap = (entry.sap >> 31) & 0x00000001;
        entry.sapType = (entry.sap >> 28) & 0x00000007;
        entry.sapDeltaTime = (entry.sap & 0x0FFFFFFF);
        return entry;
    });
    return {
        version,
        flags,
        referenceId,
        timescale,
        earliestPresentationTime,
        firstOffset,
        reserved,
        references,
    };
}/**
 * Parse a FreeSpaceBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed FreeSpaceBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function skip(view) {
    return free(view);
}/**
 * Parse a SoundMediaHeaderBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed SoundMediaHeaderBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function smhd(view) {
    return {
        ...view.readFullBox(),
        balance: view.readUint(2),
        reserved: view.readUint(2),
    };
}/**
 * Parse a SubsegmentIndexBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed SubsegmentIndexBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function ssix(view) {
    const { version, flags } = view.readFullBox();
    const subsegmentCount = view.readUint(4);
    const subsegments = view.readEntries(subsegmentCount, () => {
        const rangesCount = view.readUint(4);
        const ranges = view.readEntries(rangesCount, () => ({
            level: view.readUint(1),
            rangeSize: view.readUint(3),
        }));
        return { rangesCount, ranges };
    });
    return {
        version,
        flags,
        subsegmentCount,
        subsegments,
    };
}/**
 * Parse a SubtitleMediaHeaderBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed SubtitleMediaHeaderBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function sthd(view) {
    return view.readFullBox();
}/**
 * Parse a SampleDescriptionBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed SampleDescriptionBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function stsd(view) {
    const { version, flags } = view.readFullBox();
    const entryCount = view.readUint(4);
    return {
        version,
        flags,
        entryCount,
        entries: view.readBoxes(entryCount),
    };
}/**
 * Parse a SyncSampleBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed SyncSampleBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function stss(view) {
    const { version, flags } = view.readFullBox();
    const entryCount = view.readUint(4);
    return {
        version,
        flags,
        entryCount,
        entries: view.readEntries(entryCount, () => ({
            sampleNumber: view.readUint(4),
        })),
    };
}/**
 * Parse a WebVTTSettingsBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed WebVTTSettingsBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function sttg(view) {
    return {
        settings: view.readUtf8(-1),
    };
}/**
 * Parse a DecodingTimeToSampleBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed DecodingTimeToSampleBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function stts(view) {
    const { version, flags } = view.readFullBox();
    const entryCount = view.readUint(4);
    const entries = view.readEntries(entryCount, () => ({
        sampleCount: view.readUint(4),
        sampleDelta: view.readUint(4),
    }));
    return {
        version,
        flags,
        entryCount,
        entries,
    };
}/**
 * Parse a SegmentTypeBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed SegmentTypeBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function styp(view) {
    return ftyp(view);
}/**
 * Parse a SubSampleInformationBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed SubSampleInformationBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function subs(view) {
    const { version, flags } = view.readFullBox();
    const entryCount = view.readUint(4);
    const entries = view.readEntries(entryCount, () => {
        const sampleDelta = view.readUint(4);
        const subsampleCount = view.readUint(2);
        const subsamples = view.readEntries(subsampleCount, () => ({
            subsampleSize: view.readUint((version === 1) ? 4 : 2),
            subsamplePriority: view.readUint(1),
            discardable: view.readUint(1),
            codecSpecificParameters: view.readUint(4),
        }));
        return {
            sampleDelta,
            subsampleCount,
            subsamples,
        };
    });
    return {
        version,
        flags,
        entryCount,
        entries,
    };
}/**
 * Parse a TrackEncryptionBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed TrackEncryptionBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function tenc(view) {
    return {
        ...view.readFullBox(),
        defaultIsEncrypted: view.readUint(3),
        defaultIvSize: view.readUint(1),
        defaultKid: view.readArray(UINT, 1, 16),
    };
}/**
 * Parse a TrackFragmentDecodeTimeBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed TrackFragmentDecodeTimeBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function tfdt(view) {
    const { version, flags } = view.readFullBox();
    return {
        version,
        flags,
        baseMediaDecodeTime: view.readUint((version == 1) ? 8 : 4),
    };
}/**
 * Parse a TrackFragmentHeaderBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed TrackFragmentHeaderBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function tfhd(view) {
    const { version, flags } = view.readFullBox();
    return {
        version,
        flags,
        trackId: view.readUint(4),
        baseDataOffset: flags & 0x01 ? view.readUint(8) : undefined,
        sampleDescriptionIndex: flags & 0x02 ? view.readUint(4) : undefined,
        defaultSampleDuration: flags & 0x08 ? view.readUint(4) : undefined,
        defaultSampleSize: flags & 0x10 ? view.readUint(4) : undefined,
        defaultSampleFlags: flags & 0x20 ? view.readUint(4) : undefined,
    };
}/**
 * Parse a TrackFragmentRandomAccessBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed TrackFragmentRandomAccessBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function tfra(view) {
    const { version, flags } = view.readFullBox();
    const trackId = view.readUint(4);
    const reserved = view.readUint(4);
    const lengthSizeOfTrafNum = (reserved & 0x00000030) >> 4;
    const lengthSizeOfTrunNum = (reserved & 0x0000000C) >> 2;
    const lengthSizeOfSampleNum = (reserved & 0x00000003);
    const numberOfEntry = view.readUint(4);
    const entries = view.readEntries(numberOfEntry, () => ({
        time: view.readUint((version === 1) ? 8 : 4),
        moofOffset: view.readUint((version === 1) ? 8 : 4),
        trafNumber: view.readUint(lengthSizeOfTrafNum + 1),
        trunNumber: view.readUint(lengthSizeOfTrunNum + 1),
        sampleNumber: view.readUint(lengthSizeOfSampleNum + 1),
    }));
    return {
        version,
        flags,
        trackId,
        reserved,
        lengthSizeOfTrafNum,
        lengthSizeOfTrunNum,
        lengthSizeOfSampleNum,
        numberOfEntry,
        entries,
    };
}/**
 * Parse a TrackHeaderBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed TrackHeaderBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function tkhd(view) {
    const { version, flags } = view.readFullBox();
    const size = version === 1 ? 8 : 4;
    return {
        version,
        flags,
        creationTime: view.readUint(size),
        modificationTime: view.readUint(size),
        trackId: view.readUint(4),
        reserved1: view.readUint(4),
        duration: view.readUint(size),
        reserved2: view.readArray(UINT, 4, 2),
        layer: view.readUint(2),
        alternateGroup: view.readUint(2),
        volume: view.readTemplate(2),
        reserved3: view.readUint(2),
        matrix: view.readArray(TEMPLATE, 4, 9),
        width: view.readTemplate(4),
        height: view.readTemplate(4),
    };
}/**
 * Parse a TrackExtendsBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed TrackExtendsBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function trex(view) {
    return {
        ...view.readFullBox(),
        trackId: view.readUint(4),
        defaultSampleDescriptionIndex: view.readUint(4),
        defaultSampleDuration: view.readUint(4),
        defaultSampleSize: view.readUint(4),
        defaultSampleFlags: view.readUint(4),
    };
}/**
 * Parse a TrackRunBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed TrackRunBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function trun(view) {
    const { version, flags } = view.readFullBox();
    const sampleCount = view.readUint(4);
    let dataOffset;
    let firstSampleFlags;
    if (flags & 0x1) {
        dataOffset = view.readInt(4);
    }
    if (flags & 0x4) {
        firstSampleFlags = view.readUint(4);
    }
    const samples = view.readEntries(sampleCount, () => {
        const sample = {};
        if (flags & 0x100) {
            sample.sampleDuration = view.readUint(4);
        }
        if (flags & 0x200) {
            sample.sampleSize = view.readUint(4);
        }
        if (flags & 0x400) {
            sample.sampleFlags = view.readUint(4);
        }
        if (flags & 0x800) {
            sample.sampleCompositionTimeOffset = (version === 1) ? view.readInt(4) : view.readUint(4);
        }
        return sample;
    });
    return {
        version,
        flags,
        sampleCount,
        dataOffset,
        firstSampleFlags,
        samples,
    };
}/**
 * Parse a UrlBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed UrlBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function url(view) {
    return {
        ...view.readFullBox(),
        location: view.readString(-1),
    };
}/**
 * Parse a UrnBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed UrnBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function urn(view) {
    return {
        ...view.readFullBox(),
        name: view.readString(-1),
        location: view.readString(-1),
    };
}/**
 * Parse a WebVTTSourceLabelBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed WebVTTSourceLabelBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function vlab(view) {
    return {
        sourceLabel: view.readUtf8(-1),
    };
}/**
 * Parse a VideoMediaHeaderBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed VideoMediaHeaderBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function vmhd(view) {
    return {
        ...view.readFullBox(),
        graphicsmode: view.readUint(2),
        opcolor: view.readArray(UINT, 2, 3),
    };
}/**
 * Parse a WebVTTConfigurationBox from an IsoView
 *
 * @param view - The IsoView to read data from
 *
 * @returns A parsed WebVttConfigurationBox
 *
 * @group ISOBMFF
 *
 * @beta
 */
function vttC(view) {
    return {
        config: view.readUtf8(),
    };
}/**
 * Parse a WebVTT Empty Sample Box from an IsoView
 *
 * @returns A parsed WebVTT Empty Sample Box
 *
 * @group ISOBMFF
 *
 * @beta
 */
function vtte() {
    // Nothing should happen here.
    return {};
}const ITEM = 'Item';// 4.2.  Parsing Structured Fields
//
// 1.  Convert input_bytes into an ASCII string input_string; if
//     conversion fails, fail parsing.
//
// 2.  Discard any leading SP characters from input_string.
//
// 3.  If field_type is "list", let output be the result of running
//     Parsing a List (Section 4.2.1) with input_string.
//
// 4.  If field_type is "dictionary", let output be the result of
//     running Parsing a Dictionary (Section 4.2.2) with input_string.
//
// 5.  If field_type is "item", let output be the result of running
//     Parsing an Item (Section 4.2.3) with input_string.
//
// 6.  Discard any leading SP characters from input_string.
//
// 7.  If input_string is not empty, fail parsing.
//
// 8.  Otherwise, return output.
/**
 * Decode a structured field string into a structured field item
 *
 * @param input - The structured field string to decode
 * @returns The structured field item
 *
 * @group Structured Field
 *
 * @beta
 */
function decodeSfItem(input, options) {
    try {
        const { src, value } = parseItem(input.trim(), options);
        if (src !== '') {
            throw parseError(src, ITEM);
        }
        return value;
    }
    catch (cause) {
        throw parseError(input, ITEM, cause);
    }
}function encodeSfItem(value, params) {
    if (!(value instanceof SfItem)) {
        value = new SfItem(value, params);
    }
    return serializeItem(value);
}/**
 * Converts a ResourceTiming sample to bandwidth in bits per second (bps).
 *
 * @param sample - A ResourceTiming sample
 * @returns
 */
function getBandwidthBps(sample) {
    const durationSeconds = sample.duration / 1000;
    const bandwidthBps = sample.encodedBodySize * 8 / durationSeconds;
    return bandwidthBps;
}/**
 * Arithmetic Mean throughput estimator
 *
 * @group Throughput
 *
 * @beta
 */
class ArithmeticMeanEstimator {
    constructor() {
        this.samples = [];
    }
    sample(sample) {
        this.samples.push(sample);
    }
    getEstimate() {
        let value = 0;
        for (let i = 0; i < this.samples.length; i++) {
            value += getBandwidthBps(this.samples[i]);
        }
        return value / this.samples.length;
    }
}/**
 * Exponential Weighted Moving Average (EWMA)
 *
 * @group Throughput
 *
 * @beta
 */
class Ewma {
    constructor(alpha) {
        this.alpha = alpha;
        this.estimate = 0;
        this.totalDuration = 0;
    }
    /**
     * Samples the value of the resource and updates the estimate.
     */
    sample(weight, value) {
        const adjustedAlpha = Math.pow(this.alpha, weight);
        this.estimate = adjustedAlpha * value + (1 - adjustedAlpha) * this.estimate;
        this.totalDuration += weight;
    }
    /**
     * Returns the estimate of the value (in units, as `value` during sampling)
     *
     * For example, if the value is throughput (in bits per seconds), the estimate
     * will also be in bits per seconds.
     */
    getEstimate() {
        const zeroFactor = 1 - Math.pow(this.alpha, this.totalDuration);
        return this.estimate / zeroFactor;
    }
    /**
     * Returns the total duration of the resource (in units, as `weight` during sampling)
     */
    getTotalDuration() {
        return this.totalDuration;
    }
}/**
 * Exponential Weighted Moving Average (EWMA) throughput estimator based on 2 half-lives
 *
 * @group Throughput
 *
 * @beta
 */
class EwmaEstimator {
    constructor(options) {
        this.slowEwma = new Ewma(options.slowHalfLife);
        this.fastEwma = new Ewma(options.fastHalfLife);
    }
    sample(sample) {
        // TODO: shaka
        // 1. If `sample.encodedBodySize` is less than `this.minBytes_`, don't do anything
        // TODO: dash.js
        // 1. If `sample.encodedBodySize` is NaN or Infinity, don't do anything
        // TODO: hls.js
        // 1. If `durationSeconds` is less than `this.minDelayMs_`, make it `this.minDelayMs_` (default is 50ms)
        const durationSeconds = sample.duration / 1000;
        const bandwidthBps = getBandwidthBps(sample);
        this.slowEwma.sample(durationSeconds, bandwidthBps);
        this.fastEwma.sample(durationSeconds, bandwidthBps);
    }
    getEstimate() {
        // TODO: shaka
        // 1. Returns `defaultEstimate` for this.totalBytes < `options.minTotalBytes`
        // 1.1. Returns `options.defaultBandwidthEstimate`
        // 1.2. or `navigator.connection.downlink * 1e6` if available
        // TODO: dash.js
        // 1. Returns `NaN` for this.totalDuration <= 0
        // 2. `Math.max` for latency calculation
        // TODO: hls.js
        // 1. Returns `defaultEstimate` for this.totalDuration < `this.minDuration`
        // 1.1. Returns `config.abrEwmaDefaultEstimate` (default: 500 kbps)
        return Math.min(this.fastEwma.getEstimate(), this.slowEwma.getEstimate());
    }
    canEstimate() {
        // TODO: shaka, `this.totalBytes >= options.minTotalBytes`
        // TODO: hls.js, `this.totalDuration >= this.minDuration`
        return true;
    }
}/**
 * Harmonic Mean throughput estimator
 *
 * @group Throughput
 *
 * @beta
 */
class HarmonicMeanEstimator {
    constructor() {
        this.samples = [];
    }
    sample(sample) {
        this.samples.push(sample);
    }
    getEstimate() {
        let value = 0;
        for (let i = 0; i < this.samples.length; i++) {
            value += 1 / getBandwidthBps(this.samples[i]);
        }
        return this.samples.length / value;
    }
}/**
 * Zero-Lag Exponential Moving Average (ZLEMA) throughput estimator
 *
 * @group Throughput
 *
 * @beta
 */
class ZlemaEstimator {
    constructor() {
        this.samples = [];
    }
    sample(sample) {
        this.samples.push(sample);
    }
    getEstimate() {
        if (this.samples.length === 0) {
            return NaN;
        }
        const alpha = 2 / (this.samples.length + 1);
        let ema, zlema;
        ema = zlema = getBandwidthBps(this.samples[this.samples.length - 1]);
        for (let i = 0; i < this.samples.length; i++) {
            ema = alpha * getBandwidthBps(this.samples[i]) + (1 - alpha) * ema;
            zlema = alpha * ema + (1 - alpha) * zlema;
        }
        return zlema;
    }
}/**
 * Encodes an ArrayBuffer as a hexadecimal string.
 *
 * @param buffer - The ArrayBuffer to encode.
 * @returns The hexadecimal string representation.
 *
 * @group Utils
 *
 * @beta
 *
 * @example
 * {@includeCode ../../test/utils/arrayBufferToHex.test.ts#example}
 */
function arrayBufferToHex(buffer) {
    const view = new Uint8Array(buffer);
    return view.reduce((result, byte) => result + byte.toString(16).padStart(2, '0'), '');
}/**
 * Converts an ArrayBuffer to a UUID string.
 *
 * @param buffer - The ArrayBuffer to convert.
 * @returns The UUID string representation.
 *
 * @group Utils
 *
 * @beta
 *
 * @example
 * {@includeCode ../../test/utils/arrayBufferToUuid.test.ts#example}
 */
function arrayBufferToUuid(buffer) {
    const hex = arrayBufferToHex(buffer);
    return hex.replace(/(.{8})(.{4})(.{4})(.{4})(.{12})/, '$1-$2-$3-$4-$5');
}/**
 * Decodes a base64 encoded string into binary data
 *
 * @param str - The base64 encoded string to decode
 * @returns The decoded binary data
 *
 * @group Utils
 *
 * @beta
 *
 * @deprecated Use {@link decodeBase64} instead.
 *
 * @see {@link decodeBase64}
 */
function base64decode(str) {
    return decodeBase64(str);
}/**
 * Encodes binary data to base64
 *
 * @param binary - The binary data to encode
 * @returns The base64 encoded string
 *
 * @group Utils
 *
 * @beta
 *
 * @deprecated Use {@link encodeBase64} instead.
 *
 * @see {@link encodeBase64}
 */
function base64encode(binary) {
    return encodeBase64(binary);
}/**
 * Converts a Uint8Array to a Uint16Array by aligning its buffer.
 *
 * @param input - The Uint8Array to convert
 * @returns A properly aligned Uint16Array
 *
 * @group Utils
 * @beta
 */
function convertUint8ToUint16(input) {
    if (input.length % 2 !== 0) {
        const padded = new Uint8Array(input.length + 1);
        padded.set(input);
        return new Uint16Array(padded.buffer);
    }
    return new Uint16Array(input.buffer);
}/**
 * Text encoding types.
 *
 * @group Utils
 *
 * @beta
 */
const Encoding = {
    UTF8: UTF_8,
    UTF16: UTF_16,
    UTF16BE: UTF_16_BE,
    UTF16LE: UTF_16_LE,
};/**
 * Decodes a hexadecimal string into an ArrayBuffer.
 *
 * @param hex - The hexadecimal string to decode.
 * @returns The decoded ArrayBuffer.
 *
 * @group Utils
 *
 * @beta
 *
 * @example
 * {@includeCode ../../test/utils/hexToArrayBuffer.test.ts#example}
 */
function hexToArrayBuffer(hex) {
    const buffer = new ArrayBuffer(hex.length / 2);
    const view = new Uint8Array(buffer);
    for (let i = 0; i < hex.length; i += 2) {
        view[i / 2] = parseInt(hex.slice(i, i + 2), 16);
    }
    return buffer;
}/**
 * Generate a random v4 UUID
 *
 * @returns A random v4 UUID
 *
 * @group Utils
 *
 * @beta
 */
function uuid() {
    try {
        return crypto.randomUUID();
    }
    catch (error) {
        try {
            const url = URL.createObjectURL(new Blob());
            const uuid = url.toString();
            URL.revokeObjectURL(url);
            return uuid.slice(uuid.lastIndexOf('/') + 1);
        }
        catch (error) {
            let dt = new Date().getTime();
            const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
                const r = (dt + Math.random() * 16) % 16 | 0;
                dt = Math.floor(dt / 16);
                return (c == 'x' ? r : (r & 0x3 | 0x8)).toString(16);
            });
            return uuid;
        }
    }
}/**
 * Converts a UUID string to an ArrayBuffer.
 *
 * @param uuid - The UUID string to convert.
 * @returns The ArrayBuffer representation.
 *
 * @group Utils
 *
 * @beta
 *
 * @example
 * {@includeCode ../../test/utils/uuidToArrayBuffer.test.ts#example}
 */
function uuidToArrayBuffer(uuid) {
    const hex = uuid.replace(/-/g, '');
    return hexToArrayBuffer(hex);
}/**
 * Create a generic WebVttCue object with default values
 * that match the DOM VTTCue interface.
 *
 * @returns A WebVttCue object with default values
 *
 * @group WebVTT
 *
 * @beta
 */
function createWebVttCue() {
    return {
        id: '',
        startTime: 0,
        endTime: 0,
        region: null,
        snapToLines: true,
        line: 'auto',
        lineAlign: 'start',
        position: 'auto',
        positionAlign: 'auto',
        size: 100,
        align: 'center',
        vertical: '',
        pauseOnExit: false,
        text: '',
    };
}/**
 * Create a generic WebVttRegion object with default values
 * that match the DOM VTTRegion interface.
 *
 * @returns A WebVttRegion object with default values
 *
 * @group WebVTT
 *
 * @beta
 */
function createWebVttRegion() {
    return {
        id: '',
        width: 100,
        lines: 3,
        regionAnchorX: 0,
        regionAnchorY: 100,
        viewportAnchorX: 0,
        viewportAnchorY: 100,
        scroll: '',
    };
}/**
 * A WebVTT parsing error.
 *
 * @group WebVTT
 *
 * @beta
 */
class WebVttParsingError extends Error {
    /**
     * Create a new WebVTT parsing error.
     *
     * @param message - The message of the error.
     */
    constructor(message) {
        super(message);
        this.name = 'WebVttParsingError';
    }
}// Helper function to parse input into groups separated by 'groupDelim', and
// interpet each group as a key/value pair separated by 'keyValueDelim'.
function parseOptions(input, callback, keyValueDelim, groupDelim) {
    // TODO: Optimize parsing to avoid creating new arrays and strings.
    const groups = groupDelim ? input.split(groupDelim) : [input];
    for (const i in groups) {
        if (typeof groups[i] !== 'string') {
            continue;
        }
        const kv = groups[i].split(keyValueDelim);
        if (kv.length !== 2) {
            continue;
        }
        const k = kv[0];
        const v = kv[1].trim();
        // TODO: Return a value instead of using a callback.
        callback(k, v);
    }
}function computeSeconds(h, m, s, f) {
    return (h | 0) * 3600 + (m | 0) * 60 + (s | 0) + (f | 0) / 1000;
}
// Try to parse input as a time stamp.
function parseTimeStamp(input) {
    var _a;
    // TODO: Optimize parsing to avoid creating new arrays and strings.
    const m = input.match(/^(\d+):(\d{1,2})(:\d{1,2})?\.(\d{3})/);
    if (!m) {
        return null;
    }
    const first = parseInt(m[1]);
    const second = parseInt(m[2]);
    const third = parseInt(((_a = m[3]) === null || _a === void 0 ? void 0 : _a.replace(':', '')) || '0');
    const fourth = parseInt(m[4]);
    if (m[3]) {
        // Timestamp takes the form of [hours]:[minutes]:[seconds].[milliseconds]
        return computeSeconds(first, second, third, fourth);
    }
    else if (first > 59) {
        // Timestamp takes the form of [hours]:[minutes].[milliseconds]
        // First position is hours as it's over 59.
        return computeSeconds(first, second, 0, fourth);
    }
    else {
        // Timestamp takes the form of [minutes]:[seconds].[milliseconds]
        return computeSeconds(0, first, second, fourth);
    }
}// A settings object holds key/value pairs and will ignore anything but the first
// assignment to a specific key.
class Settings {
    constructor() {
        this.values = Object.create(null);
    }
    // Only accept the first assignment to any key.
    set(k, v) {
        if (this.get(k) || v === '') {
            return;
        }
        this.values[k] = v;
    }
    // Return the value for a key, or a default value.
    // If 'defaultKey' is passed then 'dflt' is assumed to be an object with
    // a number of possible default values as properties where 'defaultKey' is
    // the key of the property that will be chosen; otherwise it's assumed to be
    // a single value.
    get(k, dflt) {
        if (this.has(k)) {
            return this.values[k];
        }
        return dflt;
    }
    // Check whether we have a value for a key.
    has(k) {
        return k in this.values;
    }
    // Accept a setting if its one of the given alternatives.
    alt(k, v, a) {
        for (let n = 0; n < a.length; ++n) {
            if (v === a[n]) {
                this.set(k, v);
                break;
            }
        }
    }
    // Accept a setting if its a valid (signed) integer.
    integer(k, v) {
        if (/^-?\d+$/.test(v)) { // integer
            this.set(k, parseInt(v, 10));
        }
    }
    // Accept a setting if its a valid percentage.
    percent(k, v) {
        if (v.match(/^([\d]{1,3})(\.[\d]*)?%$/)) {
            const value = parseFloat(v);
            if (value >= 0 && value <= 100) {
                this.set(k, value);
                return true;
            }
        }
        return false;
    }
}const BAD_TIMESTAMP = 'Malformed time stamp.';
function parseCue(input, cue, regionList) {
    // Remember the original input if we need to throw an error.
    const oInput = input;
    // 4.1 WebVTT timestamp
    function consumeTimeStamp() {
        const ts = parseTimeStamp(input);
        if (ts === null) {
            throw new WebVttParsingError(BAD_TIMESTAMP + oInput);
        }
        // Remove time stamp from input.
        input = input.replace(/^[^\sa-zA-Z-]+/, '');
        return ts;
    }
    // 4.4.2 WebVTT cue settings
    function consumeCueSettings(input, cue) {
        const settings = new Settings();
        parseOptions(input, function (k, v) {
            switch (k) {
                case 'region': {
                    // Find the last region we parsed with the same region id.
                    for (let i = regionList.length - 1; i >= 0; i--) {
                        if (regionList[i].id === v) {
                            settings.set(k, regionList[i]);
                            break;
                        }
                    }
                    break;
                }
                case 'vertical': {
                    settings.alt(k, v, ['rl', 'lr']);
                    break;
                }
                case 'line': {
                    const vals = v.split(',');
                    const vals0 = vals[0];
                    settings.integer(k, vals0);
                    settings.percent(k, vals0) ? settings.set('snapToLines', false) : null;
                    settings.alt(k, vals0, ['auto']);
                    if (vals.length === 2) {
                        settings.alt('lineAlign', vals[1], ['start', 'center', 'end']);
                    }
                    break;
                }
                case 'position': {
                    const vals = v.split(',');
                    settings.percent(k, vals[0]);
                    if (vals.length === 2) {
                        settings.alt('positionAlign', vals[1], ['line-left', 'center', 'line-right', 'auto']);
                    }
                    break;
                }
                case 'size': {
                    settings.percent(k, v);
                    break;
                }
                case 'align': {
                    settings.alt(k, v, ['start', 'center', 'end', 'left', 'right']);
                    break;
                }
            }
        }, /:/, /\s/);
        // Apply default values for any missing fields.
        cue.region = settings.get('region', null);
        cue.vertical = settings.get('vertical', '');
        try {
            cue.line = settings.get('line', 'auto');
        }
        catch (e) {
            // eslint-ignore-line
        }
        cue.lineAlign = settings.get('lineAlign', 'start');
        cue.snapToLines = settings.get('snapToLines', true);
        cue.size = settings.get('size', 100);
        // Safari still uses the old middle value and won't accept center
        try {
            cue.align = settings.get('align', 'center');
        }
        catch (e) {
            cue.align = settings.get('align', 'middle');
        }
        try {
            cue.position = settings.get('position', 'auto');
        }
        catch (e) {
            const positions = {
                start: 0,
                left: 0,
                center: 50,
                middle: 50,
                end: 100,
                right: 100,
            };
            cue.position = settings.get('position', positions[cue.align]);
        }
        cue.positionAlign = settings.get('positionAlign', 'auto');
    }
    function skipWhitespace() {
        input = input.replace(/^\s+/, '');
    }
    // 4.1 WebVTT cue timings.
    skipWhitespace();
    cue.startTime = consumeTimeStamp(); // (1) collect cue start time
    skipWhitespace();
    if (input.substr(0, 3) !== '-->') { // (3) next characters must match "-->"
        throw new WebVttParsingError(BAD_TIMESTAMP + " (time stamps must be separated by '-->'): ");
    }
    input = input.substr(3);
    skipWhitespace();
    cue.endTime = consumeTimeStamp(); // (5) collect cue end time
    // 4.1 WebVTT cue settings list.
    skipWhitespace();
    consumeCueSettings(input, cue);
}const WebVttParserState = {
    INITIAL: 'INITIAL',
    HEADER: 'HEADER',
    REGION: 'REGION',
    STYLE: 'STYLE',
    NOTE: 'NOTE',
    BLOCKS: 'BLOCKS',
    ID: 'ID',
    CUE: 'CUE',
    CUE_TEXT: 'CUE_EXT',
    BAD_CUE: 'BAD_CUE',
    BAD_WEBVTT: 'BAD_WEBVTT',
};const BAD_SIGNATURE = 'Malformed WebVTT signature.';
const createCue = () => typeof VTTCue !== 'undefined' ? new VTTCue(0, 0, '') : createWebVttCue();
const createRegion = () => typeof VTTRegion !== 'undefined' ? new VTTRegion() : createWebVttRegion();
/**
 * A WebVTT parser.
 *
 * @group WebVTT
 *
 * @beta
 *
 * @example
 * {@includeCode ../../test/webvtt/WebVttParser.test.ts#example}
 *
 * @see {@link https://www.w3.org/TR/webvtt1/ | WebVTT Specification}
 */
class WebVttParser {
    /**
     * Create a new WebVTT parser.
     *
     * @param options - The options to use for the parser.
     */
    constructor(options = {}) {
        var _a;
        this.regionSettings = null;
        this.cue = null;
        const useDomTypes = (_a = options.useDomTypes) !== null && _a !== void 0 ? _a : true;
        this.createCue = options.createCue || useDomTypes ? createCue : createWebVttCue;
        this.createRegion = options.createRegion || useDomTypes ? createRegion : createWebVttRegion;
        this.state = WebVttParserState.INITIAL;
        this.buffer = '';
        this.style = '';
        this.regionList = [];
    }
    /**
     * Parse the given data.
     *
     * @param data - The data to parse.
     * @param reuseCue - Whether to reuse the cue.
     * @returns The parser.
     */
    parse(data, reuseCue = false) {
        var _a, _b, _c, _d, _e;
        var _f;
        // If there is no data then we will just try to parse whatever is in buffer already.
        // This may occur in circumstances, for example when flush() is called.
        if (data) {
            this.buffer += data;
        }
        const collectNextLine = () => {
            const buffer = this.buffer;
            let pos = 0;
            while (pos < buffer.length && buffer[pos] !== '\r' && buffer[pos] !== '\n') {
                ++pos;
            }
            const line = buffer.substr(0, pos);
            // Advance the buffer early in case we fail below.
            if (buffer[pos] === '\r') {
                ++pos;
            }
            if (buffer[pos] === '\n') {
                ++pos;
            }
            this.buffer = buffer.substr(pos);
            return line;
        };
        // draft-pantos-http-live-streaming-20
        // https://tools.ietf.org/html/draft-pantos-http-live-streaming-20#section-3.5
        // 3.5 WebVTT
        const parseTimestampMap = (input) => {
            var _a;
            const settings = new Settings();
            parseOptions(input, (k, v) => {
                switch (k) {
                    case 'MPEGT':
                        settings.integer(k + 'S', v);
                        break;
                    case 'LOCA':
                        settings.set(k + 'L', parseTimeStamp(v));
                        break;
                }
            }, /[^\d]:/, /,/);
            (_a = this.ontimestampmap) === null || _a === void 0 ? void 0 : _a.call(this, {
                'MPEGTS': settings.get('MPEGTS'),
                'LOCAL': settings.get('LOCAL'),
            });
        };
        // 3.2 WebVtt metadata header syntax
        const parseHeader = (input) => {
            if (input.match(/X-TIMESTAMP-MAP/)) {
                // This line contains HLS X-TIMESTAMP-MAP metadata
                parseOptions(input, (k, v) => {
                    switch (k) {
                        case 'X-TIMESTAMP-MAP':
                            parseTimestampMap(v);
                            break;
                    }
                }, /=/);
            }
        };
        // 6.1 WebVTT file parsing.
        try {
            let line;
            if (this.state === WebVttParserState.INITIAL) {
                // We can't start parsing until we have the first line.
                if (!/\r\n|\n/.test(this.buffer)) {
                    return this;
                }
                line = collectNextLine();
                // Remove the UTF-8 BOM if it exists.
                if (line.charCodeAt(0) === 0xFEFF) {
                    line = line.slice(1);
                }
                const m = line.match(/^WEBVTT([ \t].*)?$/);
                if (!m || !m[0]) {
                    throw new WebVttParsingError(BAD_SIGNATURE);
                }
                this.state = WebVttParserState.HEADER;
            }
            let alreadyCollectedLine = false;
            var sawCue = reuseCue;
            if (!reuseCue) {
                this.cue = null;
                this.regionSettings = null;
            }
            while (this.buffer) {
                // We can't parse a line until we have the full line.
                if (!/\r\n|\n/.test(this.buffer)) {
                    return this;
                }
                if (!alreadyCollectedLine) {
                    line = collectNextLine();
                }
                else {
                    alreadyCollectedLine = false;
                }
                switch (this.state) {
                    case WebVttParserState.HEADER:
                        // 13-18 - Allow a header (metadata) under the WEBVTT line.
                        if (/:/.test(line)) {
                            parseHeader(line);
                        }
                        else if (!line) {
                            // An empty line terminates the header and blocks section.
                            this.state = WebVttParserState.BLOCKS;
                        }
                        continue;
                    case WebVttParserState.REGION:
                        if (!line && this.regionSettings) {
                            // create the region
                            const region = this.createRegion();
                            region.id = this.regionSettings.get('id', '');
                            region.width = this.regionSettings.get('width', 100);
                            region.lines = this.regionSettings.get('lines', 3);
                            region.regionAnchorX = this.regionSettings.get('regionanchorX', 0);
                            region.regionAnchorY = this.regionSettings.get('regionanchorY', 100);
                            region.viewportAnchorX = this.regionSettings.get('viewportanchorX', 0);
                            region.viewportAnchorY = this.regionSettings.get('viewportanchorY', 100);
                            region.scroll = this.regionSettings.get('scroll', '');
                            // Register the region.
                            (_a = this.onregion) === null || _a === void 0 ? void 0 : _a.call(this, region);
                            // Remember the VTTRegion for later in case we parse any VTTCues that reference it.
                            this.regionList.push(region);
                            // An empty line terminates the REGION block
                            this.regionSettings = null;
                            this.state = WebVttParserState.BLOCKS;
                            break;
                        }
                        // if it's a new region block, create a new VTTRegion
                        if (this.regionSettings === null) {
                            this.regionSettings = new Settings();
                        }
                        const regionSettings = this.regionSettings;
                        // parse region options and set it as appropriate on the region
                        parseOptions(line, (k, v) => {
                            switch (k) {
                                case 'id':
                                    regionSettings.set(k, v);
                                    break;
                                case 'width':
                                    regionSettings.percent(k, v);
                                    break;
                                case 'lines':
                                    regionSettings.integer(k, v);
                                    break;
                                case 'regionanchor':
                                case 'viewportanchor':
                                    const xy = v.split(',');
                                    if (xy.length !== 2) {
                                        break;
                                    }
                                    // We have to make sure both x and y parse, so use a temporary
                                    // settings object here.
                                    const anchor = new Settings();
                                    anchor.percent('x', xy[0]);
                                    anchor.percent('y', xy[1]);
                                    if (!anchor.has('x') || !anchor.has('y')) {
                                        break;
                                    }
                                    regionSettings.set(k + 'X', anchor.get('x'));
                                    regionSettings.set(k + 'Y', anchor.get('y'));
                                    break;
                                case 'scroll':
                                    regionSettings.alt(k, v, ['up']);
                                    break;
                            }
                        }, /:/, /\s/);
                        continue;
                    case WebVttParserState.STYLE:
                        if (!line) {
                            (_b = this.onstyle) === null || _b === void 0 ? void 0 : _b.call(this, this.style);
                            this.style = '';
                            this.state = WebVttParserState.BLOCKS;
                            break;
                        }
                        this.style += line + '\n';
                        continue;
                    case WebVttParserState.NOTE:
                        // Ignore NOTE blocks.
                        if (!line) {
                            this.state = WebVttParserState.ID;
                        }
                        continue;
                    case WebVttParserState.BLOCKS:
                        if (!line) {
                            continue;
                        }
                        // Check for the start of a NOTE blocks
                        if (/^NOTE($[ \t])/.test(line)) {
                            this.state = WebVttParserState.NOTE;
                            break;
                        }
                        // Check for the start of a REGION blocks
                        if (/^REGION/.test(line) && !sawCue) {
                            this.state = WebVttParserState.REGION;
                            break;
                        }
                        // Check for the start of a STYLE blocks
                        if (/^STYLE/.test(line) && !sawCue) {
                            this.state = WebVttParserState.STYLE;
                            break;
                        }
                        this.state = WebVttParserState.ID;
                    // Process line as an ID.
                    /* falls through */
                    case WebVttParserState.ID:
                        // Check for the start of NOTE blocks.
                        if (/^NOTE($|[ \t])/.test(line)) {
                            this.state = WebVttParserState.NOTE;
                            break;
                        }
                        // 19-29 - Allow any number of line terminators, then initialize new cue values.
                        if (!line) {
                            continue;
                        }
                        sawCue = true;
                        this.cue = this.createCue();
                        (_c = (_f = this.cue).text) !== null && _c !== void 0 ? _c : (_f.text = '');
                        this.state = WebVttParserState.CUE;
                        // 30-39 - Check if this line contains an optional identifier or timing data.
                        if (line.indexOf('-->') === -1) {
                            this.cue.id = line;
                            continue;
                        }
                    // Process line as start of a cue.
                    /*falls through*/
                    case WebVttParserState.CUE:
                        // 40 - Collect cue timings and settings.
                        try {
                            parseCue(line, this.cue, this.regionList);
                        }
                        catch (e) {
                            this.reportOrThrowError(e);
                            // In case of an error ignore rest of the cue.
                            this.cue = null;
                            this.state = WebVttParserState.BAD_CUE;
                            continue;
                        }
                        this.state = WebVttParserState.CUE_TEXT;
                        continue;
                    case WebVttParserState.CUE_TEXT:
                        const hasSubstring = line.indexOf('-->') !== -1;
                        // 34 - If we have an empty line then report the cue.
                        // 35 - If we have the special substring '-->' then report the cue,
                        // but do not collect the line as we need to process the current
                        // one as a new cue.
                        if (!line || hasSubstring && (alreadyCollectedLine = true)) {
                            // We are done parsing this cue.
                            (_d = this.oncue) === null || _d === void 0 ? void 0 : _d.call(this, this.cue);
                            this.cue = null;
                            this.state = WebVttParserState.ID;
                            continue;
                        }
                        if ((_e = this.cue) === null || _e === void 0 ? void 0 : _e.text) {
                            this.cue.text += '\n';
                        }
                        this.cue.text += line.replace(/\u2028/g, '\n').replace(/u2029/g, '\n');
                        continue;
                    case WebVttParserState.BAD_CUE: // BADCUE
                        // 54-62 - Collect and discard the remaining cue.
                        if (!line) {
                            this.state = WebVttParserState.ID;
                        }
                        continue;
                }
            }
        }
        catch (e) {
            this.reportOrThrowError(e);
            // If we are currently parsing a cue, report what we have.
            if (this.state === WebVttParserState.CUE_TEXT && this.cue && this.oncue) {
                this.oncue(this.cue);
            }
            this.cue = null;
            this.regionSettings = null;
            // Enter BADWEBVTT state if header was not parsed correctly otherwise
            // another exception occurred so enter BADCUE state.
            this.state = this.state === WebVttParserState.INITIAL ? WebVttParserState.BAD_WEBVTT : WebVttParserState.BAD_CUE;
        }
        return this;
    }
    /**
     * Flush the parser.
     *
     * @returns The parser.
     */
    flush() {
        var _a;
        try {
            // Finish parsing the stream.
            this.buffer += '';
            // Synthesize the end of the current cue or region.
            if (this.cue || this.state === WebVttParserState.HEADER) {
                this.buffer += '\n\n';
                this.parse(undefined, true);
            }
            // If we've flushed, parsed, and we're still on the INITIAL state then
            // that means we don't have enough of the stream to parse the first
            // line.
            if (this.state === WebVttParserState.INITIAL) {
                throw new WebVttParsingError(BAD_SIGNATURE);
            }
        }
        catch (e) {
            this.reportOrThrowError(e);
        }
        (_a = this.onflush) === null || _a === void 0 ? void 0 : _a.call(this);
        return this;
    }
    // If the error is a ParsingError then report it to the consumer if
    // possible. If it's not a ParsingError then throw it like normal.
    reportOrThrowError(error) {
        var _a;
        if (error instanceof WebVttParsingError) {
            (_a = this.onparsingerror) === null || _a === void 0 ? void 0 : _a.call(this, error);
        }
        else {
            throw error;
        }
    }
}/**
 * Parse a WebVTT string into a WebVttParseResult.
 *
 * @param text - The WebVTT string to parse.
 * @param options - The options to use for the parser.
 * @returns The parsed WebVttParseResult.
 *
 * @group WebVTT
 *
 * @beta
 *
 * @example
 * {@includeCode ../../test/webvtt/parseWebVtt.test.ts#example}
 */
async function parseWebVtt(text, options) {
    const parser = new WebVttParser(options);
    const cues = [];
    const regions = [];
    const styles = [];
    const errors = [];
    parser.oncue = cue => cues.push(cue);
    parser.onregion = region => regions.push(region);
    parser.onstyle = style => styles.push(style);
    parser.onparsingerror = error => errors.push(error);
    parser.parse(text);
    parser.flush();
    return { cues, regions, styles, errors };
}/**
 * Convert a generic WebVTT cue to a VTTCue.
 *
 * @param cue - The WebVTT cue to convert.
 * @returns The converted VTTCue.
 *
 * @group WebVTT
 *
 * @beta
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/VTTCue | VTTCue}
 */
function toVttCue(cue) {
    const vttCue = new VTTCue(cue.startTime, cue.endTime, cue.text);
    vttCue.id = cue.id;
    vttCue.region = cue.region;
    vttCue.vertical = cue.vertical;
    vttCue.snapToLines = cue.snapToLines;
    vttCue.line = cue.line;
    vttCue.lineAlign = cue.lineAlign;
    vttCue.position = cue.position;
    vttCue.positionAlign = cue.positionAlign;
    vttCue.size = cue.size;
    vttCue.pauseOnExit = cue.pauseOnExit;
    // Safari still uses the old middle value and won't accept center
    try {
        vttCue.align = 'center';
    }
    catch (e) {
        vttCue.align = 'middle';
    }
    return vttCue;
}/**
 * Convert a WebVTT region to a VTTRegion.
 *
 * @param region - The WebVTT region to convert.
 * @returns The converted VTTRegion.
 *
 * @group WebVTT
 *
 * @beta
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/VTTRegion | VTTRegion}
 */
function toVttRegion(region) {
    const vttRegion = new VTTRegion();
    vttRegion.id = region.id;
    vttRegion.width = region.width;
    vttRegion.lines = region.lines;
    vttRegion.regionAnchorX = region.regionAnchorX;
    vttRegion.regionAnchorY = region.regionAnchorY;
    vttRegion.viewportAnchorX = region.viewportAnchorX;
    vttRegion.viewportAnchorY = region.viewportAnchorY;
    vttRegion.scroll = region.scroll;
    return vttRegion;
}/**
 * WebVTT result types.
 *
 * @group WebVTT
 *
 * @beta
 *
 * @enum
 */
const WebVttResultType = {
    CUE: 'cue',
    REGION: 'region',
    TIMESTAMP_MAP: 'timestampmap',
    STYLE: 'style',
    ERROR: 'error',
};/**
 * WebVTT transform stream transformer.
 *
 * @group WebVTT
 *
 * @beta
 */
class WebVttTransformer {
    /**
     * Creates a new WebVTT transformer.
     */
    constructor() {
        this.results = [];
        this.parser = new WebVttParser();
        this.parser.oncue = cue => this.results.push({ type: WebVttResultType.CUE, data: cue });
        this.parser.onregion = region => this.results.push({ type: WebVttResultType.REGION, data: region });
        this.parser.onstyle = style => this.results.push({ type: WebVttResultType.STYLE, data: style });
        this.parser.ontimestampmap = timestampmap => this.results.push({ type: WebVttResultType.TIMESTAMP_MAP, data: timestampmap });
        this.parser.onparsingerror = error => this.results.push({ type: WebVttResultType.ERROR, data: error });
    }
    enqueueResults(controller) {
        // TODO: Should parse errors throw?
        for (const result of this.results) {
            controller.enqueue(result);
        }
        this.results = [];
    }
    /**
     * Transforms a chunk of WebVTT data.
     *
     * @param chunk - The chunk of WebVTT data to transform.
     * @param controller - The controller to enqueue the results to.
     */
    transform(chunk, controller) {
        try {
            this.parser.parse(chunk);
            this.enqueueResults(controller);
        }
        catch (error) {
            controller.error(error);
        }
    }
    /**
     * Flushes the transformer.
     *
     * @param controller - The controller to enqueue the results to.
     */
    flush(controller) {
        try {
            this.parser.flush();
            this.enqueueResults(controller);
        }
        catch (error) {
            controller.error(error);
        }
    }
}/**
 * WebVTT transform stream.
 *
 * @group WebVTT
 *
 * @beta
 */
class WebVttTransformStream extends TransformStream {
    constructor(writableStrategy, readableStrategy) {
        super(new WebVttTransformer(), writableStrategy, readableStrategy);
    }
}/**
 * The Common Media Libray
 *
 * @packageDocumentation
 */var index=/*#__PURE__*/Object.freeze({__proto__:null,ArithmeticMeanEstimator:ArithmeticMeanEstimator,CBCS:CBCS,CENC:CENC,CHALLENGE:CHALLENGE,CLEAR_KEY_SYSTEM:CLEAR_KEY_SYSTEM,CLEAR_KEY_UUID:CLEAR_KEY_UUID,CMCD_COMMON_KEYS:CMCD_COMMON_KEYS,CMCD_DEFAULT_TIME_INTERVAL:CMCD_DEFAULT_TIME_INTERVAL,CMCD_EVENT_KEYS:CMCD_EVENT_KEYS,CMCD_EVENT_MODE:CMCD_EVENT_MODE,CMCD_FORMATTER_MAP:CMCD_FORMATTER_MAP,CMCD_HEADERS:CMCD_HEADERS,CMCD_JSON:CMCD_JSON,CMCD_KEYS:CMCD_KEYS,CMCD_OBJECT:CMCD_OBJECT,CMCD_PARAM:CMCD_PARAM,CMCD_QUERY:CMCD_QUERY,CMCD_REQUEST:CMCD_REQUEST,CMCD_REQUEST_KEYS:CMCD_REQUEST_KEYS,CMCD_REQUEST_MODE:CMCD_REQUEST_MODE,CMCD_RESPONSE_KEYS:CMCD_RESPONSE_KEYS,CMCD_RESPONSE_MODE:CMCD_RESPONSE_MODE,CMCD_SESSION:CMCD_SESSION,CMCD_STATUS:CMCD_STATUS,CMCD_V1:CMCD_V1,CMCD_V1_KEYS:CMCD_V1_KEYS,CMCD_V2:CMCD_V2,CMSD_DYNAMIC:CMSD_DYNAMIC,CMSD_STATIC:CMSD_STATIC,CMSD_V1:CMSD_V1,CONTENT_TYPE:CONTENT_TYPE,CaptionScreen:CaptionScreen,CaptionsLogger:CaptionsLogger,CmcdEncoding:CmcdEncoding,CmcdEventType:CmcdEventType,CmcdFormatters:CmcdFormatters,CmcdHeaderField:CmcdHeaderField,CmcdObjectType:CmcdObjectType,CmcdPlayerState:CmcdPlayerState,CmcdReportingMode:CmcdReportingMode,CmcdStreamType:CmcdStreamType,CmcdStreamingFormat:CmcdStreamingFormat,CmcdTransmissionMode:CmcdTransmissionMode,CmsdHeaderField:CmsdHeaderField,CmsdObjectType:CmsdObjectType,CmsdStreamType:CmsdStreamType,CmsdStreamingFormat:CmsdStreamingFormat,Cta608Channel:Cta608Channel,Cta608Parser:Cta608Parser,DATA:DATA,DEFAULT_PATHWAY_PENALTY:DEFAULT_PATHWAY_PENALTY,DEFAULT_TTL:DEFAULT_TTL,EXPIRED:EXPIRED,Encoding:Encoding,EncryptionScheme:EncryptionScheme,Ewma:Ewma,EwmaEstimator:EwmaEstimator,FAIRPLAY_KEY_SYSTEM:FAIRPLAY_KEY_SYSTEM,FAIRPLAY_UUID:FAIRPLAY_UUID,HTTP_HEADERS:HTTP_HEADERS,HW_SECURE_ALL:HW_SECURE_ALL,HW_SECURE_CRYPTO:HW_SECURE_CRYPTO,HW_SECURE_DECODE:HW_SECURE_DECODE,HarmonicMeanEstimator:HarmonicMeanEstimator,ID3_SCHEME_ID_URI:ID3_SCHEME_ID_URI,INDIVIDUALIZATION_REQUEST:INDIVIDUALIZATION_REQUEST,INT:INT,INTERNAL_ERROR:INTERNAL_ERROR,InitializationDataType:InitializationDataType,IsoView:IsoView,KEYIDS:KEYIDS,LICENSE_ACQUISITION:LICENSE_ACQUISITION,LICENSE_RELEASE:LICENSE_RELEASE,LICENSE_RENEWAL:LICENSE_RENEWAL,LICENSE_REQUEST:LICENSE_REQUEST,MP4_PROTECTION_SCHEME:MP4_PROTECTION_SCHEME,MediaKeyMessageType:MediaKeyMessageType,MediaKeyStatus:MediaKeyStatus,OUTPUT_DOWNSCALED:OUTPUT_DOWNSCALED,OUTPUT_RESTRICTED:OUTPUT_RESTRICTED,PLAYREADY_KEY_MESSAGE:PLAYREADY_KEY_MESSAGE,PLAYREADY_KEY_SYSTEM:PLAYREADY_KEY_SYSTEM,PLAYREADY_RECOMMENDATION_KEY_SYSTEM:PLAYREADY_RECOMMENDATION_KEY_SYSTEM,PLAYREADY_UUID:PLAYREADY_UUID,PenState:PenState,RELEASED:RELEASED,Row:Row,STATUS_PENDING:STATUS_PENDING,STRING:STRING,SW_SECURE_CRYPTO:SW_SECURE_CRYPTO,SW_SECURE_DECODE:SW_SECURE_DECODE,SccParser:SccParser,SfItem:SfItem,SfToken:SfToken,StyledUnicodeChar:StyledUnicodeChar,TEMPLATE:TEMPLATE,TEXT_XML_UTF8:TEXT_XML_UTF8,THUMBNAIL_TILE_SCHEME_ID_URI:THUMBNAIL_TILE_SCHEME_ID_URI,UINT:UINT,USABLE:USABLE,UTF8:UTF8,UTF_16:UTF_16,UTF_16_BE:UTF_16_BE,UTF_16_LE:UTF_16_LE,UTF_8:UTF_8,VerboseLevel:VerboseLevel,W3C_CLEAR_KEY_UUID:W3C_CLEAR_KEY_UUID,WEBM:WEBM,WIDEVINE_KEY_SYSTEM:WIDEVINE_KEY_SYSTEM,WIDEVINE_UUID:WIDEVINE_UUID,WebVttParser:WebVttParser,WebVttParsingError:WebVttParsingError,WebVttTransformStream:WebVttTransformStream,WebVttTransformer:WebVttTransformer,WidevineRobustness:WidevineRobustness,ZlemaEstimator:ZlemaEstimator,appendCmcdHeaders:appendCmcdHeaders,appendCmcdQuery:appendCmcdQuery,ardi:ardi,arrayBufferToHex:arrayBufferToHex,arrayBufferToUuid:arrayBufferToUuid,avc1:avc1,avc2:avc2,avc3:avc3,avc4:avc4,base64decode:base64decode,base64encode:base64encode,canParseId3:canParseId3,concatInitDataIdAndCertificate:concatInitDataIdAndCertificate,convertUint8ToUint16:convertUint8ToUint16,createIsoView:createIsoView,createMediaKeySystemConfiguration:createMediaKeySystemConfiguration,createWebVttCue:createWebVttCue,createWebVttRegion:createWebVttRegion,ctts:ctts,dashToHam:dashToHam,decodeBase64:decodeBase64,decodeCmcd:decodeCmcd,decodeCmsdDynamic:decodeCmsdDynamic,decodeCmsdStatic:decodeCmsdStatic,decodeFairPlayLicense:decodeFairPlayLicense,decodeIso8601Duration:decodeIso8601Duration,decodeSfDict:decodeSfDict,decodeSfItem:decodeSfItem,decodeSfList:decodeSfList,decodeText:decodeText,dref:dref,elng:elng,elst:elst,emsg:emsg,enca:enca,encodeBase64:encodeBase64,encodeCmcd:encodeCmcd,encodeCmsdDynamic:encodeCmsdDynamic,encodeCmsdStatic:encodeCmsdStatic,encodeIso8601Duration:encodeIso8601Duration,encodeSfDict:encodeSfDict,encodeSfItem:encodeSfItem,encodeSfList:encodeSfList,encv:encv,extractContentId:extractContentId,extractCta608Data:extractCta608Data,filterBoxes:filterBoxes,filterBoxesByType:filterBoxesByType,findBox:findBox,findBoxByType:findBoxByType,findCencContentProtection:findCencContentProtection,findCta608Nalus:findCta608Nalus,free:free,frma:frma,fromCmcdHeaders:fromCmcdHeaders,fromCmcdQuery:fromCmcdQuery,fromCmcdUrl:fromCmcdUrl,ftyp:ftyp,getElementsByName:getElementsByName,getId:getId,getId3Data:getId3Data,getId3Frames:getId3Frames,getId3Timestamp:getId3Timestamp,getKeySystemAccess:getKeySystemAccess,getLegacyKeySystemAccess:getLegacyKeySystemAccess,getLicenseRequestFromMessage:getLicenseRequestFromMessage,getLicenseServerUrl:getLicenseServerUrl,getLicenseServerUrlFromContentProtection:getLicenseServerUrlFromContentProtection,getPsshData:getPsshData,getPsshForKeySystem:getPsshForKeySystem,getRequestHeadersFromMessage:getRequestHeadersFromMessage,getSupportedKeySystemConfiguration:getSupportedKeySystemConfiguration,getTracksFromPresentation:getTracksFromPresentation,getTracksFromSelectionSet:getTracksFromSelectionSet,getTracksFromSwitchingSet:getTracksFromSwitchingSet,groupCmcdHeaders:groupCmcdHeaders,hamToDash:hamToDash,hamToHls:hamToHls,hdlr:hdlr,hev1:hev1,hexToArrayBuffer:hexToArrayBuffer,hlsToHam:hlsToHam,hvc1:hvc1,iden:iden,imda:imda,isCmcdCustomKey:isCmcdCustomKey,isCmcdEventKey:isCmcdEventKey,isCmcdRequestKey:isCmcdRequestKey,isCmcdResponseKey:isCmcdResponseKey,isCmcdV1Key:isCmcdV1Key,isId3TimestampFrame:isId3TimestampFrame,isValidPathwayClone:isValidPathwayClone,isValidSteeringManifest:isValidSteeringManifest,kind:kind,labl:labl,mdat:mdat,mdhd:mdhd,mehd:mehd,meta:meta,mfhd:mfhd,mfro:mfro,mp4a:mp4a,mvhd:mvhd,parseBoxes:parseBoxes,parseFrameRate:parseFrameRate,parseInitDataFromContentProtection:parseInitDataFromContentProtection,parsePsshList:parsePsshList,parseWebVtt:parseWebVtt,parseXml:parseXml,payl:payl,prepareCmcdData:prepareCmcdData,prft:prft,processUriTemplate:processUriTemplate,prsl:prsl,pssh:pssh,roundToEven:roundToEven,schm:schm,sdtp:sdtp,setDashParser:setDashParser,setDashSerializer:setDashSerializer,setHlsParser:setHlsParser,sidx:sidx,skip:skip,smhd:smhd,ssix:ssix,sthd:sthd,stringToUint16:stringToUint16,stsd:stsd,stss:stss,sttg:sttg,stts:stts,styp:styp,subs:subs,tenc:tenc,tfdt:tfdt,tfhd:tfhd,tfra:tfra,tkhd:tkhd,toBigEndianKeyId:toBigEndianKeyId,toCmcdHeaders:toCmcdHeaders,toCmcdJson:toCmcdJson,toCmcdQuery:toCmcdQuery,toCmcdReport:toCmcdReport,toCmcdUrl:toCmcdUrl,toVttCue:toVttCue,toVttRegion:toVttRegion,trex:trex,trun:trun,unescapeHtml:unescapeHtml,url:url,urlToRelativePath:urlToRelativePath,urn:urn,utf8ArrayToStr:utf8ArrayToStr,uuid:uuid,uuidToArrayBuffer:uuidToArrayBuffer,validatePresentation:validatePresentation,validateSegment:validateSegment,validateSegments:validateSegments,validateSelectionSet:validateSelectionSet,validateSelectionSets:validateSelectionSets,validateSwitchingSet:validateSwitchingSet,validateSwitchingSets:validateSwitchingSets,validateTrack:validateTrack,validateTracks:validateTracks,vlab:vlab,vmhd:vmhd,vttC:vttC,vtte:vtte});/**
 * Copyright 2024 Ceeblue B.V.
 * This file is part of https://github.com/CeeblueTV/web-utils which is released under GNU Affero General Public License.
 * See file LICENSE or go to https://spdx.org/licenses/AGPL-3.0-or-later.html for full license details.
 */
/**
 * Collects variable names for player statistics metrics across different projects (e.g., wrts, webrtc).
 * Variables remain undefined if they are not present in the stats for the current project
 * (for example, 'latency' is undefined for webrtc).
 * Includes the toCmcd() method to convert stats into a CMCD payload.
 */
class PlayerStats extends Loggable {
    /**
     * Converts the current {@link PlayerStats} snapshot into a CMCD (Common Media Client Data) payload.
     * @param url - The full URL of the media object.
     * @param trackIds -Track Id to generate the CMCD payload, keep empty to relate an CMCD OTHER object.
     * @param prevStats - Optional previous {@link PlayerStats} snapshot to calculate deltas for incremental metrics since their last reset.
     * @returns A {@link CML.Cmcd} object representing the CMCD payload.
     */
    toCmcd(url, trackIds, prevStats) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
        const cmcd = {};
        // Determine playback rate to use, preferring 'playbackRate' if available, otherwise falling back to 'playbackSpeed'
        const playBack = (_a = this.playbackRate) !== null && _a !== void 0 ? _a : this.playbackSpeed;
        let hasVideo = false;
        let hasAudio = false;
        for (const trackId of trackIds) {
            if (trackId === this.audioTrackId) {
                hasAudio = true;
            }
            else if (trackId === this.videoTrackId) {
                hasVideo = true;
            }
        }
        // Determine object type and set 'br' (bitrate): use audio, video, or sum of both depending on selected tracks
        cmcd.br = ((_b = this.audioTrackBandwidth) !== null && _b !== void 0 ? _b : 0) + ((_c = this.videoTrackBandwidth) !== null && _c !== void 0 ? _c : 0);
        if (hasAudio) {
            if (hasVideo) {
                cmcd.ot = CmcdObjectType.MUXED;
            }
            else {
                // just audio
                cmcd.ot = CmcdObjectType.AUDIO;
                cmcd.br = (_d = this.audioTrackBandwidth) !== null && _d !== void 0 ? _d : 0;
            }
        }
        else if (hasVideo) {
            // just video
            cmcd.ot = CmcdObjectType.VIDEO;
            cmcd.br = (_e = this.videoTrackBandwidth) !== null && _e !== void 0 ? _e : 0;
        }
        else {
            cmcd.ot = CmcdObjectType.OTHER;
        }
        cmcd.st = CmcdStreamType.LIVE; // Stream Type
        cmcd.cid = hash(url.pathname); // Content ID
        if (this.bufferAmount != null && playBack != null) {
            cmcd.dl = this.bufferAmount * playBack; // Deadline
        }
        if (this.stallCount != null) {
            cmcd.bs = this.stallCount - ((_f = prevStats === null || prevStats === void 0 ? void 0 : prevStats.stallCount) !== null && _f !== void 0 ? _f : 0) > 0; // Buffer Starvation
        }
        if (this.bufferAmount != null) {
            cmcd.bl = this.bufferAmount; // Buffer Length
        }
        if (this.audioByteRate != null || this.videoByteRate != null || this.dataByteRate != null) {
            // Measured mtp CMCD throughput
            cmcd.mtp = ((_g = this.audioByteRate) !== null && _g !== void 0 ? _g : 0) + ((_h = this.videoByteRate) !== null && _h !== void 0 ? _h : 0) + ((_j = this.dataByteRate) !== null && _j !== void 0 ? _j : 0);
        }
        if (playBack != null) {
            cmcd.pr = Number(playBack.toFixed(2)); // Playback Rate
        }
        // sf defaults to other ("o") if protocol is not in the map, otherwise undefined if protocol is undefined
        const sfByProtocol = {
            dash: 'd',
            hls: 'h',
            smooth: 's'
        };
        const proto = (_k = this.protocol) === null || _k === void 0 ? void 0 : _k.toLowerCase();
        if (proto != null) {
            cmcd.sf = ((_l = sfByProtocol[proto]) !== null && _l !== void 0 ? _l : 'o'); // Streaming Format
        }
        if (this.buffering != null) {
            cmcd.su = this.buffering; // Startup
        }
        return cmcd;
    }
}/**
 * Copyright 2024 Ceeblue B.V.
 * This file is part of https://github.com/CeeblueTV/web-utils which is released under GNU Affero General Public License.
 * See file LICENSE or go to https://spdx.org/licenses/AGPL-3.0-or-later.html for full license details.
 */
/**
 * An user-interface compoment to vizualize real-time metrics
 */
class UIMetrics {
    /**
     * get graph margin in pixels
     */
    get graphMargin() {
        return this._graphMargin;
    }
    /**
     * set graph margin in pixels
     */
    set graphMargin(value) {
        this._graphMargin = value;
    }
    /**
     * get text margin in pixels
     */
    get textMargin() {
        return this._textMargin;
    }
    /**
     * set text margin in pixels
     */
    set textMargin(value) {
        this._textMargin = value;
    }
    /**
     * get metric line height in pixels
     */
    get lineHeight() {
        return this._lineHeight;
    }
    /**
     * set metric line height in pixels
     */
    set lineHeight(value) {
        this._lineHeight = value;
    }
    /**
     * get label width in pixels
     */
    get labelWidth() {
        return this._labelWidth;
    }
    /**
     * set label width in pixels
     */
    set labelWidth(value) {
        this._labelWidth = value;
    }
    /**
     * get legend font size in pixels
     */
    get legendFontSize() {
        return this._legendFontSize;
    }
    /**
     * set legend font size in pixels
     */
    set legendFontSize(value) {
        this._legendFontSize = value;
    }
    /**
     * get the metric unit-step in pixels
     */
    get stepSize() {
        return this._stepSize;
    }
    /**
     * set the metric unit-step in pixels
     */
    set stepSize(value) {
        this._stepSize = value;
    }
    /**
     * Return the space width available to display average metric
     */
    get averageDisplayWidth() {
        // 7 chars (1 char width ≈ fontSize/2)
        return (this._legendFontSize / 2) * 7;
    }
    /**
     * Return the count of displayable metrics regarding the space available on the screen
     */
    get displayableCount() {
        const width = this._ui.clientWidth - this.averageDisplayWidth;
        return Math.ceil((width - this._labelWidth) / this._stepSize);
    }
    constructor(ui) {
        this._ui = ui;
        ui.addEventListener('mousemove', (event) => {
            this._mouseX = event.offsetX;
        });
        ui.addEventListener('mouseleave', (event) => {
            this._mouseX = undefined;
        });
        // default values in pixels
        this._lineHeight = 40;
        this._labelWidth = 170;
        this._graphMargin = 5;
        this._textMargin = 5;
        this._legendFontSize = 13;
        this._stepSize = 10;
        this._ranges = {};
    }
    /**
     * Reset metrics stats, essentially rescaling the metrics
     */
    reset() {
        this._ranges = {};
    }
    /**
     * build metric from stats
     * @param stats Map with stats per entry
     * @returns
     */
    display(stats) {
        if (this._html != null) {
            // CPU processing, skip one stats!
            return;
        }
        this._html = '';
        const averageDisplayWidth = this.averageDisplayWidth;
        const averageCenter = averageDisplayWidth / 2;
        const width = this._ui.clientWidth - averageDisplayWidth;
        const graphHeight = this._lineHeight - 2 * this._graphMargin;
        const graphMiddle = Math.round(this._lineHeight / 2);
        const textY = Math.round(this._lineHeight / 2 + this._textMargin);
        const titleWidth = this._labelWidth - 2 * this._textMargin;
        for (const [key, fullValues] of stats) {
            const displayableCount = Math.ceil((width - this._labelWidth) / this._stepSize);
            const values = fullValues.slice(Math.max(0, fullValues.length - displayableCount));
            if (!values.length) {
                continue;
            }
            let x = this._labelWidth + values.length * this._stepSize;
            /*
            <svg class="list-group-item p-0" style="height: 40px;" xmlns="http://www.w3.org/2000/svg">
                <text x="5" y="22">M text</text>
                <path fill="none" d="M100 0 110 22 120 0 130 20" stroke-width="1" stroke="brown"/>
            </svg>
            */
            this._html +=
                '<svg class="list-group-item p-0" style="height: ' +
                    this._lineHeight +
                    'px" xmlns="http://www.w3.org/2000/svg">';
            this._html += '<text x="' + this._textMargin + '" y="' + textY + '">' + key + '</text>';
            this._html +=
                '<text x="' +
                    titleWidth +
                    '" y="' +
                    textY +
                    '" text-anchor="end">' +
                    values[values.length - 1].toString() +
                    '</text>';
            this._html += '<path fill="none" d="M' + this._labelWidth + ' ' + graphMiddle;
            this._html += 'H' + (width + averageCenter);
            this._html += '" stroke-width="1" stroke="lightgray" stroke-dasharray="10,10"/>';
            this._html += '<path fill="none" stroke-width="1" stroke="brown" d="M';
            let min = Number.POSITIVE_INFINITY;
            let max = Number.NEGATIVE_INFINITY;
            for (let i = 0; i < values.length; ++i) {
                const value = parseFloat(values[i].toString());
                if (value < min) {
                    min = value;
                }
                if (value > max) {
                    max = value;
                }
            }
            let range = this._ranges[key];
            if (!range) {
                this._ranges[key] = range = { min: Number.POSITIVE_INFINITY, max: Number.NEGATIVE_INFINITY };
            }
            range.min = Math.min(range.min, min);
            range.max = Math.max(range.max, max);
            const delta = range.max - range.min;
            let minCircle = '';
            let maxCircle = '';
            let mouseCircle = this._mouseX == null || this._mouseX > x ? null : '';
            for (let i = 0; i < values.length; ++i) {
                x -= this._stepSize;
                const value = parseFloat(values[i].toString());
                const y = graphMiddle + (delta ? Math.round((0.5 - (value - range.min) / delta) * graphHeight) : 0);
                this._html += x + ' ' + y + ' ';
                if (value === min) {
                    maxCircle = maxCircle || this._drawCircle(x, y, value);
                }
                else if (value === max) {
                    minCircle = minCircle || this._drawCircle(x, y, value);
                }
                if (mouseCircle === '' && x <= (this._mouseX || 0)) {
                    mouseCircle = this._drawCircle(x, y, value, 'blue', '');
                }
            }
            this._html += '" />'; // end path
            // Average
            const average = Math.round((max - min) / 2);
            this._html += '<text text-anchor="middle" font-size="' + this._legendFontSize + '" y="' + textY + '">';
            this._html +=
                '<tspan x="' +
                    (width + averageCenter) +
                    '" dy="-0.5em">' +
                    (min !== max ? '≈' : '=') +
                    (min + average) +
                    '</tspan>';
            this._html += '<tspan x="' + (width + averageCenter) + '" dy="1em">±' + average + '</tspan>';
            this._html += '</text>';
            this._html += minCircle + maxCircle + (mouseCircle !== null && mouseCircle !== void 0 ? mouseCircle : '');
            if (mouseCircle) {
                this._html += mouseCircle;
            }
            this._html += '</svg>';
        }
        requestAnimationFrame(() => {
            if (this._html != null) {
                this._ui.innerHTML = this._html;
                this._html = undefined;
            }
        });
    }
    _drawCircle(x, y, value, color = 'green', fontStyle = 'italic') {
        let circle = '<circle cx="' + x + '" cy="' + y + '" r="2" fill="' + color + '" />';
        const legendFontHeight = 0.7 * this._legendFontSize;
        const graphMiddle = Math.round(this._lineHeight / 2);
        if (y < graphMiddle) {
            // legend below
            y += this.textMargin + legendFontHeight;
        }
        else {
            // legend above
            y -= this.textMargin;
        }
        circle +=
            '<text font-style="' +
                fontStyle +
                '" font-size="' +
                this._legendFontSize +
                '" x="' +
                (x - this._legendFontSize) +
                '" y="' +
                y +
                '">' +
                value +
                '</text>';
        return circle;
    }
}/**
 * Copyright 2024 Ceeblue B.V.
 * This file is part of https://github.com/CeeblueTV/web-utils which is released under GNU Affero General Public License.
 * See file LICENSE or go to https://spdx.org/licenses/AGPL-3.0-or-later.html for full license details.
 */
const VERSION = '7.8.1';export{BinaryReader,BinaryWriter,BitReader,ByteRate,index as CML,Connect,EpochTime,EventEmitter,FixMap,Log,LogLevel,Loggable,NetAddress,Numbers,PlayerStats,Queue,SDP,UIMetrics,Util,VERSION,WebSocketReliable,log};//# sourceMappingURL=web-utils.js.map
